import {
  AdminApiError,
  FLEET_QUEUE_LIST_LIMIT,
  fleetQueueListCapFired,
  type AdminClient,
  type CounterpartyAlias,
  type RecognizedCounterparty,
} from "../api.js";
import {
  chartLoadIsGenuinelyUnloaded,
  accountDisplayName,
  isLikelyBankCounterAccount,
  mountChartChooser,
  mountSingleLocation,
  moovPairableType,
  requestSlot,
  resetChartChoiceMemory,
  type ChartChooser,
} from "../chart_chooser.js";
import {
  chartLoadFromError,
  chartLoadFromSnapshot,
  type ChartLoadState,
} from "../chart_load.js";
import { clear, h, safeText } from "../dom.js";
import {
  VENDOR_SCOPE_EXTRA_CONFIRM_HELP,
  VENDOR_SCOPE_EXTRA_CONFIRM_LABEL,
  vendorScopeNeedsExtraConfirm,
} from "../vendor_rule_confirm.js";
import { matchTextCandidates } from "../match_text.js";
import { safeReceiptImageHref } from "../receipt_image.js";
import { sessionUploadCount } from "../session-uploads.js";
import {
  absentChartSnapshot,
  type AccountPath,
  type Company,
  type CompanyHealth,
  type CompanySlug,
  type QueueItem,
  type QueueReceiptContext,
  type QueueResolution,
  type QueueResolutionReviewedDecision,
  type QueueSplitPart,
  type QueueReviewedDecisionSnapshot,
  type QueueRuleKind,
  type QueueRuleScope,
  type QueueVendorClassification,
  type UntrackedCounterpartyDisposition,
  type PriorPaymentCandidate,
  type ReceiptMatchCorrectionPage,
  type RouteName,
  type Rule,
  type RuleChangeRequest,
  type RuleChangeRequestStatusFilter,
  type StaffRole,
  MOOV_CARD_SUBJECT_PREFIX,
} from "../types.js";
import {
  accountLabelAmong,
  accountLabelSet,
  companyAccountLabels,
  type AccountLabelContext,
  type CompanyAccountLabels,
  countOrMoreText,
  countText,
  countedText,
  dateText,
  localTodayIsoDate,
  moneyText,
  monthText,
  pluralText,
} from "../ui/display-words.js";
import { renderCheckNow } from "../ui/check-now.js";
import { mapWithConcurrency } from "../ui/concurrency.js";
import { createFreshnessCaption } from "../ui/freshness.js";
import type { BalancesSnapshot } from "../console/types.js";
import { measuredClearHeadline, measuredSetOf } from "../ui/measured-claim.js";
import {
  ACCOUNT_HINT,
  BANK_TEXT_NOT_ENOUGH_COPY,
  REOPEN_MONTH_BY_HAND_PREFIX,
  presenterDirectory,
  slugKey,
  MOOV_BREAKDOWN_REASON_COPY,
  WRITE_FINDING_HINT,
  cardPresentation,
  isBlockedMoovReason,
  isClosedMonthCard,
  isHoldApproveWithheld,
  holdRequiresAccountChoice,
  isMappingBlockerCard,
  isMoovApproveWithheld,
  isSetupFixCard,
  isWaitingMoovReason,
  queueAmountDecimal,
  roleAwareEscalateClause,
  MONTH_CLOSED_CHIP,
  SETUP_FIX_CHIP,
  type CardPresentation,
  type CardPresenterContext,
} from "../ui/card-presenter.js";
import { navLabel, sentence } from "../ui/sentences.js";
import { advice } from "../ui/capabilities.js";
import * as face from "../ui/face.js";
// Imported by name because page code uses `face` as a card-words variable.
import { accountLabelContext } from "../ui/face.js";
import {
  CASH_ACCOUNT_TYPES,
  decimalToCents,
  formatAsOf,
  formatCents,
  formatOutPositiveMoney,
} from "../ui/money.js";
// GRANT G15 (rd15 lane B): a card answer may split its bank line. The editor
// is the one Expense audit already uses; this page reuses it, it is not a
// second split UI.
import {
  centsToAmount,
  mountSplitEditor,
  resolvedSplitCents,
  type SplitLineDraft,
} from "../ui/split-editor.js";
import {
  cashFigure,
  isWithOwner,
  readerRole,
  reviewCards,
} from "../ui/facts.js";
import { AuthoredRefusal, failureCopy, outcomeSentence } from "../ui/failure.js";
import { runWrite, type WriteOutcome } from "../ui/write.js";

/** The one `AuthoredRefusal` class (A5a's), re-exported for this page's pins. */
export { AuthoredRefusal };
import { countOpenHubQuestions } from "./waiting.js";
import {
  defaultBookingLocation,
  HUB_LOCATION_NEEDED_RATIONALE,
} from "../../../ingest-worker/src/booking_location.js";
import {
  encodeRuleAmount,
  ruleAmountWords,
  ruleDirectionNoun,
} from "../../../ingest-worker/src/rule_amount_spec.js";
import { bookingFailedCopy, paintTodayOnboarding } from "./home.js";
import {
  takeReviewScopeHint,
  type ReviewCardRef,
  type ReviewCardSet,
} from "../ui/review-scope.js";
import {
  AM_OWNER_HIDDEN_NOTE,
  SETUP_FIX_FAMILY,
  SHOW_ALL,
  answeredWhen,
  filterPresentedCards,
  groupOrderFor,
  mountTodayFilters,
  presentQueueCard,
  renderTodayList,
  renderTodayStats,
  reviewHeadline,
  reviewPageHeader,
  splitReviewCounts,
  type GroupOrder,
  type PresentedCard,
  type TodayListFilters,
} from "./today-list.js";
import {
  ATTENTION_ESCALATE_PLACEHOLDER,
  ATTENTION_EXIT_SENTENCE,
  HARD_MATCH_REFUSE_KEEP_BANK_ESCALATION_PREFIX as WORKER_KEEP_BANK_PREFIX,
  RECEIPT_HARD_MATCH_REFUSED_SUBJECT_PREFIX as WORKER_HARD_MATCH_PREFIX,
  RECEIPT_MATCH_CONFLICT_SUBJECT_PREFIX as WORKER_CONFLICT_PREFIX,
  RECEIPT_MATCH_REVIEW_ATTACH_PREFIX as WORKER_MATCH_REVIEW_ATTACH,
  RECEIPT_MATCH_REVIEW_REJECT_PREFIX as WORKER_MATCH_REVIEW_REJECT,
  RECEIPT_MATCH_REVIEW_SUBJECT_PREFIX as WORKER_MATCH_REVIEW_PREFIX,
  closeBlockerSubject as workerCloseBlockerSubject,
  closedPeriodWriteBlockerSubjectPrefix as workerClosedPeriodWriteBlockerSubjectPrefix,
  formatPeriodLabel as workerFormatPeriodLabel,
  isBlockerQueueReason,
  isCloseLifecycleAutoClearSubject as workerIsCloseLifecycleAutoClearSubject,
  periodReopenedBlockerSubject as workerPeriodReopenedBlockerSubject,
  receiptHardMatchRefusedSubject as workerReceiptHardMatchRefusedSubject,
  receiptMatchConflictSubject as workerReceiptMatchConflictSubject,
  receiptMatchReviewSubject as workerReceiptMatchReviewSubject,
  resolveBlockerExitFamily,
  unmatchedHubGlBatchSubject as workerUnmatchedHubGlBatchSubject,
  hubGlPushRefusedSubject as workerHubGlPushRefusedSubject,
  UNMATCHED_HUB_GL_SUBJECT_PREFIX as WORKER_UNMATCHED_HUB_GL_PREFIX,
  BLOCKER_EXIT_DECLARATION as workerBlockerExitDeclaration,
} from "../../../ingest-worker/src/review_queue_blockers";
import { HUB_SOURCE_BATCH_ID_PATTERN } from "../../../packages/contracts/src/hub-source-batch-id-pattern";
import {
  amVisibleDisplayText,
  amVisibleMultilineDisplayText,
} from "../../../ingest-worker/src/am_visible_text";
import {
  CARD_CHANGED_MESSAGE,
  UNTRACKED_COUNTERPARTY_ONBOARD_CONFIRM,
  UNTRACKED_COUNTERPARTY_REASON,
  counterpartyLabelFromRationale,
  untrackedCounterpartyAliasNote,
} from "../../../ingest-worker/src/untracked_counterparty_reason";

export { FLEET_QUEUE_LIST_LIMIT, fleetQueueListCapFired };

/**
 * The Worker lists at most this many confirmed aliases, and at most this many
 * recognized counterparties, active ones first (COUNTERPARTY_ALIASES_LIST_CAP
 * in ingest-worker/src/untracked_counterparty.ts and
 * RECOGNIZED_COUNTERPARTIES_LIST_CAP in recognized_counterparty.ts; a test
 * keeps all three equal). The response has no "has more" flag.
 */
export const COUNTERPARTY_LIST_LIMIT = 500;

function counterpartyListCapped(received: number): boolean {
  return received >= COUNTERPARTY_LIST_LIMIT;
}

/**
 * Active rows come first, so the ACTIVE count is cut only when the cap fired
 * and no inactive row sits behind them; the inactive count is cut whenever the
 * cap fired.
 */
function counterpartyActiveCut(received: number, inactive: number): boolean {
  return counterpartyListCapped(received) && inactive === 0;
}

/** Re-export so portal tests can drift-check enumeration against the declaration. */
export const BLOCKER_EXIT_DECLARATION = workerBlockerExitDeclaration;

/** Fan-out concurrency for rule-approval discovery across companies. */
const RULE_APPROVAL_FANOUT = 4;

/** "All companies" as a company KEY: the empty slug, never a name. */
const ALL_COMPANIES: CompanySlug = "";

/**
 * Wire sentinels for a bare Plaid `removed` event (pipeline/worker_client.py).
 * These are "unknown," not a real date or amount — never display as facts.
 */
export const REMOVAL_WIRE_SENTINEL_DATE = "1970-01-01";

/** True when the queue item is a bank removal (eventType projection). */
export function isRemovalQueueItem(item: QueueItem): boolean {
  return item.eventType === "removed";
}

/**
 * Recoverable removal: original ledger entry was found at enqueue time, so
 * suggestedAccount carries the category to reverse. Unrecoverable: no
 * category leg — AM cannot reverse alone; escalate (or decline → terminal).
 */
export function isRecoverableRemoval(item: QueueItem): boolean {
  return isRemovalQueueItem(item) && Boolean(item.suggestedAccount);
}

export function isWireSentinelDate(txnDate: string | undefined): boolean {
  return txnDate === REMOVAL_WIRE_SENTINEL_DATE;
}

/** Zero / 0.00 wire placeholder on a bare removed event. */
export function isWireSentinelAmount(amount: string | undefined): boolean {
  if (amount === undefined || amount === "") return false;
  return /^-?0+(\.0+)?$/.test(amount);
}

/**
 * reason (+ suggestion / eventType when completability depends on them) →
 * AM question → affirmative control. Removals share reason `anomaly` with
 * other anomalies; eventType decides. Completability is keyed on
 * (reason, has-complete-suggestion) for categorisation rows — a suggestion
 * is complete only when both account AND location are resolved. State the
 * pair, do not collapse to reason alone; do not let a single tap close an
 * item that still carries an unresolved determination (K12-OP-1 / R12c).
 */
export const QUEUE_REASON_ENUMERATION = [
  {
    reason: "new-vendor",
    hasSuggestion: false as const,
    question: "What was this?",
    affirmative:
      "Use this account (account entry required). No Approve suggestion — this row carries none. Server refuses approve-with-no-account (400). Completes when AM enters an account.",
  },
  {
    // R28R-2: the row above used to claim new-vendor "never has
    // suggested_account by construction (not in_coa)". That was false when
    // written — `contracts/fixtures/queue/review-queue-rows.json` row 1 is
    // this shape, and the producer at pipeline/sync.py minted it until #702.
    // The claim also authorised the cartesian skip that hid it. Historical
    // D1 rows still carry it, so this row is reachable, not hypothetical.
    reason: "new-vendor",
    hasSuggestion: true as const,
    question: "What was this?",
    affirmative:
      "No Yes one-tap even when a complete suggestion is present. Account box is not pre-filled with the suggested account (K12-OP-2: the rejected string must not become the approval default). Location pre-fill may stay. Fields open. Completes when AM enters an account. Server refuses approve-with-no-account (400).",
  },
  {
    reason: "low-confidence",
    hasSuggestion: true as const,
    question: "What was this?",
    affirmative: "Yes — {suggestedAccount} / Use this account",
  },
  {
    reason: "low-confidence",
    hasSuggestion: true as const,
    when: "legacy booking-failure recovery (ARF1 prefix; pre-GRANT-14 rows that borrowed low-confidence — not reclassified)",
    question: "This approval could not be booked",
    // DR26P-7 = B: this row is a full recovery card. Identity is
    // categorisationCardKind === "recovery" (reason OR column-0 ARF1,
    // after hub-proposal). Tests must check these clauses against the
    // rendered card. Owner 2026-08-22 option (i): the card stays
    // recovery; Previous attempt / Use it again do not — those values
    // were not a human choice.
    recoveryCard: true as const,
    withholdsYes: true as const,
    causeOnFace: true as const,
    prefillSplitLeg: false as const,
    prefillFailedAnswer: false as const,
    previousAttemptAsFact: false as const,
    useItAgainWhenReusable: false as const,
    typedEditFailsClosedOnEmptyLocation: true as const,
    lumpEditLabel: "Book whole deposit to this account",
    affirmative:
      "No Yes one-tap. Cause on the card face. Full recovery card (DR26P-7 = B) — identity is still recovery, not ordinary. Previous attempt is not a labeled fact on this arm and Use it again is absent: suggestedAccount / suggestedLocation came from a model, a rule, or a receipt, not a person. The AM types the account and location. Previous attempt is not in the inputs. The Location metadata row does not show the suggested leaf (Date, Merchant, and Descriptor stay; Location (not configured) still names an unconfigured leaf; receipt property / location stays). Use this account submits a listed membership name; a miss does not POST; empty fields fail closed (Account is required; Location is required). Blanket retry warning: retrying may not succeed. Named unblocker only for period_locked (reopen) and hub_split (split residual). booking_failed, unknown surface, and missing ARF1 claim nothing — the cause is above; they do not name bean-check / metadata lint as the unblocker. Period-lock copy keeps its specific reopen sentence. Hub-split / lump recovery: Book whole deposit to this account; no previous-attempt fact; no Use it again (suggestedAccount is a split leg). Escalate remains. Confirmation names the lump. A hub_split lump is recorded and booked on the next run (the split is not the unblocker of that write). period_locked still names reopen. Never 'Booked to' as a completion: a simple Book only approves the card and the booking happens on the next run. Confirmation of a typed pair does not claim a previous attempt.",
  },
  {
    reason: "booking-failed",
    hasSuggestion: true as const,
    when: "GRANT (14) booking-failure recovery (reason=booking-failed is the identity; ARF1 is payload)",
    question: "This approval could not be booked",
    recoveryCard: true as const,
    withholdsYes: true as const,
    causeOnFace: true as const,
    prefillSplitLeg: false as const,
    prefillFailedAnswer: false as const,
    previousAttemptAsFact: true as const,
    useItAgainWhenReusable: true as const,
    typedEditFailsClosedOnEmptyLocation: true as const,
    lumpEditLabel: "Book whole deposit to this account",
    affirmative:
      "No Yes one-tap. Cause on the card face. Previous attempt is a labeled fact (account / location) when reusable — recovery, reason=booking-failed (GRANT-14; not the ARF1 low-confidence arm), not lump, both suggestedAccount and suggestedLocation present — and a display-only fact when the account half is held and location is absent (not lump). Display is per-half; Use it again is per-pair. Previous attempt is not in the inputs. Use it again one-click posts that remembered pair without filling the inputs, and is absent on a half pair. Use this account submits a listed membership name; a miss does not POST; empty fields fail closed (Account is required; Location is required). Use it again still one-click posts the remembered location without filling the inputs. Blanket retry warning: retrying may not succeed. Named unblocker only for period_locked (reopen) and hub_split (split residual). booking_failed, unknown surface, and missing ARF1 claim nothing — the cause is above; they do not name bean-check / metadata lint as the unblocker. Period-lock copy keeps its specific reopen sentence. Hub-split / lump recovery: Book whole deposit to this account; no previous-attempt fact; no Use it again (suggestedAccount is a split leg). Escalate remains. Confirmation names the lump. A hub_split lump is recorded and booked on the next run (the split is not the unblocker of that write). period_locked still names reopen. Never 'Booked to' as a completion: a simple Book only approves the card and the booking happens on the next run.",
  },
  {
    reason: "low-confidence",
    hasSuggestion: false as const,
    when: "location unresolved (suggested_account present, suggested_location absent)",
    question: "What was this?",
    affirmative:
      "Use this account (location entry required). No Yes — when location is unresolved; the unresolved location is shown on the card. Completes when AM enters a configured location.",
  },
  {
    reason: "low-confidence",
    hasSuggestion: false as const,
    question: "What was this?",
    affirmative:
      "Use this account (account entry required). No Approve suggestion when suggested_account is absent. Server refuses approve-with-no-account (400).",
  },
  {
    reason: "anomaly",
    when: "eventType !== removed",
    hasSuggestion: false as const,
    question: "What was this?",
    affirmative:
      "Use this account (account entry required). No Approve suggestion when suggested_account is absent. Server refuses approve-with-no-account (400).",
  },
  {
    reason: "anomaly",
    when: "eventType !== removed",
    hasSuggestion: true as const,
    question: "What was this?",
    affirmative: "Yes — {suggestedAccount} / Use this account",
  },
  {
    reason: "anomaly",
    when: "eventType === removed && recoverable",
    question: "Bank says this transaction never happened — reverse it?",
    affirmative: "Reverse it (mirrors original; no re-categorise)",
  },
  {
    reason: "anomaly",
    when: "eventType === removed && !recoverable",
    question: "Bank removed a transaction we cannot identify",
    affirmative: "Hand off to the owner (or Decline — terminal, no reversal)",
  },
  {
    reason: "possible-personal",
    hasSuggestion: true as const,
    question: "What was this?",
    affirmative: "Yes — {suggestedAccount} / Use this account",
  },
  {
    reason: "possible-personal",
    hasSuggestion: false as const,
    question: "What was this?",
    affirmative:
      "Use this account (account entry required). No Approve suggestion when suggested_account is absent. Server refuses approve-with-no-account (400).",
  },
  {
    reason: "unmatched-deposit",
    hasSuggestion: false as const,
    when: "suggestion incomplete (account or location unresolved)",
    question: "Approve this hub GL split?",
    affirmative:
      "No Approve — completeness is the offerability key, not identity. Lump warning retained. Something else… / Book whole deposit (fields already open). Completes when AM enters account and location.",
  },
  {
    // First complete unmatched-deposit row is legs-absent so a find()
    // keyed only on hasSuggestion cannot keep passing by accident
    // (R29R-1). Discriminate on `when:`.
    reason: "unmatched-deposit",
    hasSuggestion: true as const,
    when: "stored proposal missing or unparseable",
    question: "Approve this hub GL split?",
    affirmative:
      "No Approve. Cannot-approve line (stored proposal missing or unparseable). Lump warning retained. Something else… still edits to one account (lump; batch stays pending). Account is not pre-filled with a split leg.",
  },
  {
    reason: "unmatched-deposit",
    hasSuggestion: true as const,
    when: "stored legs rendered",
    question: "Approve this hub GL split?",
    affirmative:
      "Approve this hub GL split (posts the stored multi-leg proposal, not suggestedAccount). Split is on the card face. Something else… still edits to one account (lump; batch stays pending) — the lump consequence is on the face and in the edit confirmation. Account is not pre-filled with a split leg.",
  },
  {
    reason: "close-blocked",
    subjectFamily: "Month-close blocked — …",
    question: "subject (blocker card) + body",
    affirmative:
      "Escalate with note (required). No categorisation controls. Clears automatically when the close succeeds (lifecycle writer).",
  },
  {
    reason: "close-blocked",
    subjectFamily: "Period reopened — re-close required — …",
    question: "subject (blocker card) + body",
    affirmative:
      "Escalate with note (required). No categorisation controls. Clears automatically when the close succeeds (lifecycle writer on re-close).",
  },
  {
    reason: "close-blocked",
    subjectFamily: "Receipt match conflict — …",
    question: "subject (blocker card) + body",
    affirmative:
      "Escalate with note (required). No categorisation controls. Clears automatically when the conflict is gone (system clearer).",
  },
  {
    reason: "close-blocked",
    subjectFamily: "Receipt hard-match refused — …",
    question: "subject (blocker card) + body",
    affirmative:
      'Escalate with a note starting with "keep-bank:" (required). No categorisation controls. That disposition clears on the next match pass.',
  },
  {
    reason: "close-blocked",
    subjectFamily: "Receipt match review — …",
    question: "subject (blocker card) + body",
    affirmative:
      "Confirm the match, pick the right transaction from the candidates, or reject it (Not this transaction — the card transaction returns to the missing-receipt shelf). Hand off with a note (required) when the owner must see the pairing. No categorisation controls. This card does not clear on its own.",
  },
  {
    reason: "close-blocked",
    subjectFamily: "Closed-period write blocked — …",
    question: "subject (blocker card) + body",
    affirmative:
      "Escalate with note (required). No categorisation controls. Clears automatically when the period is reopened (system:period-reopened).",
  },
  {
    reason: "close-blocked",
    subjectFamily: "Missing Plaid account_id — … / Unmapped Plaid account — …",
    question: "subject (blocker card) + body",
    affirmative:
      "No control on the card fixes it: neither Escalate nor Send to practice is offered, for either role (round-3 decision). No categorisation controls. Clears automatically on the Worker side; the Unmapped row names the mapping clearer, the Missing account_id row states only that the line clears when it is processed again. The Missing account_id card does not claim a connection clears it. There is no action on this card.",
  },
  {
    reason: "close-blocked",
    subjectFamily: "Unmatched hub GL batch — …",
    question: "subject (blocker card) + body",
    affirmative:
      "Escalate with note (required). No categorisation controls. When remaining candidates exist, clears automatically when a deposit allocates the batch. When remaining_candidates=0 and consumed=0, nothing has arrived yet that can allocate — waiting may still work; the card does not promise an automatic clear. A net-zero batch has no deposit and will not clear that way. Missing or unparseable census also will not clear automatically. A batch whose candidate deposits were consumed without allocating (remaining=0 consumed=1) does not currently have a deposit; a later posted inflow in the period can still allocate — escalate if waiting cannot.",
  },
  {
    reason: "close-blocked",
    subjectFamily: "Hub GL push refused — …",
    question: "subject (blocker card) + body",
    affirmative:
      "Escalate with note (required). No categorisation controls. Clears automatically when a later hub GL push for this period is accepted and stored (system:hub-gl-push-accepted). Quote the request id to books-support.",
  },
  {
    reason: "statement-blocked",
    subjectFamily: "Statement reconciliation blocked — …",
    question: "subject (blocker card) + body",
    affirmative:
      "Escalate with note (required). No categorisation controls. Clears after statement confirmation and successful assertion dispatch.",
  },
  {
    reason: "statement-blocked",
    subjectFamily: "Stale published assertion — …",
    question: "subject (blocker card) + body",
    affirmative:
      "Escalate with note (required). No categorisation controls. Does not clear when you confirm the replacement statement. The published assertion stays until a reviewed correction of that publication (Owner reopen if the period is locked).",
  },
  {
    reason: "escrow-balance",
    subjectFamily: "Escrow balance — …",
    question: "subject (blocker card) + body",
    affirmative:
      "Record a disbursement (date, amount, kind). Card stays until the books agree. Escalate with note (required). Send to practice asks them — this card returns still open. No categorisation controls. No confirm control — this is not a categorisation.",
  },
  {
    reason: "untracked-counterparty",
    hasSuggestion: false as const,
    question: "{counterparty} is not a company we track",
    affirmative:
      "Mark as external writes a recognized_counterparties row (not a companies row; we do not keep books for them). Create company writes company_drafts after the owner's onboard confirm string. This is an existing company records a confirmed alias (AM pick, never inferred). No Yes one-tap. No account. Not sent to practice.",
  },
  {
    reason: "receipt_card_not_attached",
    hasSuggestion: true as const,
    question: "Book this intercompany pair?",
    affirmative:
      "The receipt property, matched company, and pre-filled pair are on the card. Booking saves that pair. Editing books the account you pick instead. Nothing posts until you decide. The card is not attached to the receipt property.",
  },
] as const;

export const RECEIPT_CARD_NOT_ATTACHED_COPY =
  "This charge was paid on a card that is not attached to the receipt property. Booking saves the intercompany pair shown here. Editing books the account you pick instead. Nothing posts until you decide.";

/**
 * The one want-to-put-it-off word (docs/design/rd-polish-rules.md §1:
 * "Skip for now"). The action keeps `data-action="later"`: the control did not
 * move, only what it says.
 */
export const SKIP_FOR_NOW_LABEL = "Skip for now";

/**
 * The group every card the console cannot answer is listed under (Y5 brief
 * item 4): "Setup fixes". One header for the state, so those cards stop
 * mixing with the questions a person can answer here.
 */
export const SETUP_FIX_GROUP = SETUP_FIX_FAMILY;
export const SETUP_FIX_GROUP_LABEL = "Setup fixes";

/**
 * What dismissal really does, for the cards in that group: it books nothing
 * and the card does not come back for the same payment. It does not claim the
 * month can be reopened from here.
 */
export const BULK_DISMISS_SETUP_COPY =
  "These cards cannot be answered here. Dismissing keeps nothing booked.";

/**
 * The hint under the existing-company picker. The owner is never told to
 * escalate to himself (Y5 brief item 7); the account manager keeps the
 * hand-off. An unlisted company still cannot be submitted from the card.
 */
export function existingCompanyAliasHint(args: {
  companiesLoaded: boolean;
  choiceCount: number;
  role: StaffRole;
}): string {
  if (!args.companiesLoaded) {
    // The owner sees the same failed list, so telling the account manager to
    // escalate would send both to a person who cannot load it either. The fact
    // is the same for both roles.
    return "Could not load companies. An unlisted company cannot be submitted from this card.";
  }
  if (args.choiceCount === 0) return "No other companies are listed to pick.";
  return "Pick the company this label should resolve to. Nothing is pre-selected.";
}

/**
 * Extract the AI-proposed location that failed the membership gate.
 * Pipeline writes ``location 'X' is not configured for this company`` into
 * ai_rationale and deliberately leaves suggested_location null (the rejected
 * string must never become the approval default). Display that unresolved
 * determination on the card face — not inside the collapsed disclosure.
 *
 * Anchored at start-of-rationale (or after a prior newline) so a merchant
 * string that happens to contain the same phrase later cannot displace the
 * pipeline-emitted determination.
 */
export function unresolvedLocationFromRationale(
  aiRationale: string | undefined,
): string | null {
  if (!aiRationale) return null;
  const match =
    /(?:^|\n)location ['"](.+?)['"] is not configured/i.exec(aiRationale);
  return match?.[1] ?? null;
}

/** True when both booking determinations (account + location) are suggested. */
export function hasCompleteSuggestion(item: QueueItem): boolean {
  return Boolean(item.suggestedAccount) && Boolean(item.suggestedLocation);
}

/**
 * Hub-proposal identity is reason `unmatched-deposit` (#612 / GRANT 10).
 * Completeness (account+location) is offerability, not identity —
 * `canOneTapHubSplit` carries that conjunct. Incomplete unmatched-deposit
 * is still hub-proposal (lump warning; no Approve).
 */
export function isHubGlProposalItem(item: QueueItem): boolean {
  return item.reason === "unmatched-deposit";
}

/** Matches pipeline/rationale_markers.py HUB_APPLIED_LINE_PREFIX. */
export const HUB_APPLIED_LINE_PREFIX = "HGL1";

/** Matches pipeline/rationale_markers.py LOAN_STATEMENT_LINE_PREFIX. */
export const LOAN_STATEMENT_LINE_PREFIX = "LS1";

/** GRANT (17) full-match of the imported pattern. Do not copy. Do not narrow. */
const HUB_SOURCE_BATCH_ID_RE = new RegExp(HUB_SOURCE_BATCH_ID_PATTERN);

/**
 * Matches pipeline/rationale_markers.py BOOKING_FAILURE_LINE_PREFIX.
 * Payload inside ai_rationale, written only by
 * `_enqueue_approved_booking_failure`. Card identity is `reason`
 * (owner 2026-08-17 / PGS26C-3). Not a new API field.
 */
export const BOOKING_FAILURE_LINE_PREFIX = "ARF1";

export const BOOKING_FAILURE_SURFACES = [
  "booking_failed",
  "hub_split",
  "period_locked",
] as const;

/** Matches pipeline/rationale_markers.py LUMP_CLAIM_FLAG. */
export const LUMP_CLAIM_FLAG = "lump";

export type BookingFailureSurface = (typeof BOOKING_FAILURE_SURFACES)[number];

export type BookingFailureRationale = {
  surface: string;
  cause: string;
  lump: boolean;
};

/** Drop a single trailing U+000D. Recognition never trims anything else. */
export function dropTrailingCR(line: string): string {
  return line.endsWith("\r") ? line.slice(0, -1) : line;
}

/** True when a rationale line is PREFIX or PREFIX+TAB at column 0. */
export function isReservedRationaleLine(line: string): boolean {
  const body = dropTrailingCR(line);
  const prefixes = [
    BOOKING_FAILURE_LINE_PREFIX,
    HUB_APPLIED_LINE_PREFIX,
    LOAN_STATEMENT_LINE_PREFIX,
  ] as const;
  return prefixes.some(
    (prefix) => body === prefix || body.startsWith(`${prefix}\t`),
  );
}

/** QueueResolution.note maxLength in contracts/books-api.yaml. GRANT (12) VOID if raised. */
export const QUEUE_RESOLUTION_NOTE_MAX = 500;

export type StoredHubAppliedLeg = { account: string; amount: string };

export type StoredHubApplied = {
  sourceBatchId: string;
  lineSetSha256Hex: string;
  applied: StoredHubAppliedLeg[];
  encoding: string;
};

/** Parse the HGL1 stored-split line the writer will post. */
export function parseStoredHubApplied(
  text: string | undefined,
): StoredHubApplied | null {
  if (!text) return null;
  let encoding: string | null = null;
  for (const raw of text.split("\n")) {
    const line = dropTrailingCR(raw);
    if (line.startsWith(`${HUB_APPLIED_LINE_PREFIX}\t`)) {
      encoding = line;
      break;
    }
  }
  if (encoding === null) return null;
  const parts = encoding.split("\t");
  const sourceBatchId = parts[1];
  const digest = parts[2];
  const legsText = parts[3];
  if (
    parts.length !== 4 ||
    parts[0] !== HUB_APPLIED_LINE_PREFIX ||
    sourceBatchId === undefined ||
    digest === undefined ||
    legsText === undefined
  ) {
    return null;
  }
  if (
    HUB_SOURCE_BATCH_ID_RE.exec(sourceBatchId)?.[0] !== sourceBatchId ||
    digest.length !== 64 ||
    !/^[0-9a-f]+$/.test(digest) ||
    !legsText
  ) {
    return null;
  }
  const applied: StoredHubAppliedLeg[] = [];
  const seen = new Set<string>();
  for (const leg of legsText.split(";")) {
    const eq = leg.indexOf("=");
    if (eq <= 0) return null;
    const account = leg.slice(0, eq);
    const amount = leg.slice(eq + 1);
    if (!account || seen.has(account) || !/^-?\d+(\.\d+)?$/.test(amount)) {
      return null;
    }
    const amountNumber = Number(amount);
    // Predicate only — the carried leg amount stays the original string.
    // Number.isFinite is defense behind the regex, not a money conversion.
    if (!Number.isFinite(amountNumber) || amountNumber === 0) {
      return null;
    }
    seen.add(account);
    applied.push({ account, amount });
  }
  if (applied.length === 0) return null;
  return {
    sourceBatchId,
    lineSetSha256Hex: digest,
    applied,
    encoding,
  };
}

/** Matches pipeline.loan_statement._WIRE_AMOUNT_RE. */
const LOAN_STATEMENT_WIRE_AMOUNT_RE =
  /^-?(0|[1-9]\d{0,12})(\.\d{1,2})?$/;

export type StoredLoanStatementLeg = {
  amountRaw: string;
  amount: number | null;
  citation: string;
  absent: boolean;
};

export type StoredLoanStatement = {
  section: "txn_table" | "paid_last_month";
  column: string;
  principal: StoredLoanStatementLeg;
  interest: StoredLoanStatementLeg;
  escrow: StoredLoanStatementLeg;
  fees: StoredLoanStatementLeg;
  encoding: string;
};

/** True when column-0 of the first U+000A line is LS1 or LS1+TAB. */
export function looksLikeLs1(text: string | null | undefined): boolean {
  if (!text) return false;
  const first = dropTrailingCR(text.split("\n")[0] ?? "");
  return (
    first === LOAN_STATEMENT_LINE_PREFIX ||
    first.startsWith(`${LOAN_STATEMENT_LINE_PREFIX}\t`)
  );
}

function parseLoanStatementComponent(
  raw: string,
  expectedPrefix: "P" | "I" | "E" | "F",
): StoredLoanStatementLeg | null {
  const pipe = raw.indexOf("|");
  if (pipe < 0) return null;
  const kind = raw.slice(0, pipe);
  const citation = raw.slice(pipe + 1);
  if (!citation) return null;
  const eq = kind.indexOf("=");
  if (eq < 0) return null;
  const prefix = kind.slice(0, eq);
  const value = kind.slice(eq + 1);
  if (prefix !== expectedPrefix || !value) return null;
  if (value === "ABSENT") {
    if (expectedPrefix !== "E") return null;
    return { amountRaw: "ABSENT", amount: null, citation, absent: true };
  }
  if (!LOAN_STATEMENT_WIRE_AMOUNT_RE.test(value)) return null;
  const amount = Number(value);
  if (!Number.isFinite(amount)) return null;
  if (expectedPrefix === "E" && amount <= 0) return null;
  return { amountRaw: value, amount, citation, absent: false };
}

/** Parse an LS1 line. Scans every U+000A line; drops one trailing U+000D. */
export function parseStoredLoanStatement(
  text: string | null | undefined,
): StoredLoanStatement | null {
  if (!text) return null;
  let encoding: string | null = null;
  for (const raw of text.split("\n")) {
    const line = dropTrailingCR(raw);
    if (line.startsWith(`${LOAN_STATEMENT_LINE_PREFIX}\t`)) {
      encoding = line;
      break;
    }
  }
  if (encoding === null) return null;
  const parts = encoding.split("\t");
  if (parts.length !== 7 || parts[0] !== LOAN_STATEMENT_LINE_PREFIX) {
    return null;
  }
  const section = parts[1];
  const column = parts[2];
  const principalRaw = parts[3];
  const interestRaw = parts[4];
  const escrowRaw = parts[5];
  const feesRaw = parts[6];
  if (section !== "txn_table" && section !== "paid_last_month") return null;
  if (!column || column.includes("\n") || column.includes("\t")) return null;
  if (
    principalRaw === undefined ||
    interestRaw === undefined ||
    escrowRaw === undefined ||
    feesRaw === undefined
  ) {
    return null;
  }
  const principal = parseLoanStatementComponent(principalRaw, "P");
  const interest = parseLoanStatementComponent(interestRaw, "I");
  const escrow = parseLoanStatementComponent(escrowRaw, "E");
  const fees = parseLoanStatementComponent(feesRaw, "F");
  if (!principal || !interest || !escrow || !fees) return null;
  if (principal.absent || interest.absent || fees.absent) return null;
  return {
    section,
    column,
    principal,
    interest,
    escrow,
    fees,
    encoding,
  };
}

export function loanStatementAccount(queueId: number): string {
  return `LoanStmt:Q${String(queueId)}`;
}

export function loanStatementPaidSectionPhrase(
  stored: StoredLoanStatement,
): string {
  if (stored.section === "txn_table") {
    return "the dated transaction table (PAYMENT / Collection rows)";
  }
  return `Past payment breakdown → ${amVisibleDisplayText(stored.column)}`;
}

/** Strip machine HGL1 / ARF1 / LS1 lines so the disclosure stays human-readable. */
export function displayAiRationale(text: string | undefined): string {
  if (!text) return "";
  return text
    .split("\n")
    .filter((line) => !isReservedRationaleLine(line))
    .join("\n")
    .trim();
}

/**
 * Ordinary loan-character cards may offer a PDF upload. `:Loans:` is the
 * CoA leaf; loan / mortgage / escrow are the census words in bank text.
 */
export function hasLoanStatementCharacter(item: QueueItem): boolean {
  if (item.suggestedAccount?.includes(":Loans:")) return true;
  const haystack = [
    item.descriptor,
    item.merchant ?? "",
    displayAiRationale(item.aiRationale),
  ]
    .join("\n")
    .toLowerCase();
  return /\b(?:loan|mortgage|escrow)\b/.test(haystack);
}

/**
 * Column-0 prefix written by decide_from_proposal when evidence, not the
 * frozen reason enum, queued the card (R35O-2). Lockstep with
 * pipeline.ai_categorizer.EVIDENCE_QUEUE_RATIONALE_PREFIX.
 */
export const EVIDENCE_QUEUE_RATIONALE_PREFIX =
  "corroboration does not answer this booking (";

/**
 * Column-0 prefix when mortgage-statement extraction is not bookable.
 * Lockstep with pipeline.loan_statement.LOAN_STATEMENT_UNREAD_RATIONALE_PREFIX.
 * Not a reserved identity line — #878 / R35O-2: name the cause on the card
 * without a new review_queue.reason.
 */
export const LOAN_STATEMENT_UNREAD_RATIONALE_PREFIX =
  "could not read the statement (";

/** Token after the prefix (`no_corroboration`, `judgment_equity_needs_character`, …). */
export function parseEvidenceQueueWhy(
  text: string | undefined,
): string | null {
  const body = displayAiRationale(text);
  if (!body.startsWith(EVIDENCE_QUEUE_RATIONALE_PREFIX)) return null;
  const rest = body.slice(EVIDENCE_QUEUE_RATIONALE_PREFIX.length);
  const semi = rest.indexOf(";");
  if (semi <= 0) return null;
  return rest.slice(0, semi);
}

/** Token after the unread prefix (`no_paid_section`, `ambiguous_paid_rows`). */
export function parseLoanStatementUnreadWhy(
  text: string | undefined,
): string | null {
  const body = displayAiRationale(text);
  if (!body.startsWith(LOAN_STATEMENT_UNREAD_RATIONALE_PREFIX)) return null;
  const rest = body.slice(LOAN_STATEMENT_UNREAD_RATIONALE_PREFIX.length);
  const semi = rest.indexOf(";");
  if (semi <= 0) return null;
  return rest.slice(0, semi);
}

/** AM-facing unread-statement cause. Distinct from low-confidence. */
export function loanStatementUnreadFaceCopy(why: string): string {
  switch (why) {
    case "no_paid_section":
      return "Couldn't read the statement — no paid section found.";
    case "ambiguous_paid_rows":
      return "Couldn't read the statement — two candidate rows, could not choose.";
    default:
      return "Couldn't read the statement.";
  }
}

/**
 * Lockstep with `pipeline.loan_statement._first_line_is_protected` and
 * ingest-worker `firstLineIsProtectedRationale`. Column-0 of the first
 * U+000A line, one trailing CR dropped, nothing else trimmed. ARF1 / HGL1
 * identity lines cannot be replaced by an LS1 upgrade.
 */
export function firstLineIsProtectedRationale(
  text: string | null | undefined,
): boolean {
  if (!text) return false;
  const first = dropTrailingCR(text.split("\n")[0] ?? "");
  return (
    first === BOOKING_FAILURE_LINE_PREFIX ||
    first.startsWith(`${BOOKING_FAILURE_LINE_PREFIX}\t`) ||
    first === HUB_APPLIED_LINE_PREFIX ||
    first.startsWith(`${HUB_APPLIED_LINE_PREFIX}\t`)
  );
}

/**
 * R37O-3 lockstep with ingest-worker `loanStatementUploadRefusedForPending`.
 * Hub-proposal (`unmatched-deposit`) is not a statement-upload card.
 * Recovery-plain is not refused here.
 */
export function loanStatementUploadRefusedForPending(pending: {
  reason: string;
  aiRationale?: string | null;
}): boolean {
  if (pending.reason === "unmatched-deposit") return true;
  return firstLineIsProtectedRationale(pending.aiRationale);
}

/** Facts that gate Book this split, distinct from the AM's confirm clicks. */
export type LoanSplitBookOffer = {
  suggestionInMembership: boolean;
  locationRequired: boolean;
  suggestedAccountSelectable: boolean;
  suggestedLocationSelectable: boolean;
  loanBookableFees: boolean;
};

export const LOAN_STATEMENT_UPLOAD_BLOCKED_COPY =
  "This card's booking-failure record cannot be replaced. Resolve the failure first, then upload.";

export const LOAN_STATEMENT_UPLOAD_INVITE_COPY =
  "Upload the mortgage statement PDF. It is read for the paid-section split (not Explanation of amount due).";

export const LOAN_STATEMENT_UPLOAD_REREAD_COPY =
  "Upload a different statement to re-read. Only cited figures come back to this card.";

export const LOAN_STATEMENT_UPLOAD_RECEIVED_COPY =
  `Statement received. Reload ${navLabel("queue")} after the next read.`;

export const LOAN_STATEMENT_MISMATCH_NEXT_STEP_COPY =
  "A figure that does not match cannot book from this card — send it to the owner with the correct number.";

/**
 * R4-22. The owner is never told to send a card to themselves: the person who
 * can correct the figure reads what to correct, the account manager keeps the
 * ask.
 *
 * Round-2 review, item 8 (decided): the owner is told the plain fact and no
 * more. The re-read sentence below is added only where the upload control is
 * really on the card, so no role is told to upload something no card offers.
 */
export const LOAN_STATEMENT_MISMATCH_OWNER_NEXT_STEP_COPY =
  "A figure that does not match cannot book from this card.";

export const LOAN_STATEMENT_MISMATCH_REREAD_COPY =
  "Upload a different statement to re-read it.";

export function loanStatementMismatchNextStepCopy(
  role: StaffRole = "account_manager",
): string {
  return role === "owner"
    ? LOAN_STATEMENT_MISMATCH_OWNER_NEXT_STEP_COPY
    : LOAN_STATEMENT_MISMATCH_NEXT_STEP_COPY;
}

export const LOAN_STATEMENT_BOOK_POSTS_LEAD = "Book this split posts";

export function loanStatementLumpWarning(item: QueueItem): string {
  return categorisationCardKind(item) === "recovery"
    ? "Use this account books the payment as one account."
    : "Something else books the payment as one account.";
}

export function loanSplitUnavailableReason(
  item: QueueItem,
  offer?: LoanSplitBookOffer,
): string {
  const accountPresent = Boolean(item.suggestedAccount);
  const locationPresent = Boolean(item.suggestedLocation);
  const accountSelectable =
    offer?.suggestedAccountSelectable ?? accountPresent;
  const locationSelectable =
    offer?.suggestedLocationSelectable ?? locationPresent;
  if (!accountPresent && !locationPresent) {
    return "Book this split is not available — this card has no suggested account or location.";
  }
  if (!accountPresent) {
    return "Book this split is not available — this card has no suggested account.";
  }
  if (!locationPresent) {
    return "Book this split is not available — this card has no suggested location.";
  }
  if (!accountSelectable && !locationSelectable) {
    return "Book this split is not available — the suggested account or location is not on the chart.";
  }
  if (!accountSelectable) {
    return "Book this split is not available — the suggested account is not on the chart.";
  }
  if (!locationSelectable || offer?.locationRequired) {
    return "Book this split is not available — the suggested location is not on the chart.";
  }
  return "Book this split is not available — the suggestion is not on the chart.";
}

export function loanStatementMismatchEscalateNote(
  components: readonly string[],
  role: StaffRole = "account_manager",
): string {
  const first = components[0];
  if (first === undefined) return "";
  const last = components[components.length - 1];
  const listed =
    components.length === 1 || last === undefined
      ? `${first} does not match the source line`
      : `${components.slice(0, -1).join(", ")} and ${last} do not match the source line`;
  return `${listed}. ${loanStatementMismatchNextStepCopy(role)}`;
}

/** Face copy for a stored or unparseable LS1. Empty when the card has none. */
export function loanStatementBookingConsequenceLines(
  item: QueueItem,
  offer?: LoanSplitBookOffer,
): string[] {
  const storedLoan = parseStoredLoanStatement(item.aiRationale);
  const lump = loanStatementLumpWarning(item);
  if (storedLoan !== null) {
    const suggestionInMembership =
      offer?.suggestionInMembership ?? hasCompleteSuggestion(item);
    const loanBookableFees =
      offer?.loanBookableFees ?? storedLoan.fees.amount === 0;
    if (suggestionInMembership && loanBookableFees) {
      return [
        `${LOAN_STATEMENT_BOOK_POSTS_LEAD} ${loanStatementPaidSectionPhrase(storedLoan)}, not Explanation of amount due (that is next month's payment). ${lump}`,
      ];
    }
    if (!suggestionInMembership) {
      return [`${loanSplitUnavailableReason(item, offer)} ${lump}`];
    }
    return [lump];
  }
  if (looksLikeLs1(item.aiRationale)) {
    return [
      `This loan-statement split cannot be booked from this card (stored proposal missing or unparseable). ${lump}`,
    ];
  }
  return [];
}

/** AM-facing cause. Not the model rationale — R29O-3 forbids dumping that. */
export function evidenceQueueFaceCopy(why: string): string {
  switch (why) {
    case "no_corroboration":
      return "Needs corroboration. Bank text alone does not auto-commit.";
    case "judgment_equity_needs_character":
      return "Equity bookings need evidence of what kind of transaction this is.";
    case "judgment_intercompany_needs_character":
      return "Intercompany bookings need evidence of what kind of transaction this is.";
    case "judgment_loan_needs_character":
      return "Loan bookings need evidence of what kind of transaction this is.";
    case "weak_below_confidence":
      return "Party or instrument evidence is present, but confidence is below the auto-commit bar.";
    case "evidence_gate_off_low_confidence":
      return "Confidence is below the auto-commit bar.";
    default:
      if (why.startsWith("judgment_") && why.endsWith("_needs_character")) {
        return "This account class needs evidence of what kind of transaction this is.";
      }
      return "Needs corroboration before this booking can auto-commit.";
  }
}

function whyQueuedDisclosureFacts(item: QueueItem): DocumentFragment[] {
  // R35O-2: stop calling the field "Reason" when the stored enum is a lie.
  if (item.reason === "low-confidence") {
    const unreadWhy = parseLoanStatementUnreadWhy(item.aiRationale);
    if (unreadWhy) {
      return [fact("Why queued", loanStatementUnreadFaceCopy(unreadWhy))];
    }
    const why = parseEvidenceQueueWhy(item.aiRationale);
    if (why) return [fact("Why queued", evidenceQueueFaceCopy(why))];
  }
  // The stored enum itself is technical (Y5 brief item 5) and is rendered by
  // `technicalDisclosure` for an account manager only.
  return [];
}

/**
 * Parse ARF1 payload (surface, cause, lump). This parse is not identity.
 * Card identity is `categorisationCardKind` (hub-proposal first, then
 * recovery). Production identity may not key on this parse; payload
 * helpers may parse only after `kind === "recovery"` (or inside
 * `categorisationCardKind` itself). Missing / ordinary / HGL1 / leading
 * junk → null. Unknown surfaces map to booking_failed (Python encoder
 * lockstep).
 */
export function parseBookingFailureRationale(
  text: string | undefined,
): BookingFailureRationale | null {
  if (!text) return null;
  const lines = text.split("\n");
  const first = dropTrailingCR(lines[0] ?? "");
  if (
    first !== BOOKING_FAILURE_LINE_PREFIX &&
    !first.startsWith(`${BOOKING_FAILURE_LINE_PREFIX}\t`)
  ) {
    return null;
  }
  const parts = first.split("\t");
  if (parts[0] !== BOOKING_FAILURE_LINE_PREFIX) return null;
  const rawSurface =
    parts[1] && parts[1].length > 0 ? parts[1] : "booking_failed";
  const known = (BOOKING_FAILURE_SURFACES as readonly string[]).includes(
    rawSurface,
  );
  const surface = known ? rawSurface : "booking_failed";
  const flags = parts.slice(2);
  // Legacy: ARF1\thub_split with no flag still claims lump (cards written
  // before the explicit flag). Unknown surfaces do not.
  const lump = flags.includes(LUMP_CLAIM_FLAG) || rawSurface === "hub_split";
  const cause = displayAiRationale(lines.slice(1).join("\n"));
  return { surface, cause, lump };
}

/** Writer-contract prefix check (same spec as the Worker). Not a parser. */
export function isBookingFailureWriterPrefix(
  text: string | undefined,
): boolean {
  if (!text) return false;
  const first = dropTrailingCR(text.split("\n")[0] ?? "");
  return (
    first === BOOKING_FAILURE_LINE_PREFIX ||
    first.startsWith(`${BOOKING_FAILURE_LINE_PREFIX}\t`)
  );
}

/**
 * Closed categorisation identity. Hub-proposal first so an overlapping
 * row (unmatched-deposit AND booking-failed OR column-0 ARF1) is not
 * representable as recovery — identity is the reason, not completeness.
 * Names may move; the closed set and this order may not. DR26P-7 = B
 * lives inside the recovery family only.
 */
export type CategorisationCardKind = "hub-proposal" | "recovery" | "ordinary";

export function categorisationCardKind(item: QueueItem): CategorisationCardKind {
  if (isHubGlProposalItem(item)) return "hub-proposal";
  if (
    item.reason === "booking-failed" ||
    parseBookingFailureRationale(item.aiRationale) !== null
  ) {
    return "recovery";
  }
  return "ordinary";
}

/**
 * Recovery iff `categorisationCardKind` is `"recovery"`. The reason-OR-parse
 * lives only inside that function so it cannot be recomputed beside a live
 * hub-proposal. A forged successful parse on low-confidence is still a
 * full recovery card (DR26P-7 = B) because it is not unmatched-deposit.
 */
export function isBookingFailureRecoveryCard(item: QueueItem): boolean {
  return categorisationCardKind(item) === "recovery";
}

/** Yes-withholding is the recovery kind, not a looser parse-only check. */
export function withholdsYesOneTap(item: QueueItem): boolean {
  return isBookingFailureRecoveryCard(item);
}

/**
 * Ordinary Yes one-tap. Not a fourth card kind: new-vendor stays ordinary
 * (headline "What was this?") but is never one-tap, even with a complete
 * suggestion — the suggested account is not a value this flow has
 * validated (K12-OP-2); current producers write none. Hub-proposal uses
 * Approve, not this Yes. Recovery withholds via kind.
 */
export function offersOrdinaryYesOneTap(item: QueueItem): boolean {
  return (
    categorisationCardKind(item) === "ordinary" &&
    item.reason !== "new-vendor" &&
    hasCompleteSuggestion(item) &&
    parseStoredLoanStatement(item.aiRationale) === null &&
    !looksLikeLs1(item.aiRationale)
  );
}

export function categorisationQuestion(item: QueueItem): string {
  const kind = categorisationCardKind(item);
  switch (kind) {
    case "hub-proposal":
      return "Approve this hub GL split?";
    case "recovery":
      return "This approval could not be booked";
    case "ordinary":
      return "What was this?";
    default: {
      const _exhaustive: never = kind;
      return _exhaustive;
    }
  }
}

/**
 * The claim: choosing a single account books the whole deposit as a lump
 * and leaves the hub split pending. Keys on that claim, not on
 * ``surface === "hub_split"`` — a locked hub split must keep lump true
 * even if the recovery surface is period_locked (r26a CROSS-LANE).
 * Both arms read `categorisationCardKind` — do not call
 * `isHubGlProposalItem` and the parse-OR independently here.
 */
export function claimsLumpBooking(item: QueueItem): boolean {
  const kind = categorisationCardKind(item);
  if (kind === "hub-proposal") return true;
  if (kind === "recovery") {
    // payload-parse: kind is already recovery; this reads lump, not identity.
    return parseBookingFailureRationale(item.aiRationale)?.lump === true;
  }
  return false;
}

/**
 * The previous-attempt fact and Use it again claim a human already chose.
 * That is true only on GRANT-14 `booking-failed` recovery. Column-0 ARF1
 * on `low-confidence` is still a full recovery card (DR26P-7 = B); the
 * pair there came from a model, a rule, or a receipt. Owner 2026-08-22
 * option (i): hide the fact and the one-click on that arm rather than
 * reword. Kind is not this gate — do not reopen B. Do not add a
 * `suggestion_origin` column. Hub-proposal is excluded by kind first.
 */
export function previousAttemptWasHumanChoice(item: QueueItem): boolean {
  return (
    categorisationCardKind(item) === "recovery" &&
    item.reason === "booking-failed"
  );
}

/**
 * `suggestedLocation` is a booking determination (same class as
 * `suggestedAccount`), not bank metadata. The facts `dl` sits next to
 * Date / Merchant / Descriptor — an AM reads those as already-true.
 *
 * Show the leaf there only when the card is allowed to present a
 * suggestion or a human previous choice:
 * - ordinary / hub-proposal: the card is a proposal (Yes / Approve)
 * - GRANT-14 booking-failed: a human chose it; Previous attempt says so
 *
 * Hide it on the ARF1 recovery arm (`recovery` and not a human previous
 * choice). Option (i) already removed the labelled claim; this row was
 * the same values without the verb. Unconfigured leaves use the other
 * branch (`Location (not configured)`) and stay — K12-OP-1.
 *
 * Do not key this on kind alone (that reopens DR26P-7 = B) or on
 * `reusablePreviousAttempt` (that would hide a half-pair human location
 * that is not reusable). Provenance is `previousAttemptWasHumanChoice`.
 */
export function showsSuggestedLocationAsCardFact(item: QueueItem): boolean {
  if (!item.suggestedLocation?.trim()) return false;
  if (categorisationCardKind(item) !== "recovery") return true;
  return previousAttemptWasHumanChoice(item);
}

/**
 * The remembered one-account answer on a recovery card, when it is legal
 * to offer as a distinct "Use it again" write. Null means no one-click
 * control (not a human previous choice, lump, half-answer, missing
 * account or location). Display of a held account on a half pair is
 * `displayedPreviousAttempt`, not this helper — reuse is per-pair;
 * display is per-half (R29O-2). Do not treat a split-leg
 * `suggestedAccount` as reusable (R24O-3). Provenance is
 * `previousAttemptWasHumanChoice` — kind alone would re-attach a human
 * claim to the ARF1 arm.
 */
export type ReusablePreviousAttempt = {
  account: string;
  location: string;
};

export const USE_PREVIOUS_ATTEMPT_LABEL = "Use it again";

export function reusablePreviousAttempt(
  item: QueueItem,
): ReusablePreviousAttempt | null {
  if (!previousAttemptWasHumanChoice(item)) return null;
  if (claimsLumpBooking(item)) return null;
  const account = item.suggestedAccount?.trim() ?? "";
  const location = item.suggestedLocation?.trim() ?? "";
  if (!account || !location) return null;
  return { account, location };
}

/**
 * Display-only previous attempt. Per-half and lump-excluded. Does not
 * call `reusablePreviousAttempt` — if display keyed on the pair gate,
 * a later loosening of that gate would mint `Use it again` on a half
 * pair by coincidence (#718). Account-absent stays null (the CoA-refuse
 * cause already names the rejected account). Location-absent still
 * shows the held account. Provenance is `previousAttemptWasHumanChoice`
 * independently of the pair gate — the ARF1 arm has no fact row even
 * when both halves are present (owner option (i); accepted cost).
 */
export function displayedPreviousAttempt(
  item: QueueItem,
  companies: AccountLabelContext,
  among: readonly AccountPath[] = [],
): string | null {
  if (!previousAttemptWasHumanChoice(item)) return null;
  if (claimsLumpBooking(item)) return null;
  const account = item.suggestedAccount?.trim() ?? "";
  if (!account) return null;
  const location = item.suggestedLocation?.trim() ?? "";
  if (location) {
    // B1c-2: the account is named in words, told apart from a twin when the
    // page holds the company's chart — never the raw ledger path (rc-3 row 13).
    return `Previous attempt: ${face.account(account, companies, among)} / ${amVisibleDisplayText(location)}`;
  }
  const unresolved = unresolvedLocationFromRationale(item.aiRationale);
  const locationNote = unresolved
    ? `(no location — '${amVisibleDisplayText(unresolved)}' is not configured)`
    : "(no location)";
  return `Previous attempt: ${face.account(account, companies, among)} / ${locationNote}`;
}

export const PERIOD_LOCKED_RETRY_SENTENCE =
  "If the period is still locked, booking from this card will fail again. " +
  `${REOPEN_MONTH_BY_HAND_PREFIX} ${advice("reopen_month", "account_manager")} Then use the account fields below.`;

/**
 * R4-20. Decision 2 gives both roles the same ledger sentence for reopening a
 * month; the tail says what to do on this card afterwards.
 */
export const PERIOD_LOCKED_RETRY_OWNER_SENTENCE =
  "If the period is still locked, booking from this card will fail again. " +
  `${REOPEN_MONTH_BY_HAND_PREFIX} ${advice("reopen_month", "owner")} Then use the account fields below.`;

export function periodLockedRetrySentence(
  role: StaffRole = "account_manager",
): string {
  return role === "owner" ? PERIOD_LOCKED_RETRY_OWNER_SENTENCE : PERIOD_LOCKED_RETRY_SENTENCE;
}

/**
 * Owner 2026-08-18 option B: a retry warning on every booking-failure
 * recovery card whose surface is not period_locked or hub_split. Named
 * unblocker is omitted — four production refuses share surface=
 * booking_failed, and claiming bean-check on the three that are not
 * bean-check is R29O-1.
 */
export const BOOKING_FAILED_RETRY_CLAIM_NOTHING_SENTENCE =
  "Retrying from this card may not succeed. The cause is above.";

export const BOOKING_FAILED_MISSING_CAUSE_SENTENCE =
  "A previous approval could not be booked. The specific cause was not recorded.";

export const BOOKING_FAILED_RETRY_WITHOUT_CAUSE_SENTENCE =
  "Retrying from this card may not succeed.";

export const HUB_SPLIT_RETRY_MAY_NOT_SUCCEED_SENTENCE =
  "Retrying from this card may not succeed. Booking a single account will not re-apply the split; an owner may need to restore the configuration or residual this split needs. The account fields below still work if a lump is the intended outcome.";

export const RECOVERY_REJECT_CONFIRMATION =
  "Dismissed — this approval will not be retried.";

/**
 * GRANT G15 (rd15 lane B, owner 2026-11-02). The sentence the split panel says
 * in the card itself: a split answer cannot also create a standing rule,
 * because a rule names one account.
 */
export const SPLIT_NO_STANDING_RULE_TEXT =
  "A split cannot also make a standing rule: a rule names one account.";

/** What the card says once a split answer is recorded (the run books it). */
export const SPLIT_CONFIRMATION =
  "Split recorded. The next run books it as one entry, with a line for each part.";

/** Recovery edit hedge keyed on (surface × this answer), not the prior refusal. */
export const RECOVERY_HUB_SPLIT_LUMP_HEDGE =
  "This answer is recorded and will be booked on the next run.";

export const RECOVERY_PERIOD_LOCKED_HEDGE =
  "This answer is recorded and will be retried; it will fail again until an owner reopens the period.";

export const RECOVERY_BOOKING_FAILED_CLAIM_NOTHING_HEDGE =
  "This answer is recorded and will be retried. The cause is above.";

export const RECOVERY_BOOKING_FAILED_WITHOUT_CAUSE_HEDGE =
  "This answer is recorded and will be retried.";

/** Checkbox + aria-label. Opt-in only; radios name the width (GRANT 16). */
export const MAKE_RULE_CHECKBOX_COPY =
  "Also make a standing rule for future matches";

/** First sentence of a click-time duplicate refusal. Worker message leads with this. */
export const STANDING_RULE_ALREADY_EXISTS_HEADLINE =
  "This standing rule already exists.";

export type ExistingStandingRuleDetails = {
  id: number | null;
  source: "applied" | "queue";
  queueId?: number;
  account: string;
  location: string;
  amount: string | null;
  matchText: string;
  kind?: "posting" | "vendor_identity";
};

function isExistingStandingRuleDetails(
  value: unknown,
): value is ExistingStandingRuleDetails {
  if (value === null || typeof value !== "object") return false;
  const rec = value as Record<string, unknown>;
  if (rec.id !== null && typeof rec.id !== "number") return false;
  if (rec.source !== "applied" && rec.source !== "queue") return false;
  if (typeof rec.matchText !== "string" || rec.matchText.length === 0) return false;
  if (rec.queueId !== undefined && typeof rec.queueId !== "number") return false;
  if (rec.kind === "vendor_identity") {
    if (rec.amount !== null && rec.amount !== undefined && typeof rec.amount !== "string") {
      return false;
    }
    return true;
  }
  if (typeof rec.account !== "string" || rec.account.length === 0) return false;
  if (typeof rec.location !== "string" || rec.location.length === 0) return false;
  if (rec.amount !== null && typeof rec.amount !== "string") return false;
  return true;
}

/** Structured click-time duplicate from a contract-shaped 409 body, or null. */
export function existingStandingRuleFromError(
  error: unknown,
): ExistingStandingRuleDetails | null {
  if (!(error instanceof AdminApiError)) return null;
  const body = error.body;
  if (body === null || typeof body !== "object") return null;
  const err = (body as { error?: unknown }).error;
  if (err === null || typeof err !== "object") return null;
  const details = (err as { details?: unknown }).details;
  if (details === null || typeof details !== "object") return null;
  const existing = (details as { existingRule?: unknown }).existingRule;
  return isExistingStandingRuleDetails(existing) ? existing : null;
}

function renderExistingStandingRule(
  details: ExistingStandingRuleDetails,
  offeredAccounts: readonly string[],
  companies: AccountLabelContext,
): HTMLElement {
  // Owner decision 7: a card describes the rule, it never prints "Rule 17" or
  // "queue item 12". The size and the direction come from the one codec, so a
  // money-in rule does not read as money out (R12 A2-3).
  const amountBit = ruleAmountWords(details.amount, moneyText);
  // The rule's match text and location are stored bank and staff text: the
  // person reads the cleaned form and the account in words (rule 20).
  const matchShown = amVisibleDisplayText(details.matchText);
  const body =
    details.kind === "vendor_identity"
      ? `A rule already identifies ${matchShown} as the vendor. Later matches still return to ${navLabel("queue")} for account and location.`
      : `A rule already books ${matchShown} (${amountBit}) to ${accountLabelAmong(details.account, offeredAccounts, companies)} at ${amVisibleDisplayText(details.location)}.`;
  return h(
    "div",
    { class: "existing-standing-rule", "data-role": "existing-standing-rule" },
    [
      h("p", {}, [STANDING_RULE_ALREADY_EXISTS_HEADLINE]),
      h("p", {}, [body]),
      h("p", {}, [
        details.kind === "vendor_identity"
          ? "This is the same 1099 kind already on file. Untick the standing-rule box to book without creating another identity, or choose the other 1099 kind to record a correction."
          : "Untick the standing-rule box to book this transaction without creating another rule.",
      ]),
    ],
  );
}

export const RULE_SCOPE_VENDOR_COPY = "Any amount";

export const RULE_SCOPE_REQUIRED_COPY =
  "Choose whether this standing rule is this amount or any amount.";

export const MATCH_TEXT_REQUIRED_COPY =
  "Choose which text this standing rule should match.";

export const RULE_KIND_REQUIRED_COPY =
  "Choose whether later matches always share this category, or only the vendor.";

export const MATCH_TEXT_LEGEND = "Match future transactions on";

export const RULE_KIND_LEGEND = "What this rule remembers";

export const RULE_KIND_POSTING_COPY =
  "Vendor and category — later matches book the same account and location";

export const RULE_KIND_VENDOR_IDENTITY_COPY =
  `Vendor only — later matches return to ${navLabel("queue")} for account and location`;

export const VENDOR_CLASSIFICATION_REQUIRED_COPY =
  "Choose whether this vendor is a 1099 contractor or a non-1099 vendor.";

export const VENDOR_CLASSIFICATION_LEGEND = "What kind of vendor is this";

export const VENDOR_CLASSIFICATION_1099_COPY =
  "1099 contractor — name survives into the ledger and onto its own 1099 line";

export const VENDOR_CLASSIFICATION_NON_1099_COPY =
  "Non-1099 vendor — name survives into the ledger; not a 1099 filing row";

export const ATTACH_PRIOR_PAYMENTS_HEADING =
  "Attach previously paid invoices to this vendor";

export const ATTACH_TWINS_COPY =
  "These payments cannot be attached separately.";

/** Exact Worker 422 body for a partial twin cohort (no trailing period). */
export const ATTACH_TWINS_WORKER_MESSAGE =
  "These payments cannot be attached separately";

export const ATTACH_NOT_ATTACHABLE_COPY =
  "One or more payments cannot be attached.";

/** Exact Worker 422 body when a plaid_id is not in the facts snapshot. */
export const ATTACH_NOT_ATTACHABLE_WORKER_MESSAGE =
  "One or more payments cannot be attached";

export const ATTACH_NON_1099_CONFIRM_COPY =
  "Attaching these payments removes them from the 1099 census and the filing list.";

export const ATTACH_1099_MINT_CONFIRM_COPY =
  "These attached payments will appear on the 1099 census and the filing list.";

export const ATTACH_MOVE_CONFIRM_COPY =
  "One or more selected payments are already on file for another vendor. Attach them to this vendor instead.";

export const ATTACH_PRIOR_PAYMENTS_NO_DATE_COPY =
  "This card has no transaction date, so prior payments cannot be listed for a tax year.";

export const ATTACH_GENERIC_FAILURE_COPY = "Could not attach.";

export function attachTaxYearForQueueItem(item: QueueItem): string | null {
  const raw = item.txnDate;
  if (typeof raw !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
  if (isWireSentinelDate(raw)) return null;
  return raw.slice(0, 4);
}

export function attachPeriodForQueueItem(item: QueueItem): string | null {
  const year = attachTaxYearForQueueItem(item);
  return year === null ? null : `${year}-12`;
}

export function attachPriorPaymentsHeadingForYear(year: string): string {
  return `${ATTACH_PRIOR_PAYMENTS_HEADING} for ${year}`;
}

export function attachPriorPaymentsEmptyCopy(year: string): string {
  return `No prior posted payments in ${year}.`;
}

/**
 * Attach-route failure copy. Twin and not-attachable 422s keep their Worker
 * sentences (R34O-4). Every other status — including 403 — stays generic so
 * this picker is not an authorisation oracle.
 */
export function priorPaymentAttachFailureCopy(error: unknown): string {
  if (!(error instanceof AdminApiError)) return ATTACH_GENERIC_FAILURE_COPY;
  if (error.status === 422 && bodyMessageIs(error, ATTACH_TWINS_WORKER_MESSAGE)) {
    return ATTACH_TWINS_COPY;
  }
  if (error.status === 422 && bodyMessageIs(error, ATTACH_NOT_ATTACHABLE_WORKER_MESSAGE)) {
    return ATTACH_NOT_ATTACHABLE_COPY;
  }
  const requestId = adminErrorRequestId(error.body);
  if (requestId !== null) {
    return `${ATTACH_GENERIC_FAILURE_COPY} Try again or ask for help, quoting request id ${requestId}.`;
  }
  return ATTACH_GENERIC_FAILURE_COPY;
}

export function ruleScopeAmountCopy(item: QueueItem): string {
  return `Only ${formatAmount(queueAmountDecimal(item))}`;
}

/**
 * Pre-#731 producer constant. Historical D1 rows still carry this; the
 * face must keep naming it so this lane does not depend on r30d's
 * f-string reaching every row.
 */
export const NEW_VENDOR_MISSING_COA_LEGACY_RATIONALE =
  "rule account missing from chart of accounts";

/**
 * Confirmation hedge for a recovery edit. The write is the element:
 * (ARF1 surface × whether this answer is a lump). Do not splice an
 * unblocker from the previous refusal — hub_split blocks a split retry,
 * not the lump the AM just submitted. Default is claim-nothing (R29O-1):
 * do not name bean-check as the unblocker of an unknown cause.
 */
export function recoveryEditHedge(
  item: QueueItem,
  answer: { lump: boolean },
): string {
  if (categorisationCardKind(item) !== "recovery") {
    throw new Error(
      "recoveryEditHedge is recovery-only; non-recovery callers must not inherit a booking-failure hedge",
    );
  }
  // payload-parse: kind is already recovery; this reads surface, not identity.
  const recovery = parseBookingFailureRationale(item.aiRationale);
  const surface = recovery?.surface;
  if (surface === "hub_split" && answer.lump) {
    return RECOVERY_HUB_SPLIT_LUMP_HEDGE;
  }
  if (surface === "period_locked") {
    return RECOVERY_PERIOD_LOCKED_HEDGE;
  }
  if (recovery?.cause) {
    return RECOVERY_BOOKING_FAILED_CLAIM_NOTHING_HEDGE;
  }
  return RECOVERY_BOOKING_FAILED_WITHOUT_CAUSE_HEDGE;
}

/** Card-face copy for a booking consequence the disclosure must not hide. */
export function bookingConsequenceCopy(
  item: QueueItem,
  offer?: LoanSplitBookOffer,
  role: StaffRole = "account_manager",
): string[] {
  const kind = categorisationCardKind(item);
  const stored = parseStoredHubApplied(item.aiRationale);
  switch (kind) {
    case "recovery": {
      const lines: string[] = [];
      // payload-parse: kind is already recovery; this reads cause/lump, not identity.
      const recovery = parseBookingFailureRationale(item.aiRationale);
      if (recovery?.cause) {
        lines.push(recovery.cause);
      } else {
        lines.push(BOOKING_FAILED_MISSING_CAUSE_SENTENCE);
      }
      if (recovery?.lump) {
        const batch = stored?.sourceBatchId;
        lines.push(
          batch
            ? `There is no one-tap for this — choose an account below. Booking a single account books the whole deposit as a lump and leaves hub batch ${batch} pending.`
            : "There is no one-tap for this — choose an account below. Booking a single account books the whole deposit as a lump and leaves the hub split pending.",
        );
      }
      // Option B (owner 2026-08-18): every recovery card carries a retry
      // warning. period_locked keeps its specific reopen sentence — do not
      // flatten it into the generic (item 1 mirror). booking_failed /
      // unknown / missing ARF1 claim nothing: four production refuses
      // share that surface, and naming bean-check is R29O-1.
      if (recovery?.surface === "period_locked") {
        lines.push(periodLockedRetrySentence(role));
      } else if (recovery?.surface === "hub_split") {
        lines.push(HUB_SPLIT_RETRY_MAY_NOT_SUCCEED_SENTENCE);
      } else if (recovery?.cause) {
        lines.push(BOOKING_FAILED_RETRY_CLAIM_NOTHING_SENTENCE);
      } else {
        lines.push(BOOKING_FAILED_RETRY_WITHOUT_CAUSE_SENTENCE);
      }
      // LS1 copy is not ordinary-only. A recovery card carrying a split
      // still has to say what Book does and what the lump costs (R36O-4).
      lines.push(...loanStatementBookingConsequenceLines(item, offer));
      return lines;
    }
    case "hub-proposal": {
      const lines: string[] = [];
      const legsPresent = stored !== null && stored.applied.length > 0;
      const oneTap =
        legsPresent && hasCompleteSuggestion(item);
      if (!legsPresent) {
        lines.push(
          "This hub GL split cannot be approved from this card (stored proposal missing or unparseable).",
        );
      }
      const batch = stored?.sourceBatchId;
      const lumpWarning = batch
        ? `Choosing a single account books the whole deposit as a lump and leaves hub batch ${batch} pending.`
        : "Choosing a single account books the whole deposit as a lump and leaves the hub split pending.";
      // "Approve posts this split." is the affirmative control's sentence.
      // Same legs+completeness fact that gates the button. The lump
      // warning stays when Approve is withheld — that card has only the lump.
      if (oneTap) {
        lines.push(`Approve posts this split. ${lumpWarning}`);
      } else {
        lines.push(lumpWarning);
      }
      return lines;
    }
    case "ordinary": {
      const loanLines = loanStatementBookingConsequenceLines(item, offer);
      if (loanLines.length > 0) return loanLines;
      // R29O-3: whatever human rationale the new-vendor row already
      // carries goes on the face. After #731 that string names the rule
      // and the account; historical rows still name the old constant.
      // Do not regex the account out of the text. Do not dump
      // low-confidence / possible-personal / anomaly rationales.
      if (item.reason === "new-vendor") {
        const rationale = displayAiRationale(item.aiRationale);
        if (rationale) return [rationale];
      }
      // Owner 2026-08-31: unread statement is not low-confidence. Name
      // not_found vs ambiguous on the face (#878 shape, no new reason).
      if (item.reason === "low-confidence") {
        const unreadWhy = parseLoanStatementUnreadWhy(item.aiRationale);
        if (unreadWhy) return [loanStatementUnreadFaceCopy(unreadWhy)];
      }
      // R35O-2: evidence-gated cards borrowed low-confidence. Name the
      // cause on the face without dumping model rationale. Mirror:
      // possible-personal / anomaly / ordinary-confidence still [].
      if (item.reason === "low-confidence") {
        const why = parseEvidenceQueueWhy(item.aiRationale);
        if (why) return [evidenceQueueFaceCopy(why)];
      }
      return [];
    }
    default: {
      const _exhaustive: never = kind;
      return _exhaustive;
    }
  }
}

/** True when the queue item is a close/statement blocker (not a categorisation). */
export function isMoovMovementQueueItem(item: QueueItem): boolean {
  return (
    item.reason === "close-blocked" &&
    (item.subject ?? "").startsWith(MOOV_CARD_SUBJECT_PREFIX)
  );
}

export function isBlockerQueueItem(item: QueueItem): boolean {
  return isBlockerQueueReason(item.reason);
}

export function isUntrackedCounterpartyQueueItem(item: QueueItem): boolean {
  return item.reason === UNTRACKED_COUNTERPARTY_REASON;
}

const PRACTICE_TXN_REASONS = new Set([
  "new-vendor",
  "low-confidence",
  "anomaly",
  "possible-personal",
]);

/**
 * Whether Today may offer Send to the hub. Hub-linked is the same
 * approximation Waiting uses (`listCompanies` + isActive && hubLink !== null),
 * not `hub_linked_companies`. `hubLinkedSlugs === null` means the list failed
 * — fail closed (no control). Removals, unmatched-deposit, and booking-failed
 * stay owner-only: those must not become a hub category question.
 */
/** Send-to-hub is a question. Escrow-balance cards return still open. */
export function sendToPracticeConfirmationText(item: QueueItem): string {
  if (item.reason === "escrow-balance") {
    return "Asked the hub. This card is hidden until they answer, then returns still open.";
  }
  return "Sent to the hub.";
}

/**
 * DECIDED (owner, round-11 decision 4; the orchestrator's recommendation,
 * accepted with "ok, your rec"): the item-13 button reads "Start an onboarding
 * draft". The note sent to the Worker stays
 * `UNTRACKED_COUNTERPARTY_ONBOARD_CONFIRM`, which is the string it checks
 * (`ingest-worker/src/untracked_counterparty_reason.ts`).
 */
export const ONBOARD_DRAFT_LABEL = "Start an onboarding draft";

export function canSendQueueItemToPractice(
  item: QueueItem,
  hubLinkedSlugs: ReadonlySet<string> | null,
): boolean {
  if (hubLinkedSlugs === null) return false;
  if (!hubLinkedSlugs.has(item.company)) return false;
  if (isRemovalQueueItem(item)) return false;
  // Round-3 review, item 3: a bank line with no account on it is a Books-side
  // blocker. The practice cannot fix it either, so nobody is offered the
  // hand-off — the card says so instead.
  if (isMappingBlockerCard(item)) return false;
  if (item.reason === "unmatched-deposit" || item.reason === "booking-failed") {
    return false;
  }
  if (item.reason === UNTRACKED_COUNTERPARTY_REASON) {
    return false;
  }
  if (isBlockerQueueItem(item)) return true;
  return PRACTICE_TXN_REASONS.has(item.reason);
}

/**
 * Subject constructors + exit copy — imported from ingest-worker
 * `review_queue_blockers.ts` (single declaration, fix-r17d). Re-exported
 * under the portal names tests and call sites already use.
 */
export const RECEIPT_MATCH_CONFLICT_SUBJECT_PREFIX = WORKER_CONFLICT_PREFIX;
export const RECEIPT_HARD_MATCH_REFUSED_SUBJECT_PREFIX = WORKER_HARD_MATCH_PREFIX;
export const RECEIPT_MATCH_REVIEW_SUBJECT_PREFIX = WORKER_MATCH_REVIEW_PREFIX;
export const RECEIPT_MATCH_REVIEW_ATTACH_PREFIX = WORKER_MATCH_REVIEW_ATTACH;
export const RECEIPT_MATCH_REVIEW_REJECT_PREFIX = WORKER_MATCH_REVIEW_REJECT;
export const HARD_MATCH_REFUSE_KEEP_BANK_ESCALATION_PREFIX =
  WORKER_KEEP_BANK_PREFIX;

export function formatClosePeriodLabel(period: string): string {
  return workerFormatPeriodLabel(period);
}

export function closeBlockerSubject(period: string): string {
  return workerCloseBlockerSubject(period);
}

export function periodReopenedBlockerSubject(period: string): string {
  return workerPeriodReopenedBlockerSubject(period);
}

export function receiptMatchConflictSubject(plaidTransactionId: string): string {
  return workerReceiptMatchConflictSubject(plaidTransactionId);
}

export function unmatchedHubGlBatchSubject(
  period: string,
  sourceBatchId: string,
): string {
  return workerUnmatchedHubGlBatchSubject(period, sourceBatchId);
}

export function hubGlPushRefusedSubject(period: string): string {
  return workerHubGlPushRefusedSubject(period);
}

export function receiptHardMatchRefusedSubject(
  plaidTransactionId: string,
): string {
  return workerReceiptHardMatchRefusedSubject(plaidTransactionId);
}

export function receiptMatchReviewSubject(plaidTransactionId: string): string {
  return workerReceiptMatchReviewSubject(plaidTransactionId);
}

export function isCloseLifecycleAutoClearSubject(
  subject: string | undefined,
): boolean {
  return workerIsCloseLifecycleAutoClearSubject(subject);
}

/**
 * True when subject equals a receipt-match-conflict constructor output.
 * System clearer: `system:receipt-match-conflict-cleared` (ingest-worker).
 */
export function isReceiptMatchConflictSubject(
  subject: string | undefined,
): boolean {
  if (subject === undefined || subject.length === 0) return false;
  const family = resolveBlockerExitFamily({
    reason: "close-blocked",
    subject,
  });
  return family?.id === "receipt-match-conflict";
}

/**
 * True when subject equals a hard-match-refuse constructor output.
 * AM terminal disposition: escalate with `keep-bank:` note (fix-r15g).
 */
export function isHardMatchRefusedSubject(
  subject: string | undefined,
): boolean {
  if (subject === undefined || subject.length === 0) return false;
  const family = resolveBlockerExitFamily({
    reason: "close-blocked",
    subject,
  });
  return family?.id === "hard-match-refuse";
}

export function isReceiptMatchReviewSubject(
  subject: string | undefined,
): boolean {
  if (subject === undefined || subject.length === 0) return false;
  const family = resolveBlockerExitFamily({
    reason: "close-blocked",
    subject,
  });
  return family?.id === "receipt-match-review";
}

/**
 * R4-05: the Worker reads any trimmed, case-insensitive note starting
 * `attach:` or `reject:` as the account manager's disposition and resolves the
 * card. A free escalation note must never be executed as a decision, so on a
 * receipt-match card it is refused with one plain sentence. The two
 * disposition buttons do not come through here: they send their own prefixed
 * notes by design.
 */
export const RECEIPT_NOTE_DISPOSITION_REFUSAL =
  "A note cannot start with Attach: or Reject:";

export function receiptMatchNoteRefusal(
  item: QueueItem,
  note: string,
): string | null {
  if (!isReceiptMatchReviewSubject(item.subject)) return null;
  const lower = note.trim().toLowerCase();
  if (lower.startsWith("attach:") || lower.startsWith("reject:")) {
    return RECEIPT_NOTE_DISPOSITION_REFUSAL;
  }
  return null;
}

export type BlockerExitKind =
  | "close-lifecycle"
  | "closed-period-write"
  | "mapping-clear"
  | "statement"
  | "receipt-match-conflict"
  | "hard-match-refuse"
  | "receipt-match-review"
  | "unmatched-hub-gl"
  | "hub-gl-push-refused"
  | "escrow-balance"
  | "attention";

/** Machine census the Worker writes on unmatched-hub-gl bodies (R23O-3). */
export const HUB_BATCH_CENSUS_LABEL = "hub_batch_census";

export const UNMATCHED_HUB_GL_NET_ZERO_EXIT_SENTENCE =
  "This batch nets to zero — there is no deposit to allocate. It will not clear automatically. Escalate if it is blocking close.";

export const UNMATCHED_HUB_GL_NET_ZERO_ESCALATE_PLACEHOLDER =
  "Why this net-zero batch is blocking close (required)";

export const UNMATCHED_HUB_GL_UNPARSEABLE_EXIT_SENTENCE =
  "This batch's census could not be fully read. Do not assume it will clear automatically. Escalate if waiting cannot work.";

export const UNMATCHED_HUB_GL_MISSING_EXIT_SENTENCE =
  "This batch has no readable census. Do not assume it will clear automatically. Escalate if waiting cannot work.";

export const UNMATCHED_HUB_GL_NO_REMAINING_EXIT_SENTENCE =
  "No deposit currently remains that can allocate this batch. A later posted inflow in the period can still allocate it. Escalate if waiting cannot.";

export const UNMATCHED_HUB_GL_AWAITING_EXIT_SENTENCE =
  "Nothing has arrived yet that can allocate this batch. Waiting may still work; escalate if waiting cannot.";

export const UNMATCHED_HUB_GL_MISSING_ESCALATE_PLACEHOLDER =
  "Why waiting cannot clear this batch (required)";

export const UNMATCHED_HUB_GL_NO_REMAINING_ESCALATE_PLACEHOLDER =
  "Why this unmatched hub GL batch is blocking close (required)";

export const UNMATCHED_HUB_GL_AWAITING_ESCALATE_PLACEHOLDER =
  "Why waiting cannot work for this batch (required)";

export type UnmatchedHubGlCensus =
  | { kind: "missing"; examined: string }
  | { kind: "unparseable"; examined: string }
  | { kind: "zero"; net: string; legs: number; examined: string }
  | {
      kind: "no_remaining_candidate";
      net: string;
      legs: number;
      remainingCandidates: number;
      examined: string;
    }
  | {
      kind: "awaiting_candidate";
      net: string;
      legs: number;
      remainingCandidates: number;
      examined: string;
    }
  | {
      kind: "remaining_candidate_exists";
      net: string;
      legs: number;
      remainingCandidates: number;
      examined: string;
    };

function decodeCensusField(raw: string): string | null {
  if (raw.startsWith('"')) {
    try {
      const parsed: unknown = JSON.parse(raw);
      return typeof parsed === "string" ? parsed : null;
    } catch {
      return null;
    }
  }
  return raw;
}

function netStringIsZero(net: string): boolean | null {
  const match = /^(-)?(0|[1-9]\d{0,12})(?:\.(\d{1,2}))?$/.exec(net);
  if (match === null || match[2] === undefined) return null;
  const frac = (match[3] ?? "").padEnd(2, "0");
  return match[2] === "0" && frac === "00";
}

/**
 * Parse the Worker census on an unmatched-hub-gl body.
 * Missing (legacy bodies) is distinct from present-but-unparseable.
 */
export function parseUnmatchedHubGlCensus(body: string): UnmatchedHubGlCensus {
  const matches = [
    ...body.matchAll(
      new RegExp(
        `(?:^|\\n)${HUB_BATCH_CENSUS_LABEL}:\\s+hub_batch_net=(\\S+)\\s+legs=(\\S+)(?:\\s+remaining_candidates=(\\S+)\\s+consumed=(\\S+))?`,
        "g",
      ),
    ),
  ];
  if (matches.length === 0) {
    return { kind: "missing", examined: "census_line=missing" };
  }
  if (matches.length !== 1) {
    return {
      kind: "unparseable",
      examined: `census_line=duplicate count=${String(matches.length)}`,
    };
  }
  const match = matches[0];
  const netRaw = match?.[1];
  const legsRaw = match?.[2];
  const remainingRaw = match?.[3];
  const consumedRaw = match?.[4];
  const examined =
    `census_line=present net_raw=${netRaw ?? "missing"} ` +
    `legs_raw=${legsRaw ?? "missing"} ` +
    `remaining_raw=${remainingRaw ?? "missing"} ` +
    `consumed_raw=${consumedRaw ?? "missing"}`;
  if (netRaw === undefined || legsRaw === undefined) {
    return { kind: "unparseable", examined };
  }
  const net = decodeCensusField(netRaw);
  const legsText = decodeCensusField(legsRaw);
  if (net === null || legsText === null) {
    return { kind: "unparseable", examined: `${examined} decode=failed` };
  }
  if (net === "unparseable") {
    return { kind: "unparseable", examined: `${examined} net=unparseable` };
  }
  const zero = netStringIsZero(net);
  const legs = /^[0-9]+$/.test(legsText) ? Number(legsText) : Number.NaN;
  if (zero === null || !Number.isInteger(legs)) {
    return {
      kind: "unparseable",
      examined: `${examined} net=${net} legs=${legsText}`,
    };
  }
  if (remainingRaw === undefined || consumedRaw === undefined) {
    return {
      kind: "unparseable",
      examined: `${examined} remaining_or_consumed=missing`,
    };
  }
  const remainingText = decodeCensusField(remainingRaw);
  const consumedText = decodeCensusField(consumedRaw);
  if (remainingText === null || consumedText === null) {
    return {
      kind: "unparseable",
      examined: `${examined} remaining_consumed_decode=failed`,
    };
  }
  const remainingCandidates = /^[0-9]+$/.test(remainingText)
    ? Number(remainingText)
    : Number.NaN;
  if (!Number.isInteger(remainingCandidates) || !/^[01]$/.test(consumedText)) {
    return {
      kind: "unparseable",
      examined: `${examined} remaining=${remainingText} consumed=${consumedText}`,
    };
  }
  const consumed = consumedText === "1";
  if (zero) {
    return {
      kind: "zero",
      net,
      legs,
      examined: `${examined} net=${net} legs=${String(legs)}`,
    };
  }
  // remaining=0 AND consumed: a candidate was used up (R26O-3). Do not
  // overload that kind for remaining=0 consumed=0 — that cell is the
  // month-start wait AND the stranded-batch steady state (R27R-1).
  if (remainingCandidates === 0 && consumed) {
    return {
      kind: "no_remaining_candidate",
      net,
      legs,
      remainingCandidates,
      examined: `${examined} net=${net} remaining=0 consumed=1`,
    };
  }
  if (remainingCandidates === 0) {
    return {
      kind: "awaiting_candidate",
      net,
      legs,
      remainingCandidates,
      examined: `${examined} net=${net} remaining=0 consumed=0`,
    };
  }
  return {
    kind: "remaining_candidate_exists",
    net,
    legs,
    remainingCandidates,
    examined: `${examined} net=${net} remaining=${String(remainingCandidates)} consumed=${consumedText}`,
  };
}

function unmatchedHubGlExitSentence(
  item: QueueItem,
  automatic: string,
): string {
  const census = parseUnmatchedHubGlCensus(item.body ?? "");
  if (census.kind === "zero") {
    return UNMATCHED_HUB_GL_NET_ZERO_EXIT_SENTENCE;
  }
  if (census.kind === "remaining_candidate_exists") {
    return automatic;
  }
  if (census.kind === "awaiting_candidate") {
    return UNMATCHED_HUB_GL_AWAITING_EXIT_SENTENCE;
  }
  if (census.kind === "no_remaining_candidate") {
    return UNMATCHED_HUB_GL_NO_REMAINING_EXIT_SENTENCE;
  }
  if (census.kind === "unparseable") {
    console.log(`UNMATCHED_HUB_GL_CENSUS_UNPARSEABLE ${census.examined}`);
    return UNMATCHED_HUB_GL_UNPARSEABLE_EXIT_SENTENCE;
  }
  console.log(`UNMATCHED_HUB_GL_CENSUS_MISSING ${census.examined}`);
  return UNMATCHED_HUB_GL_MISSING_EXIT_SENTENCE;
}

function unmatchedHubGlEscalatePlaceholder(
  item: QueueItem,
  waitingOnDeposit: string,
): string {
  const census = parseUnmatchedHubGlCensus(item.body ?? "");
  if (census.kind === "zero") {
    return UNMATCHED_HUB_GL_NET_ZERO_ESCALATE_PLACEHOLDER;
  }
  if (census.kind === "remaining_candidate_exists") {
    return waitingOnDeposit;
  }
  if (census.kind === "awaiting_candidate") {
    return UNMATCHED_HUB_GL_AWAITING_ESCALATE_PLACEHOLDER;
  }
  if (census.kind === "no_remaining_candidate") {
    return UNMATCHED_HUB_GL_NO_REMAINING_ESCALATE_PLACEHOLDER;
  }
  return UNMATCHED_HUB_GL_MISSING_ESCALATE_PLACEHOLDER;
}

/**
 * Blocker exit kind — reads the shared declaration (fix-r17d). Named exits
 * only for families the declaration knows; unknown subjects stay attention.
 * Do not add another is…Subject() here — update the declaration.
 */
export function blockerExitKind(item: QueueItem): BlockerExitKind {
  const family = resolveBlockerExitFamily(item);
  if (family === null) return "attention";
  return family.exitKind as BlockerExitKind;
}

/**
 * Round-3 review, MUST-FIX 2. `Missing Plaid account_id` shares the mapping
 * clearer with `Unmapped Plaid account`, but the sentence that names it is
 * false for this one: connecting an account never clears a line that arrived
 * with no account on it. The declared sentence is not used here, and the raw
 * `system:processing-mark` code is not printed either — the account manager has
 * no mapping to hand to support. The face (card-presenter) says the same thing
 * in its own words.
 */
export const MISSING_ACCOUNT_EXIT_SENTENCE =
  "It can't be fixed from the console. It clears on its own when the line is processed again.";

/**
 * Blocker exit sentence — from the shared declaration, read for the person
 * looking at the card. DECIDED (owner, round-12 decision 4): "Remove the
 * "(system: …)" codes from the AM's to-do cards. Keep them under "Show
 * details"." The role sentences carry no code; `blockerClearerCode` supplies
 * the raw name to the disclosure. Mirror (both directions): do not invent a
 * route (R13O-3); do not claim automatic clear when the AM must act
 * (hard-match refuse keep-bank); do not deny a clearer that exists (R12O-6 /
 * R14O-4 / R16O-2). The one exception is `missing-account-id`, whose mapping
 * clearer exists but whose mapping sentence does not apply (round-3 review,
 * MUST-FIX 2).
 */
export function blockerExitSentence(
  item: QueueItem,
  role: StaffRole = "account_manager",
): string {
  const family = resolveBlockerExitFamily(item);
  if (family === null) {
    // The attention sentence is the same for both roles, and it never named a
    // clearer code.
    return ATTENTION_EXIT_SENTENCE;
  }
  // The one exception (carryover): `missing-account-id` keeps its own sentence,
  // because the mapping clearer exists but its sentence is false for this line.
  if (family.id === "missing-account-id") return MISSING_ACCOUNT_EXIT_SENTENCE;
  // Decision 4: the role's sentence from the declaration, already written with
  // no "(system: …)" code. The census override keeps the one card-body case
  // (carryover: the role sentences cover only remaining_candidate_exists).
  const roleSentence = role === "owner" ? family.ownerSentence : family.amSentence;
  return family.id === "unmatched-hub-gl"
    ? unmatchedHubGlExitSentence(item, roleSentence)
    : roleSentence;
}

/**
 * The clearer's raw name for the "Show details" block (decision 4). The face
 * never carries it; the disclosure may, so support can trace the clear. The
 * missing-account override keeps its own sentence with no code (carryover).
 */
export function blockerClearerCode(item: QueueItem): string | null {
  const family = resolveBlockerExitFamily(item);
  if (family === null || family.id === "missing-account-id") return null;
  const code = family.resolvedBy;
  return code !== null && code.length > 0 ? code : null;
}

/** Escalate-note placeholder — states matching exit kind. */
export function blockerEscalatePlaceholder(item: QueueItem): string {
  const family = resolveBlockerExitFamily(item);
  if (family === null) return ATTENTION_ESCALATE_PLACEHOLDER;
  if (family.id === "unmatched-hub-gl") {
    return unmatchedHubGlEscalatePlaceholder(item, family.escalatePlaceholder);
  }
  return family.escalatePlaceholder;
}

/**
 * The account manager's control word for handing a card to the owner
 * (rd-polish-rules §1). It is a control label, not a sentence: the button opens
 * the note and posts the escalation. Held ONCE (E-B5) and recorded as a
 * correct-use entry; no capability row models "escalate this card".
 */
const ESCALATE_TO_OWNER_LABEL = "Ask the owner";

/**
 * Primary escalate control label — names keep-bank where that is the exit
 * (R16O-1). Every other blocker keeps the account manager's ask, in the one
 * decided word ("Ask the owner", rd-polish-rules §1 role rule); the owner is
 * never offered it, so no role reads a hand-off to itself (Y6 brief item 3).
 */
export function blockerEscalateButtonLabel(item: QueueItem): string {
  if (blockerExitKind(item) === "hard-match-refuse") {
    return "Keep bank line";
  }
  return ESCALATE_TO_OWNER_LABEL;
}

/** Confirm control for the open escalate fields. */
export function blockerEscalateConfirmLabel(item: QueueItem): string {
  if (blockerExitKind(item) === "hard-match-refuse") {
    return "Confirm keep-bank";
  }
  return "Send to owner";
}

/** Success copy after escalate POST — must not teach owner-handoff for keep-bank. */
export function blockerEscalateSuccessText(item: QueueItem): string {
  if (blockerExitKind(item) === "hard-match-refuse") {
    return "Keep-bank recorded — clears on the next match pass.";
  }
  return "Escalated to owner.";
}

/**
 * Window + hard cap for the **rejected** Today reader only.
 *
 * Asymmetry (orchestrator 2026-08-03, correcting N-G): informational states
 * expire; action-needed states do not. `rejected` needs no action — bounding
 * it keeps old noise off Today. `failed` needs action — it is **not**
 * time-bounded and **not** capped (a timer is not a resolution).
 */
export const RECENT_REJECTED_WINDOW_MS = 14 * 24 * 60 * 60 * 1000;
export const RECENT_REJECTED_LIMIT = 10;

/**
 * Timestamp for the rejected recent-window. Prefer amDecidedAt, else createdAt.
 * Unparseable → null (caller fail-opens toward visibility — F5).
 */
function rejectedTimestampMs(row: RuleChangeRequest): number | null {
  const raw = row.amDecidedAt ?? row.createdAt;
  const t = Date.parse(raw);
  return Number.isNaN(t) ? null : t;
}

function isRecentlyRejected(
  row: RuleChangeRequest,
  nowMs: number = Date.now(),
): boolean {
  const t = rejectedTimestampMs(row);
  if (t === null) return true; // fail open toward visibility
  return nowMs - t <= RECENT_REJECTED_WINDOW_MS;
}

export interface RecentRejectedSelection {
  /** Rows inside the window, newest first, before the hard cap. */
  inWindow: RuleChangeRequest[];
  /** Rows initially rendered (≤ LIMIT). */
  shown: RuleChangeRequest[];
  /** True count in-window (N-F — report before slice). */
  totalInWindow: number;
  /** True when the hard cap dropped rows (disclose + offer Show all — F4). */
  capFired: boolean;
}

/**
 * Rejected-only window + cap (N-F). Count is taken before slice; headings
 * must use `totalInWindow`, not `shown.length`. Unparseable timestamps sort
 * as newest so fail-open toward visibility is not defeated by the cap (F5).
 */
export function selectRecentRejected(
  rows: RuleChangeRequest[],
  nowMs: number = Date.now(),
): RecentRejectedSelection {
  const inWindow = rows
    .filter((r) => r.status === "rejected" && isRecentlyRejected(r, nowMs))
    .sort((a, b) => {
      // F5: null (unparseable) sorts as nowMs — front of newest-first — so
      // the cap cannot hide the row we claimed to fail-open toward showing.
      const ta = rejectedTimestampMs(a) ?? nowMs;
      const tb = rejectedTimestampMs(b) ?? nowMs;
      if (tb !== ta) return tb - ta;
      return a.id.localeCompare(b.id);
    });
  const totalInWindow = inWindow.length;
  const shown = inWindow.slice(0, RECENT_REJECTED_LIMIT);
  return {
    inWindow,
    shown,
    totalInWindow,
    capFired: totalInWindow > RECENT_REJECTED_LIMIT,
  };
}

/**
 * Fetch one status independently (N-A). A failure returns null for that
 * status alone — never discards another status's rows via a shared catch.
 * Do not wrap awaiting/failed/rejected in one try/catch (that was the
 * High finding that stranded the approve affordance).
 */
async function settledRuleList(
  client: AdminClient,
  slug: CompanySlug,
  status: RuleChangeRequestStatusFilter,
): Promise<RuleChangeRequest[] | null> {
  try {
    const { requests } = await client.listRuleChangeRequests(slug, { status });
    return requests;
  } catch {
    return null;
  }
}

export interface QueuePageOptions {
  client: AdminClient;
  operatorId: string;
  /**
   * Today is fleet-wide (addendum: one unfiltered queue call). The one
   * exception is a capped fleet list: the Worker then returns only the oldest
   * 500 across all companies, so a company named here (or by Home's Review
   * button) is read with its own company-scoped list and its cards show
   * (integration re-check E5). It also scopes the Dismissed read.
   *
   * rd16 U review, item 4: the shell hands in the same value the header shows,
   * through `slugText` (the sanctioned slug-to-key boundary).
   */
  companyFilter?: string;
  /**
   * The company To review is scoped to ("" = all companies). The shell owns it
   * (rd16 U review, item 4): it passes the value in so the header and the list
   * always agree, and the page's own filter changes are reported back through
   * `onCompanyFilterChange`. The page also writes it on its own re-renders so a
   * refresh after an answer keeps the person where they were. A company key,
   * never words.
   */
  reviewScope?: CompanySlug;
  /**
   * Report the company scope the LIST is showing whenever it changes — the
   * page's own company filter, or a capped-list re-read. The shell stores it so
   * the header picker and the list cannot disagree (rd16 U review, item 4).
   */
  onCompanyFilterChange?: (company: CompanySlug | "") => void;
  /**
   * Set by the page itself, never by a caller: the `YYYY-MM` month scope the
   * page settled on (a hand-off from Months or Activity), so its own later
   * re-renders keep the month filter. "" means every month.
   */
  reviewMonth?: string;
  /**
   * Set by the page itself, never by a caller: the exact set of cards a Months
   * line counted (round 2, MUST-FIX 1), so "4 cards to review" opens those
   * four rather than the month's union.
   */
  reviewSet?: ReviewCardSet;
  root: HTMLElement;
  /**
   * The page clock, for the Worker's 24-month bucket window (round 3,
   * MUST-FIX 3). Tests pin it; the page otherwise uses the wall clock.
   */
  now?: () => Date;
  /** Return false if a newer navigate superseded this render; drop results. */
  isCurrent?: () => boolean;
  /** Cross-nav: open Rules scoped to a company (confirmation "View rules"). */
  navigateTo?: (route: RouteName, opts?: { company?: CompanySlug }) => Promise<void>;
  /**
   * Notify shell of pending count for the Today badge (null = suppress).
   * `capped` true means the fleet list hit the server cap, so the number is a
   * lower bound.
   */
  onPendingCount?: (count: number | null, capped?: boolean) => void;
  /** Optional company display-name map (slug → short label). */
  companyLabels?: Map<string, string>;
  /** The shell's ledger segment to company name read (rd13 L1, review round 2). */
  companySegments?: ReadonlyMap<string, string>;
  /** Presentation role — AM reward state (§4.5) when account_manager. */
  role?: StaffRole | null;
  /**
   * The filters the page was showing when it started a re-render (round 10,
   * item 8b). A bulk accept re-reads the list; the Type/Show/Search/Company
   * choice must not snap back to All under the person.
   */
  reviewFilters?: TodayListFilters;
}

/**
 * Successful dispositions that re-render Today (dismiss / rule decision) stash
 * their live-region message here so clear(root) does not erase it.
 * Bound to one render attempt: renderQueuePage consume-or-clears on every
 * exit (success, failure, or superseded) so a stranded stash cannot leak
 * onto an unrelated later render.
 */
let pendingTodayAnnounce: string | null = null;

/**
 * Item 9 (Q-c): the "More: aliases and counterparties" disclosure survives the
 * page re-render an alias action triggers, so an open panel does not snap shut.
 * Bound to one render attempt like the announce stash.
 */
let pendingMoreOpen = false;

/**
 * G6: a successful Reopen re-renders the page. The visible confirmation line
 * and the open state of the Dismissed section survive that re-render through
 * these two stashes, bound to one render attempt like the two above.
 */
let pendingTodayConfirmation: string | null = null;

/**
 * R4-12 (round 3): the failure sentence of a write that finished after a newer
 * render took the screen. `today-live` is clipped to one pixel, so replaying a
 * failure there alone shows a sighted person nothing; the next render paints
 * this into a visible `[data-role="carried-failure"]` line beside the
 * confirmation line.
 *
 * R4-13 / E3: it is a LIST, and the card's id travels with it. Two held writes
 * that finish under one Refresh with opposite outcomes have two sentences, not
 * one: a single slot kept whichever landed last and silently dropped the other.
 * The list is drained when a render actually PAINTS it (never at render start),
 * so a render that is superseded between its start and its paint cannot swallow
 * a failure the newer render then has to show.
 */
type CarriedFailure = {
  /** The card the failure belongs to; null for a page-wide (bulk) failure. */
  itemId: number | null;
  text: string;
};
let pendingTodayFailures: CarriedFailure[] = [];
let pendingDismissedOpen = false;

/**
 * R4-11 / R4-12: writes the page knows are still running, keyed by card id and
 * by the page root. It survives a whole-page re-render (the same root) but a
 * new mount starts clean: a stale id from a previous page must never disable a
 * card on this one. A write that finishes under a newer render hands its
 * outcome to that render instead of a detached node.
 */
const inFlightCardWritesByRoot = new WeakMap<HTMLElement, Set<number>>();

function writesInFlightFor(root: HTMLElement): Set<number> {
  let set = inFlightCardWritesByRoot.get(root);
  if (set === undefined) {
    set = new Set<number>();
    inFlightCardWritesByRoot.set(root, set);
  }
  return set;
}

/**
 * Round 2, item 3: a bulk Accept this page started, keyed by the page root and
 * the company it was run for. A whole-page re-render rebuilds the panel; the
 * rebuilt panel must come back saying the run is in flight instead of offering
 * a second press of the same ids.
 */
let bulkAcceptsInFlightByRoot = new WeakMap<HTMLElement, Set<CompanySlug>>();

function bulkAcceptsInFlightFor(root: HTMLElement): Set<CompanySlug> {
  let set = bulkAcceptsInFlightByRoot.get(root);
  if (set === undefined) {
    set = new Set<string>();
    bulkAcceptsInFlightByRoot.set(root, set);
  }
  return set;
}

/**
 * One render generation per page root. A stale async render from a page that
 * is no longer on screen must not make a live card look superseded.
 */
let queueRenderGenerationByRoot = new WeakMap<HTMLElement, number>();

function nextRenderToken(root: HTMLElement): number {
  const next = (queueRenderGenerationByRoot.get(root) ?? 0) + 1;
  queueRenderGenerationByRoot.set(root, next);
  return next;
}

function currentRenderToken(root: HTMLElement): number {
  return queueRenderGenerationByRoot.get(root) ?? 0;
}

export function resetQueueWriteStateForTests(): void {
  queueRenderGenerationByRoot = new WeakMap<HTMLElement, number>();
  bulkAcceptsInFlightByRoot = new WeakMap<HTMLElement, Set<string>>();
}

/** Forward-only: detach stops future matches; historical matches stay. */
export const ALIAS_DETACH_FORWARD_ONLY_COPY =
  "Detaching stops this nickname matching going forward. Transfers already matched stay matched.";

/** Forward-only: withdraw stops future matches; historical matches stay. */
export const RECOGNIZED_COUNTERPARTY_FORWARD_ONLY_COPY =
  "Withdrawing stops this party matching going forward. Transfers already matched stay matched.";

export const MARK_EXTERNAL_NOT_A_LEDGER_COPY =
  "This marks the party as one whose books we do not keep. We will not keep books, open a workspace, or add a backup obligation for it. It becomes a fleet-wide standing matching rule.";

export const MARK_EXTERNAL_FLEET_SCOPE_COPY =
  "This recognition applies across companies we keep books for. Withdraw it from the recognized counterparties list if the pick was wrong.";

export const CREATE_COMPANY_DRAFT_COPY =
  "This creates an onboarding draft, not a provisioned ledger company. Books, a workspace, and backup start only after onboarding finishes.";

export const ALIAS_FLEET_SCOPE_COPY =
  "Confirming this nickname is a fleet-wide standing matching rule for a company whose books we keep.";

async function paintConfirmedAliasesPanel(opts: {
  client: AdminClient;
  host: HTMLElement;
  stillCurrent: () => boolean;
  announce: (message: string) => void;
  onChanged: (message?: string) => void;
  /** The reader's role, so a refusal is worded for them (round-2 LOW). */
  role?: StaffRole | null | undefined;
}): Promise<void> {
  const { client, host } = opts;
  clear(host);
  host.append(
    h("p", { class: "muted", "data-role": "counterparty-aliases-loading" }, [
      "Loading confirmed aliases…",
    ]),
  );
  let aliases: CounterpartyAlias[];
  try {
    const body = await client.listCounterpartyAliases();
    aliases = body.aliases;
  } catch {
    if (!opts.stillCurrent()) return;
    clear(host);
    host.append(
      h("p", { class: "banner banner-err", "data-role": "counterparty-aliases-error" }, [
        "Couldn't load confirmed aliases.",
      ]),
    );
    return;
  }
  if (!opts.stillCurrent()) return;
  clear(host);

  const active = aliases.filter((row) => row.active);
  const detached = aliases.filter((row) => !row.active);

  host.append(
    h("h2", { class: "lede", "data-role": "counterparty-aliases-heading" }, [
      active.length === 0
        ? "Confirmed aliases"
        : `Confirmed aliases (${countOrMoreText(active.length, !counterpartyActiveCut(aliases.length, detached.length))} active)`,
    ]),
  );
  host.append(
    h("p", { class: "muted", "data-role": "counterparty-aliases-lede" }, [
      "A confirmed nickname is a standing matching rule. Detach if the pick was wrong.",
    ]),
  );

  const list = h("ul", { class: "token-list", "data-role": "counterparty-aliases-list" });
  if (active.length === 0) {
    list.append(
      h("li", { class: "empty", "data-role": "counterparty-aliases-empty" }, [
        "No confirmed aliases.",
      ]),
    );
  }
  for (const row of active) {
    list.append(renderActiveAliasRow(row, client, {
      announce: opts.announce,
      onChanged: opts.onChanged,
      role: opts.role,
    }));
  }
  host.append(list);

  if (detached.length > 0) {
    host.append(
      h("h3", { class: "muted", "data-role": "counterparty-aliases-detached-heading" }, [
        `Detached (${countOrMoreText(detached.length, !counterpartyListCapped(aliases.length))})`,
      ]),
    );
    const history = h("ul", {
      class: "token-list",
      "data-role": "counterparty-aliases-detached-list",
    });
    for (const row of detached) {
      const who = row.detachedBy ?? "—";
      const when = row.detachedAt !== null ? dateText(row.detachedAt) : "—";
      history.append(
        h("li", {
          class: "token-row revoked",
          "data-role": "counterparty-alias-detached",
          "data-alias-id": String(row.id),
        }, [
          h("div", {}, [
            h("strong", { "data-role": "counterparty-alias-text" }, [
              amVisibleDisplayText(row.aliasText),
            ]),
            h("span", { class: "muted" }, [
              ` → ${amVisibleDisplayText(row.companyDisplayName)} · detached ${when} by ${amVisibleDisplayText(who)}`,
            ]),
          ]),
        ]),
      );
    }
    host.append(history);
  }
}

async function paintRecognizedCounterpartiesPanel(opts: {
  client: AdminClient;
  host: HTMLElement;
  stillCurrent: () => boolean;
  announce: (message: string) => void;
  onChanged: (message?: string) => void;
  /** The render's one company directory (B1c-1). */
  directory: face.CompanyDirectory;
  /** The reader's role, so a refusal is worded for them (round-2 LOW). */
  role?: StaffRole | null | undefined;
}): Promise<void> {
  const { client, host } = opts;
  clear(host);
  host.append(
    h("p", { class: "muted", "data-role": "recognized-counterparties-loading" }, [
      "Loading recognized counterparties…",
    ]),
  );
  let counterparties: RecognizedCounterparty[];
  try {
    const body = await client.listRecognizedCounterparties();
    counterparties = body.counterparties;
  } catch {
    if (!opts.stillCurrent()) return;
    clear(host);
    host.append(
      h("p", { class: "banner banner-err", "data-role": "recognized-counterparties-error" }, [
        "Couldn't load recognized counterparties.",
      ]),
    );
    return;
  }
  if (!opts.stillCurrent()) return;
  clear(host);

  const active = counterparties.filter((row) => row.active);
  const withdrawn = counterparties.filter((row) => !row.active);

  host.append(
    h("h2", { class: "lede", "data-role": "recognized-counterparties-heading" }, [
      active.length === 0
        ? "Recognized counterparties"
        : `Recognized counterparties (${countOrMoreText(active.length, !counterpartyActiveCut(counterparties.length, withdrawn.length))} active)`,
    ]),
  );
  host.append(
    h("p", { class: "muted", "data-role": "recognized-counterparties-lede" }, [
      "A recognized party is someone we transact with whose books we do not keep. Withdraw if the pick was wrong.",
    ]),
  );

  const list = h("ul", {
    class: "token-list",
    "data-role": "recognized-counterparties-list",
  });
  if (active.length === 0) {
    list.append(
      h("li", { class: "empty", "data-role": "recognized-counterparties-empty" }, [
        "No recognized counterparties.",
      ]),
    );
  }
  for (const row of active) {
    list.append(renderActiveRecognizedRow(row, client, {
      announce: opts.announce,
      onChanged: opts.onChanged,
      role: opts.role,
      directory: opts.directory,
    }));
  }
  host.append(list);

  if (withdrawn.length > 0) {
    host.append(
      h("h3", { class: "muted", "data-role": "recognized-counterparties-withdrawn-heading" }, [
        `Withdrawn (${countOrMoreText(withdrawn.length, !counterpartyListCapped(counterparties.length))})`,
      ]),
    );
    const history = h("ul", {
      class: "token-list",
      "data-role": "recognized-counterparties-withdrawn-list",
    });
    for (const row of withdrawn) {
      const who = row.withdrawnBy ?? "—";
      const when = row.withdrawnAt !== null ? dateText(row.withdrawnAt) : "—";
      history.append(
        h("li", {
          class: "token-row revoked",
          "data-role": "recognized-counterparty-withdrawn",
          "data-recognition-id": String(row.id),
        }, [
          h("div", {}, [
            h("strong", { "data-role": "recognized-counterparty-label" }, [
              amVisibleDisplayText(row.labelText),
            ]),
            h("span", { class: "muted" }, [
              ` · origin ${face.company(row.originCompany, opts.directory, "sentence")} · withdrawn ${when} by ${amVisibleDisplayText(who)}`,
            ]),
          ]),
        ]),
      );
    }
    host.append(history);
  }
}

function renderActiveRecognizedRow(
  row: RecognizedCounterparty,
  client: AdminClient,
  opts: {
    announce: (message: string) => void;
    onChanged: (message?: string) => void;
    role?: StaffRole | null | undefined;
    directory: face.CompanyDirectory;
  },
): HTMLLIElement {
  const item = h("li", {
    class: "token-row",
    "data-role": "recognized-counterparty-active",
    "data-recognition-id": String(row.id),
  });
  const characterBit =
    row.reviewedCharacter !== null ? ` · ${row.reviewedCharacter}` : "";
  item.append(
    h("div", {}, [
      h("strong", { "data-role": "recognized-counterparty-label" }, [
        amVisibleDisplayText(row.labelText),
      ]),
      h("span", { class: "muted" }, [
        ` · origin ${face.company(row.originCompany, opts.directory, "sentence")}${amVisibleDisplayText(characterBit)} · confirmed ${dateText(row.confirmedAt)} by ${amVisibleDisplayText(row.confirmedBy)}`,
      ]),
    ]),
  );

  const actions = h("div", { class: "actions", "data-role": "recognized-counterparty-actions" });
  const withdrawBtn = h(
    "button",
    {
      type: "button",
      class: "btn btn-ghost",
      "data-action": "withdraw-recognized-counterparty",
      "aria-label": `Withdraw recognition ${amVisibleDisplayText(row.labelText)}`,
    },
    ["Withdraw"],
  );
  const confirm = h("div", {
    class: "confirm-line",
    "data-role": "recognized-counterparty-withdraw-confirm",
    hidden: "true",
  });
  confirm.append(
    h("p", { "data-role": "recognized-counterparty-withdraw-copy" }, [
      RECOGNIZED_COUNTERPARTY_FORWARD_ONLY_COPY,
    ]),
  );
  const confirmBtn = h(
    "button",
    {
      type: "button",
      class: "btn",
      "data-action": "withdraw-recognized-counterparty-confirm",
    },
    ["Confirm withdraw"],
  );
  const cancelBtn = h(
    "button",
    {
      type: "button",
      class: "btn btn-ghost",
      "data-action": "withdraw-recognized-counterparty-cancel",
    },
    ["Cancel"],
  );
  confirm.append(h("div", { class: "actions" }, [confirmBtn, cancelBtn]));

  withdrawBtn.addEventListener("click", () => {
    confirm.hidden = false;
    confirm.removeAttribute("hidden");
    confirmBtn.focus();
  });
  cancelBtn.addEventListener("click", () => {
    confirm.hidden = true;
    confirm.setAttribute("hidden", "true");
  });
  confirmBtn.addEventListener("click", () => {
    void (async () => {
      const outcome = await runWrite({
        key: cardWriteKey(item, "withdraw-recognized"),
        scope: item,
        gate: () => null,
        send: () => client.withdrawRecognizedCounterparty(row.id),
        stillCurrent: () => item.isConnected,
      });
      if (outcome.kind !== "landed") {
        opts.announce(writeFailureCopy(outcome.error, opts.role));
        return;
      }
      // M2: the page callback runs after `runWrite` returns, so a re-render
      // under the write cannot swallow the confirmation.
      const message = `Withdrew recognition of ${amVisibleDisplayText(row.labelText)}.`;
      opts.announce(message);
      opts.onChanged(message);
    })();
  });

  actions.append(withdrawBtn);
  item.append(actions, confirm);
  return item;
}

function renderActiveAliasRow(
  row: CounterpartyAlias,
  client: AdminClient,
  opts: {
    announce: (message: string) => void;
    onChanged: (message?: string) => void;
    role?: StaffRole | null | undefined;
  },
): HTMLLIElement {
  const item = h("li", {
    class: "token-row",
    "data-role": "counterparty-alias-active",
    "data-alias-id": String(row.id),
  });
  item.append(
    h("div", {}, [
      h("strong", { "data-role": "counterparty-alias-text" }, [
        amVisibleDisplayText(row.aliasText),
      ]),
      h("span", { class: "muted" }, [
        ` → ${amVisibleDisplayText(row.companyDisplayName)} · confirmed ${dateText(row.confirmedAt)} by ${amVisibleDisplayText(row.confirmedBy)}`,
      ]),
    ]),
  );

  const actions = h("div", { class: "actions", "data-role": "counterparty-alias-actions" });
  const detachBtn = h(
    "button",
    {
      type: "button",
      class: "btn btn-ghost",
      "data-action": "detach-counterparty-alias",
      "aria-label": `Detach alias ${amVisibleDisplayText(row.aliasText)}`,
    },
    ["Detach"],
  );
  const confirm = h("div", {
    class: "confirm-line",
    "data-role": "counterparty-alias-detach-confirm",
    hidden: "true",
  });
  confirm.append(
    h("p", { "data-role": "counterparty-alias-detach-copy" }, [
      ALIAS_DETACH_FORWARD_ONLY_COPY,
    ]),
  );
  const confirmBtn = h(
    "button",
    {
      type: "button",
      class: "btn",
      "data-action": "detach-counterparty-alias-confirm",
    },
    ["Confirm detach"],
  );
  const cancelBtn = h(
    "button",
    {
      type: "button",
      class: "btn btn-ghost",
      "data-action": "detach-counterparty-alias-cancel",
    },
    ["Cancel"],
  );
  confirm.append(h("div", { class: "actions" }, [confirmBtn, cancelBtn]));

  detachBtn.addEventListener("click", () => {
    confirm.hidden = false;
    confirm.removeAttribute("hidden");
    detachBtn.setAttribute("hidden", "true");
    confirmBtn.focus();
  });
  cancelBtn.addEventListener("click", () => {
    confirm.hidden = true;
    confirm.setAttribute("hidden", "true");
    detachBtn.removeAttribute("hidden");
    detachBtn.focus();
  });
  confirmBtn.addEventListener("click", () => {
    void (async () => {
      const outcome = await runWrite({
        key: cardWriteKey(item, "detach-alias"),
        scope: item,
        gate: () => null,
        send: () => client.detachCounterpartyAlias(row.id),
        stillCurrent: () => item.isConnected,
      });
      if (outcome.kind !== "landed") {
        opts.announce(writeFailureCopy(outcome.error, opts.role));
        return;
      }
      opts.onChanged(
        `Detached “${amVisibleDisplayText(row.aliasText)}” from ${amVisibleDisplayText(row.companyDisplayName)}.`,
      );
    })();
  });

  actions.append(detachBtn, confirm);
  item.append(actions);
  return item;
}

/** Test seam — reset between tests so a stranded stash cannot cross cases. */
export function resetPendingTodayAnnounceForTests(): void {
  pendingTodayAnnounce = null;
  pendingTodayConfirmation = null;
  pendingTodayFailures = [];
  pendingDismissedOpen = false;
  resetChartChoiceMemory();
}

async function loadQueueChartSnapshots(
  client: AdminClient,
  companies: readonly string[],
): Promise<Map<string, ChartLoadState>> {
  const unique = [...new Set(companies)];
  const map = new Map<string, ChartLoadState>();
  if (unique.length === 0) return map;
  const loaded = await mapWithConcurrency(unique, 4, async (slug) => {
    try {
      const snapshot = await client.getChartSnapshot(slug);
      return { slug, load: chartLoadFromSnapshot(snapshot) };
    } catch {
      return { slug, load: chartLoadFromError(slug) };
    }
  });
  for (const row of loaded) {
    map.set(row.slug, row.load);
  }
  return map;
}

const STAGE_MS = 300;

const SHORT_MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

const NAMED_PERIOD = /(January|February|March|April|May|June|July|August|September|October|November|December)\s+(\d{4})/;

/**
 * The Worker's month-close window (`MAX_WINDOW_MONTHS` in
 * `ingest-worker/src/months.ts`): the current month and the 23 before it.
 */
const WORKER_MONTH_WINDOW = 24;

function periodIndex(period: string): number {
  const year = Number.parseInt(period.slice(0, 4), 10);
  const month = Number.parseInt(period.slice(5, 7), 10);
  return year * 12 + (month - 1);
}

/**
 * Whether the Worker's `buildMonthWindow` can name this period: its current
 * UTC month and the 23 before it. `monthForSubject` only searches that window,
 * so a subject naming an older month — or one after the current month — is
 * left undated there; the portal must leave it undated too or the undated line
 * and To review disagree (round 3, MUST-FIX 3). The Worker also floors the
 * window at the company's books_start, which the Months payload does not
 * carry; see Q4.
 */
function withinWorkerMonthWindow(period: string, now: Date): boolean {
  const current = now.getUTCFullYear() * 12 + now.getUTCMonth();
  const index = periodIndex(period);
  return index <= current && index > current - WORKER_MONTH_WINDOW;
}

/** `October 2026` -> `2026-10`, for the Worker's `formatPeriodLabel` output. */
function periodFromMonthLabel(label: string): string | null {
  const match = /^([A-Z][a-z]+) ([0-9]{4})$/.exec(label);
  if (match === null) return null;
  const monthName = match[1];
  const year = match[2];
  if (monthName === undefined || year === undefined) return null;
  const index = SHORT_MONTH_NAMES.indexOf(monthName as (typeof SHORT_MONTH_NAMES)[number]);
  if (index < 0) return null;
  return `${year}-${String(index + 1).padStart(2, "0")}`;
}

/**
 * Every period a subject could possibly name: one per `<Month> <YYYY>` and per
 * `YYYY-MM` it contains. A candidate only counts once a Worker constructor has
 * reproduced the subject, so a subject that merely mentions a month is not
 * dated (round 2, MUST-FIX 2).
 */
function candidatePeriods(subject: string): string[] {
  const found: string[] = [];
  const add = (period: string | null): void => {
    if (period !== null && !found.includes(period)) found.push(period);
  };
  for (const match of subject.matchAll(new RegExp(NAMED_PERIOD.source, "g"))) {
    if (match[1] !== undefined && match[2] !== undefined) {
      add(periodFromMonthLabel(`${match[1]} ${match[2]}`));
    }
  }
  for (const match of subject.matchAll(/\b(\d{4}-(?:0[1-9]|1[0-2]))\b/g)) {
    add(match[1] ?? null);
  }
  return found;
}

/**
 * The month `ingest-worker/src/months.ts` buckets a subject into, mirrored
 * shape for shape and in the same order (`monthForSubject`): the two
 * month-close subjects, the closed-month-write prefix, the `", <Month> <YYYY>"`
 * suffix (statement blockers), the refused hub push (`YYYY-MM`) and the
 * unmatched hub GL batch. Nothing else names a month.
 */
export function workerMonthForSubject(
  subject: string,
  now: Date = new Date(),
): string | null {
  // Newest first, exactly as `monthForSubject` walks `buildMonthWindow`, and
  // only the periods that window can name.
  const candidates = candidatePeriods(subject)
    .filter((period) => withinWorkerMonthWindow(period, now))
    .sort((a, b) => periodIndex(b) - periodIndex(a));
  for (const period of candidates) {
    if (subject === workerCloseBlockerSubject(period)) return period;
  }
  for (const period of candidates) {
    if (subject === workerPeriodReopenedBlockerSubject(period)) return period;
  }
  for (const period of candidates) {
    if (subject.startsWith(workerClosedPeriodWriteBlockerSubjectPrefix(period))) {
      return period;
    }
  }
  for (const period of candidates) {
    if (subject.endsWith(`, ${workerFormatPeriodLabel(period)}`)) return period;
  }
  for (const period of candidates) {
    if (subject === workerHubGlPushRefusedSubject(period)) return period;
  }
  for (const period of candidates) {
    if (
      subject.startsWith(
        `${WORKER_UNMATCHED_HUB_GL_PREFIX}${workerFormatPeriodLabel(period)} (`,
      )
    ) {
      return period;
    }
  }
  return null;
}

/**
 * The month a card belongs to, as the Worker buckets it in
 * `ingest-worker/src/months.ts`: its transaction date first, then the month its
 * subject names. The Months link counts that bucket, so To review must narrow
 * by the same rule or the two numbers would disagree (round 10, C-4).
 */
export function queueItemPeriod(item: QueueItem, now: Date = new Date()): string | null {
  const txn = /^\d{4}-\d{2}/.exec(item.txnDate ?? "");
  if (txn !== null) return txn[0];
  return workerMonthForSubject(item.subject ?? "", now);
}

/** How a counted set is named in the scope notice (singular, plural). */
const REVIEW_SET_NOUNS: Record<ReviewCardSet, readonly [string, string]> = {
  cards_to_review: ["card to review", "cards to review"],
  cards_with_owner: ["escalated card", "escalated cards"],
  close_blocked: ["month-close card", "month-close cards"],
  closed_month_write_blocked: [
    "closed-month change card",
    "closed-month change cards",
  ],
  reclose_needed: ["card to close again", "cards to close again"],
};

/**
 * Whether a card is in the set a Months line counted, for that month (or for
 * the undated line when `period` is null). This is the Worker's counting
 * ladder (`getAdminMonths`) in the same order: the close subjects first, then
 * the closed-month-write prefix, then escalation, and everything else is
 * `cards_to_review`. Cards sent to the practice are not in this list at all.
 */
export function itemMatchesReviewSet(
  item: QueueItem,
  period: string | null,
  set: ReviewCardSet,
  now: Date = new Date(),
): boolean {
  const own = queueItemPeriod(item, now);
  if (period === null) {
    if (own !== null) return false;
  } else if (own !== period) {
    return false;
  }
  const subject = item.subject ?? "";
  if (set === "close_blocked") {
    return period !== null && subject === workerCloseBlockerSubject(period);
  }
  if (set === "reclose_needed") {
    return period !== null && subject === workerPeriodReopenedBlockerSubject(period);
  }
  if (set === "closed_month_write_blocked") {
    return (
      period !== null &&
      subject.startsWith(workerClosedPeriodWriteBlockerSubjectPrefix(period))
    );
  }
  // A lifecycle card is counted by its own line whatever its escalation, so
  // it is never part of the owner or review sets for that month.
  const lifecycle =
    period !== null &&
    (subject === workerCloseBlockerSubject(period) ||
      subject === workerPeriodReopenedBlockerSubject(period) ||
      subject.startsWith(workerClosedPeriodWriteBlockerSubjectPrefix(period)));
  if (set === "cards_with_owner") return isWithOwner(item) && !lifecycle;
  return !isWithOwner(item) && !lifecycle;
}

/** Whether a card is the one a hand-off named (bank id, wallet id, or card id). */
export function queueItemMatchesCardRef(item: QueueItem, ref: ReviewCardRef): boolean {
  if (ref.source === "queue") return String(item.id) === ref.refId;
  if (ref.source === "moov") {
    const subject = item.subject ?? "";
    return (
      subject.startsWith(MOOV_CARD_SUBJECT_PREFIX) &&
      subject.slice(MOOV_CARD_SUBJECT_PREFIX.length).trim() === ref.refId
    );
  }
  return item.plaidTransactionId === ref.refId;
}

/**
 * The fleet-list cap line in plain words (round 10, item 8f). The server
 * returns its own LIMIT; say what is shown and that more may exist.
 */
export function queueCapSentence(): string {
  return `Showing the oldest ${String(FLEET_QUEUE_LIST_LIMIT)}. More may exist.`;
}

/**
 * The one company directory for a To review render (B1c-1).
 *
 * Built from the company rows the page already read (`listCompanies`), so no
 * read is added (request-budget.test.ts pins them). `complete` is the honest
 * answer to "did that read see every company": false when it failed, so a
 * company the directory cannot name reads "this company" (decision 14) rather
 * than a guess. When the read failed, the shell's name map is the fallback
 * source of rows — it is the same words the page used before.
 */
function queueDirectory(
  companies: readonly Company[] | null,
  labels: ReadonlyMap<string, string> | undefined,
  segments: ReadonlyMap<string, string> | undefined,
): face.CompanyDirectory {
  const rows: Array<{ slug: CompanySlug; displayName: string | null }> =
    companies === null
      ? []
      : companies.map((company) => ({
          slug: company.slug,
          displayName: company.displayName,
        }));
  const known = new Set(rows.map((row) => row.slug));
  // The caller's name map is the same words the shell read; a caller that
  // supplies names for companies this page's read does not carry (a unit test,
  // a company list that came back empty) keeps them. The row read wins.
  for (const [slug, displayName] of labels ?? new Map<string, string>()) {
    if (known.has(slug)) continue;
    known.add(slug);
    rows.push({ slug, displayName });
  }
  // The directory carries the session's segment read, so every card and list
  // that builds its labels from THIS directory names the company from the
  // intercompany read rather than from the slug (rd13 L1, review round 2).
  return face.createCompanyDirectory(rows, {
    complete: companies !== null,
    segments: segments ?? new Map<string, string>(),
  });
}

/**
 * B1a-4: the ONE return-to-account-manager path. Every card that offers the
 * owner control calls this, so every path clears `escalationClearedAt` and the
 * card stops reading as escalated (the third copy used to leave it set).
 */
function returnCardToAm(args: {
  item: QueueItem;
  client: AdminClient;
  scope: HTMLElement;
  stillCurrent: () => boolean;
}): Promise<WriteOutcome> {
  return runWrite({
    key: cardWriteKey(args.scope, "return-to-am"),
    scope: args.scope,
    gate: () => null,
    send: () => args.client.returnQueueItemToAm(args.item.company, args.item.id),
    afterLanded: () => {
      args.item.escalationClearedAt = new Date().toISOString();
    },
    stillCurrent: args.stillCurrent,
  });
}

/**
 * To review — every pending card in one grouped list.
 * Single fleet-wide GET /v1/admin/queue?status=pending (addendum condition 3).
 * The open row still uses the existing stage card. Q-b owns the face rework.
 */
export async function renderQueuePage(callerOpts: QueuePageOptions): Promise<void> {
  // R4-12: one generation per render attempt. A write that finishes after a
  // newer render owns the screen hands its outcome to that render.
  const renderToken = nextRenderToken(callerOpts.root);
  // The page keeps its own copy of the options so the scope it settles on
  // (capped fleet list, E5) travels with every refresh it starts below.
  const opts: QueuePageOptions = { ...callerOpts };
  // One clock for this render: the Worker's 24-month bucket window is measured
  // from it, so every bucket decision on the page agrees (round 3, MUST-FIX 3).
  const pageClock = opts.now?.() ?? new Date();
  // Bind stash to THIS attempt before any await — consume-or-clear on every exit.
  const reviewHint = takeReviewScopeHint();
  const announceForThisRender = pendingTodayAnnounce;
  pendingTodayAnnounce = null;
  const moreOpenForThisRender = pendingMoreOpen;
  pendingMoreOpen = false;
  const confirmationForThisRender = pendingTodayConfirmation;
  pendingTodayConfirmation = null;
  // Snapshot, and drain only on paint: a superseded render must leave the
  // sentences for the render that actually reaches the screen.
  const failuresForThisRender = [...pendingTodayFailures];
  const dismissedOpenForThisRender = pendingDismissedOpen;
  pendingDismissedOpen = false;

  const { client, root } = opts;
  const inFlightCardWrites = writesInFlightFor(root);
  const stillCurrent = (): boolean => opts.isCurrent?.() ?? true;
  clear(root);
  root.append(h("p", { class: "muted", "data-role": "today-loading" }, ["Loading…"]));

  // listQueue may throw — stash already cleared above, so a later successful
  // render must not announce a disposition whose re-render never completed.
  const companiesPromise = client.listCompanies().then(
    (body) => body.companies,
    () => null,
  );
  const { items } = await client.listQueue({ status: "pending" });
  if (!stillCurrent()) return;

  // M1: worker LIMIT 500 with no has_more — disclose when the page is full.
  const queueCapFired = fleetQueueListCapFired(items.length);

  // E5: once the fleet list is capped it is only the oldest 500 across all
  // companies, so a company whose cards are newer than that has none in it
  // (Home counts 20 for Rentals while To review showed 0). A company the person
  // came for, or picked in the filter, is read with its own scoped list. The
  // uncapped path makes no extra request.
  // The scope is a company KEY, not words: it filters this list and scopes one
  // read. `reviewScope` is the page's own memory of it (B1c-1).
  let scopeCompany: CompanySlug = "";
  let shownItems = items;
  let scopeCapFired = false;
  if (queueCapFired) {
    // `reviewScope` is this page's own memory and is already trimmed; the
    // caller's filter may not be. The tail is a company KEY, so the empty slug
    // is the "all companies" value rather than a widened plain string.
    if (opts.reviewScope !== undefined) {
      // This page's own memory wins, including its "all companies" value.
      scopeCompany = opts.reviewScope;
    } else if (opts.companyFilter !== undefined) {
      scopeCompany = opts.companyFilter.trim();
    } else {
      scopeCompany = reviewHint?.company ?? ALL_COMPANIES;
    }
    opts.reviewScope = scopeCompany;
    if (scopeCompany !== "") {
      const own = await client.listQueue({ company: scopeCompany, status: "pending" });
      if (!stillCurrent()) return;
      shownItems = own.items.filter((item) => item.company === scopeCompany);
      scopeCapFired = fleetQueueListCapFired(own.items.length);
    }
  } else if (opts.reviewScope === undefined && (reviewHint?.company ?? "") !== "") {
    // The fleet list is complete, so the company hint needs no second read: it
    // becomes the page's own company filter below (round 10, C-4). It is kept
    // on the options so this page's later re-renders stay where the person is.
    opts.reviewScope = reviewHint?.company ?? "";
  }

  // The list on this page is a capped slice (the fleet list, or a company's own
  // list that itself hit the cap): every count and total taken from it is a lower
  // bound and must say so, like the nav badge (F3). A company-scoped read that is
  // complete shows exact numbers.
  const listIsLowerBound = queueCapFired && (scopeCompany === "" || scopeCapFired);

  const companies = await companiesPromise;
  // B1c-1: ONE directory per render, from the rows this page already read.
  const directory = queueDirectory(companies, opts.companyLabels, opts.companySegments);
  // The page's ONE account-label context, built once from the same directory
  // its company words come from (rd13 L1): every account this page names takes
  // it, so a between-companies account reads "Due from <company>".
  const labelCompanies = accountLabelContext(directory);
  const hubLinkedSlugs =
    companies === null
      ? null
      : new Set(
          companies
            .filter((c) => c.isActive && c.hubLink !== null)
            .map((c) => c.slug),
        );

  // Stalest first (longest-waiting is most valuable).
  const allPending = shownItems
    .filter((item) => item.status === "pending")
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

  // Decision 10: pass the role the page already holds, so an owner is never
  // demoted to the account manager before the nav bus registers the role.
  const roleForCards: StaffRole = readerRole(opts.role);

  // The ONE card-count derivation (B1c-3, facts.ts). The AM stage excludes
  // escalated items (the owner's work); the owner's stage still includes them
  // so resolution stays on the existing path.
  const personaCards = reviewCards(
    measuredSetOf(allPending, !listIsLowerBound),
    roleForCards,
  );
  const pending = personaCards.openable;

  // A month hand-off (Months, Activity) narrows this persona's list to that
  // period; a Months line also names WHICH cards it counted (round 2,
  // MUST-FIX 1). The hint applies to the first render only; the page keeps it
  // on its own options so its later re-renders do not lose the person's place.
  //
  // The one card the person came for (Activity's "Go to To review") is found
  // among this persona's pending cards BEFORE any month filter: a card the
  // Worker leaves undated (a wallet card whose wallet row is missing) would
  // otherwise be filtered out and the page would say "Nothing needs you"
  // while the card exists (round 2, MUST-FIX 3).
  const openCardRef: ReviewCardRef | null = reviewHint?.openCardRef ?? null;
  const openCard =
    openCardRef === null
      ? undefined
      : pending.find((item) => queueItemMatchesCardRef(item, openCardRef));
  let monthFilter = (opts.reviewMonth ?? reviewHint?.month ?? "").trim();
  if (openCard !== undefined) {
    // The named card decides the month, not the row the person pressed: use
    // its own bucket (or no month at all when it has none).
    monthFilter = queueItemPeriod(openCard, pageClock) ?? "";
  }
  if (monthFilter !== "") opts.reviewMonth = monthFilter;
  else delete opts.reviewMonth;
  const reviewSet: ReviewCardSet | null = opts.reviewSet ?? reviewHint?.set ?? null;
  if (reviewSet !== null) opts.reviewSet = reviewSet;

  // The badge counts the whole fleet list, never the one company this page
  // may be scoped to (E5), and says "or more" when that list is capped (E2).
  const fleetPending = items.filter((item) => item.status === "pending");
  const badgeCount = reviewCards(
    measuredSetOf(fleetPending, !queueCapFired),
    roleForCards,
  ).openable.length;
  opts.onPendingCount?.(badgeCount, queueCapFired);

  const labels = opts.companyLabels ?? new Map<string, string>();
  const loadedAt = new Date();

  clear(root);

  const live = h("div", {
    class: "aria-live",
    "aria-live": "polite",
    "data-role": "today-live",
  });
  root.append(live);

  // AM's "Onboarding waiting on you" rides above the queue. It starts without
  // awaiting so a slow status fan-out never holds Today, and it drops its
  // result if this render was superseded (same rule as Owner Home).
  const todayOnboarding = h("section", {
    class: "today-onboarding",
    "data-role": "today-onboarding",
    hidden: "true",
  });
  root.append(todayOnboarding);
  if (opts.role === "account_manager" && labels.size > 0) {
    void paintTodayOnboarding(
      todayOnboarding,
      [...labels.keys()],
      "account_manager",
      (slug) => face.company(slug, directory, "sentence"),
      client,
      (slug) => {
        void opts.navigateTo?.("company-checklist", { company: slug });
      },
      stillCurrent,
    ).catch(() => undefined);
  }

  const freshnessHost = h("div", { "data-role": "freshness-host" });
  root.append(freshnessHost);
  const paintFreshness = (): void => {
    clear(freshnessHost);
    freshnessHost.append(
      createFreshnessCaption({
        updatedAt: loadedAt,
        onRefresh: () => {
          void renderQueuePage(opts);
        },
      }),
    );
  };
  paintFreshness();

  // Check now (account managers): Today is fleet-wide, so the control carries
  // its own company chooser. Renders nothing without companies.
  const checkNowHost = h("div", { "data-role": "check-now-host" });
  root.append(checkNowHost);
  if (opts.role === "account_manager") {
    renderCheckNow({
      client,
      companyChoices: labels,
      root: checkNowHost,
      stillCurrent,
      ...(opts.navigateTo === undefined
        ? {}
        : { openAlerts: () => void opts.navigateTo?.("health") }),
    });
  }

  // Cap disclosure — live state when the known server LIMIT has fired.
  const capBanner = h("p", {
    class: "banner banner-err",
    role: "status",
    "data-role": "queue-cap-fired",
    hidden: "true",
  });
  root.append(capBanner);
  if (queueCapFired) {
    capBanner.hidden = false;
    capBanner.removeAttribute("hidden");
    clear(capBanner);
    const scopeName = face.company(scopeCompany, directory, "sentence");
    // A month filter over a capped slice is a slice of a slice: the month may
    // be missing cards, and the page must not read as a complete month
    // (round 2, MUST-FIX 4b).
    const monthBit =
      monthFilter === "" ? "" : `, so ${monthText(monthFilter)} may be missing cards`;
    capBanner.append(
      document.createTextNode(
        scopeCompany === ""
          ? queueCapSentence()
          : scopeCapFired
            ? `Showing the oldest ${String(FLEET_QUEUE_LIST_LIMIT)} for ${scopeName}${monthBit} — more may exist. Not all caught up.`
            : `The list for all companies is capped at ${String(FLEET_QUEUE_LIST_LIMIT)}, so this shows only ${scopeName}. Choose All companies to see the oldest ${String(FLEET_QUEUE_LIST_LIMIT)}.`,
      ),
    );
  }

  // Standing-rule approval queue host — painted after announce() exists.
  const approvalHost = h("div", {
    class: "rule-approval-queue",
    "data-role": "rule-approval-queue",
  });
  root.append(approvalHost);

  root.append(reviewPageHeader());
  const headline = h("h2", {
    class: "today-headline",
    tabindex: "-1",
    "data-role": "today-headline",
  });
  root.append(headline);
  const statsHost = h("div", {
    class: "today-stats",
    "data-role": "today-stats",
    "aria-label": `${navLabel("queue")} summary`,
  });
  root.append(statsHost);
  const filtersHost = h("div", { class: "today-filters", "data-role": "today-filters" });
  root.append(filtersHost);
  // What this visit is filtered to, and one click out of it (round 10, C-4).
  // Only a hand-off (or a month it settled on) paints this; a person who
  // filters by hand already has the control in front of them.
  const scopeHost = h("div", {
    class: "banner banner-info review-scope",
    "data-role": "review-scope",
    hidden: "true",
  });
  root.append(scopeHost);

  // B4: Answered recently sits directly under the list header, collapsed,
  // not at the bottom of the page. Its tile opens it.
  const answeredHost = h("div", { "data-role": "answered-recently-host" });
  root.append(answeredHost);

  // G6: Dismissed sits directly under Answered recently, collapsed, and makes
  // no request until it is opened for the first time.
  const dismissedHost = h("div", { "data-role": "dismissed-host" });
  root.append(dismissedHost);

  const substance = h("div", { "data-role": "today-substance" });
  root.append(substance);

  const confirmationHost = h("div", {
    class: "confirm-line",
    "data-role": "confirmation-host",
  });
  root.append(confirmationHost);

  // R4-12 (round 3): a carried FAILURE gets a visible line beside the
  // confirmation line. The aria-live region above is clipped to 1px, so a
  // failure replayed only there is invisible to a sighted person.
  const carriedFailureHost = h("div", { "data-role": "carried-failure-host" });
  root.append(carriedFailureHost);

  /**
   * R4-13 / E3: a card's own success answers ITS carried failure and no other
   * card's. Clearing the whole host dropped a sibling card's failure that had
   * been released by the same Refresh.
   */
  const dropCarriedFailureFor = (itemId: number): void => {
    for (const line of [
      ...carriedFailureHost.querySelectorAll<HTMLElement>('[data-role="carried-failure"]'),
    ]) {
      if (line.getAttribute("data-queue-id") === String(itemId)) line.remove();
    }
    pendingTodayFailures = pendingTodayFailures.filter(
      (entry) => entry.itemId !== itemId,
    );
  };

  const stage = h("div", { class: "stage", "data-role": "today-stage" });
  root.append(stage);

  const moreHost = h("div", { "data-role": "more-panels-host" });
  root.append(moreHost);
  if (opts.role === "account_manager") {
    root.append(
      h("p", { class: "today-note", "data-role": "today-am-note" }, [AM_OWNER_HIDDEN_NOTE]),
    );
  }

  // Full pending list for this persona. Stalest first. Any row can open.
  // A named set (Months) narrows by the Worker's partition; otherwise a month
  // hand-off narrows by the Worker's month bucket.
  const stack =
    reviewSet !== null
      ? pending.filter((item) =>
          itemMatchesReviewSet(
            item,
            monthFilter === "" ? null : monthFilter,
            reviewSet,
            pageClock,
          ),
        )
      : monthFilter === ""
        ? [...pending]
        : pending.filter((item) => queueItemPeriod(item, pageClock) === monthFilter);
  const stackAtLoad = stack.length;

  const companySlugs = [...new Set(pending.map((item) => item.company))];
  const snapshots = await loadQueueChartSnapshots(client, companySlugs);
  // Accounts that read the same are told apart everywhere one is named (F2):
  // the company's own chart is the set they are told apart within.
  const accountLabels = companyAccountLabels(
    (company) => snapshots.get(company)?.snapshot.selectableAccounts ?? [],
    labelCompanies,
  );
  if (!stillCurrent()) return;


  /** One state per card: the same predicate the chip, the group and the bar use. */
  function isSetupFix(item: QueueItem): boolean {
    return isSetupFixCard(item, {
      hasSelectableLocations: locationAvailability(snapshots.get(item.company)) !== "empty",
    });
  }

  function presentedFromStack(): PresentedCard[] {
    return stack.map((item) => {
      // The context is built as a value so the chart fact reaches the
      // presenter through `presentQueueCard`, whose own signature this lane
      // does not own.
      const cardCtx = {
        directory,
        role: roleForCards,
        hasSelectableLocations: locationAvailability(snapshots.get(item.company)) !== "empty",
        accountWords: accountLabels.whenShared,
      };
      const card = presentQueueCard(item, cardCtx, loadedAt);
      // Y5 brief item 4: every card the console cannot answer is listed under
      // one "Setup fixes" group, using the list's own grouping (no new data
      // source): one family value and its label. A card a person can answer
      // keeps its own family.
      if (!isSetupFix(item)) return card;
      return { ...card, family: SETUP_FIX_GROUP, familyLabel: SETUP_FIX_GROUP_LABEL };
    });
  }

  /** Count failed-apply cards actually in the DOM — L1 measures the surface. */
  const countRenderedFailedCards = (): number =>
    approvalHost.querySelectorAll('[data-role="rule-apply-failed-card"]').length;

  /**
   * Headline from what is on screen (L1) plus whether failed-rule fan-out
   * settled (R18O-3). Never re-query to invent a card that is not rendered,
   * never say "All caught up." over a visible failure card, a queue cap, or
   * an incomplete failed measure.
   *
   * `failedPainted`: undefined = fan-out not settled yet; null = incomplete;
   * number = cards painted (0 = measured clear of failures).
   */
  const setHeadlineFromSurface = (failedPainted?: number | null): void => {
    clear(headline);
    // R4-30: the "N cards are with the owner" path is gone. Against the real
    // Worker an account manager's To review never receives an escalated card,
    // so it always counted 0 and could only ever describe a state this page
    // cannot show. The account manager learns about owner-held cards from the
    // "with the owner" lines on Months, Activity and Home; the owner's own To
    // review still lists them as Needs you (they are in `stack`).
    if (stack.length > 0) {
      // Same number the nav badge and Home tile use (stack length for this
      // persona). A company filter scopes the sentence and names the scope:
      // "2 things need you for X · 12 in all", never two bare numbers that
      // disagree beside a scoped tile (Y6 send-back round 1).
      const presented = presentedFromStack();
      if (filters.company === "") {
        const needsYou = splitReviewCounts(presented).needsYou;
        headline.append(
          document.createTextNode(reviewHeadline(stack.length, needsYou, listIsLowerBound)),
        );
        return;
      }
      const companyName = face.company(filters.company, directory, "sentence");
      const scoped = presented.filter((card) => card.item.company === filters.company);
      const scopedLine =
        scoped.length === 0
          ? reviewSet === "reclose_needed"
            ? `This month was reopened and needs closing again for ${companyName}`
            : `Nothing needs you for ${companyName}`
          : `${reviewHeadline(scoped.length, splitReviewCounts(scoped).needsYou, listIsLowerBound)} for ${companyName}`;
      const allBit =
        scoped.length === stack.length
          ? ""
          : ` · ${countOrMoreText(stack.length, !listIsLowerBound)} in all`;
      headline.append(document.createTextNode(`${scopedLine}${allBit}.`));
      return;
    }
    // The reopened-month line is counted from the month status, not from a
    // card, so an empty list must say what is true about the month and never
    // "Nothing needs you" (round 3, MUST-FIX 2).
    if (reviewSet === "reclose_needed") {
      headline.append(
        document.createTextNode("This month was reopened and needs closing again."),
      );
      return;
    }
    // A month scope with nothing in it is not an empty queue (round 10, C-4):
    // say which month is empty, never "All caught up." over a fleet that still
    // holds work. `pending` is the whole persona list before the month filter.
    // On a capped slice the month may simply not be in it, so the sentence is
    // a lower bound too (round 2, MUST-FIX 4b).
    if (monthFilter !== "" && pending.length > 0) {
      const companyBit =
        filters.company === ""
          ? ""
          : ` for ${face.company(filters.company, directory, "sentence")}`;
      headline.append(
        document.createTextNode(
          listIsLowerBound
            ? `No ${monthText(monthFilter)} cards are in the oldest ${String(FLEET_QUEUE_LIST_LIMIT)}${companyBit}. More may exist.`
            : `Nothing needs you in ${monthText(monthFilter)}${companyBit}.`,
        ),
      );
      return;
    }
    // An undated set with nothing in it (Months' "not tied to a month" line).
    if (reviewSet !== null && pending.length > 0) {
      const companyBit =
        filters.company === ""
          ? ""
          : ` for ${face.company(filters.company, directory, "sentence")}`;
      headline.append(
        document.createTextNode(
          listIsLowerBound
            ? `No undated cards are in the oldest ${String(FLEET_QUEUE_LIST_LIMIT)}${companyBit}. More may exist.`
            : `Nothing needs you that is not tied to a month${companyBit}.`,
        ),
      );
      return;
    }
    // B1c-4 (rc-4 C7): "All caught up." is a claim about everything measured.
    // A month scope or a counted card set means this page shows one slice of
    // the fleet, so a scoped page says what is true about its scope instead of
    // the fleet-wide clear claim. It only REPLACES the clear claim's words: the
    // failure-card, cap and incomplete-measure gates below still run first, so
    // an incomplete measure never reads as clear (review of B1c, MEDIUM).
    const scopedMonthWords = monthFilter === "" ? "" : monthText(monthFilter);
    const scopedClear =
      scopedMonthWords !== "" || reviewSet !== null
        ? (() => {
            const companyBit =
              filters.company === ""
                ? ""
                : ` for ${face.company(filters.company, directory, "sentence")}`;
            return scopedMonthWords === ""
              ? `Nothing needs you that is not tied to a month${companyBit}.`
              : `Nothing needs you in ${scopedMonthWords}${companyBit}.`;
          })()
        : null;
    const failedVisible = countRenderedFailedCards();
    if (failedVisible > 0) {
      const failHeadline =
        failedVisible === 1
          ? "1 standing-rule apply failure is listed above."
          : `${String(failedVisible)} standing-rule apply failures are listed above.`;
      headline.append(document.createTextNode(failHeadline));
      return;
    }
    if (queueCapFired) {
      headline.append(
        document.createTextNode(
          `Queue list capped at ${String(FLEET_QUEUE_LIST_LIMIT)} — more pending may exist.`,
        ),
      );
      return;
    }
    const clearClaim = measuredClearHeadline({
      workCount: 0,
      // undefined → not yet measured; treat as incomplete (do not assert clear).
      measureComplete: failedPainted !== undefined && failedPainted !== null,
      clearCopy: "All caught up.",
      incompleteCopy:
        failedPainted === undefined
          ? "Checking standing-rule apply failures…"
          : "Couldn't finish checking standing-rule apply failures.",
    });
    // R20C-6: never `?? "All caught up."` — that sentence is R18O-3's finding,
    // and the fallback is reachable the moment workCount stops being a literal
    // 0 (null means "work remains"; the helper's contract forbids inventing
    // clear copy here). workCount is 0 above, so null is unreachable today;
    // refuse rather than restore the lie.
    if (clearClaim === null) {
      return;
    }
    headline.append(
      document.createTextNode(
        clearClaim === "All caught up." && scopedClear !== null ? scopedClear : clearClaim,
      ),
    );
  };

  const announce = (message: string): void => {
    clear(live);
    live.append(document.createTextNode(message));
  };

  // Q-c selection and "Answered recently" state. The bar is painted at the
  // end of this function, once the list helpers exist.
  const selectedIds = new Set<number>();
  let answeredRows: QueueItem[] = [];
  // G2. The answered list is read newest-first with the Worker's cap of
  // FLEET_QUEUE_LIST_LIMIT, fleet-wide. `answeredCapped` says the list behind
  // `answeredRows` hit that cap, so a count taken from it is a lower bound;
  // `answeredScope` is the company that list was read for ("" = the fleet),
  // and `answeredFleetCapped` says the fleet list itself hit the cap, which is
  // when a company's share of it cannot be trusted and its own list is read.
  let answeredCapped = false;
  let answeredScope: CompanySlug = ALL_COMPANIES;
  let answeredFleetCapped = false;
  let answeredReadSeq = 0;
  let answeredHealths: CompanyHealth[] | null = null;
  let answeredHealthsAt = 0;
  let answeredFailed = false;
  let answeredLoaded = false;
  let answeredOpen = false;
  // Round 2, item 3: a bulk accept this page started and has not finished owns
  // the panel of its company. Re-opening that company's panel after a render
  // shows the run is still going instead of a live Accept button.
  const bulkAcceptsInFlight = bulkAcceptsInFlightFor(root);
  const firstBulkInFlight = [...bulkAcceptsInFlight][0];
  let openAcceptCompany: CompanySlug | null = firstBulkInFlight ?? null;

  // G6: the Dismissed list is lazy (no request until opened) and has its own
  // failed state, so a failed read can never be painted as "Nothing dismissed.".
  let dismissedRows: QueueItem[] = [];
  let dismissedFailed = false;
  let dismissedLoaded = false;
  let dismissedLoading = false;
  // R4-06, mirroring the answered list: `dismissedCapped` says the list behind
  // R4-06, mirroring the answered list: `dismissedCapped` says the read behind
  // the rows in hand hit the Worker's cap (so the count is a lower bound), and
  // `dismissedFleetCapped` says the FLEET read itself was capped, which is when
  // one company's share of it cannot be trusted and its own list is read. The
  // second fact is sticky across a company-scoped read: it is about the fleet
  // list, not about whichever company the rows were last read for.
  // `dismissedScope` is the company the rows in hand were read for ("" = the
  // fleet), so the filter can tell a client-side slice from a missing read
  // (rd16 U review round 2, item 2; round 3: the count lost the fleet's cap).
  let dismissedCapped = false;
  let dismissedFleetCapped = false;
  let dismissedScope: CompanySlug = ALL_COMPANIES;
  let dismissedOpen = dismissedOpenForThisRender;
  // The plain result of the last Reopen attempt, shown as visible text inside
  // the section (the live region alone is invisible to a sighted person).
  let dismissedMessage: string | null = null;
  let dismissedMessageEl: HTMLElement | null = null;

  // Replay only if this attempt still owns the screen.
  if (announceForThisRender !== null && stillCurrent()) {
    announce(announceForThisRender);
  }
  // G6: the visible half of a successful Reopen, in the same place the
  // escalation and booking confirmations appear.
  if (confirmationForThisRender !== null && stillCurrent()) {
    confirmationHost.append(
      h("p", { class: "confirm-copy", "data-role": "reopened-confirm" }, [
        confirmationForThisRender,
      ]),
    );
  }
  // R4-12 (round 3): the visible half of a failure that outlived the render it
  // happened in (a bulk Accept, or a held write released by a Refresh). The
  // live region announces it too; this is what a person can see.
  if (failuresForThisRender.length > 0 && stillCurrent()) {
    for (const failure of failuresForThisRender) {
      carriedFailureHost.append(
        h(
          "p",
          {
            class: "inline-error",
            role: "alert",
            "data-role": "carried-failure",
            ...(failure.itemId === null
              ? {}
              : { "data-queue-id": String(failure.itemId) }),
          },
          [failure.text],
        ),
      );
    }
    // R4-13 / E3: the sentence stands while its card is still open. A later
    // render caused by ANOTHER card's success must not erase it (the single
    // slot did exactly that when the success landed second). An entry is
    // dropped when its card leaves the list, or when that card's own answer
    // succeeds (`dropCarriedFailureFor`). A page-wide (bulk) failure keeps the
    // one-shot behaviour it had.
    const stillPendingIds = new Set(pending.map((entry) => entry.id));
    pendingTodayFailures = pendingTodayFailures.filter(
      (entry) => entry.itemId !== null && stillPendingIds.has(entry.itemId),
    );
  }

  // Item 9: aliases and counterparties are painted by `paintMore()` below the
  // list, inside a closed disclosure. They still load on the same pass as
  // before (see paintMore for why the read count was not reduced).

  // Standing-rule approval queue (fix-r10a + failed/rejected readers) —
  // beside Today, not a buried System page. awaiting_approval / failed /
  // rejected each settle independently (N-A).
  // Owner also fans out `dismissed` so an AM disposition cannot erase the
  // audit trail from the Owner's view (H1).
  // View rules only for Owner — AM is denied the Rules route (M2).
  const canNavigateRules = opts.role === "owner" && opts.navigateTo !== undefined;
  const approvalPaint = paintRuleApprovalQueue({
    client,
    host: approvalHost,
    labels,
    directory,
    stillCurrent,
    announce,
    role: roleForCards,
    showDismissedAudit: opts.role === "owner",
    // R4-04: a rule's account is worded with the company's own chart, so a
    // twin reads as its full label and never as a colon path.
    accountWords: (company, account) => accountLabels.label(company, account),
    ...(canNavigateRules && opts.navigateTo
      ? { navigateTo: opts.navigateTo }
      : {}),
    onChanged: (message?: string) => {
      // Stash before re-render — announce-then-clear(root) would destroy live.
      if (message !== undefined) pendingTodayAnnounce = message;
      void renderQueuePage(opts);
    },
  });

  const paintRewardSubstance = (): void => {
    // Lazy after headline — never delay the fleet answer (addendum §4 / §3).
    void (async () => {
      try {
        // Await approval paint so the headline can measure rendered cards (L1).
        const failedPainted = await approvalPaint;
        if (!stillCurrent() || stack.length > 0) return;
        setHeadlineFromSurface(failedPainted);
        // A month scope with no cards in it is not an empty queue: the headline
        // above says "Nothing needs you in October 2026." and the fleet can
        // still hold work. Never claim "Nothing needs you." here (C-4).
        if (monthFilter !== "" || reviewSet !== null) return;

        // AM reward (§4.5): Today empty + Waiting empty → "Nothing needs you."
        // Only when Waiting fan-out succeeds, no failed cards are on screen,
        // and the queue list is not truncated (M1 / L1).
        if (opts.role === "account_manager") {
          const waiting = await countOpenHubQuestions(client);
          if (!stillCurrent() || stack.length > 0) return;
          const awaitingRules = await countAwaitingRuleApprovals(client, labels);
          if (!stillCurrent() || stack.length > 0) return;
          if (waiting === 0 && awaitingRules === 0) {
            if (failedPainted === null) {
              // Unknown failed fan-out — headline already names the gap (R18O-3);
              // never claim Nothing needs you when we cannot see the failed list.
              return;
            }
            if (countRenderedFailedCards() > 0 || queueCapFired) {
              return;
            }
            clear(headline);
            headline.append(document.createTextNode("Nothing needs you."));
            clear(substance);
            const n = sessionUploadCount();
            if (n > 0) {
              const noun = n === 1 ? "statement" : "statements";
              substance.append(
                h("p", {
                  class: "today-substance",
                  "data-role": "session-uploads-reward",
                }, [
                  `${String(n)} ${noun} uploaded this session.`,
                ]),
              );
            }
            return;
          }
          // Waiting unknown or non-empty → foundation reward, not the AM claim.
          if (waiting === null || awaitingRules === null || awaitingRules > 0) {
            // Fall through to balances garnish; headline already set from surface.
          }
        }

        const companies = await loadCompanySlugs(client, labels);
        if (!stillCurrent() || stack.length > 0) return;
        const snapshots = await Promise.all(
          companies.map(async (slug) => {
            try {
              return await client.getBalances(slug);
            } catch {
              return null;
            }
          }),
        );
        if (!stillCurrent() || stack.length > 0) return;
        clear(substance);
        const failed = snapshots.some((s) => s === null);
        if (failed) {
          // Completeness (C-20): never flatten away failed members and paint
          // a partial cash total — render an explicit error state.
          substance.append(
            h("p", {
              class: "today-substance today-substance-error",
              "data-role": "cash-sum-error",
              role: "status",
            }, [
              "Couldn't check balances across all companies.",
            ]),
          );
          return;
        }
        const asOfs = snapshots
          .filter((s): s is NonNullable<typeof s> => s !== null && s.asOf !== null)
          .map((s) => s.asOf as string);
        if (asOfs.length > 0) {
          const oldest = asOfs.reduce((a, b) => (a < b ? a : b));
          substance.append(
            h("p", { class: "today-substance", "data-role": "balances-as-of" }, [
              `Balances as of ${formatAsOf(oldest)}.`,
            ]),
          );
        }
        const cashLine = formatCashLine(snapshots);
        if (cashLine === null) {
          // Mixed currency / malformed — omit, don't invent a number.
        } else if (cashLine.status === "error") {
          substance.append(
            h("p", {
              class: "today-substance today-substance-error",
              "data-role": "cash-sum-error",
              role: "status",
            }, [cashLine.message]),
          );
        } else {
          substance.append(
            h("p", { class: "today-substance", "data-role": "cash-sum" }, [cashLine.text]),
          );
        }
      } catch {
        // Substance is garnish — silence on failure.
      }
    })();
  };

  /**
   * Put focus on the card after the one just answered, else the list heading
   * (round 10, C-8). Shared by Skip and every answered write, so the two paths
   * cannot disagree.
   */
  const focusAfterAnswer = (item: QueueItem, next: QueueItem | undefined): void => {
    queueMicrotask(() => {
      if (next === undefined || next.id === item.id) {
        headline.focus();
        return;
      }
      const heading = stage.querySelector<HTMLElement>(
        `[data-role="today-row"][data-queue-id="${String(next.id)}"] .question`,
      );
      (heading ?? headline).focus();
    });
  };

  const focusHeadlineOrCard = (preventScroll = false): void => {
    const focusOptions = preventScroll ? { preventScroll: true } : undefined;
    if (stack.length === 0) {
      headline.focus(focusOptions);
      return;
    }
    const heading = stage.querySelector<HTMLElement>(".question");
    // A filter can hide every openable card while the stack still holds cards
    // (answer the last card of the filtered company). Focus lands on the
    // headline — where the scoped count is — never on <body>.
    (heading ?? headline).focus(focusOptions);
  };

  const expandedIds = new Set<number>();
  const collapsedGroups = new Set<string>();
  const bodies = new Map<number, HTMLElement>();
  let cachedCanLater: boolean | null = null;
  /**
   * The company this page is scoped to when it opens: the capped-list read's
   * scope, else the caller's `reviewScope` (a company key), else the older
   * `companyFilter` option — a direct caller passes that one and nothing else
   * (rd16 U review round 3: `queue-dismissed` caught the read losing it).
   */
  const pageScope: CompanySlug =
    scopeCompany !== ""
      ? scopeCompany
      : (opts.reviewScope ?? (opts.companyFilter ?? "").trim());
  let filters: TodayListFilters = opts.reviewFilters ?? {
    search: "",
    company: pageScope,
    family: "",
    show: SHOW_ALL,
  };
  /**
   * The group order recorded on the first paint. Answering a card must not
   * re-sort the list under the person (Y6 brief item 1): every later paint
   * sorts by this, so a group keeps the place it was first drawn in.
   */
  let sessionOrder: GroupOrder | null = null;
  const groupOrder = (): GroupOrder => {
    sessionOrder ??= groupOrderFor(presentedFromStack());
    return sessionOrder;
  };

  /**
   * The card to open after one is answered: the next card in the SAME group,
   * then the same company, then the next company in the order the person is
   * already reading. Only cards the current filters SHOW can be chosen — a
   * filtered-out card cannot open (Y6 send-back round 1: booking the last card
   * of a company filter left focus on <body> while a hidden card was "open").
   */
  function nextCardInGroup(item: QueueItem): QueueItem | undefined {
    // The row order on screen IS what the person is reading, and the group
    // header order lives in renderTodayList, not in the stack order. Read the
    // next row from the DOM so focus and the opened card can never disagree
    // with the visual list (round 10, C-8).
    const rows = [
      ...stage.querySelectorAll<HTMLElement>('[data-role="today-row"]'),
    ];
    const position = rows.findIndex(
      (row) => row.getAttribute("data-queue-id") === String(item.id),
    );
    const nextId = position >= 0 ? rows[position + 1]?.getAttribute("data-queue-id") : undefined;
    if (nextId === undefined || nextId === null) return undefined;
    return (
      stack.find((row) => String(row.id) === nextId) ??
      presentedFromStack().find((card) => String(card.item.id) === nextId)?.item
    );
  }

  function dropAnswered(item: QueueItem): void {
    const next = nextCardInGroup(item);
    const index = stack.findIndex((row) => row.id === item.id);
    if (index >= 0) stack.splice(index, 1);
    bodies.delete(item.id);
    expandedIds.delete(item.id);
    selectedIds.delete(item.id);
    // The card after the answered one is the open one; an older open card must
    // not keep the stage (round 10, C-8).
    if (next !== undefined) {
      expandedIds.clear();
      expandedIds.add(next.id);
    }
  }

  // Expanded card face is cardPresentation. Row titles already were.
  // Whole-app tests that still want the literal "What was this?" on a
  // new-vendor row are not this lane's files.
  function cardBody(item: QueueItem): HTMLElement {
    const canLater = stack.length > 1;
    if (cachedCanLater !== canLater) {
      // R4-11: keep the node of a card whose write is still running. Clearing
      // every body rebuilt that card with live buttons while its request was
      // still in flight, and its failure text landed on a detached node. A
      // settled card still re-renders when its data changes.
      for (const [id, body] of [...bodies]) {
        if (body.getAttribute("data-busy") !== "true") bodies.delete(id);
      }
      cachedCanLater = canLater;
    }
    const cached = bodies.get(item.id);
    if (cached !== undefined) return cached;
    const isThisRender = (): boolean => renderToken === currentRenderToken(root);
    // R4-12: a write that finishes after a newer render owns the screen hands
    // its outcome to that render (one re-read) instead of painting a detached
    // node. Nothing in flight means nothing changes. Round 2: a FAILURE is
    // carried too; round 3 also paints it visibly (the live region is 1px).
    const handOffStaleOutcome = (
      text: string | null,
      kind: "confirmation" | "failure" = "confirmation",
    ): boolean => {
      inFlightCardWrites.delete(item.id);
      if (isThisRender()) return false;
      if (text !== null) {
        pendingTodayAnnounce = text;
        if (kind === "confirmation") pendingTodayConfirmation = text;
        else pendingTodayFailures.push({ itemId: item.id, text });
      }
      void renderQueuePage({ ...opts, reviewFilters: filters });
      return true;
    };
    const node: HTMLElement = renderStageCard({
      item,
      client,
      chartLoad:
        snapshots.get(item.company) ??
        chartLoadFromSnapshot(absentChartSnapshot(item.company)),
      directory,
      canLater,
      hubLinkedSlugs,
      companies,
      ...(opts.role !== undefined ? { role: opts.role } : {}),
      ...(opts.navigateTo ? { navigateTo: opts.navigateTo } : {}),
      announce,
      onBusyChange: (busy) => {
        if (busy) inFlightCardWrites.add(item.id);
        else inFlightCardWrites.delete(item.id);
        node.setAttribute("data-busy", busy ? "true" : "false");
      },
      onDismissCard: (target) => {
        openDismissConfirm([target]);
      },
      onLater: () => {
        clear(confirmationHost);
        // R4-17: after Skip the open card is the next card ON SCREEN, the same
        // successor the answer paths and the focus use. The stack order is not
        // the visual order once the list is grouped.
        const next = nextCardInGroup(item) ?? firstCardOnScreen(item);
        expandedIds.delete(item.id);
        if (next !== undefined && next.id !== item.id) expandedIds.add(next.id);
        paintList();
        focusAfterAnswer(item, next);
      },
      onEscalated: (result) => {
        if (handOffStaleOutcome(result.confirmationText)) return;
        // A card's own success answers its own carried failure, nothing else.
        dropCarriedFailureFor(item.id);
        clear(confirmationHost);
        confirmationHost.append(
          h("p", { class: "confirm-copy", "data-role": "escalated-confirm" }, [
            result.confirmationText,
          ]),
        );
        announce(result.confirmationText);
        const next = nextCardInGroup(item);
        dropAnswered(item);
        paintCounts(false);
        focusAfterAnswer(item, next);
      },
      onResolved: (result) => {
        if (handOffStaleOutcome(result.confirmationText)) return Promise.resolve();
        // A card's own success answers its own carried failure, nothing else.
        dropCarriedFailureFor(item.id);
        clear(confirmationHost);
        confirmationHost.append(result.confirmation);
        // R16c / R16O-2: do not claim finality before the ledger write.
        // Booking is async; #380 re-queues failed approvals onto Today.
        // Announce the card's own confirmation only (quiet success mirror).
        announce(result.confirmationText);
        const next = nextCardInGroup(item);
        dropAnswered(item);
        paintCounts(false);
        focusAfterAnswer(item, next);
        // Q-c: the answer must appear under "Answered recently" without a
        // reload. A reject (close/dismiss) simply adds no row.
        loadAnsweredRecently();
        // A rejected wallet card joins Dismissed; keep an opened list true.
        refreshDismissed();
        if (dismissedOpen) paintDismissed();
        return Promise.resolve();
      },
      onResolveFailed: (message) => {
        // Card remains; buttons re-enabled inside the card. A failure that
        // lands after a newer render keeps its sentence on that render
        // (round-2 item 2).
        if (handOffStaleOutcome(message, "failure")) return;
        inFlightCardWrites.delete(item.id);
      },
      onAlreadyAnswered: () => {
        inFlightCardWrites.delete(item.id);
        pendingTodayAnnounce = ALREADY_ANSWERED_COPY;
        // Keep the filters the person chose: the re-read is about the card,
        // not about resetting the list (round 2, item 6).
        void renderQueuePage({ ...opts, reviewFilters: filters });
      },
    });
    // R4-12: a card rebuilt while its write is still running comes back busy,
    // so the person cannot press the same answer twice.
    if (inFlightCardWrites.has(item.id)) {
      node.setAttribute("data-busy", "true");
      for (const control of node.querySelectorAll<
        HTMLButtonElement | HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >("button, input, textarea, select")) {
        control.disabled = true;
      }
    }
    bodies.set(item.id, node);
    return node;
  }

  /** R4-17: the first card row on screen, for Skip wrapping past the last row. */
  function firstCardOnScreen(item: QueueItem): QueueItem | undefined {
    const first = stage.querySelector<HTMLElement>('[data-role="today-row"]');
    const firstId = first?.getAttribute("data-queue-id");
    if (firstId === undefined || firstId === null) return undefined;
    if (firstId === String(item.id)) return undefined;
    return (
      stack.find((row) => String(row.id) === firstId) ??
      presentedFromStack().find((card) => String(card.item.id) === firstId)?.item
    );
  }

  function focusSnapshot(): { id: string | null; role: string; key: string | null } | null {
    const active = document.activeElement;
    if (!(active instanceof HTMLElement) || !stage.contains(active)) return null;
    const role = active.getAttribute("data-role");
    if (role === null) return null;
    const row = active.closest<HTMLElement>("[data-queue-id]");
    return {
      id: row?.getAttribute("data-queue-id") ?? null,
      role,
      key: active.getAttribute("data-group-key"),
    };
  }

  function restoreFocus(snapshot: ReturnType<typeof focusSnapshot>): void {
    if (snapshot === null) return;
    let next: HTMLElement | null = null;
    if (snapshot.id !== null) {
      next =
        stage.querySelector<HTMLElement>(
          `[data-queue-id="${snapshot.id}"][data-role="${snapshot.role}"]`,
        ) ??
        stage.querySelector<HTMLElement>(
          `[data-queue-id="${snapshot.id}"] [data-role="${snapshot.role}"]`,
        );
    }
    if (next === null && snapshot.key !== null) {
      // The group key can hold raw merchant text (quotes, backslashes), so
      // compare attribute values instead of building a selector from it.
      for (const candidate of stage.querySelectorAll<HTMLElement>("[data-group-key]")) {
        if (
          candidate.getAttribute("data-group-key") === snapshot.key &&
          candidate.getAttribute("data-role") === snapshot.role
        ) {
          next = candidate;
          break;
        }
      }
    }
    if (next !== null) {
      next.focus();
      return;
    }
    if (snapshot.id !== null) {
      stage
        .querySelector<HTMLElement>(
          `[data-queue-id="${snapshot.id}"] [data-role="today-row-toggle"]`,
        )
        ?.focus();
    }
  }

  function paintList(): void {
    const focus = focusSnapshot();
    renderTodayList(
      stage,
      presentedFromStack(),
      filters,
      expandedIds,
      collapsedGroups,
      (id) => {
        if (expandedIds.has(id)) expandedIds.delete(id);
        else expandedIds.add(id);
        paintList();
      },
      (groupKey) => {
        if (collapsedGroups.has(groupKey)) collapsedGroups.delete(groupKey);
        else collapsedGroups.add(groupKey);
        paintList();
      },
      (item) => cardBody(item),
      {
        order: groupOrder(),
        ...(listIsLowerBound ? { capped: true } : {}),
        selection: {
          selected: selectedIds,
          disabled: confirmOpen,
          onToggleRow: (id) => {
            toggleSelected([id]);
          },
          onToggleGroup: (ids) => {
            const all = ids.length > 0 && ids.every((id) => selectedIds.has(id));
            for (const id of ids) {
              if (all) selectedIds.delete(id);
              else selectedIds.add(id);
            }
            paintList();
          },
        },
        groupAction: (company) => {
          // The group this header belongs to is built from the cards the filters
          // leave, so the count, the panel and the ids it posts are the shown
          // cards of this company and nothing else (re-check H3). Never slice
          // `stack`: a card the Search, Type or Show filter hides is not here.
          const companyItems = company.families.flatMap((family) =>
            family.movements.flatMap((movement) => movement.cards.map((card) => card.item)),
          );
          const base = selectBulkAccept(companyItems, {
            company: company.slug,
            minConfidence: null,
            account: "",
            offeredAccounts:
              snapshots.get(company.slug)?.snapshot.selectableAccounts ?? [],
          });
          const count = base.accept.length;
          if (count === 0) return null;
          const expanded = openAcceptCompany === company.slug;
          return {
            label: `Accept ${String(count)} suggestion${count === 1 ? "" : "s"}`,
            expanded,
            onActivate: () => {
              const opening = !expanded;
              openAcceptCompany = expanded ? null : company.slug;
              paintList();
              if (opening) {
                queueMicrotask(() => {
                  stage
                    .querySelector<HTMLElement>('[data-role="bulk-accept-confidence"]')
                    ?.focus();
                });
              }
            },
            panel: expanded
              ? mountBulkAcceptPanel({
                  items: companyItems,
                  client,
                  live,
                  stillCurrent,
                  fixedCompany: company.slug,
                  directory,
                  offeredAccounts: (slug) =>
                    snapshots.get(slug)?.snapshot.selectableAccounts ?? [],
                  // Round 3: the failure a bulk call carries is the decided
                  // plain sentence, which needs the person's role (403 wording).
                  ...(opts.role !== undefined ? { role: opts.role } : {}),
                  // Round 2, item 3: the run travels across the render, so a
                  // rebuilt panel comes back busy rather than ready.
                  busy: bulkAcceptsInFlight.has(company.slug),
                  onBusyChange: (busy) => {
                    if (busy) bulkAcceptsInFlight.add(company.slug);
                    else bulkAcceptsInFlight.delete(company.slug);
                  },
                  onAccepted: (message) => {
                    pendingTodayAnnounce = message;
                    // Keep the Type/Show/Search/Company choice (item 8b).
                    void renderQueuePage({ ...opts, reviewFilters: filters });
                  },
                  onFailed: (message) => {
                    // X2: re-read the list before painting any result. The
                    // failure the call wrote must survive that re-read, in the
                    // visible line as well as the live region (round 3,
                    // MUST-FIX 1).
                    if (message !== "") {
                      pendingTodayAnnounce = message;
                      pendingTodayFailures.push({ itemId: null, text: message });
                    }
                    void renderQueuePage({ ...opts, reviewFilters: filters });
                  },
                })
              : null,
          };
        },
      },
    );
    restoreFocus(focus);
    for (const tile of statsHost.querySelectorAll<HTMLElement>("[data-tile]")) {
      // The tile's pressed state reads the same word the Show filter holds:
      // the chip word itself (Y6 brief item 8).
      const chipWord = tile.getAttribute("data-chip");
      const pressed = chipWord !== null && filters.show === chipWord;
      if (tile instanceof HTMLButtonElement) {
        tile.setAttribute("aria-pressed", pressed ? "true" : "false");
      }
    }
    syncBulkBar();
  }

  /**
   * What this visit is filtered to, in the words the link used, with one click
   * to clear it (round 10, C-4: "Showing October 2026 for 123 Test Street
   * Rentals. Show everything"). A Months line counted a SET of cards, so the
   * notice names the set and the number the person is now looking at; that
   * number is taken from the list itself, so it cannot repeat a stale count
   * (round 2, MUST-FIX 1). Only a hand-off-scoped visit paints it.
   */
  const scopeNoticeApplies =
    monthFilter !== "" || (opts.reviewScope ?? "") !== "" || reviewSet !== null;
  const clearReviewScope = (): void => {
    opts.reviewScope = "";
    delete opts.reviewMonth;
    delete opts.reviewSet;
    opts.reviewFilters = { search: "", company: "", family: "", show: SHOW_ALL };
    // The header picker reads the same scope, so "Show everything" must clear it
    // too (rd16 U review round 2, item 1): the list went to every company while
    // the header still named the old one.
    opts.onCompanyFilterChange?.("");
    void renderQueuePage(opts);
  };
  const setScopeSentence = (
    set: ReviewCardSet,
    companyText: string,
    monthLabel: string,
  ): string => {
    const [one, many] = REVIEW_SET_NOUNS[set];
    // The number the notice prints is the number of rows this page is showing
    // (the company filter is part of the list), never the pre-filter stack.
    const shown =
      filters.company === ""
        ? stack.length
        : stack.filter((item) => item.company === filters.company).length;
    // Nothing in the set: the headline says why (empty, or a capped slice);
    // the notice only says which scope the person is in, with no "0" count.
    if (shown === 0) {
      return companyText !== "" && monthLabel !== ""
        ? `Showing ${monthLabel} for ${companyText}.`
        : monthLabel !== ""
          ? `Showing ${monthLabel}.`
          : `Showing ${companyText}.`;
    }
    const noun = countedText(shown, !listIsLowerBound, one, many);
    return companyText !== "" && monthLabel !== ""
      ? `Showing ${noun} for ${companyText} in ${monthLabel}.`
      : companyText !== ""
        ? `Showing ${noun} for ${companyText}.`
        : monthLabel !== ""
          ? `Showing ${noun} in ${monthLabel}.`
          : `Showing ${noun}.`;
  };
  const paintScopeNotice = (): void => {
    clear(scopeHost);
    const companyText =
      filters.company === "" ? "" : face.company(filters.company, directory, "sentence");
    const monthLabel = monthFilter === "" ? "" : monthText(monthFilter);
    if (
      !scopeNoticeApplies ||
      (companyText === "" && monthLabel === "" && reviewSet === null)
    ) {
      scopeHost.hidden = true;
      scopeHost.setAttribute("hidden", "true");
      return;
    }
    const sentence =
      reviewSet !== null
        ? setScopeSentence(reviewSet, companyText, monthLabel)
        : monthLabel !== "" && companyText !== ""
          ? `Showing ${monthLabel} for ${companyText}.`
          : monthLabel !== ""
            ? `Showing ${monthLabel}.`
            : `Showing ${companyText}.`;
    scopeHost.hidden = false;
    scopeHost.removeAttribute("hidden");
    const everything = h(
      "button",
      {
        type: "button",
        class: "btn btn-ghost",
        "data-action": "clear-review-scope",
      },
      ["Show everything"],
    );
    everything.addEventListener("click", clearReviewScope);
    scopeHost.append(
      h("div", { class: "actions" }, [
        h(
          "p",
          {
            class: "review-scope-notice",
            role: "status",
            "data-role": "review-scope-notice",
          },
          [sentence],
        ),
        everything,
      ]),
    );
  };

  function paintCounts(focusCard: boolean, scrollToCard = true): void {
    if (stack.length > 0) clear(substance);
    // AM stack excludes escalated; Owner stack includes them. In both cases
    // stack.length is the badge for the active persona, filters or not.
    // When the list is filtered to one company the tiles count that company
    // and say so, so a tile number and a row count can never disagree
    // (Y6 brief item 7).
    const allCards = presentedFromStack();
    const scoped =
      filters.company === ""
        ? allCards
        : allCards.filter((card) => card.item.company === filters.company);
    const scope = {
      ...(filters.company === ""
        ? {}
        : { companyName: face.company(filters.company, directory, "sentence") }),
      ...(listIsLowerBound ? { capped: true } : {}),
    };
    renderTodayStats(
      statsHost,
      scoped,
      (show) => {
        controls.setShow(show);
      },
      answeredTileText(),
      () => {
        answeredOpen = true;
        paintAnswered();
        answeredHost.scrollIntoView({ block: "nearest" });
      },
      scope,
    );
    setHeadlineFromSurface();
    paintScopeNotice();
    // Answered cards leave the stack; the badge falls by as many. With the
    // page scoped to one company (E5) the stack is not the fleet, so the fleet
    // count is moved by what left it.
    opts.onPendingCount?.(badgeCount - (stackAtLoad - stack.length), queueCapFired);
    paintList();
    // A scoped empty page also waits for the measure before its clear words
    // (review of B1c): paintRewardSubstance stops after the headline for a scope.
    if (stack.length === 0) paintRewardSubstance();
    if (focusCard) {
      queueMicrotask(() => {
        focusHeadlineOrCard(!scrollToCard);
      });
    }
  }

  /**
   * The answered rows the current company filter shows. The tile, the panel
   * and its warning count the same rows, so a scoped tile cannot sit beside an
   * unscoped panel (Y6 send-back round 1, note (a)).
   */
  const answeredShown = (): QueueItem[] => {
    const scoped =
      filters.company === ""
        ? answeredRows
        : answeredRows.filter((row) => row.company === filters.company);
    // A month hand-off narrows this panel and its tile the same way it narrows
    // the pending list, so one scope cannot count two populations (C-4).
    return monthFilter === ""
      ? scoped
      : scoped.filter((row) => queueItemPeriod(row, pageClock) === monthFilter);
  };

  /**
   * The answered count, or null while it is not known: before the read
   * settles and after a failed read. A failed read is not zero answers.
   */
  const answeredCountText = (words: "tile" | "toggle"): string | null => {
    if (!answeredLoaded || answeredFailed) return null;
    const rows = answeredShown().length;
    return words === "tile"
      ? countText(rows, !answeredCapped)
      : countOrMoreText(rows, !answeredCapped);
  };
  const answeredTileText = (): string => answeredCountText("tile") ?? "—";

  // -- Q-c bulk bar ---------------------------------------------------------

  let confirmOpen = false;

  function visibleSelectedIds(): Set<number> {
    const visible = filterPresentedCards(presentedFromStack(), filters);
    return new Set(
      visible.map((card) => card.item.id).filter((id) => selectedIds.has(id)),
    );
  }

  function selectedItems(): QueueItem[] {
    // A hidden selected card is never sent (A4).
    const visible = visibleSelectedIds();
    return stack.filter((item) => visible.has(item.id));
  }

  function pruneHiddenSelection(): void {
    const visible = visibleSelectedIds();
    for (const id of [...selectedIds]) {
      if (!visible.has(id)) selectedIds.delete(id);
    }
  }

  function toggleSelected(ids: readonly number[]): void {
    if (confirmOpen) return;
    const all = ids.length > 0 && ids.every((id) => selectedIds.has(id));
    for (const id of ids) {
      if (all) selectedIds.delete(id);
      else selectedIds.add(id);
    }
    paintList();
  }

  function setAnsweredTile(): void {
    const value = statsHost.querySelector<HTMLElement>(
      '[data-tile="answered-recently"] .stat-value',
    );
    if (value !== null) value.textContent = answeredTileText();
  }

  function selectAllBox(): HTMLInputElement {
    return h("input", {
      type: "checkbox",
      class: "today-bulk-select-all",
      "data-role": "select-all-review",
      "data-action": "select-all-review",
      "aria-label": "Select all shown cards",
    });
  }

  const selectAll = selectAllBox();
  const selectedCount = h("span", {
    class: "today-bulk-count",
    "data-role": "bulk-selected-count",
  });
  const accountSelect = h("select", {
    class: "field-input",
    "data-role": "bulk-book-account",
    "aria-label": "Account to book the selected cards to",
  });
  const bookButton = h(
    "button",
    {
      type: "button",
      class: "btn btn-primary",
      "data-role": "bulk-book",
      "data-action": "bulk-book",
    },
    ["Book 0"],
  );
  const dismissButton = h(
    "button",
    {
      type: "button",
      class: "btn",
      "data-role": "bulk-dismiss",
      "data-action": "bulk-dismiss",
    },
    ["Dismiss 0"],
  );
  const clearButton = h(
    "button",
    {
      type: "button",
      class: "btn btn-ghost",
      "data-role": "bulk-clear",
      "data-action": "clear-bulk-selection",
    },
    ["Clear"],
  );
  const bulkNote = h("p", {
    class: "today-bulk-note",
    "data-role": "bulk-note",
  });
  const progress = h("p", {
    class: "today-bulk-progress",
    role: "status",
    "data-role": "bulk-progress",
  });
  const failuresHost = h("div", {
    class: "today-bulk-failures",
    "data-role": "bulk-failures",
  });
  const confirmHost = h("div", { "data-role": "bulk-confirm-host" });
  let bulkRunning = false;

  const bulkBar = h(
    "section",
    {
      class: "today-bulk",
      "data-role": "bulk-actions",
      "aria-label": "Answer several cards",
      hidden: "true",
    },
    [
      h("div", { class: "today-bulk-row" }, [
        h("label", { class: "today-bulk-select-all-label" }, [
          selectAll,
          " Select all shown",
        ]),
        selectedCount,
      ]),
      h("div", { class: "today-bulk-controls" }, [
        h("label", { class: "today-bulk-account" }, [
          h("span", { class: "field-label" }, ["Book to"]),
          accountSelect,
        ]),
        bookButton,
        dismissButton,
        clearButton,
      ]),
      bulkNote,
      progress,
      confirmHost,
      failuresHost,
    ],
  );
  // Directly before the list, not inside a wrapper: a sticky bar is confined
  // to its parent's box, so a same-height wrapper would stop it sticking.
  stage.before(bulkBar);
  function bulkBookable(items: readonly QueueItem[]): QueueItem[] {
    const companies = [...new Set(items.map((item) => item.company))];
    if (companies.length !== 1) return [];
    const company = companies[0] ?? "";
    const locations = snapshots.get(company)?.snapshot.selectableLocations ?? [];
    const onlyLocation = soleBookingLocation(locations);
    return items.filter((item) => {
      const decision = bulkBookDecision(item);
      if (decision === "book") {
        if ((item.body ?? "").trim() === "location_not_chosen" && locations.length === 0) {
          return false;
        }
        return true;
      }
      return decision === "needs_location" && onlyLocation !== "";
    });
  }

  function phoneLayout(): boolean {
    return window.matchMedia("(max-width: 640px)").matches;
  }

  /** A chosen account's words among the accounts that company offers (E6). */
  function bulkAccountText(company: CompanySlug, account: AccountPath): string {
    return accountLabelAmong(
      account,
      snapshots.get(company)?.snapshot.selectableAccounts ?? [],
      labelCompanies,
    );
  }

  /**
   * What one bulk run achieved, in the words the single card uses (round 3,
   * item 1). A Book only approves: the booking happens on the next run and can
   * still fail, so it says "will be booked", never "Booked".
   */
  function bulkSuccessSentence(
    kind: "book" | "dismiss",
    count: number,
    company: CompanySlug,
    choice: string,
  ): string {
    if (kind === "dismiss") return `Dismissed ${String(count)}.`;
    const account = bulkAccountText(company, choice);
    return count === 1
      ? `Approved 1 card. It will be booked to ${account} on the next run.`
      : `Approved ${String(count)} cards. They will be booked to ${account} on the next run.`;
  }

  function syncBulkBar(): void {
    pruneHiddenSelection();
    const items = selectedItems();
    const visible = filterPresentedCards(presentedFromStack(), filters);
    const visibleIds = visible.map((card) => card.item.id);
    const allShown = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
    const someShown = visibleIds.some((id) => selectedIds.has(id));
    selectAll.checked = allShown;
    selectAll.indeterminate = someShown && !allShown;
    selectAll.disabled = visibleIds.length === 0 || bulkRunning || confirmOpen;
    selectedCount.textContent = `${String(items.length)} selected`;

    const companies = [...new Set(items.map((item) => item.company))];
    const company = companies.length === 1 ? (companies[0] ?? "") : "";
    const previous = accountSelect.value;
    clear(accountSelect);
    accountSelect.append(h("option", { value: "" }, ["Choose an account"]));
    const accounts =
      company === "" ? [] : (snapshots.get(company)?.snapshot.selectableAccounts ?? []);
    const accountTexts = accountLabelSet(accounts, labelCompanies);
    for (const account of accounts) {
      // The option value is the full ledger name; only the visible text is
      // shortened, so submit stays an exact match. Two accounts that would
      // read the same are told apart (re-check E6).
      accountSelect.append(
        h("option", { value: account }, [
          accountTexts.get(account) ?? accountDisplayName(account, labelCompanies),
        ]),
      );
    }
    accountSelect.value = accounts.includes(previous) ? previous : "";
    accountSelect.disabled = bulkRunning || confirmOpen || company === "";

    const choice = accountSelect.value;
    const judgment = choice !== "" && isJudgmentClassAccount(choice);
    const book = bulkBookable(items);
    const dismissable = items.filter(isMoovMovementQueueItem);
    bookButton.textContent = `Book ${String(book.length)}`;
    bookButton.disabled =
      bulkRunning ||
      confirmOpen ||
      book.length === 0 ||
      (!phoneLayout() && choice === "") ||
      judgment ||
      companies.length > 1;
    dismissButton.textContent = `Dismiss ${String(dismissable.length)}`;
    dismissButton.disabled = bulkRunning || confirmOpen || dismissable.length === 0;
    clearButton.disabled = bulkRunning || confirmOpen || items.length === 0;
    // A per-card Dismiss would open a second dialog over a running or open one.
    for (const cardDismiss of stage.querySelectorAll<HTMLButtonElement>(
      '[data-action="dismiss-card"]',
    )) {
      cardDismiss.disabled = bulkRunning || confirmOpen;
    }

    let note: string;
    if (items.length === 0) {
      note = "Select cards to answer several at once.";
    } else if (companies.length > 1) {
      note = BULK_ONE_COMPANY_COPY;
    } else if (judgment) {
      note = BULK_JUDGMENT_REFUSAL_COPY;
    } else {
      const parts: string[] = [];
      // Round 6 (should-fix 2): the bar's word is the word on the card. The
      // old bucket was every `needs_setup` reason plus every setup fix, so an
      // anomaly ("Needs you"), a closed month ("Month closed") and a planless
      // wait ("Waiting on the system") were all called setup fixes. The chip
      // comes from the same presentation the card renders, and only cards the
      // bulk Book cannot take are described.
      const booked = new Set(book.map((item) => item.id));
      const counts = new Map<string, number>();
      for (const item of items) {
        if (booked.has(item.id)) continue;
        const chip = cardPresentation(item, {
          directory,
          role: roleForCards,
          hasSelectableLocations:
            locationAvailability(snapshots.get(item.company)) !== "empty",
          accountWords: accountLabels.whenShared,
        }).chip.text;
        counts.set(chip, (counts.get(chip) ?? 0) + 1);
      }
      const chipCount = (chip: string): number => counts.get(chip) ?? 0;
      const setupCount = chipCount(SETUP_FIX_CHIP);
      if (setupCount > 0) {
        // Count and pronoun agree: one card is *a* setup fix and *it* cannot
        // be booked here; several are setup fixes.
        parts.push(
          setupCount === 1
            ? `${pluralText(1, "selected card", "selected cards")} is a setup fix: it cannot be booked here.`
            : `${pluralText(setupCount, "selected card", "selected cards")} are setup fixes: they cannot be booked here.`,
        );
      }
      const monthCount = chipCount(MONTH_CLOSED_CHIP);
      if (monthCount > 0) {
        parts.push(
          monthCount === 1
            ? `${pluralText(1, "selected card", "selected cards")} is on a closed month: it cannot be booked here.`
            : `${pluralText(monthCount, "selected card", "selected cards")} are on a closed month: they cannot be booked here.`,
        );
      }
      const needsYouCount = chipCount("Needs you");
      if (needsYouCount > 0) {
        parts.push(
          needsYouCount === 1
            ? `${pluralText(1, "selected card", "selected cards")} needs you: it cannot be booked here.`
            : `${pluralText(needsYouCount, "selected card", "selected cards")} need you: they cannot be booked here.`,
        );
      }
      const waitingCount = chipCount("Waiting on the system");
      if (waitingCount > 0) {
        parts.push(
          waitingCount === 1
            ? `${pluralText(1, "selected card", "selected cards")} is waiting on the system: it cannot be booked here yet.`
            : `${pluralText(waitingCount, "selected card", "selected cards")} are waiting on the system: they cannot be booked here yet.`,
        );
      }
      if (book.length === 0) {
        parts.push("None of these can be booked together. Answer them one at a time.");
      } else if (choice === "") {
        parts.push("Choose the account to book them to.");
      }
      if (book.length > 0 && book.length < items.length) {
        // Each card the bulk Book leaves out is described once above. This
        // sentence is only for the ones no sentence above named; repeating the
        // whole remainder made 6 + 2 read as "8 more" (integration review).
        const described = setupCount + monthCount + needsYouCount + waitingCount;
        const left = items.length - book.length - described;
        if (left > 0) {
          parts.push(
            `${String(left)} other selected card${left === 1 ? "" : "s"} need${left === 1 ? "s" : ""} their own answer.`,
          );
        }
      }
      if (dismissable.length > 0 && dismissable.length < items.length) {
        const left = items.length - dismissable.length;
        parts.push(
          `Dismiss works on wallet payment cards; ${String(left)} other card${left === 1 ? "" : "s"} need${left === 1 ? "s" : ""} an answer first.`,
        );
      }
      note = parts.join(" ");
    }
    bulkNote.textContent = note;

    // Keep the bar up while it holds a failure: a single-card Dismiss selects
    // nothing, so without this its 409/403 would be painted into a hidden bar.
    const show =
      items.length > 0 || confirmOpen || failuresHost.childElementCount > 0;
    // The last run's "1 of 1" stays readable until the bar is hidden (the
    // run's own end state), but a bar that comes back for a NEW selection must
    // not open on it.
    if (show && bulkBar.hasAttribute("hidden") && !bulkRunning) progress.textContent = "";
    if (show) bulkBar.removeAttribute("hidden");
    else bulkBar.setAttribute("hidden", "true");
    stage.classList.toggle("today-bulk-open", show);
    root.style.paddingBottom = show
      ? window.matchMedia("(max-width: 640px)").matches
        ? // R8b measured the sticky bars at 186px on a phone (118px bar + 62px
          // nav + offset), so 150px left the last controls behind them.
          "190px"
        : "120px"
      : "";
  }

  let confirmOpener: HTMLElement | null = null;
  let confirmEscape: ((event: KeyboardEvent) => void) | null = null;

  function setConfirmOpen(open: boolean): void {
    confirmOpen = open;
    // Lock every selection control while the dialog is open (A1).
    paintList();
  }

  function closeBulkConfirm(restoreOpener = true): void {
    if (confirmEscape !== null) {
      document.removeEventListener("keydown", confirmEscape);
      confirmEscape = null;
    }
    clear(confirmHost);
    confirmHost.classList.remove("open");
    confirmOpen = false;
    paintList();
    const opener = confirmOpener;
    confirmOpener = null;
    // R4-18: Escape (and Cancel) still return focus to the opener. A finished
    // run does not: the opener is hidden or disabled by then, and the person
    // is sent back into the queue stage instead of <body>.
    if (restoreOpener && opener !== null && opener.isConnected) opener.focus();
    syncBulkBar();
  }

  function beginConfirm(
    section: HTMLElement,
    opener: HTMLElement | null,
    focus: HTMLElement,
  ): void {
    // Never stack Escape listeners: drop any left by an earlier dialog.
    if (confirmEscape !== null) {
      document.removeEventListener("keydown", confirmEscape);
      confirmEscape = null;
    }
    confirmOpener = opener;
    clear(confirmHost);
    confirmHost.append(section);
    confirmHost.classList.add("open");
    setConfirmOpen(true);
    const onKey = (event: KeyboardEvent): void => {
      if (!stage.isConnected) {
        // The page was re-rendered or left with the dialog open: do not leak.
        document.removeEventListener("keydown", onKey);
        if (confirmEscape === onKey) confirmEscape = null;
        return;
      }
      if (event.key !== "Escape") return;
      // A running bulk action owns the dialog; Escape must not unlock the
      // selection mid-run.
      if (bulkRunning) return;
      event.preventDefault();
      closeBulkConfirm();
    };
    confirmEscape = onKey;
    document.addEventListener("keydown", onKey);
    focus.focus();
  }

  function sameShownSelection(
    items: readonly QueueItem[],
    shownIds: readonly number[],
  ): boolean {
    const current = items.map((item) => item.id).sort((a, b) => a - b);
    const shown = [...shownIds].sort((a, b) => a - b);
    return (
      current.length === shown.length &&
      current.every((id, index) => id === shown[index])
    );
  }

  function showStaleDialog(section: HTMLElement): void {
    let stale = section.querySelector<HTMLElement>('[data-role="bulk-confirm-stale"]');
    if (stale === null) {
      stale = h("p", {
        class: "banner banner-err",
        role: "alert",
        "data-role": "bulk-confirm-stale",
      });
      section.append(stale);
    }
    stale.textContent = "The selection changed. Close this and choose the cards again.";
  }

  function runBulk(
    kind: "book" | "dismiss",
    items: readonly QueueItem[],
    choice: string,
    note: string,
  ): void {
    if (bulkRunning) return;
    bulkRunning = true;
    progress.textContent = `0 of ${String(items.length)}`;
    clear(failuresHost);
    for (const button of confirmHost.querySelectorAll("button")) {
      button.disabled = true;
    }
    syncBulkBar();
    const label = kind === "book" ? "Book" : "Dismiss";
    const bookCompany = items[0]?.company ?? "";
    const bookLocations =
      snapshots.get(bookCompany)?.snapshot.selectableLocations ?? [];
    const onlyLocation = soleBookingLocation(bookLocations);
    void (async () => {
      const batch: { value: BulkQueueOutcome | null } = { value: null };
      const writeOutcome = await runWrite({
        key: cardWriteKey(confirmHost, `bulk-${kind}-${items.map((item) => item.id).join(",")}`),
        scope: confirmHost,
        gate: () => null,
        send: async () => {
          const result = await runBulkQueueAction({
            items,
            resolutionFor:
              kind === "book"
                ? (item) => bulkBookResolution(item, choice, onlyLocation)
                : () => bulkDismissResolution(note),
            resolve: (id, body) => client.resolveQueueItem(id, body),
            onProgress: (done, total) => {
              progress.textContent = `${String(done)} of ${String(total)}`;
            },
            role: roleForCards,
          });
          batch.value = result;
          return result;
        },
        stillCurrent,
      });
      if (writeOutcome.kind !== "landed") {
        bulkRunning = false;
        syncBulkBar();
        if (writeOutcome.error !== undefined && stillCurrent()) {
          const message = paintOutcomeFailure(failuresHost, writeOutcome, roleForCards);
          announce(`${label} failed: ${message}`);
        }
        return;
      }
      if (batch.value === null) {
        bulkRunning = false;
        syncBulkBar();
        return;
      }
      if (!stillCurrent()) return;
      const outcome = batch.value;
      // A genuine "already answered" 409 means the card is gone from the
      // list; it is not a card the person can retry. Remove it so no live
      // Book or Dismiss control stays on screen (round 2, item 5).
      const answered = outcome.failures.filter((failure) => failure.alreadyAnswered);
      const retryable = outcome.failures.filter((failure) => !failure.alreadyAnswered);
      for (const item of [
        ...outcome.succeeded,
        ...answered.map((failure) => failure.item),
      ]) {
        // A card that has landed (or was answered elsewhere) must not accept
        // another press while the list re-reads around it (round-2 M5).
        const landedNode = bodies.get(item.id);
        if (landedNode !== undefined) lockCardControls(landedNode);
        selectedIds.delete(item.id);
        const index = stack.findIndex((row) => row.id === item.id);
        if (index >= 0) stack.splice(index, 1);
        bodies.delete(item.id);
        expandedIds.delete(item.id);
      }
      openAcceptCompany = null;
      bulkRunning = false;
      // R4-18: the dialog's opener is hidden or disabled after a run; focus
      // goes back into the queue stage, never to <body>. Keep one remaining
      // card open so there is a card to land on.
      if (
        stack.length > 0 &&
        ![...expandedIds].some((id) => stack.some((row) => row.id === id))
      ) {
        expandedIds.clear();
        const first = stack[0];
        if (first !== undefined) expandedIds.add(first.id);
      }
      closeBulkConfirm(false);
      paintFailures(retryable);
      paintCounts(false);
      // Round 2, item 6: a run with failures painted the list of cards that
      // did not save just above the stage. Focusing the stage with the usual
      // scroll would push that list out of view, so keep the scroll where the
      // person can read why the run was partial.
      focusHeadlineOrCard(retryable.length > 0);
      loadAnsweredRecently();
      if (kind === "dismiss" && outcome.succeeded.length + answered.length > 0) {
        refreshDismissed();
      }
      // The announcement (round 3, items 1 and 3). A bulk Book only APPROVES
      // the cards: the booking happens on the next run and can still fail
      // (R15d), so it never says "Booked". The success sentence is kept even
      // when other cards in the run were already answered elsewhere, and a
      // card removed from this list locally is not told to "refresh".
      const successCopy =
        outcome.succeeded.length === 0
          ? ""
          : kind === "dismiss" && outcome.failures.length === 0
            ? dismissSelectionMentionsFeeTopup(items)
              ? "Dismissed. If the problem is still there, some cards can come back."
              : BULK_DISMISS_TOAST_COPY
            : bulkSuccessSentence(
                kind,
                outcome.succeeded.length,
                bookCompany,
                choice,
              );
      const answeredCopy =
        answered.length === 0
          ? ""
          : `${String(answered.length)} ${answered.length === 1 ? "was" : "were"} already answered.`;
      const retryCopy =
        retryable.length === 0
          ? ""
          : `${String(retryable.length)} could not be answered.`;
      // One message, sentences separated by a space only where both exist.
      const afterSuccess =
        successCopy !== "" && (answeredCopy !== "" || retryCopy !== "")
          ? " "
          : "";
      const afterAnswered =
        answeredCopy !== "" && retryCopy !== "" ? " " : "";
      const sentences = `${successCopy}${afterSuccess}${answeredCopy}${afterAnswered}${retryCopy}`;
      // Nothing approved and nothing retryable: every card was answered
      // elsewhere, so keep the single-card sentence.
      const allAnsweredElsewhere =
        outcome.succeeded.length === 0 && retryable.length === 0;
      announce(allAnsweredElsewhere ? ALREADY_ANSWERED_COPY : sentences);
      syncBulkBar();
    })();
  }

  function paintFailures(failures: readonly BulkQueueFailure[]): void {
    clear(failuresHost);
    if (failures.length === 0) return;
    const list = h("ul", { class: "today-bulk-failure-list" });
    for (const failure of failures) {
      const face = cardPresentation(failure.item, {
        directory,
        role: roleForCards,
        accountWords: accountLabels.whenShared,
      });
      list.append(
        h(
          "li",
          {
            "data-role": "bulk-failure",
            "data-queue-id": String(failure.item.id),
          },
          [
            h("span", { "data-role": "bulk-failure-title" }, [amVisibleDisplayText(face.title)]),
            h("span", { "data-role": "bulk-failure-reason" }, [failure.reason]),
          ],
        ),
      );
    }
    const closeFailures = h(
      "button",
      {
        type: "button",
        class: "btn btn-ghost",
        "data-action": "close-bulk-failures",
      },
      ["Close"],
    );
    closeFailures.addEventListener("click", () => {
      clear(failuresHost);
      syncBulkBar();
    });
    failuresHost.append(
      h("h3", { role: "alert" }, [
        `Could not answer ${String(failures.length)} card${failures.length === 1 ? "" : "s"}.`,
      ]),
      list,
      closeFailures,
    );
  }

  function openBookConfirm(): void {
    const items = bulkBookable(selectedItems());
    const phone = phoneLayout();
    let choice = accountSelect.value;
    if (items.length === 0 || isJudgmentClassAccount(choice)) return;
    if (!phone && choice === "") return;
    if ([...new Set(items.map((item) => item.company))].length > 1) return;
    const shownIds = items.map((item) => item.id);
    const opener =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const total = bulkTotalLabel(items);
    const cancel = h(
      "button",
      { type: "button", class: "btn btn-ghost", "data-action": "cancel-bulk" },
      ["Cancel"],
    );
    const confirm = h(
      "button",
      { type: "button", class: "btn btn-primary", "data-action": "confirm-bulk-book" },
      [`Book ${String(items.length)}`],
    );
    const countLine = h("p", { "data-role": "bulk-confirm-count" });
    const paintCount = (): void => {
      countLine.textContent =
        choice === ""
          ? `Choose the account to book ${String(items.length)} card${items.length === 1 ? "" : "s"} to.`
          : `This books ${String(items.length)} card${items.length === 1 ? "" : "s"} to ${bulkAccountText(items[0]?.company ?? "", choice)}.`;
    };
    paintCount();
    cancel.addEventListener("click", () => {
      closeBulkConfirm();
    });

    const company = items[0]?.company ?? "";
    const accounts =
      company === "" ? [] : (snapshots.get(company)?.snapshot.selectableAccounts ?? []);
    const dialogAccount =
      phone && accounts.length > 0
        ? h("select", {
            class: "field-input",
            "data-role": "bulk-confirm-account",
            "aria-label": "Account to book the selected cards to",
          })
        : null;
    if (dialogAccount !== null) {
      dialogAccount.append(h("option", { value: "" }, ["Choose an account"]));
      const accountTexts = accountLabelSet(accounts, labelCompanies);
      for (const account of accounts) {
        dialogAccount.append(
          h("option", { value: account }, [
            accountTexts.get(account) ?? accountDisplayName(account, labelCompanies),
          ]),
        );
      }
      dialogAccount.value = choice;
    }
    const paintConfirm = (): void => {
      confirm.disabled =
        choice === "" || isJudgmentClassAccount(choice);
    };
    paintConfirm();
    dialogAccount?.addEventListener("change", () => {
      choice = dialogAccount.value;
      paintCount();
      paintConfirm();
    });

    const section = h(
      "section",
      {
        class: "today-confirm",
        role: "dialog",
        "aria-modal": "true",
        "aria-labelledby": "bulk-book-heading",
        "data-role": "bulk-confirm",
        "data-kind": "book",
      },
      [
        h("h3", { id: "bulk-book-heading", tabindex: "-1" }, [
          `Book ${String(items.length)} card${items.length === 1 ? "" : "s"}?`,
        ]),
        ...(dialogAccount === null
          ? []
          : [
              h("label", { class: "today-confirm-account" }, [
                "Book to",
                dialogAccount,
              ]),
            ]),
        countLine,
        h("p", { "data-role": "bulk-confirm-total" }, [
          total === null
            ? "The total amount is not known for every card."
            : `Total: ${total}.`,
        ]),
        h("div", { class: "actions" }, [cancel, confirm]),
      ],
    );
    confirm.addEventListener("click", () => {
      // A1: the dialog's shown set must still be the current selection. The
      // controls are locked while open; this is the second line of defence.
      const current = bulkBookable(selectedItems());
      if (
        !sameShownSelection(current, shownIds) ||
        choice === "" ||
        isJudgmentClassAccount(choice)
      ) {
        showStaleDialog(section);
        return;
      }
      runBulk("book", current, choice, "");
    });
    beginConfirm(
      section,
      opener,
      dialogAccount ?? section.querySelector("h3") ?? confirm,
    );
  }

  function openDismissConfirm(fixedItems?: readonly QueueItem[]): void {
    // A second dialog over a running or open one would capture a stale
    // selection and, mid-run, its Confirm would silently do nothing.
    if (bulkRunning || confirmOpen) return;
    const selection = fixedItems ?? selectedItems();
    const items = selection.filter(isMoovMovementQueueItem);
    if (items.length === 0) return;
    // Y5 brief item 4. Only a wallet card can be dismissed, so say what the
    // dialog is about: the setup cards that were picked and will not go, and
    // what dismissal really does for the ones that will.
    const pickedSetup = selection.filter((row) => isSetupFix(row));
    const skipped = pickedSetup.length - items.length;
    const allSetup = items.length > 0 && items.every((row) => isSetupFix(row));
    const shownIds = items.map((item) => item.id);
    const opener =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const noteInput = h("textarea", {
      "data-role": "bulk-dismiss-note",
      "aria-label": "Why these cards are being dismissed (required)",
      maxlength: String(BULK_DISMISS_NOTE_MAX),
      rows: "3",
    });
    const hint = h("p", { class: "muted", "data-role": "bulk-dismiss-hint" }, [
      `A note of 1 to ${String(BULK_DISMISS_NOTE_MAX)} characters, shared by every card.`,
    ]);
    const cancel = h(
      "button",
      { type: "button", class: "btn btn-ghost", "data-action": "cancel-bulk" },
      ["Cancel"],
    );
    const confirm = h(
      "button",
      {
        type: "button",
        class: "btn btn-primary",
        "data-action": "confirm-bulk-dismiss",
      },
      [`Dismiss ${String(items.length)}`],
    );
    confirm.disabled = true;
    noteInput.addEventListener("input", () => {
      const problem = bulkDismissNoteProblem(noteInput.value);
      confirm.disabled = problem !== null;
      hint.textContent =
        problem ?? `A note of 1 to ${String(BULK_DISMISS_NOTE_MAX)} characters, shared by every card.`;
    });
    cancel.addEventListener("click", () => {
      closeBulkConfirm();
    });
    const feeTopupWarning = dismissSelectionMentionsFeeTopup(items)
      ? [
          h("p", { "data-role": "bulk-dismiss-fee-topup" }, [
            BULK_DISMISS_FEE_TOPUP_COPY,
          ]),
        ]
      : [];
    const one = items.length === 1 ? items[0] : undefined;
    const oneFace =
      one === undefined
        ? null
        : cardPresentation(one, {
            directory,
            role: roleForCards,
            accountWords: accountLabels.whenShared,
          });
    const section = h(
      "section",
      {
        class: "today-confirm",
        role: "dialog",
        "aria-modal": "true",
        "aria-labelledby": "bulk-dismiss-heading",
        "data-role": "bulk-confirm",
        "data-kind": "dismiss",
      },
      [
        h("h3", { id: "bulk-dismiss-heading", tabindex: "-1" }, [
          `Dismiss ${String(items.length)} card${items.length === 1 ? "" : "s"}?`,
        ]),
        // Another card can be visible behind the sheet, so one dismissal names
        // the card it is about (round 10, item 8g).
        ...(one !== undefined && oneFace !== null
          ? [
              h("p", { class: "muted", "data-role": "bulk-dismiss-which" }, [
                `${amVisibleDisplayText(oneFace.title)} · ${moneyText(queueAmountDecimal(one), { fallback: "Amount unknown" })}`,
              ]),
            ]
          : []),
        h("p", { "data-role": "bulk-dismiss-warning" }, [BULK_DISMISS_CONFIRM_COPY]),
        ...(allSetup
          ? [h("p", { "data-role": "bulk-dismiss-setup" }, [BULK_DISMISS_SETUP_COPY])]
          : []),
        ...(skipped > 0
          ? [
              h("p", { "data-role": "bulk-dismiss-skipped" }, [
                `${String(skipped)} of the cards you picked cannot be dismissed and stay in the list.`,
              ]),
            ]
          : []),
        ...feeTopupWarning,
        h("p", { "data-role": "bulk-dismiss-undo" }, [
          bulkDismissUndoCopy(items.length),
        ]),
        h("label", {}, ["Why these are being dismissed (required)", noteInput]),
        hint,
        h("div", { class: "actions" }, [cancel, confirm]),
      ],
    );
    confirm.addEventListener("click", () => {
      const current = (fixedItems ?? selectedItems()).filter(isMoovMovementQueueItem);
      if (!sameShownSelection(current, shownIds)) {
        showStaleDialog(section);
        return;
      }
      const note = noteInput.value.trim();
      if (bulkDismissNoteProblem(note) !== null) return;
      runBulk("dismiss", current, "", note);
    });
    beginConfirm(section, opener, noteInput);
  }

  selectAll.addEventListener("change", () => {
    if (confirmOpen) return;
    const visible = filterPresentedCards(presentedFromStack(), filters);
    for (const card of visible) {
      if (selectAll.checked) selectedIds.add(card.item.id);
      else selectedIds.delete(card.item.id);
    }
    paintList();
  });
  accountSelect.addEventListener("change", () => {
    syncBulkBar();
  });
  bookButton.addEventListener("click", () => {
    openBookConfirm();
  });
  dismissButton.addEventListener("click", () => {
    openDismissConfirm();
  });
  clearButton.addEventListener("click", () => {
    selectedIds.clear();
    paintList();
  });

  // -- Answered recently (G5) ----------------------------------------------

  function paintAnswered(): void {
    clear(answeredHost);
    const shownRows = answeredShown();
    const count = shownRows.length;
    const toggleCount = answeredCountText("toggle");
    const toggle = h(
      "button",
      {
        type: "button",
        class: "today-answered-toggle",
        "data-role": "answered-recently-toggle",
        "data-action": "toggle-answered-recently",
        "aria-expanded": answeredOpen ? "true" : "false",
      },
      // No number until a read settled, and none after a failed one: never
      // "Answered recently (0)" above "Couldn't check what was answered".
      [toggleCount === null ? "Answered recently" : `Answered recently (${toggleCount})`],
    );
    toggle.addEventListener("click", () => {
      answeredOpen = !answeredOpen;
      paintAnswered();
    });
    const body = h("div", {
      class: "today-answered-body",
      "data-role": "answered-recently-body",
    });
    if (!answeredOpen) {
      body.hidden = true;
      body.setAttribute("hidden", "true");
    }
    if (!answeredLoaded) {
      body.append(h("p", { class: "muted", "data-role": "answered-checking" }, [
        "Checking…",
      ]));
    } else if (answeredFailed) {
      body.append(
        h("p", { class: "muted", "data-role": "answered-failed" }, [
          "Couldn't check what was answered recently.",
        ]),
      );
    } else if (count === 0) {
      // R4-27: a capped read that found nothing here cannot say nothing was
      // answered — the newest 500 rows may all be system-resolved, with the
      // person's answers past the cap. The company-scoped read that follows a
      // capped fleet still drops system-resolved rows (`answeredRecentlyItems`),
      // so the count is of answers, never of the system's own resolutions.
      body.append(
        h("p", { class: "muted", "data-role": "answered-empty" }, [
          answeredCapped
            ? "Could not see every answer; more may exist."
            : "Nothing answered in the last 7 days.",
        ]),
      );
    } else {
      const companies = [...new Set(shownRows.map((item) => item.company))];
      const warning = bookingRunWarningCopy(answeredHealths, companies, Date.now(), roleForCards);
      // "Saved. The books pick it up at the next booking run." is printed by
      // the card's own confirmation and nowhere else (Y6 brief item 1): the
      // sentence is the answer's result, not a standing caption, and a
      // dismissal books nothing so it must never print it.
      if (warning !== null) {
        body.append(
          h(
            "p",
            {
              class: "banner banner-err",
              role: "status",
              "data-role": "answered-run-warning",
            },
            [warning],
          ),
        );
      }
      const list = h("ul", { class: "today-answered-list" });
      for (const item of shownRows) {
        const face = cardPresentation(item, {
          directory,
          role: roleForCards,
          accountWords: accountLabels.whenShared,
        });
        // The one account helper (Y6 brief item 8c): a person reads
        // "Expenses › Office", never the raw colon path. Worded outside the
        // h() children so the branded company reaches the helper directly.
        const answeredAccount = accountLabels.label(
          item.company,
          item.resolvedAccount ?? "",
        );
        list.append(
          h(
            "li",
            {
              class: "today-answered-row",
              "data-role": "answered-row",
              "data-queue-id": String(item.id),
              "data-company": item.company,
            },
            [
              h("span", { "data-role": "answered-row-title" }, [amVisibleDisplayText(face.title)]),
              h("span", { "data-role": "answered-row-account" }, [answeredAccount]),
              h("span", { "data-role": "answered-row-when" }, [
                answeredWhen(item.resolvedAt, loadedAt),
              ]),
            ],
          ),
        );
      }
      body.append(list);
    }
    answeredHost.append(
      h("section", { class: "today-answered", "data-role": "answered-recently" }, [
        toggle,
        body,
      ]),
    );
  }

  // -- Dismissed (G6) -------------------------------------------------------

  function paintDismissed(): void {
    clear(dismissedHost);
    dismissedMessageEl = null;
    // The page's company filter scopes this list too (round 10, item 8d): a
    // dismissed row from another company is not on screen under this filter.
    // R4-06: when the capped fleet forced a company's own read, those rows are
    // already that company's, so the filter is a no-op and the count is exact
    // for it (up to that read's own cap).
    const scopedDismissed =
      filters.company === ""
        ? dismissedRows
        : dismissedRows.filter((row) => row.company === filters.company);
    const dismissedScopeName =
      filters.company === ""
        ? ""
        : ` for ${face.company(filters.company, directory, "sentence")}`;
    const count = scopedDismissed.length;
    // R4-06. The completeness of the number on the toggle. The Worker answers a
    // flat list with no "cut short" flag, and it DROPS rows it cannot read
    // AFTER `LIMIT 500` (`ingest-worker/src/admin.ts` listQueue skips a subject
    // that does not parse and only logs a count). So a sub-limit response
    // cannot be told from a truncated one — a list of 499 can hide the cap
    // (round-2 review, SHOULD-FIX 8).
    //
    // The one place this page KNOWS its section was cut is a read that hit the
    // limit (the fleet read, or the company's own re-read). There the count is
    // a lower bound: "N or more", i.e. there may be more. An empty result keeps
    // a plain count (the empty body's own sentence carries the "could not see"
    // case). The residue this cannot see — drops below the limit with no cap
    // ever observed — is reported in the PR's Q6, because no response field
    // distinguishes it.
    const dismissedMayBeMore = dismissedCapped || dismissedFleetCapped;
    const countLabel = countOrMoreText(count, !(dismissedMayBeMore && count > 0));
    const toggle = h(
      "button",
      {
        type: "button",
        class: "today-dismissed-toggle",
        "data-role": "dismissed-toggle",
        "data-action": "toggle-dismissed",
        "aria-expanded": dismissedOpen ? "true" : "false",
      },
      // A failed read has no count: never paint it as "Dismissed (0)".
      [
        dismissedLoaded && !dismissedFailed
          ? `Dismissed (${countLabel}${dismissedScopeName})`
          : "Dismissed",
      ],
    );
    toggle.addEventListener("click", () => {
      dismissedOpen = !dismissedOpen;
      if (dismissedOpen && !dismissedLoaded && !dismissedLoading) {
        loadDismissed();
        return;
      }
      paintDismissed();
    });
    const body = h("div", {
      class: "today-dismissed-body",
      "data-role": "dismissed-body",
    });
    if (!dismissedOpen) {
      body.hidden = true;
      body.setAttribute("hidden", "true");
    }
    const messageEl = h(
      "p",
      { class: "today-dismissed-message", "data-role": "dismissed-message" },
      [dismissedMessage ?? ""],
    );
    messageEl.hidden = dismissedMessage === null;
    dismissedMessageEl = messageEl;
    body.append(messageEl);
    if (!dismissedLoaded && !dismissedLoading) {
      // Nothing has been read yet: say nothing about the contents.
    } else if (dismissedLoading) {
      body.append(h("p", { class: "muted", "data-role": "dismissed-checking" }, [
        "Checking…",
      ]));
    } else if (dismissedFailed) {
      body.append(
        h("p", { class: "muted", "data-role": "dismissed-failed" }, [
          DISMISSED_FAILED_COPY,
        ]),
      );
      const retry = h(
        "button",
        {
          type: "button",
          class: "btn btn-ghost",
          "data-action": "retry-dismissed",
        },
        ["Try again"],
      );
      retry.addEventListener("click", () => {
        setDismissedMessage(null);
        loadDismissed();
      });
      body.append(retry);
    } else if (count === 0) {
      // R4-06: a capped read that found none here cannot say there are none —
      // the rows may be past the cap. "Nothing dismissed." is only true when
      // the list behind it was the whole list.
      body.append(
        h("p", { class: "muted", "data-role": "dismissed-empty" }, [
          dismissedCapped ? DISMISSED_EMPTY_CAPPED_COPY : DISMISSED_EMPTY_COPY,
        ]),
      );
    } else {
      body.append(
        h("p", { class: "muted", "data-role": "dismissed-explain" }, [
          DISMISSED_EXPLAIN_COPY,
        ]),
      );
      if (dismissedCapped) {
        body.append(
          h("p", { "data-role": "dismissed-cap" }, [DISMISSED_CAP_COPY]),
        );
      }
      const list = h("ul", { class: "today-dismissed-list" });
      // The company only earns a word when the section holds more than one.
      const dismissedCompanies = new Set(scopedDismissed.map((row) => row.company));
      const showCompany = dismissedCompanies.size > 1;
      for (const item of scopedDismissed) {
        const cardFace = cardPresentation(item, {
          directory,
          role: roleForCards,
          accountWords: accountLabels.whenShared,
        });
        // The Worker refuses an AM reopening a currently escalated card (403);
        // only the owner may. The Worker does not return such a row to an AM,
        // but never offer a control that would be refused.
        const canReopen =
          roleForCards === "owner" || !isWithOwner(item);
        // One button per row. The lock is local to this paint: a second click
        // lands on the same element before the first request settles.
        let reopenInFlight = false;
        const reopenButton = h(
          "button",
          {
            type: "button",
            class: "btn btn-ghost",
            "data-action": "reopen-card",
            "aria-label": "Reopen this card",
          },
          ["Reopen"],
        );
        reopenButton.addEventListener("click", () => {
          if (reopenInFlight) return;
          reopenInFlight = true;
          reopenButton.disabled = true;
          setDismissedMessage(null);
          void (async () => {
            const reopened = await reopenDismissedCard(item);
            if (!reopened && stillCurrent()) {
              reopenInFlight = false;
              reopenButton.disabled = false;
            }
          })().catch(() => undefined);
        });
        const paymentDate = shortDateLabel(item.txnDate);
        const noteText = dismissedNoteText(item.resolutionNote);
        // Every dismissed row carries its own amount and date: two rows of one
        // payment are otherwise indistinguishable, and the fallback title
        // ("This needs a look") carries no money at all (Y6 brief item 2).
        const amountText = moneyText(queueAmountDecimal(item), { fallback: "Amount unknown" });
        list.append(
          h(
            "li",
            {
              class: "today-dismissed-row",
              "data-role": "dismissed-row",
              "data-queue-id": String(item.id),
              "data-company": item.company,
            },
            [
              h("div", { class: "today-dismissed-main" }, [
                h("div", { class: "today-dismissed-line" }, [
                  h("span", { "data-role": "dismissed-row-title" }, [amVisibleDisplayText(cardFace.title)]),
                  h("span", { "data-role": "dismissed-row-amount" }, [amountText]),
                  ...(paymentDate === null
                    ? []
                    : [
                        h("span", { "data-role": "dismissed-row-date" }, [
                          paymentDate,
                        ]),
                      ]),
                  ...(showCompany
                    ? [
                        h("span", { "data-role": "dismissed-row-company" }, [
                          face.company(item.company, directory, "sentence"),
                        ]),
                      ]
                    : []),
                  h("span", { "data-role": "dismissed-row-when" }, [
                    `Dismissed ${dismissedAtLabel(item.resolvedAt)}`,
                  ]),
                ]),
                ...(noteText === null
                  ? []
                  : [
                      h(
                        "p",
                        {
                          class: "today-dismissed-note",
                          "data-role": "dismissed-row-note",
                        },
                        [`Note: ${noteText}`],
                      ),
                    ]),
              ]),
              ...(canReopen ? [reopenButton] : []),
            ],
          ),
        );
      }
      body.append(list);
    }
    dismissedHost.append(
      h("section", { class: "today-dismissed", "data-role": "dismissed" }, [
        toggle,
        body,
      ]),
    );
  }

  /** Update the visible result line in place (keeps focus on the button). */
  function setDismissedMessage(message: string | null): void {
    dismissedMessage = message;
    if (dismissedMessageEl !== null) {
      dismissedMessageEl.textContent = message ?? "";
      dismissedMessageEl.hidden = message === null;
    }
  }

  async function reopenDismissedCard(item: QueueItem): Promise<boolean> {
    const outcome = await runWrite({
      key: cardWriteKey(dismissedHost, "reopen-card"),
      scope: dismissedHost,
      gate: () => null,
      send: () => client.reopenQueueItem(item.company, item.id),
      stillCurrent,
    });
    if (outcome.kind !== "landed") {
      if (stillCurrent()) {
        const failure = dismissedReopenFailureCopy(outcome.error, roleForCards);
        // Visible text inside the section, and the live-region announcement.
        setDismissedMessage(failure);
        announce(failure);
        // 409 means the list moved under us; re-read it so the section is true.
        if (outcome.error instanceof AdminApiError && outcome.error.status === 409) {
          loadDismissed();
        }
      }
      return false;
    }
    if (!stillCurrent()) return true;
    announce(DISMISSED_REOPEN_SUCCESS);
    // It is a NEW pending card, not this dismissed row moving. Re-render so
    // the pending list the Worker now serves (with the new id) is shown. The
    // announcement, the visible confirmation and the open section ride over
    // the re-render through the stashes.
    pendingTodayAnnounce = DISMISSED_REOPEN_SUCCESS;
    pendingTodayConfirmation = DISMISSED_REOPEN_SUCCESS;
    pendingDismissedOpen = dismissedOpen;
    try {
      await renderQueuePage(opts);
    } catch {
      // The re-render failed after the reopen succeeded. It left the page on
      // "Loading…"; say what happened instead of leaving it there.
      if (stillCurrent()) {
        const loading = root.querySelector('[data-role="today-loading"]');
        if (loading !== null) {
          clear(root);
          root.append(
            h(
              "p",
              {
                class: "muted",
                role: "status",
                "data-role": "dismissed-reopened-reload",
              },
              [DISMISSED_REOPEN_RELOAD],
            ),
          );
        }
      }
    }
    return true;
  }

  function loadDismissed(): void {
    if (dismissedLoading) return;
    dismissedLoading = true;
    dismissedFailed = false;
    dismissedLoaded = false;
    dismissedRows = [];
    dismissedCapped = false;
    dismissedFleetCapped = false;
    dismissedScope = "";
    paintDismissed();
    void fetchDismissedRows();
  }

  /**
   * The newest read of the dismissed list owns the section: an older read that
   * settles later (one begun before a dismiss) must not paint over it.
   */
  let dismissedReadSeq = 0;

  /**
   * A dismiss, a bulk dismiss or an answer just changed what is dismissed.
   * The section holds its list in this page, so re-read it (the client has
   * already forgotten its cached answer, because the write went through it).
   * A list that was never opened was never read, so nothing is stale yet.
   */
  function refreshDismissed(): void {
    if (!dismissedLoaded && !dismissedLoading) return;
    void fetchDismissedRows();
  }

  async function fetchDismissedRows(): Promise<void> {
    const seq = (dismissedReadSeq += 1);
    // The company the LIST is showing right now, never the option this render
    // was opened with (rd16 U review round 2, item 2): after a header pick and
    // then All companies (or Show everything), the opening option still named
    // the old company, so Dismissed showed one company under an All view.
    //
    // The scope is recorded when the read STARTS, not when it lands: a filter
    // change while this read is in flight must still know which company its
    // rows will answer for (review round 3 — comparing a landed scope raced the
    // read and left the filter unable to tell "answer in hand" from "answer
    // coming").
    const scoped = filters.company;
    dismissedScope = scoped;
    try {
      const fleet = await client.listQueue(
        scoped === ""
          ? { dismissed: true }
          : { dismissed: true, company: scoped },
      );
      // R4-06: the cap is decided by the length actually LOADED, never by the
      // length the client-side filter leaves. A company's dismissed rows can
      // sit below the fleet's newest 500, so when the fleet list is capped and
      // a company is chosen, that company's own list is read instead of
      // slicing a list that may not contain it.
      const fleetCapped = fleetQueueListCapFired(fleet.items.length);
      // A scoped read says nothing about the FLEET's own cap, so it never
      // clears it (review round 3: a company read after a capped fleet read
      // still has to say "or more", because the Worker drops unreadable rows
      // below its LIMIT and sends no "cut short" flag).
      if (scoped === "") dismissedFleetCapped = fleetCapped;
      let rows = fleet.items;
      let capped = fleetQueueListCapFired(rows.length);
      if (fleetCapped && filters.company !== "" && filters.company !== scoped) {
        const own = await client.listQueue({
          dismissed: true,
          company: filters.company,
        });
        rows = own.items.filter((item) => item.company === filters.company);
        capped = fleetQueueListCapFired(own.items.length);
      }
      if (!stillCurrent() || seq !== dismissedReadSeq) return;
      dismissedRows = rows;
      dismissedCapped = capped;
      dismissedLoaded = true;
      dismissedLoading = false;
      dismissedFailed = false;
      paintDismissed();
    } catch {
      if (!stillCurrent() || seq !== dismissedReadSeq) return;
      dismissedRows = [];
      dismissedCapped = false;
      dismissedLoaded = true;
      dismissedLoading = false;
      dismissedFailed = true;
      paintDismissed();
    }
  }

  // -- More: aliases and counterparties (item 9) ---------------------------

  let moreOpen = moreOpenForThisRender;

  function paintMore(): void {
    clear(moreHost);
    const toggle = h(
      "button",
      {
        type: "button",
        class: "today-more-toggle",
        "data-role": "more-panels-toggle",
        "data-action": "toggle-more-panels",
        "aria-expanded": moreOpen ? "true" : "false",
      },
      ["More: aliases and counterparties"],
    );
    const body = h("div", {
      class: "today-more-body",
      "data-role": "more-panels-body",
    });
    const aliasesHost = h("div", { "data-role": "counterparty-aliases" });
    const recognizedHost = h("div", { "data-role": "recognized-counterparties" });
    body.append(aliasesHost, recognizedHost);
    const setOpen = (open: boolean): void => {
      moreOpen = open;
      pendingMoreOpen = open;
      toggle.setAttribute("aria-expanded", open ? "true" : "false");
      body.hidden = !open;
      if (open) body.removeAttribute("hidden");
      else body.setAttribute("hidden", "true");
    };
    if (!moreOpen) {
      body.hidden = true;
      body.setAttribute("hidden", "true");
    }
    toggle.addEventListener("click", () => {
      setOpen(!moreOpen);
    });
    moreHost.append(
      h("section", { class: "today-more", "data-role": "more-panels" }, [
        toggle,
        body,
      ]),
    );
    // Behaviour unchanged: both panels still load on the same pass as before.
    // The acceptance-budget read reduction is NOT taken here — two unowned
    // suites assert these panels on first render, and the rule-status reads
    // feed the failed-card headline, so lazy loading would change behaviour.
    const onChanged = (message?: string): void => {
      pendingMoreOpen = moreOpen;
      if (message !== undefined) pendingTodayAnnounce = message;
      void renderQueuePage(opts);
    };
    void paintConfirmedAliasesPanel({
      client,
      host: aliasesHost,
      stillCurrent,
      announce,
      onChanged,
      role: roleForCards,
    });
    void paintRecognizedCounterpartiesPanel({
      client,
      host: recognizedHost,
      stillCurrent,
      announce,
      onChanged,
      role: roleForCards,
      directory,
    });
  }

  function loadAnsweredRecently(showChecking = false): void {
    const since = new Date(
      Date.now() - ANSWERED_RECENTLY_WINDOW_DAYS * 24 * 60 * 60 * 1000,
    ).toISOString();
    const seq = (answeredReadSeq += 1);
    // The company the person is looking at: when the fleet list turns out to
    // be capped, that company's own list is read instead of slicing the fleet's.
    const company = filters.company;
    if (showChecking) {
      answeredLoaded = false;
      answeredFailed = false;
      answeredRows = [];
      paintAnswered();
      setAnsweredTile();
    }
    void (async () => {
      try {
        const fleet = await client.listQueue({ resolvedSince: since });
        const fleetCapped = fleetQueueListCapFired(fleet.items.length);
        let source = fleet.items;
        let capped = fleetCapped;
        let scope: CompanySlug = ALL_COMPANIES;
        if (fleetCapped && company !== "") {
          const own = await client.listQueue({ company, resolvedSince: since });
          source = own.items.filter((item) => item.company === company);
          capped = fleetQueueListCapFired(own.items.length);
          scope = company;
        }
        const rows = answeredRecentlyItems(source, Date.now());
        let healths = answeredHealths;
        if (
          rows.length > 0 &&
          (healths === null || Date.now() - answeredHealthsAt > 60_000)
        ) {
          try {
            healths = (await client.getFleetHealth()).companies;
            answeredHealthsAt = Date.now();
          } catch {
            healths = null;
          }
        }
        if (!stillCurrent() || seq !== answeredReadSeq) return;
        answeredRows = rows;
        answeredHealths = healths;
        answeredFailed = false;
        answeredLoaded = true;
        answeredCapped = capped;
        answeredFleetCapped = fleetCapped;
        answeredScope = scope;
        paintAnswered();
        setAnsweredTile();
      } catch {
        if (!stillCurrent() || seq !== answeredReadSeq) return;
        answeredFailed = true;
        answeredLoaded = true;
        answeredRows = [];
        answeredCapped = false;
        paintAnswered();
        // A failed read has no number: the tile shows a dash, not 0.
        setAnsweredTile();
      }
    })();
  }

  const controls = mountTodayFilters(filtersHost, presentedFromStack(), (next) => {
    // A capped fleet list cannot show a company's cards by filtering: read that
    // company's own list (or the fleet list again for All companies).
    if (queueCapFired && next.company !== scopeCompany) {
      opts.reviewScope = next.company;
      opts.onCompanyFilterChange?.(next.company);
      void renderQueuePage(opts);
      return;
    }
    // The company the filters are leaving, read before `filters` moves: the
    // dismissed read below is re-read on a change, and comparing with the
    // SCOPE a read settled on would race an in-flight one (rd16 U review round
    // 2, item 2).
    const companyChanged = next.company !== filters.company;
    // The header picker reads the same scope, so a change here is reported to
    // the shell (rd16 U review, item 4).
    if (companyChanged) opts.onCompanyFilterChange?.(next.company);
    filters = next;
    // A4: a card hidden by a filter is no longer selected. The bar and any
    // confirm dialog act only on what the person can see.
    pruneHiddenSelection();
    // The tiles count the same scope the filter shows, so their labels have
    // to be repainted with it, and the answered panel lists the same rows the
    // answered tile counts (Y6 brief item 7, send-back note (a)).
    // G2. When the fleet's answered list was capped, a company's rows in it
    // are a slice; read that company's own list (or the fleet's again). A read
    // still in flight was started for the old company, so it is started over.
    if (!answeredLoaded || (answeredFleetCapped && next.company !== answeredScope)) {
      loadAnsweredRecently(true);
    } else {
      paintAnswered();
    }
    paintCounts(false, false);
    if (dismissedOpen) {
      // Two reasons to read again, and only two (rd16 U review round 2, item 2;
      // round 3 keeps the first one's economy):
      //   * the rows in hand were read for one company, so a change (including
      //     back to All companies) cannot be answered by filtering them;
      //   * the fleet list was capped, so one company's share of it is unknown
      //     and its own list is read instead (R4-06).
      // A complete fleet read answers any company by filtering, with no read.
      const needsRead =
        next.company !== dismissedScope &&
        (dismissedFleetCapped || dismissedScope !== "");
      if (needsRead) {
        void fetchDismissedRows();
      } else {
        paintDismissed();
      }
    }
   }, {
    // The company this visit is filtered to, named even when it has no CARD to
    // list: a company with nothing to review left the page's own dropdown
    // blank, because its options were built from the cards only (rd16 U review
    // round 2, item 8). A capped fleet list also offers every company, not just
    // those in the capped slice.
    extraCompanies: [
      ...new Set([
        ...(queueCapFired
          ? [...labels.keys(), ...(companies ?? []).map((c) => c.slug)]
          : []),
        ...(filters.company === "" ? [] : [filters.company]),
      ]),
    ].map((slug) => ({ value: slug, label: face.company(slug, directory, "sentence") })),
    ...(queueCapFired ? { initialCompany: scopeCompany } : {}),
    // The controls must show the filters the LIST is using (round 2, item 6):
    // before this, a capped re-read read "All" while the rows were already
    // filtered.
    initialFilters: filters,
  });
  const openedForHandOff = openCard;
  const stalest = stack[0];
  if (openedForHandOff !== undefined) expandedIds.add(openedForHandOff.id);
  else if (stalest !== undefined) expandedIds.add(stalest.id);
  paintAnswered();
  // A reopen re-render keeps the section open, so read the (now shorter) list.
  if (dismissedOpen) loadDismissed();
  else paintDismissed();
  paintMore();
  // On load focus stays at the top of the page, on the heading, not on the
  // first card (round 10, C-8): the Type filter was 126 Tabs away from it.
  paintCounts(false, false);
  if (openedForHandOff !== undefined) {
    // The person came from a hub row that names this card. Put the card in
    // front of them: expand (above), highlight, and scroll it into view
    // (rd L2). jsdom has no scrollIntoView, so the guard keeps tests working.
    const row = stage.querySelector<HTMLElement>(
      `[data-role="today-row"][data-queue-id="${String(openedForHandOff.id)}"]`,
    );
    if (row !== null) {
      row.classList.add("today-row-handoff");
      if (typeof row.scrollIntoView === "function") {
        row.scrollIntoView({ block: "center" });
      }
    }
  } else if (openCardRef !== null) {
    // A hand-off named a card that is no longer pending (booked or answered
    // meanwhile), so there is nothing to open. Say so once instead of landing
    // the person at the top of the list with no explanation (rd L2 review
    // round 1).
    root.insertBefore(
      h("p", {
        class: "banner banner-info",
        role: "status",
        "data-role": "handoff-already-done",
      }, ["That card is already done, so it is not in this list now."]),
      stage,
    );
  }
  if (!root.contains(document.activeElement)) headline.focus({ preventScroll: true });
  loadAnsweredRecently();
}

function bindValueSync(input: HTMLInputElement, onChange: () => void): void {
  const proto = HTMLInputElement.prototype;
  Object.defineProperty(input, "value", {
    configurable: true,
    enumerable: true,
    get(): string {
      const value: unknown = Reflect.get(proto, "value", input);
      return typeof value === "string" ? value : "";
    },
    set(next: string) {
      Reflect.set(proto, "value", next, input);
      onChange();
    },
  });
}

function confidenceWord(confidence: number | undefined): "fairly sure" | "not sure" {
  if (typeof confidence !== "number" || !Number.isFinite(confidence) || confidence < 0.5) {
    return "not sure";
  }
  return "fairly sure";
}

function plainBankRationale(text: string | undefined): string {
  const body = displayAiRationale(text).replace(/\s+/g, " ").trim();
  if (body.length === 0 || body.startsWith("ARF1") || /\b[a-z]+_[a-z0-9_]*\b/.test(body)) {
    return BANK_TEXT_NOT_ENOUGH_COPY;
  }
  return body;
}

function bankSuggestionSentence(
  item: QueueItem,
  accountWords: (account: string) => string,
): string | null {
  if (item.eventType === "removed") return null;
  if (item.aiRationale?.trim() === HUB_LOCATION_NEEDED_RATIONALE) {
    // R12 A2-6: the hub booked nothing because it could not pick a location.
    // Say what it suggested and what the person must do. The account is real
    // even when confidence is NULL, so no "(not sure)" is implied here.
    const account = item.suggestedAccount?.trim() ?? "";
    const named = account.length === 0 ? "an account" : accountWords(account);
    return `The hub suggested ${named}. Choose a location to book it.`;
  }
  if (
    item.reason !== "low-confidence" &&
    item.reason !== "anomaly" &&
    item.reason !== "possible-personal"
  ) {
    return null;
  }
  const account = item.suggestedAccount?.trim() ?? "";
  if (account.length === 0) return null;
  return `Suggested account ${accountWords(account)} (${confidenceWord(item.confidence)}). ${amVisibleDisplayText(plainBankRationale(item.aiRationale))}`;
}

/**
 * The one location a bulk Book may prefill (R12 A2-6): the shared helper's
 * "sole" answer, so the portal and the hub answer agree by construction.
 * Today's behaviour is unchanged: one listed location (even "na" alone)
 * prefills; none, or two or more, does not.
 */
export function soleBookingLocation(locations: readonly string[]): string {
  const decision = defaultBookingLocation(locations);
  return decision.kind === "sole" ? decision.location : "";
}

function moneyMovesIn(item: QueueItem): boolean {
  const cents = decimalToCents(queueAmountDecimal(item) ?? "");
  return cents !== null && cents > 0n;
}

/**
 * The amount spec the pipeline would mint from this card (R12 A2-5): a deposit
 * card gives a money-in rule (`-<amount>`, or `-0.00` when the scope is
 * vendor-only), a charge card gives an unsigned rule. Built through the one
 * codec, so the noun and the request cannot disagree (round eight A-M3).
 */
function ruleSpecForCard(
  item: QueueItem,
  scope: QueueRuleScope | undefined,
): string | undefined {
  const magnitude =
    scope === "vendor"
      ? null
      : (queueAmountDecimal(item)?.trim().replace(/^-/, "") ?? null);
  return encodeRuleAmount(magnitude, moneyMovesIn(item) ? "in" : "either");
}

/** Money out is a payment; money in is a deposit (round 10, item 8c). */
function ruleNoun(item: QueueItem, scope?: QueueRuleScope): "payment or deposit" | "deposit" {
  return ruleDirectionNoun(ruleSpecForCard(item, scope));
}

/** An amount with no sign, for a rule sentence that names its size. */
function unsignedAmount(item: QueueItem): string {
  const signed = queueAmountDecimal(item) ?? "";
  return formatAmount(signed.startsWith("-") ? signed.slice(1) : signed);
}

function ruleMatchPreview(item: QueueItem, scope?: QueueRuleScope): string {
  if (isMoovMovementQueueItem(item)) {
    const pairable = moovPairableType(item.descriptor);
    const noun = pairable ?? "wallet movement";
    const cents = decimalToCents(queueAmountDecimal(item) ?? "");
    if (cents === null || cents === 0n) {
      // No usable amount means no direction: keep the words neutral rather
      // than call an unknown movement "out of this wallet" (round 2, item 8).
      return `every wallet ${noun}`;
    }
    // R4-14 swept the CARD strings; round 12 (rd-polish-rules §1) sweeps this
    // rule preview too: a Moov payment as a source is a "wallet payment".
    return `every wallet ${noun} ${cents > 0n ? "into" : "out of"} this wallet`;
  }
  // Bank text is untrusted: the sentence is read by a person, so a bidi or line
  // separator must not reorder or split it (the rule itself still matches the
  // stored text; see the match-text radios).
  const who = amVisibleDisplayText((item.merchant ?? item.descriptor ?? "").trim());
  const noun = ruleNoun(item, scope);
  return who.length > 0
    ? `every ${noun} that matches ${who}`
    : `every ${noun} that matches this payee`;
}

/** The width bit of a rule sentence: the amount the rule is for, or any. */
function ruleWidthBit(item: QueueItem, scope: QueueRuleScope | undefined): string {
  if (scope === "vendor") return " at any amount";
  // An amount the card does not carry has no size to name; "of —" would be a
  // broken sentence, so say nothing (round 2, item 8).
  if (scope === "amount") {
    const cents = decimalToCents(queueAmountDecimal(item) ?? "");
    if (cents === null || cents === 0n) return "";
    return ` of ${unsignedAmount(item)}`;
  }
  return "";
}

/** What the person has picked for a standing rule, nothing more. */
export interface StandingRuleChoices {
  matchText: string | null;
  ruleKind: QueueRuleKind | null;
  ruleScope: QueueRuleScope | null;
  vendorClassification: QueueVendorClassification | null;
}

/** The rule fields a resolve request carries. */
export interface StandingRuleFields {
  matchText?: string;
  ruleKind?: QueueRuleKind;
  ruleScope?: QueueRuleScope;
  vendorClassification?: QueueVendorClassification;
}

/**
 * The ONE builder of both halves of a standing rule on a bank card: the
 * fields the request carries and the sentence read before pressing (round
 * eight A-M3: the sentence used to say "every charge that matches Synthetic
 * Paint Shop" while the request carried the picked text "CITY" at any amount).
 * The sentence is written FROM the fields, so the two cannot differ. Nothing
 * chosen yet reads as the payee and carries no fields.
 */
export function standingRuleRequest(
  item: QueueItem,
  choices: StandingRuleChoices,
): { fields: StandingRuleFields; preview: string } {
  const { matchText, ruleKind, ruleScope, vendorClassification } = choices;
  const fields: StandingRuleFields = {
    ...(matchText ? { matchText } : {}),
    ...(ruleKind ? { ruleKind } : {}),
    ...(ruleKind === "vendor_identity"
      ? vendorClassification
        ? { vendorClassification }
        : {}
      : ruleScope
        ? { ruleScope }
        : {}),
  };
  const subject = fields.matchText
    ? `every ${ruleNoun(item, fields.ruleScope)} that matches "${amVisibleDisplayText(fields.matchText)}"`
    : ruleMatchPreview(item, fields.ruleScope);
  const preview =
    fields.matchText === undefined
      ? "Choose the text to match on first."
      : fields.ruleKind === "vendor_identity"
        ? `${subject}, named as the vendor (each one still comes to ${navLabel("queue")})`
        : `${subject}${ruleWidthBit(item, fields.ruleScope)}`;
  return { fields, preview };
}

/** The same for a wallet payment: its only choice is the width. */
export function moovRuleRequest(
  item: QueueItem,
  rule: boolean,
  broad: boolean,
): { fields: { ruleScope?: QueueRuleScope }; preview: string } {
  const fields: { ruleScope?: QueueRuleScope } = rule ? { ruleScope: broad ? "vendor" : "amount" } : {};
  // Nothing is said about a rule while the opt-in box is unticked (item 8c).
  const preview = rule
    ? `${ruleMatchPreview(item)}${ruleWidthBit(item, fields.ruleScope)}`
    : "";
  return { fields, preview };
}

/**
 * False only when the chart is loaded and lists no location at all: a wallet
 * card asking for a location then has nothing to pick, so it is a setup fix.
 * Undefined when the chart is not loaded, so nothing is reclassified on a
 * guess (round-1 review, SHOULD-FIX 3).
 */
type LocationAvailability = "list" | "empty" | "absent" | "error";

function locationAvailability(load: ChartLoadState | undefined): LocationAvailability {
  if (load === undefined) return "absent";
  if (load.status === "ok") {
    return load.snapshot.selectableLocations.length === 0 ? "empty" : "list";
  }
  return load.status === "absent" ? "absent" : "error";
}

/** The presenter context a stage card is rendered with. */
function presentationContext(
  opts: StageCardOpts,
): CardPresenterContext {
  return {
    directory: opts.directory,
    role: opts.role === "account_manager" ? "account_manager" : "owner",
    hasSelectableLocations: locationAvailability(opts.chartLoad) !== "empty",
    accountWords: stageAccountLabels(opts).whenShared,
  };
}

/** The labels for the accounts this card's company offers (F2). */
function stageAccountLabels(opts: StageCardOpts): CompanyAccountLabels {
  return companyAccountLabels(
    () => opts.chartLoad.snapshot.selectableAccounts,
    accountLabelContext(opts.directory),
  );
}

function cardFaceBlock(
  opts: StageCardOpts,
  titleRole: string,
): { face: CardPresentation; root: HTMLElement } {
  const face = cardPresentation(opts.item, presentationContext(opts));
  const suggestion = bankSuggestionSentence(
    opts.item,
    (account) => stageAccountLabels(opts).label(opts.item.company, account),
  );
  const root = h("div", { class: "card-face", "data-role": "card-face" }, [
    h("h2", { class: "question", tabindex: "-1", "data-role": titleRole }, [amVisibleDisplayText(face.title)]),
    h("p", { class: "card-happened", "data-role": "card-happened" }, [face.happened]),
    ...(opts.omitFaceSuggest === true
      ? []
      : [h("p", { class: "card-suggest", "data-role": "card-suggest" }, [face.suggest])]),
    h(
      "p",
      {
        class: "card-chip",
        "data-role": "card-chip",
        "data-tone": face.chip.tone,
      },
      [face.chip.text],
    ),
    ...(suggestion === null
      ? []
      : [
          h("p", { class: "card-bank-suggestion", "data-role": "bank-suggestion" }, [
            suggestion,
          ]),
        ]),
  ]);
  return { face, root };
}

function showDetailsSummary(): HTMLElement {
  return h(
    "summary",
    { "data-action": "disclose", "aria-label": "Show details" },
    ["Show details"],
  );
}

function moreOptionsPanel(children: readonly HTMLElement[]): HTMLElement {
  return h("details", { class: "more-options", "data-role": "more-options" }, [
    h(
      "summary",
      { "aria-label": "More options" },
      ["More options"],
    ),
    ...children,
  ]);
}

function mountLocationControl(args: {
  selectable: readonly string[];
  companies: AccountLabelContext;
  className: string;
  ariaLabel: string;
  placeholder: string;
  initialValue: string;
  load: ChartLoadState;
  groupKey?: string;
}): ChartChooser {
  const unique = [...new Set(args.selectable)];
  if (unique.length === 1) {
    const only = unique[0] ?? "";
    return mountSingleLocation({
      name: only,
      className: args.className,
      ariaLabel: args.ariaLabel,
    });
  }
  return mountChartChooser({
    kind: "location",
    selectable: args.selectable,
    companies: args.companies,
    className: args.className,
    ariaLabel: args.ariaLabel,
    placeholder: args.placeholder,
    initialValue: args.initialValue,
    load: args.load,
    ...(args.groupKey !== undefined ? { groupKey: args.groupKey } : {}),
  });
}

function renderStageCard(opts: {
  item: QueueItem;
  client: AdminClient;
  chartLoad: ChartLoadState;
  directory: face.CompanyDirectory;
  canLater: boolean;
  hubLinkedSlugs: ReadonlySet<string> | null;
  companies: readonly Company[] | null;
  role?: StaffRole | null;
  navigateTo?: (route: RouteName, opts?: { company?: CompanySlug }) => Promise<void>;
  announce: (message: string) => void;
  onLater: () => void;
  onDismissCard?: (item: QueueItem) => void;
  /**
   * Y5 brief item 2: a blocked wallet card prints its next step once, in the
   * blocked-action paragraph that replaces the approve control. The face must
   * not print the same sentence above it.
   */
  omitFaceSuggest?: boolean;
  onEscalated: (result: { confirmationText: string }) => void;
  onResolved: (result: {
    kind: "approve" | "edit" | "reject";
    confirmation: HTMLElement;
    confirmationText: string;
  }) => Promise<void>;
  onResolveFailed: (message: string) => void;
  onAlreadyAnswered?: () => void;
  /** R4-11/R4-12: the card started or finished a write. */
  onBusyChange?: (busy: boolean) => void;
}): HTMLElement {
  // Derive from eventType — never sniff reason/rationale (fix-r10b / #287).
  if (isRemovalQueueItem(opts.item)) {
    return renderRemovalStageCard(opts);
  }
  if (isMoovMovementQueueItem(opts.item)) {
    return renderMoovStageCard(opts);
  }
  if (isBlockerQueueItem(opts.item)) {
    return renderBlockerStageCard(opts);
  }
  if (isUntrackedCounterpartyQueueItem(opts.item)) {
    return renderUntrackedCounterpartyStageCard(opts);
  }
  return renderCategorisationStageCard(opts);
}

type StageCardOpts = {
  item: QueueItem;
  client: AdminClient;
  chartLoad: ChartLoadState;
  directory: face.CompanyDirectory;
  canLater: boolean;
  hubLinkedSlugs: ReadonlySet<string> | null;
  companies: readonly Company[] | null;
  role?: StaffRole | null;
  navigateTo?: (route: RouteName, opts?: { company?: CompanySlug }) => Promise<void>;
  announce: (message: string) => void;
  onLater: () => void;
  onDismissCard?: (item: QueueItem) => void;
  /**
   * Y5 brief item 2: a blocked wallet card prints its next step once, in the
   * blocked-action paragraph that replaces the approve control. The face must
   * not print the same sentence above it.
   */
  omitFaceSuggest?: boolean;
  onEscalated: (result: { confirmationText: string }) => void;
  onResolved: (result: {
    kind: "approve" | "edit" | "reject";
    confirmation: HTMLElement;
    confirmationText: string;
  }) => Promise<void>;
  onResolveFailed: (message: string) => void;
  /**
   * A 409 means someone else answered first: re-read the list so the card's
   * Book control cannot stay live (round 10, C-6).
   */
  onAlreadyAnswered?: () => void;
  /** R4-11/R4-12: the card started or finished a write. */
  onBusyChange?: (busy: boolean) => void;
};

function sendToPracticeControls(args: {
  item: QueueItem;
  client: AdminClient;
  card: HTMLElement;
  status: HTMLElement;
  /** R4-28: the 403 sentence is role-aware (the owner owns the console). */
  role?: StaffRole | null;
  announce: (message: string) => void;
  onEscalated: (result: { confirmationText: string }) => void;
  onResolveFailed: (message: string) => void;
  onAlreadyAnswered?: () => void;
  onBusyChange?: (busy: boolean) => void;
  onOpen: () => void;
  onClose?: () => void;
}): {
  button: HTMLButtonElement;
  fields: HTMLElement;
  setOpen: (open: boolean) => void;
  setBusy: (busy: boolean) => void;
  isOpen: () => boolean;
  setButtonVisible: (visible: boolean) => void;
} {
  const role = readerRole(args.role);
  // DECIDED (owner, round-12 decision 12): "Send to practice" becomes "Send to
  // the hub". The label comes from the one decided home; the dialog's other
  // words say "the hub" too, and the aria-labels follow the visible words.
  const hubLabel = sentence("send_to_hub", role);
  const noteInput = h("input", {
    type: "text",
    class: "note-input",
    placeholder: "Why the hub should see this (required)",
    maxlength: "500",
    "aria-label": face.ariaFor(`${hubLabel} note`),
    "data-role": "send-to-practice-note",
  });
  const confirmLabel = `Confirm ${hubLabel.charAt(0).toLowerCase()}${hubLabel.slice(1)}`;
  const confirmBtn = h(
    "button",
    {
      type: "button",
      class: "btn btn-primary",
      "data-action": "send-to-practice-confirm",
      "aria-label": face.ariaFor(confirmLabel),
      hidden: "true",
    },
    [hubLabel],
  );
  const cancelBtn = h(
    "button",
    {
      type: "button",
      class: "btn btn-ghost",
      "data-action": "send-to-practice-cancel",
      "aria-label": face.ariaFor(`Cancel ${hubLabel}`),
      hidden: "true",
    },
    ["Cancel"],
  );
  const button = h(
    "button",
    {
      type: "button",
      class: "btn btn-ghost",
      "data-action": "send-to-practice",
      "aria-label": face.ariaFor(hubLabel),
    },
    [hubLabel],
  );
  const fields = h(
    "div",
    {
      class: "fields",
      "data-role": "send-to-practice-fields",
      hidden: "true",
    },
    [
      h("label", {}, ["Note for the hub", noteInput]),
      h("div", { class: "actions" }, [confirmBtn, cancelBtn]),
    ],
  );
  let open = false;
  const setOpen = (next: boolean): void => {
    open = next;
    fields.hidden = !next;
    if (next) {
      fields.removeAttribute("hidden");
      confirmBtn.hidden = false;
      cancelBtn.hidden = false;
      confirmBtn.removeAttribute("hidden");
      cancelBtn.removeAttribute("hidden");
      button.hidden = true;
      button.setAttribute("hidden", "true");
    } else {
      fields.setAttribute("hidden", "true");
      confirmBtn.hidden = true;
      cancelBtn.hidden = true;
      confirmBtn.setAttribute("hidden", "true");
      cancelBtn.setAttribute("hidden", "true");
      button.hidden = false;
      button.removeAttribute("hidden");
    }
  };
  const setBusy = (busy: boolean): void => {
    button.disabled = busy;
    confirmBtn.disabled = busy;
    cancelBtn.disabled = busy;
  };
  const setButtonVisible = (visible: boolean): void => {
    if (open) return;
    button.hidden = !visible;
    if (visible) button.removeAttribute("hidden");
    else button.setAttribute("hidden", "true");
  };
  button.addEventListener("click", () => {
    args.onOpen();
    setOpen(true);
    noteInput.focus();
  });
  cancelBtn.addEventListener("click", () => {
    setOpen(false);
    args.onClose?.();
  });
  confirmBtn.addEventListener("click", () => {
    void (async () => {
      args.onBusyChange?.(true);
      const outcome = await runWrite({
        key: cardWriteKey(args.card, "send-to-practice"),
        scope: args.card,
        gate: () => (noteInput.value.trim() ? null : "A note is required to send to the hub."),
        send: async () => {
          args.card.classList.add("exiting");
          await Promise.all([
            args.client.escalateQueueItemToPractice(args.item.company, args.item.id, {
              note: noteInput.value.trim(),
            }),
            sleep(STAGE_MS),
          ]);
        },
        stillCurrent: () => args.card.isConnected,
        live: args.status,
      });
      if (outcome.kind === "landed") {
        lockCardControls(args.card);
        // M2: the page callback belongs to the write's outcome, not to the
        // card's `afterLanded` (which `stillCurrent()` skips once the page has
        // re-rendered under it).
        args.card.classList.add("resolved");
        args.onEscalated({
          confirmationText: sendToPracticeConfirmationText(args.item),
        });
        return;
      }
      if (outcome.kind === "refused" && outcome.status === 0) {
        // M1: the gate refused, so nothing was sent — leave nothing busy.
        args.onBusyChange?.(false);
        return;
      }
      args.card.classList.remove("exiting");
      args.onBusyChange?.(false);
      if (outcome.error !== undefined && isAlreadyAnsweredError(outcome.error)) {
        lockCardControls(args.card);
        args.onAlreadyAnswered?.();
        return;
      }
      // R4-28: a company the Worker says is not hub-linked can never be sent
      // from this card, so it keeps its own sentence for both roles.
      let message: string;
      if (
        outcome.error !== undefined &&
        bodyMessageIs(outcome.error, "Company is not hub-linked")
      ) {
        clear(args.status);
        args.status.append(document.createTextNode(SEND_TO_PRACTICE_NOT_LINKED_COPY));
        message = SEND_TO_PRACTICE_NOT_LINKED_COPY;
      } else {
        message = paintOutcomeFailure(args.status, outcome, args.role);
      }
      args.announce(message);
      args.onResolveFailed(message);
    })();
  });
  return { button, fields, setOpen, setBusy, isOpen: () => open, setButtonVisible };
}

/**
 * Bank-removal card (F3 + Finding A).
 *
 * Recoverable: Reverse it / Decline — no account, location, or make_rule.
 * Unrecoverable: identify via plaid id; escalate (or decline → terminal);
 * never offer approve/edit that cannot complete.
 */
function renderRemovalStageCard(opts: StageCardOpts): HTMLElement {
  const { item, client } = opts;
  const canEscalate = opts.role !== "owner";
  const recoverable = isRecoverableRemoval(item);
  const card = h("article", {
    class: "queue-card removal-card",
    "data-queue-id": String(item.id),
    "data-company": item.company,
    "data-event-type": "removed",
    "data-removal": recoverable ? "recoverable" : "unrecoverable",
  });

  const presented = cardFaceBlock(opts, "card-face-title");


  const amountLine = recoverable
    ? h("p", { class: "stage-amount money" }, [formatAmount(queueAmountDecimal(item))])
    : h("p", { class: "stage-amount money muted" }, ["Amount unknown"]);

  const factNodes: DocumentFragment[] = [];
  // R4-07: no raw bank transaction id on the face — it is not a fact a person
  // reads, and on an unrecoverable row it is not the only identifier either
  // (merchant, descriptor and amount are printed below). The id stays behind
  // "Show details" for the one case that has nothing else to name it by.
  if (recoverable) {
    factNodes.push(
      fact("Date", isWireSentinelDate(item.txnDate) ? "unknown" : dateText(item.txnDate)),
    );
    factNodes.push(fact("Merchant", safeText(item.merchant)));
    factNodes.push(fact("Descriptor", safeText(item.descriptor)));
    appendPlaidGrantFactNodes(factNodes, item);
    factNodes.push(
      fact(
        "Originally booked as",
        stageAccountLabels(opts).label(item.company, item.suggestedAccount ?? ""),
      ),
    );
    if (item.suggestedLocation) {
      factNodes.push(fact("Location", safeText(item.suggestedLocation)));
    }
  } else {
    // Sentinels are not facts — do not render 1970-01-01 / 0.00. The date is
    // the display form (R4-07): never a raw YYYY-MM-DD on a face.
    if (item.txnDate && !isWireSentinelDate(item.txnDate)) {
      factNodes.push(fact("Date", dateText(item.txnDate)));
    }
    if (item.amount && !isWireSentinelAmount(item.amount)) {
      factNodes.push(fact("Amount", formatAmount(queueAmountDecimal(item))));
    }
    if (item.merchant) factNodes.push(fact("Merchant", safeText(item.merchant)));
    if (item.descriptor) {
      factNodes.push(fact("Descriptor", safeText(item.descriptor)));
    }
    appendPlaidGrantFactNodes(factNodes, item);
  }

  const facts = h("dl", { class: "facts" }, factNodes);
  // R4-07: the bank transaction id is the one identifier an unrecoverable row
  // can still be found by. It lives in the disclosure, in both cases, so the
  // face carries no raw internal id and the row is still identifiable.
  const removalIdFact = item.plaidTransactionId
    ? [fact("Bank transaction id", safeText(item.plaidTransactionId))]
    : [];

  // F-4: condition copy on whether escalate is offered and who is viewing.
  // Re-derive on return-to-am — prose computed once at render would keep
  // naming the hidden return control after the Owner clears escalation.
  const explain = h(
    "p",
    { class: "removal-explain", "data-role": "removal-explain" },
    [],
  );
  const paintExplain = (): void => {
    clear(explain);
    const textOpts: {
      recoverable: boolean;
      escalated: boolean;
      role?: StaffRole | null;
    } = {
      recoverable,
      escalated: isWithOwner(item),
    };
    if (opts.role !== undefined) textOpts.role = opts.role;
    explain.append(document.createTextNode(removalExplainText(textOpts)));
  };
  paintExplain();

  const removalTechnical = technicalDisclosure(opts.role ?? "account_manager", [
    fact("Reason", item.reason),
    fact("Event", "removed"),
  ]);
  const why = h("details", { class: "why-disclosure" }, [
    showDetailsSummary(),
    h("dl", { class: "facts" }, [
      fact("Waiting", formatAge(item.createdAt)),
      ...removalIdFact,
    ]),
    ...(removalTechnical === null ? [] : [removalTechnical]),
  ]);

  const status = h(
    "p",
    { class: "status", role: "status", "data-role": "status" },
    [],
  );

  // No account / location / make_rule inputs — they are inert for a reversal
  // and silently discarded by build_plaid_corrections (F3). Do not offer them.

  const reverseBtn = recoverable
    ? h(
        "button",
        {
          type: "button",
          class: "btn btn-primary",
          "data-action": "reverse-removal",
          "aria-label": "Reverse it — mirror the original entry",
        },
        ["Reverse it"],
      )
    : null;

  const declineBtn = h(
    "button",
    {
      type: "button",
      class: recoverable ? "btn btn-ghost" : "btn btn-secondary",
      "data-action": "decline-removal",
      "aria-label": "Decline — don't reverse, close the row",
    },
    ["Decline — don't reverse"],
  );

  const laterBtn = h(
    "button",
    {
      type: "button",
      class: "btn btn-ghost",
      "data-action": "later",
      "aria-label": SKIP_FOR_NOW_LABEL,
    },
    [SKIP_FOR_NOW_LABEL],
  );
  if (!opts.canLater) {
    laterBtn.hidden = true;
    laterBtn.setAttribute("hidden", "true");
  }

  const escalateNoteInput = h("input", {
    type: "text",
    class: "note-input",
    placeholder: "Why you can't resolve this (required)",
    maxlength: "500",
    "aria-label": "Escalation note",
    "data-role": "escalate-note",
  });
  const escalateConfirmBtn = h(
    "button",
    {
      type: "button",
      class: "btn btn-primary",
      "data-action": "escalate-confirm",
      "aria-label": "Confirm sending to the owner",
      hidden: "true",
    },
    ["Send to owner"],
  );
  const escalateCancelBtn = h(
    "button",
    {
      type: "button",
      class: "btn btn-ghost",
      "data-action": "escalate-cancel",
      "aria-label": "Cancel escalation",
      hidden: "true",
    },
    ["Cancel"],
  );
  // Unrecoverable: escalate is the honest primary exit (Finding A).
  const escalateBtn = h(
    "button",
    {
      type: "button",
      class: recoverable ? "btn btn-ghost" : "btn btn-primary",
      "data-action": "escalate",
      "aria-label": ESCALATE_TO_OWNER_LABEL,
      ...(!canEscalate ? { hidden: "true" } : {}),
    },
    [ESCALATE_TO_OWNER_LABEL],
  );
  const escalateFields = h(
    "div",
    {
      class: "fields",
      "data-role": "escalate-fields",
      hidden: "true",
    },
    [
      h("label", {}, ["Escalation note", escalateNoteInput]),
      h("div", { class: "actions" }, [escalateConfirmBtn, escalateCancelBtn]),
    ],
  );

  const returnToAmBtn = h(
    "button",
    {
      type: "button",
      class: "btn btn-ghost",
      "data-action": "return-to-am",
      "aria-label": "Return to account manager",
      hidden: "true",
    },
    ["Return to account manager"],
  );

  const escalatedBanner = isWithOwner(item)
    ? h(
        "p",
        {
          class: "escalation-banner",
          "data-role": "escalation-banner",
        },
        [
          `Escalated by ${amVisibleDisplayText(safeText(item.escalatedBy, "staff"))}: ${amVisibleDisplayText(safeText(item.escalationNote, ""))}`,
        ],
      )
    : null;

  if (isWithOwner(item)) {
    escalateBtn.hidden = true;
    escalateBtn.setAttribute("hidden", "true");
    if (opts.role === "owner") {
      returnToAmBtn.hidden = false;
      returnToAmBtn.removeAttribute("hidden");
    }
  }

  let escalateOpen = false;

  const setEscalateOpen = (open: boolean): void => {
    escalateOpen = open;
    escalateFields.hidden = !open;
    if (open) {
      escalateFields.removeAttribute("hidden");
      escalateConfirmBtn.hidden = false;
      escalateCancelBtn.hidden = false;
      escalateConfirmBtn.removeAttribute("hidden");
      escalateCancelBtn.removeAttribute("hidden");
      escalateBtn.setAttribute("hidden", "true");
      escalateBtn.hidden = true;
      if (reverseBtn) {
        reverseBtn.hidden = true;
        reverseBtn.setAttribute("hidden", "true");
      }
      declineBtn.hidden = true;
      declineBtn.setAttribute("hidden", "true");
      laterBtn.hidden = true;
      laterBtn.setAttribute("hidden", "true");
    } else {
      escalateFields.setAttribute("hidden", "true");
      escalateConfirmBtn.hidden = true;
      escalateCancelBtn.hidden = true;
      escalateConfirmBtn.setAttribute("hidden", "true");
      escalateCancelBtn.setAttribute("hidden", "true");
      if (!isWithOwner(item) && canEscalate) {
        escalateBtn.hidden = false;
        escalateBtn.removeAttribute("hidden");
      }
      if (reverseBtn) {
        reverseBtn.hidden = false;
        reverseBtn.removeAttribute("hidden");
      }
      declineBtn.hidden = false;
      declineBtn.removeAttribute("hidden");
      if (opts.canLater) {
        laterBtn.hidden = false;
        laterBtn.removeAttribute("hidden");
      }
    }
  };

  /** The one failure path for this card's writes (dispatch-b §3.1). */
  const finishExitFailure = (outcome: WriteOutcome): void => {
    card.classList.remove("exiting");
    opts.onBusyChange?.(false);
    const message = paintOutcomeFailure(status, outcome, opts.role);
    opts.announce(message);
    opts.onResolveFailed(message);
  };

  const exitLanded = async (kind: "approve" | "reject"): Promise<void> => {
    const { node, text } = buildRemovalConfirmation(kind, item, (account) =>
      stageAccountLabels(opts).label(item.company, account),
    );
    card.classList.add("resolved");
    await opts.onResolved({ kind, confirmation: node, confirmationText: text });
  };

  laterBtn.addEventListener("click", () => {
    opts.onLater();
  });

  if (reverseBtn) {
    reverseBtn.addEventListener("click", () => {
      void (async () => {
        opts.onBusyChange?.(true);
        clear(status);
        const outcome = await runWrite({
          key: cardWriteKey(card, "removal-approve"),
          scope: card,
          gate: () => null,
          // Approve only — no account/location/makeRule. Reversal mirrors the
          // original; honouring an AM-chosen account would be wrong.
          send: async () => {
            card.classList.add("exiting");
            await client.resolveQueueItem(item.id, { action: "approve", makeRule: false });
          },
          stillCurrent: () => card.isConnected,
        });
        if (outcome.kind === "landed") {
          lockCardControls(card);
          // M2: the page callback runs on the outcome, not inside
          // `afterLanded`, so a re-render under the write cannot strand it.
          await exitLanded("approve");
          return;
        }
        if (outcome.error !== undefined && isAlreadyAnsweredError(outcome.error)) {
          lockCardControls(card);
          opts.onAlreadyAnswered?.();
          return;
        }
        finishExitFailure(outcome);
      })();
    });
  }

  declineBtn.addEventListener("click", () => {
    void (async () => {
      opts.onBusyChange?.(true);
      clear(status);
      const outcome = await runWrite({
        key: cardWriteKey(card, "removal-reject"),
        scope: card,
        gate: () => null,
        send: async () => {
          card.classList.add("exiting");
          await client.resolveQueueItem(item.id, { action: "reject", makeRule: false });
        },
        stillCurrent: () => card.isConnected,
      });
      if (outcome.kind === "landed") {
        lockCardControls(card);
        await exitLanded("reject");
        return;
      }
      if (outcome.error !== undefined && isAlreadyAnsweredError(outcome.error)) {
        lockCardControls(card);
        opts.onAlreadyAnswered?.();
        return;
      }
      finishExitFailure(outcome);
    })();
  });

  escalateBtn.addEventListener("click", () => {
    setEscalateOpen(true);
    escalateNoteInput.focus();
  });
  escalateCancelBtn.addEventListener("click", () => {
    setEscalateOpen(false);
  });
  escalateConfirmBtn.addEventListener("click", () => {
    void (async () => {
      opts.onBusyChange?.(true);
      const outcome = await runWrite({
        key: cardWriteKey(card, "removal-escalate"),
        scope: card,
        gate: () => (escalateNoteInput.value.trim() ? null : "Escalation note is required."),
        send: async () => {
          clear(status);
          card.classList.add("exiting");
          await Promise.all([
            client.escalateQueueItem(item.company, item.id, {
              note: escalateNoteInput.value.trim(),
            }),
            sleep(STAGE_MS),
          ]);
        },
        stillCurrent: () => card.isConnected,
        live: status,
      });
      if (outcome.kind === "landed") {
        lockCardControls(card);
        card.classList.add("resolved");
        opts.onEscalated({ confirmationText: "Escalated to owner." });
        return;
      }
      if (outcome.kind === "refused" && outcome.status === 0) {
        opts.onBusyChange?.(false);
        return;
      }
      if (outcome.error !== undefined && isAlreadyAnsweredError(outcome.error)) {
        lockCardControls(card);
        opts.onAlreadyAnswered?.();
        return;
      }
      finishExitFailure(outcome);
    })();
  });

  returnToAmBtn.addEventListener("click", () => {
    void (async () => {
      opts.onBusyChange?.(true);
      clear(status);
      const outcome = await returnCardToAm({
        item,
        client,
        scope: card,
        stillCurrent: () => card.isConnected,
      });
      if (outcome.kind !== "landed") {
        if (outcome.error !== undefined && isAlreadyAnsweredError(outcome.error)) {
          lockCardControls(card);
          opts.onAlreadyAnswered?.();
          return;
        }
        finishExitFailure(outcome);
        return;
      }
      // `item.escalationClearedAt` was set in `afterLanded`, so the visible
      // controls and `paintExplain` agree with `isQueueItemEscalated`.
      clear(status);
      status.append(document.createTextNode("Returned to account manager."));
      returnToAmBtn.hidden = true;
      returnToAmBtn.setAttribute("hidden", "true");
      if (escalatedBanner) escalatedBanner.remove();
      escalateBtn.hidden = false;
      escalateBtn.removeAttribute("hidden");
      paintExplain();
      opts.onBusyChange?.(false);
    })().catch(() => undefined);
  });

  void escalateOpen;

  const actionChildren: HTMLElement[] = [];
  if (reverseBtn) actionChildren.push(reverseBtn);
  actionChildren.push(declineBtn, laterBtn, ...(canEscalate ? [escalateBtn] : []), returnToAmBtn);

  const children: Array<Node | string> = [
    presented.root,
    amountLine,
    facts,
    explain,
    why,
  ];
  if (escalatedBanner) children.push(escalatedBanner);
  children.push(
    ...(canEscalate ? [escalateFields] : []),
    h("div", { class: "actions" }, actionChildren),
    status,
  );
  card.append(...children);
  return card;
}

function buildRemovalConfirmation(
  kind: "approve" | "edit" | "reject",
  item: QueueItem,
  accountWords: (account: string) => string,
): { node: HTMLElement; text: string } {
  const wrap = h("div", { class: "confirm-line", "data-role": "confirmation" });
  if (kind === "reject") {
    const text = "Declined — no reversal posted.";
    wrap.append(document.createTextNode(text));
    return { node: wrap, text };
  }
  // R4-08: the account is worded through the shared label (Y0's
  // `accountLabel`, twin-aware here), never the raw colon path.
  const account =
    item.suggestedAccount === undefined || item.suggestedAccount === ""
      ? "the original account"
      : accountWords(item.suggestedAccount);
  const text = `Reversal queued — mirrors ${account}.`;
  wrap.append(document.createTextNode(text));
  return { node: wrap, text };
}

/**
 * Close / statement blocker card (feat-today-dispositions-complete Input 2).
 *
 * Renders subject + body. No categorisation controls. Exit sentences come
 * from the shared blocker-exit declaration (fix-r17d) — close-lifecycle,
 * closed-period-write, mapping, and receipt-conflict clear via system
 * writers; statement is conditional on confirm+dispatch; hard-match-refuse
 * is AM keep-bank; unknown subjects stay attention.
 */

const ESCROW_DISBURSEMENT_KINDS = [
  "taxes",
  "insurance",
  "mortgage_insurance",
] as const;

const ESCROW_AMOUNT_PATTERN = /^(0|[1-9]\d{0,12})(\.\d{1,2})?$/;

function escrowDisbursementEntryError(args: {
  date: string;
  amount: string;
  kind: string;
}): string | null {
  if (args.date.length === 0) {
    return "escrow disbursement date is required";
  }
  if (!/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/.test(args.date)) {
    return "escrow disbursement date must be a valid calendar date";
  }
  const [yearRaw, monthRaw, dayRaw] = args.date.split("-");
  const year = Number(yearRaw);
  const month = Number(monthRaw);
  const day = Number(dayRaw);
  const dt = new Date(Date.UTC(year, month - 1, day));
  if (
    dt.getUTCFullYear() !== year ||
    dt.getUTCMonth() !== month - 1 ||
    dt.getUTCDate() !== day
  ) {
    return "escrow disbursement date must be a valid calendar date";
  }
  if (args.date > localTodayIsoDate()) {
    return "escrow disbursement date cannot be in the future";
  }
  if (args.amount.length === 0) {
    return "escrow disbursement amount is required";
  }
  if (!ESCROW_AMOUNT_PATTERN.test(args.amount) || Number(args.amount) <= 0) {
    return "escrow disbursement amount must be greater than zero";
  }
  if (
    args.kind !== "taxes" &&
    args.kind !== "insurance" &&
    args.kind !== "mortgage_insurance"
  ) {
    return "escrow disbursement kind must be taxes, insurance, or mortgage_insurance";
  }
  return null;
}

function untrackedResolveReviewedDecision(
  item: QueueItem,
  disposition: UntrackedCounterpartyDisposition,
  targetSlug?: CompanySlug,
): QueueResolutionReviewedDecision | null {
  const snap = item.reviewedDecision;
  if (snap === undefined) return null;
  const body: QueueResolutionReviewedDecision = { ...snap, disposition };
  if (targetSlug !== undefined) body.targetSlug = targetSlug;
  return body;
}

function characterSourceCopy(snapshot: QueueReviewedDecisionSnapshot | undefined): string | null {
  if (snapshot === undefined) return null;
  if (snapshot.characterSource === "hub_transaction_context") {
    const character = snapshot.character ?? "unspecified";
    return `${character} from the hub transaction context`;
  }
  return "No hub transaction character was shown on this card";
}

function renderUntrackedCounterpartyStageCard(opts: StageCardOpts): HTMLElement {
  const { item, client } = opts;
  const role: StaffRole = opts.role ?? "account_manager";
  const label = amVisibleDisplayText(
    counterpartyLabelFromRationale(item.aiRationale, item.merchant),
  );
  const snapshot = item.reviewedDecision;
  const card = h("article", {
    class: "queue-card untracked-counterparty-card",
    "data-queue-id": String(item.id),
    "data-company": item.company,
    "data-reason": item.reason,
    "data-role": "untracked-counterparty-card",
  });

  const presented = cardFaceBlock(opts, "untracked-counterparty-headline");
  const amountLine = h("p", { class: "stage-amount money" }, [formatAmount(queueAmountDecimal(item))]);
  const bodyPara = h(
    "p",
    { class: "blocker-body", "data-role": "untracked-counterparty-body" },
    [
      `This transfer names ${label}. If this is a nickname for a company we already track, confirm which one. Otherwise mark it as a recognized counterparty whose books we do not keep, or create a company to start onboarding.`,
    ],
  );
  const characterLine = characterSourceCopy(snapshot);
  const facts = h("dl", { class: "facts" }, [
    ...(item.txnDate ? [fact("Date", safeText(item.txnDate))] : []),
    ...(item.merchant ? [fact("Counterparty", safeText(item.merchant))] : []),
    ...(item.descriptor ? [fact("Descriptor", safeText(item.descriptor))] : []),
    ...(snapshot !== undefined
      ? [
          fact("From", safeText(snapshot.fromLabel)),
          fact("To", safeText(snapshot.toLabel)),
        ]
      : []),
    ...(characterLine !== null
      ? [fact("Character", safeText(characterLine))]
      : []),
    ...(snapshot?.contextAmount
      ? [fact("Hub amount", safeText(snapshot.contextAmount))]
      : []),
    ...(snapshot?.contextDate
      ? [fact("Hub date", safeText(snapshot.contextDate))]
      : []),
  ]);
  const technical = technicalDisclosure(role, [fact("Reason", item.reason)]);
  const why = h("details", { class: "why-disclosure" }, [
    showDetailsSummary(),
    h("dl", { class: "facts" }, [fact("Waiting", formatAge(item.createdAt))]),
    ...(technical === null ? [] : [technical]),
  ]);
  const status = h(
    "p",
    { class: "status", role: "status", "data-role": "status" },
    [],
  );
  if (snapshot === undefined) {
    status.append(document.createTextNode(CARD_CHANGED_MESSAGE));
  }

  const markExternalBtn = h(
    "button",
    {
      type: "button",
      class: "btn btn-primary",
      "data-action": "mark-external",
      "aria-label": "Mark as external",
    },
    ["Mark as external"],
  );
  const createCompanyBtn = h(
    "button",
    {
      type: "button",
      class: "btn btn-ghost",
      "data-action": "create-company",
      "aria-label": "Create company",
    },
    ["Create company"],
  );
  const existingCompanyBtn = h(
    "button",
    {
      type: "button",
      class: "btn btn-ghost",
      "data-action": "confirm-alias",
      "aria-label": "This is an existing company",
    },
    ["This is an existing company"],
  );
  const primaryActions: HTMLButtonElement[] = [
    markExternalBtn,
    createCompanyBtn,
    existingCompanyBtn,
  ];
  const confirmCreateBtn = h(
    "button",
    {
      type: "button",
      class: "btn btn-primary",
      "data-action": "create-company-confirm",
      "aria-label": face.ariaFor(ONBOARD_DRAFT_LABEL),
      hidden: "true",
    },
    [ONBOARD_DRAFT_LABEL],
  );
  const cancelCreateBtn = h(
    "button",
    {
      type: "button",
      class: "btn btn-ghost",
      "data-action": "create-company-cancel",
      "aria-label": "Cancel create company",
      hidden: "true",
    },
    ["Cancel"],
  );
  const confirmMarkExternalBtn = h(
    "button",
    {
      type: "button",
      class: "btn btn-primary",
      "data-action": "mark-external-confirm",
      "aria-label": "Confirm recognized counterparty",
      hidden: "true",
    },
    ["Confirm recognized counterparty"],
  );
  const cancelMarkExternalBtn = h(
    "button",
    {
      type: "button",
      class: "btn btn-ghost",
      "data-action": "mark-external-cancel",
      "aria-label": "Cancel mark as external",
      hidden: "true",
    },
    ["Cancel"],
  );
  const laterBtn = h(
    "button",
    {
      type: "button",
      class: "btn btn-ghost",
      "data-action": "later",
      "aria-label": SKIP_FOR_NOW_LABEL,
    },
    [SKIP_FOR_NOW_LABEL],
  );
  if (!opts.canLater) {
    laterBtn.hidden = true;
    laterBtn.setAttribute("hidden", "true");
  }
  const markExternalConfirm = h(
    "div",
    {
      class: "fields",
      "data-role": "mark-external-confirm-fields",
    },
    [
      h("p", { "data-role": "untracked-counterparty-mark-external-copy" }, [
        MARK_EXTERNAL_NOT_A_LEDGER_COPY,
      ]),
      h("p", { class: "muted", "data-role": "untracked-counterparty-mark-external-fleet" }, [
        MARK_EXTERNAL_FLEET_SCOPE_COPY,
      ]),
      h("p", { class: "muted", "data-role": "untracked-counterparty-mark-external-forward" }, [
        RECOGNIZED_COUNTERPARTY_FORWARD_ONLY_COPY,
      ]),
      ...(characterLine !== null
        ? [
            h("p", { "data-role": "untracked-counterparty-mark-external-character" }, [
              `Character: ${characterLine}. Recognizing this party does not silently confirm a different character.`,
            ]),
          ]
        : []),
      h("div", { class: "actions" }, [confirmMarkExternalBtn, cancelMarkExternalBtn]),
    ],
  );
  const createCompanyConfirm = h(
    "div",
    {
      class: "fields",
      "data-role": "create-company-confirm-fields",
    },
    [
      h("p", { "data-role": "untracked-counterparty-create-company-draft" }, [
        CREATE_COMPANY_DRAFT_COPY,
      ]),
      h("div", { class: "actions" }, [confirmCreateBtn, cancelCreateBtn]),
    ],
  );
  const aliasChoices = (opts.companies ?? []).filter(
    (company) => company.slug !== item.company,
  );
  const aliasPicker = h("select", {
    class: "input",
    "aria-label": "Existing company for this label",
    "data-role": "untracked-counterparty-alias-company",
  });
  aliasPicker.append(h("option", { value: "" }, ["Select a company"]));
  for (const company of aliasChoices) {
    aliasPicker.append(
      h("option", { value: company.slug }, [
        face.company(company.slug, opts.directory, "sentence"),
      ]),
    );
  }
  const confirmAliasBtn = h(
    "button",
    {
      type: "button",
      class: "btn btn-primary",
      "data-action": "confirm-alias-confirm",
      "aria-label": "Confirm this label is that company",
      hidden: "true",
      disabled: "true",
    },
    ["Confirm this alias"],
  );
  const cancelAliasBtn = h(
    "button",
    {
      type: "button",
      class: "btn btn-ghost",
      "data-action": "confirm-alias-cancel",
      "aria-label": "Cancel existing-company alias",
      hidden: "true",
    },
    ["Cancel"],
  );
  const aliasHint =
    opts.companies === null
      ? existingCompanyAliasHint({ companiesLoaded: false, choiceCount: 0, role })
      : existingCompanyAliasHint({
          companiesLoaded: true,
          choiceCount: aliasChoices.length,
          role,
        });
  const aliasConfirm = h(
    "div",
    {
      class: "fields",
      "data-role": "confirm-alias-fields",
    },
    [
      h("p", { class: "muted", "data-role": "confirm-alias-hint" }, [aliasHint]),
      h("p", { "data-role": "untracked-counterparty-alias-fleet" }, [ALIAS_FLEET_SCOPE_COPY]),
      h("p", { class: "muted", "data-role": "untracked-counterparty-alias-forward" }, [
        ALIAS_DETACH_FORWARD_ONLY_COPY,
      ]),
      aliasPicker,
      h("div", { class: "actions" }, [confirmAliasBtn, cancelAliasBtn]),
    ],
  );
  const setElHidden = (el: HTMLElement, hidden: boolean): void => {
    el.hidden = hidden;
    if (hidden) el.setAttribute("hidden", "true");
    else el.removeAttribute("hidden");
  };
  const confirmPanels: HTMLElement[] = [
    markExternalConfirm,
    createCompanyConfirm,
    aliasConfirm,
  ];
  const confirmSlot = h(
    "div",
    {
      class: "fields",
      "data-role": "untracked-counterparty-confirm",
      hidden: "true",
    },
    confirmPanels,
  );
  for (const panel of confirmPanels) setElHidden(panel, true);

  const syncAliasConfirmEnabled = (): void => {
    const slug = aliasPicker.value;
    const canConfirm =
      opts.companies !== null &&
      slug.length > 0 &&
      aliasChoices.some((company) => company.slug === slug);
    confirmAliasBtn.disabled = !canConfirm;
  };
  const setBusy = (busy: boolean): void => {
    opts.onBusyChange?.(busy);
    for (const btn of primaryActions) btn.disabled = busy;
    laterBtn.disabled = busy;
    for (const panel of confirmPanels) {
      for (const btn of panel.querySelectorAll("button")) {
        btn.disabled = busy;
      }
    }
    if (!busy) syncAliasConfirmEnabled();
  };
  const setPrimaryHidden = (hidden: boolean): void => {
    for (const btn of primaryActions) setElHidden(btn, hidden);
  };
  const showConfirmPanel = (panel: HTMLElement | null): void => {
    const open = panel !== null;
    setElHidden(confirmSlot, !open);
    for (const candidate of confirmPanels) {
      const show = candidate === panel;
      setElHidden(candidate, !show);
      for (const btn of candidate.querySelectorAll("button")) {
        setElHidden(btn, !show);
      }
    }
    setPrimaryHidden(open);
    if (panel === aliasConfirm) syncAliasConfirmEnabled();
  };

  const refuseStale = (): boolean => {
    if (snapshot !== undefined) return false;
    clear(status);
    status.append(document.createTextNode(CARD_CHANGED_MESSAGE));
    opts.announce(CARD_CHANGED_MESSAGE);
    return true;
  };

  const finishUntracked = async (
    outcome: WriteOutcome,
    confirmationText: string,
  ): Promise<void> => {
    if (outcome.kind === "landed") {
      lockCardControls(card);
      const wrap = h("div", { class: "confirm-line", "data-role": "confirmation" });
      wrap.append(document.createTextNode(confirmationText));
      card.classList.add("resolved");
      await opts.onResolved({
        kind: "approve",
        confirmation: wrap,
        confirmationText,
      });
      return;
    }
    card.classList.remove("exiting");
    // E-B10: only the Worker's "already resolved" body is an answered card.
    if (outcome.error !== undefined && isAlreadyAnsweredError(outcome.error)) {
      lockCardControls(card);
      clear(status);
      status.append(document.createTextNode(ALREADY_ANSWERED_COPY));
      opts.announce(ALREADY_ANSWERED_COPY);
      opts.onAlreadyAnswered?.();
      return;
    }
    const message = paintOutcomeFailure(status, outcome, opts.role);
    opts.announce(message);
    setBusy(false);
    opts.onResolveFailed(message);
  };

  laterBtn.addEventListener("click", () => {
    opts.onLater();
  });
  markExternalBtn.addEventListener("click", () => {
    if (refuseStale()) return;
    showConfirmPanel(markExternalConfirm);
    confirmMarkExternalBtn.focus();
  });
  cancelMarkExternalBtn.addEventListener("click", () => {
    showConfirmPanel(null);
  });
  confirmMarkExternalBtn.addEventListener("click", () => {
    if (refuseStale()) return;
    const reviewedDecision = untrackedResolveReviewedDecision(item, "mark-external");
    if (reviewedDecision === null) {
      refuseStale();
      return;
    }
    void (async () => {
      opts.onBusyChange?.(true);
      const outcome = await runWrite({
        key: cardWriteKey(card, ""),
        scope: card,
        gate: () => null,
        send: async () => {
          clear(status);
          card.classList.add("exiting");
          await client.resolveQueueItem(item.id, { action: "approve", reviewedDecision });
          await sleep(STAGE_MS);
        },
        stillCurrent: () => card.isConnected,
      });
      await finishUntracked(
        outcome,
        `Recognized ${label} as a counterparty whose books we do not keep.`,
      );
    })();
  });
  createCompanyBtn.addEventListener("click", () => {
    if (refuseStale()) return;
    showConfirmPanel(createCompanyConfirm);
    confirmCreateBtn.focus();
  });
  cancelCreateBtn.addEventListener("click", () => {
    showConfirmPanel(null);
  });
  confirmCreateBtn.addEventListener("click", () => {
    if (refuseStale()) return;
    const reviewedDecision = untrackedResolveReviewedDecision(item, "create-company");
    if (reviewedDecision === null) {
      refuseStale();
      return;
    }
    void (async () => {
      opts.onBusyChange?.(true);
      const outcome = await runWrite({
        key: cardWriteKey(card, ""),
        scope: card,
        gate: () => null,
        send: async () => {
          clear(status);
          card.classList.add("exiting");
          await client.resolveQueueItem(item.id, {
            action: "approve",
            // The Worker compares this exact note to recognise the disposition
            // (`untracked_counterparty.ts`); it is the owner's string and stays.
            note: UNTRACKED_COUNTERPARTY_ONBOARD_CONFIRM,
            reviewedDecision,
          });
          await sleep(STAGE_MS);
        },
        stillCurrent: () => card.isConnected,
      });
      // R4-16: only a draft row exists at this point, so the confirmation says
      // what really happened and where the work continues — never that the
      // company is about to be "fully onboarded".
      await finishUntracked(
        outcome,
        `Started an onboarding draft for ${label}. Finish it under ${navLabel("add-company")}.`,
      );
    })();
  });
  existingCompanyBtn.addEventListener("click", () => {
    if (refuseStale()) return;
    showConfirmPanel(aliasConfirm);
    aliasPicker.focus();
  });
  cancelAliasBtn.addEventListener("click", () => {
    aliasPicker.value = "";
    syncAliasConfirmEnabled();
    showConfirmPanel(null);
  });
  aliasPicker.addEventListener("change", () => {
    syncAliasConfirmEnabled();
  });
  confirmAliasBtn.addEventListener("click", () => {
    if (refuseStale()) return;
    const slug = aliasPicker.value;
    if (
      opts.companies === null ||
      slug.length === 0 ||
      !aliasChoices.some((company) => company.slug === slug)
    ) {
      return;
    }
    const reviewedDecision = untrackedResolveReviewedDecision(
      item,
      "confirm-alias",
      slug,
    );
    if (reviewedDecision === null) {
      refuseStale();
      return;
    }
    void (async () => {
      opts.onBusyChange?.(true);
      const outcome = await runWrite({
        key: cardWriteKey(card, ""),
        scope: card,
        gate: () => null,
        send: async () => {
          clear(status);
          card.classList.add("exiting");
          await client.resolveQueueItem(item.id, {
            action: "approve",
            note: untrackedCounterpartyAliasNote(slug),
            reviewedDecision,
          });
          await sleep(STAGE_MS);
        },
        stillCurrent: () => card.isConnected,
      });
      await finishUntracked(
        outcome,
        `Locked ${label} to ${face.company(slug, opts.directory, "sentence")}.`,
      );
    })();
  });

  card.append(
    presented.root,
    amountLine,
    bodyPara,
    facts,
    ...(characterLine !== null
      ? [
          h("p", { class: "muted", "data-role": "untracked-counterparty-character" }, [
            `Character: ${characterLine}`,
          ]),
        ]
      : []),
    why,
    status,
    h(
      "div",
      { class: "actions", "data-role": "untracked-counterparty-actions" },
      [...primaryActions, laterBtn],
    ),
    confirmSlot,
  );
  return card;
}

/**
 * GL-35. A queued Moov wallet movement is a close-blocked card the person can
 * now answer. This keeps the blocker card whole (Later, Escalate) and adds the
 * approve section: pick the account (and the location where the company has
 * several), optionally save a rule, approve.
 *
 * Honest timing, stated on the card: approving records the answer and clears
 * the card now; the entry itself is booked by the next booking run.
 *
 * Round-2 review, SHOULD-FIX 10: "written to the ledger" is ledger jargon on a
 * face. The card says "booked", the word the rest of the console uses.
 *
 * Round-3 review, SHOULD-FIX 6: the booking run can still refuse the write, so
 * the promise is hedged the way R4-15 hedges the rule it adds.
 */
export const MOOV_APPROVE_TIMING_COPY =
  "Booking clears this card now. It is booked on the next booking run if nothing refuses it.";
export const MOOV_RULE_AMOUNT_COPY = "Only this amount";
export const MOOV_RULE_BROAD_COPY = "Any amount";
export const MOOV_RULE_BROAD_CONFIRM_COPY =
  "I understand this books every wallet movement of this type, whatever the amount, to this account";

/**
 * Owner round 17 / K-gate round 40 (R40O-3). A card for a tenant payment that
 * carries a per-kind breakdown cannot be approved to one account: the Worker
 * refuses it and the payment books itself on the next booking run once the cause
 * is fixed. The card body is a reason code; this is the plain sentence for each
 * one. Keep the code list in step with `_SPLIT_ONLY_REASONS` and the
 * `breakdown_` prefix rule in `pipeline/moov_wallet.py` (`_card`).
 */
/**
 * Breakdown reasons whose card must not say the payment books itself: the fix
 * is a NEW payment or a person's decision, so this movement never books. The
 * face carries the whole story, so the exit line is left empty rather than
 * contradicting it (round-2 review, MUST-FIX 1; round-4 review, findings 2
 * and 3).
 */
const MOOV_BREAKDOWN_NOT_SELF_BOOKING: ReadonlySet<string> = new Set([
  // A reversing entry is an owner decision, never an automatic one.
  "breakdown_booked_then_reversed",
  // The split is re-decided and the write retried by the next run.
  "tenant_split_booking_failed",
  // The separate payments are NEW movements; this mixed payment never books.
  "breakdown_mixed_kinds",
  // The money has to be moved out of the trust wallet by hand, as a new
  // movement; this one can never book where it landed.
  "breakdown_income_in_trust_wallet",
  // The stored breakdown is frozen at the Worker's first validated read
  // (`storeTenantBreakdown`, ON CONFLICT DO NOTHING) and the gross is fixed,
  // so the amounts can never add up for THIS movement; the corrected amounts
  // come on a new payment (round-4 review, finding 2).
  "breakdown_sum_mismatch",
  // A deposit in a property wallet books nowhere. The deposit that books is
  // the money once it is moved into the trust wallet — a new movement, and
  // nothing here can move money (round-4 review, finding 3).
  "breakdown_deposit_not_in_trust_wallet",
  // Round 5 (should-fix 1): the fee row may also have FAILED, and a failed
  // fee row never settles — so the unconditional "books itself once fixed"
  // cannot be proved for this reason. The face carries the conditional truth
  // ("When the fee settles, the deposit books itself; if the fee failed, its
  // own card says so.") and the exit line is left empty.
  "fee_row_missing",
  // `property_not_owned` is NOT here, and deliberately: the Worker derives the
  // location on every read from the books' own config, so once the books own
  // that property the SAME movement books — it belongs beside
  // `property_not_configured` (round-4 review, finding 1).
]);

export const MOOV_BREAKDOWN_SELF_BOOKS_COPY =
  "This payment books itself on the next booking run once that is fixed. It cannot be approved to a single account.";
export const MOOV_BREAKDOWN_GENERIC_COPY =
  "This tenant payment could not be booked automatically.";
// One table, one home: the cause sentences live in `ui/card-presenter.ts`
// (`MOOV_BREAKDOWN_REASON_COPY`). This page reads them; it does not keep a
// second copy (rd r12 B1b-i). The face map below is the same table.

/**
 * Item 8 (Q-c): the ON-SCREEN copy for a breakdown reason. The words are the
 * shared table's own (rd r12 B1b-i: one home, `ui/card-presenter.ts`), so this
 * is a pass-through rather than a second wording; a face-only change would
 * land here, once.
 * Round 5: the fee_row_missing override is gone — the map sentence now carries
 * the conditional truth ("When the fee settles, the deposit books itself; if
 * the fee failed, its own card says so.") with no jargon, so face and details
 * are one sentence again.
 */
const MOOV_BREAKDOWN_ON_SCREEN_COPY: Readonly<Record<string, string>> = {
  ...MOOV_BREAKDOWN_REASON_COPY,
};

/**
 * TFT-1b, round-4 review (finding 4): the exit line under a closable fee-topup
 * card is per-reason data, because one shared sentence promising "The entries
 * book themselves…" was exactly the claim four reviews kept finding one layer
 * down — it rendered on reasons the pipeline writes nothing for, ever. Every
 * entry below is proved from the pipeline (`pipeline/moov_wallet.py`) and the
 * Worker (`ingest-worker/src/moov_trust_fee_topup.ts`):
 * - books itself — the blocking value is derived again on every read, the
 *   write re-runs on every booking run, or the missing side is re-read on
 *   every sync, so the SAME movement books once the named cause is fixed;
 * - never books — the stored breakdown or top-up is frozen at the first
 *   validated read (ON CONFLICT DO NOTHING) and wallet rows never change, so
 *   no later run can book this movement: a corrected top-up is a NEW payment,
 *   and Books cannot move money;
 * - no claim — the code cannot prove the movement ever books, so the line
 *   promises nothing.
 * A reason missing from the map gets the neutral line (no claim), never the
 * self-booking one. The close-with-note offer ends the line EXCEPT on the
 * decide reasons (round 5): their face's own next step names the close
 * control, so the line would say it twice on one card.
 */
const MOOV_TOPUP_EXIT_SELF =
  "Nothing here can be approved to an account. This payment books itself on the next booking run once that is fixed. If it was dealt with another way, close this card with a note: closing books nothing.";
const MOOV_TOPUP_EXIT_NEW_PAYMENT =
  "Nothing here can be approved to an account. A corrected top-up would be a new payment; this one never books.";
const MOOV_TOPUP_EXIT_NEUTRAL =
  "Nothing here can be approved to an account.";
const MOOV_TOPUP_EXIT_ARRIVAL =
  "Nothing here can be approved to an account. When the top-up arrives, it books itself and the fee is repaid. If it was dealt with another way, close this card with a note: closing books nothing.";
const MOOV_TOPUP_EXIT_TWIN =
  "Nothing here can be approved to an account. The earliest top-up for this deposit keeps the fee while it is live; if that one is refused, this top-up books on a later run. If it was dealt with another way, close this card with a note: closing books nothing.";
const MOOV_TOPUP_EXIT_RETRY =
  "Nothing here can be approved to an account. Books tries again on the next run. If it keeps failing, the account manager has to look at it. If it was dealt with another way, close this card with a note: closing books nothing.";
const MOOV_TOPUP_EXIT_COPY: Readonly<Record<string, string>> = {
  // Waits: the deposit or the other wallet's row has not been read yet
  // (`moov_wallet.py` 2315-2320); once it is, this same top-up books.
  fee_topup_deposit_unseen: MOOV_TOPUP_EXIT_SELF,
  fee_topup_counterpart_unseen: MOOV_TOPUP_EXIT_SELF,
  // The write re-runs every booking run (2288-2295, 2350-2352, 2416-2418), so
  // opening the account, listing the property or reopening the month lets the
  // same top-up book.
  fee_topup_account_not_in_chart: MOOV_TOPUP_EXIT_SELF,
  fee_topup_location_unknown: MOOV_TOPUP_EXIT_SELF,
  fee_topup_period_locked: MOOV_TOPUP_EXIT_SELF,
  // Round 6 (should-fix 1): a write that failed is retried every run, but a
  // PERSISTENT failure (moov_wallet.py 2291-2295 cards any exception) needs a
  // person, and "once that is fixed" named no cause a reader could fix. The
  // line names the retry and the person instead.
  fee_topup_booking_failed: MOOV_TOPUP_EXIT_RETRY,
  // The Worker re-verdicts a top-up on every read from the books' own config
  // (`trustFeeTopupView`): the trust map (383-384, 530) and the payer's hub
  // properties (386-387), so a books-side fix lets the same top-up book.
  topup_company_not_served: MOOV_TOPUP_EXIT_SELF,
  topup_property_not_owned: MOOV_TOPUP_EXIT_SELF,
  // The card sits on an already-booked deposit whose fee receivable is still
  // open (2505-2513): nothing about THIS movement books; the top-up, when it
  // arrives, books itself and the receivable is cleared (2419-2438).
  fee_topup_missing: MOOV_TOPUP_EXIT_ARRIVAL,
  // The money or the tags are wrong for good: a duplicate is money moved
  // twice (2344-2348, Worker V5), a closed or absent receivable can never be
  // cleared by this top-up (2405-2411, 2392-2394), a pre-switch deposit's fee
  // was expensed (Worker H1), and a wallet row's currency, direction, shape,
  // amount or transfer status never changes (Worker V1-V4).
  fee_topup_duplicate:
    "Nothing here can be approved to an account. A deposit's fee is repaid once, so this second top-up never books.",
  fee_topup_receivable_closed:
    "Nothing here can be approved to an account. The fee is no longer owed, so this top-up never books.",
  fee_topup_without_receivable:
    "Nothing here can be approved to an account. The deposit owes no fee, so this top-up never books.",
  topup_pre_switch:
    "Nothing here can be approved to an account. The deposit's fee was booked as an expense before the switch, so this top-up never books.",
  topup_has_fee:
    "Nothing here can be approved to an account. A top-up that was itself charged a processor fee never books; how to book that fee is not decided here.",
  // Round-2 review, MUST-FIX 1: this reason is NOT a "never books" one. R2b's
  // replay path books a top-up the books already expect, so the face states the
  // disagreement (FEE_TOPUP_AMOUNT_MISMATCH_COPY) and this line only says the
  // one thing certain here — nothing on the card approves it to an account.
  fee_topup_amount_mismatch: MOOV_TOPUP_EXIT_NEUTRAL,
  topup_amount_differs: MOOV_TOPUP_EXIT_NEW_PAYMENT,
  topup_not_usd: MOOV_TOPUP_EXIT_NEW_PAYMENT,
  topup_rows_unexpected: MOOV_TOPUP_EXIT_NEW_PAYMENT,
  topup_wrong_direction: MOOV_TOPUP_EXIT_NEW_PAYMENT,
  topup_not_trust_destination: MOOV_TOPUP_EXIT_NEW_PAYMENT,
  topup_deposit_not_a_deposit: MOOV_TOPUP_EXIT_NEW_PAYMENT,
  topup_fee_differs: MOOV_TOPUP_EXIT_NEW_PAYMENT,
  topup_lease_differs: MOOV_TOPUP_EXIT_NEW_PAYMENT,
  topup_property_differs: MOOV_TOPUP_EXIT_NEW_PAYMENT,
  topup_transfer_not_completed: MOOV_TOPUP_EXIT_NEW_PAYMENT,
  // No claim: an unreadable top-up's refusal is stored once, so the code
  // cannot prove the movement ever books; the reversed-deposit card's face
  // already names the write-off decision, and the line adds nothing.
  fee_topup_unreadable: MOOV_TOPUP_EXIT_NEUTRAL,
  fee_receivable_deposit_reversed: MOOV_TOPUP_EXIT_NEUTRAL,
  // Round 5 (MUST-FIX 1): the Worker's duplicate verdict counts an
  // earlier-READ twin that may only be AWAITING its other wallet (M2: the
  // verdict can flip), and it is re-derived on every read — so "already
  // repaid" was false twice. The earliest-read twin keeps the deposit while
  // it is live; if it dies, this same top-up books.
  topup_duplicate: MOOV_TOPUP_EXIT_TWIN,
};

/** The exit line for a closable fee-topup card, per reason (see the map). */
export function moovFeeTopupExitCopy(code: string): string {
  return MOOV_TOPUP_EXIT_COPY[code] ?? MOOV_TOPUP_EXIT_NEUTRAL;
}
export const MOOV_FEE_TOPUP_CLOSE_NOTE_LABEL = "Note (required: what was done)";
export const MOOV_FEE_TOPUP_CLOSE_COPY = "Close card";
/**
 * M3. The cards a person can close with a note: every fee top-up reason, and the deposit's
 * reversed-with-fee-owed reason. Keep it in step with `isClosableFeeTopupReason` in
 * `ingest-worker/src/moov_trust_fee_topup.ts` (the Worker refuses any other close).
 */
export function isMoovFeeTopupClosableReason(code: string): boolean {
  return (
    Object.hasOwn(MOOV_BREAKDOWN_REASON_COPY, code) &&
    (code.startsWith("fee_topup_") ||
      code.startsWith("topup_") ||
      code === "fee_receivable_deposit_reversed")
  );
}

/**
 * TFT-1b. A top-up whose tags failed the Worker's shape check is recorded as a refusal
 * (a `topup_*` reason code) and reaches the AM as a refused card, `breakdown_refused_<code>`,
 * which is approved to a category with a note like any refused payment. This is the extra
 * sentence for each code. Keep it in step with `TOPUP_PARSE_REFUSALS` in
 * `ingest-worker/src/moov_trust_fee_topup.ts`.
 */
const MOOV_REFUSED_TOPUP_CAUSE_COPY: Readonly<Record<string, string>> = {
  topup_keys_unexpected:
    "This was a fee top-up transfer, but its details had a key a top-up must not have, or lacked one it must have.",
  topup_has_labels:
    "This was a fee top-up transfer, but its details also carried tenant payment amounts, which a top-up never does.",
  topup_version_unknown:
    "This was a fee top-up transfer, in a format Books does not know yet.",
  topup_fee_not_digits: "This was a fee top-up transfer, but its fee was not a whole number of cents.",
  topup_fee_too_long: "This was a fee top-up transfer, but its fee was too large to be a fee.",
  topup_fee_none: "This was a fee top-up transfer, but its fee was zero.",
  topup_source_transfer_malformed:
    "This was a fee top-up transfer, but the deposit transfer it names is not a valid transfer id.",
  topup_source_payment_malformed:
    "This was a fee top-up transfer, but the payment it names is not a valid payment id.",
  topup_property_malformed:
    "This was a fee top-up transfer, but its property was missing or not in a usable form.",
  topup_lease_malformed:
    "This was a fee top-up transfer, but its lease number was missing or not in the tenant portal's format.",
};

/** The extra sentence for a refused fee top-up card, or null for any other refusal. */
export function moovRefusedTopupSentence(body: string | null): string | null {
  const code = (body ?? "").trim();
  if (!code.startsWith(`${MOOV_BREAKDOWN_REFUSED_PREFIX}_`)) return null;
  const cause = code.slice(MOOV_BREAKDOWN_REFUSED_PREFIX.length + 1);
  return Object.hasOwn(MOOV_REFUSED_TOPUP_CAUSE_COPY, cause)
    ? (MOOV_REFUSED_TOPUP_CAUSE_COPY[cause] ?? null)
    : null;
}

/**
 * TFT-1b. An approval to an account only the system writes (the trust fee receivable)
 * is carded again as `approval_account_system_only`. This card is approvable: the person
 * picks another account.
 */
export const MOOV_APPROVAL_SYSTEM_ONLY_COPY =
  "That account is reserved for the system and cannot be chosen by hand. Choose another account.";
export function moovApprovalFailureSentence(body: string | null): string | null {
  return (body ?? "").trim() === "approval_account_system_only"
    ? MOOV_APPROVAL_SYSTEM_ONLY_COPY
    : null;
}

/**
 * TR-2 (owner round 24). A tenant payment whose labels the Worker refused is not
 * a split: it is researched and booked by the AM to a category, with a note. Its
 * card reason starts with `breakdown_refused` (`pipeline/moov_wallet.py`,
 * `run_moov_movements`). Keep the prefix in step with that file.
 */
export const MOOV_BREAKDOWN_REFUSED_PREFIX = "breakdown_refused";
export const MOOV_REFUSED_BREAKDOWN_COPY =
  "The tenant portal's labels for this payment were refused, so it was not booked automatically. It needs research: choose the category and write a note about what you found. The note is required and is saved with the entry.";
export const MOOV_REFUSED_NOTE_LABEL = "Note (required: what you found)";

/**
 * A refused payment whose earlier approval could not book is re-carded by
 * `pipeline/moov_wallet.py` (`_card`) as `breakdown_refused_<cause>`, so it stays
 * a refused card: the note field is shown and there is no make-rule box, as the
 * Worker requires a note. The cause is one of these codes; keep it in step with
 * the approval branch of `run_moov_movements`.
 */
const MOOV_REFUSED_FAILURE_REASON_COPY: Readonly<Record<string, string>> = {
  location_not_chosen: "no location was chosen",
  account_not_in_chart: "the account chosen is not open in the books",
  period_locked: "the month this payment belongs to is closed",
  approval_booking_failed: "booking it failed",
  approval_account_system_only: "the account chosen is reserved for the system",
  // Round-2 review, MUST-FIX 2: the Worker refuses an approval that points a
  // payment at its own wallet account (`approval_account_is_wallet`), so the
  // re-carded spelling needs its sentence like every other cause.
  approval_account_is_wallet: "the account chosen is this payment's own wallet account",
};

/** The extra sentence for a re-carded refused payment, or null for a plain refusal. */
export function moovRefusedFailureSentence(body: string | null): string | null {
  const code = (body ?? "").trim();
  if (!code.startsWith(`${MOOV_BREAKDOWN_REFUSED_PREFIX}_`)) return null;
  const cause = code.slice(MOOV_BREAKDOWN_REFUSED_PREFIX.length + 1);
  if (!Object.hasOwn(MOOV_REFUSED_FAILURE_REASON_COPY, cause)) return null;
  return `The payment's labels were refused, and the approval could not book because ${MOOV_REFUSED_FAILURE_REASON_COPY[cause] ?? "of a problem"}. Research it and approve again with a note.`;
}

/** True for a Moov card whose payment's labels were refused (owner round 24). */
export function isMoovRefusedBreakdownQueueItem(item: QueueItem): boolean {
  if (!isMoovMovementQueueItem(item)) return false;
  return (item.body ?? "").trim().startsWith(MOOV_BREAKDOWN_REFUSED_PREFIX);
}

/**
 * True for a Moov card whose body is a tenant-breakdown reason code that cannot
 * be approved to one account. A `breakdown_refused*` card is the exception: the
 * Worker approves it (no stored split), so it renders the ordinary approve.
 */
export function isMoovBreakdownQueueItem(item: QueueItem): boolean {
  if (!isMoovMovementQueueItem(item)) return false;
  if (isMoovRefusedBreakdownQueueItem(item)) return false;
  const code = (item.body ?? "").trim();
  return code.startsWith("breakdown_") || Object.hasOwn(MOOV_BREAKDOWN_REASON_COPY, code);
}

export function moovBreakdownReasonSentence(body: string | null): string {
  const code = (body ?? "").trim();
  if (Object.hasOwn(MOOV_BREAKDOWN_ON_SCREEN_COPY, code)) {
    return MOOV_BREAKDOWN_ON_SCREEN_COPY[code] ?? MOOV_BREAKDOWN_GENERIC_COPY;
  }
  return MOOV_BREAKDOWN_GENERIC_COPY;
}

function renderMoovStageCard(opts: StageCardOpts): HTMLElement {
  const { item, client, chartLoad } = opts;
  // The card's account words take the render's ONE directory (rd13 L1).
  const companies = accountLabelContext(opts.directory);
  const code = (item.body ?? "").trim();
  const snapshot = chartLoad.snapshot;
  // Only "the chart is published and lists no location" means the books need
  // a location. An absent or failed chart keeps the card's own chooser, whose
  // copy is about the chart, not about the books.
  const noLocationForChoice =
    code === "location_not_chosen" && locationAvailability(chartLoad) === "empty";
  // Same question the bulk Book asks (bulkBookDecision), so single and bulk agree.
  const blocked = isMoovApproveWithheld(code) || noLocationForChoice;
  // A blocked card's next step is the blocked-action paragraph below (it also
  // carries the no-location case, which only this renderer knows). A
  // breakdown card has no blocked-action paragraph, so its face keeps the
  // one next step the presenter wrote.
  const card = renderBlockerStageCard({
    ...opts,
    omitFaceSuggest: blocked && !isMoovBreakdownQueueItem(item),
  });
  card.setAttribute("data-moov-card", "true");
  const moovFace = cardPresentation(item, {
    directory: opts.directory,
    role: opts.role === "account_manager" ? "account_manager" : "owner",
    accountWords: stageAccountLabels(opts).whenShared,
  });
  card.setAttribute("data-moov-blocked", blocked ? "true" : "false");

  const movementLine = (): HTMLElement => {
    // Y5 brief item 5: plain facts a person can read — where it came from, how
    // much, and the day. The raw descriptor is bank text and stays technical.
    const day = dateText(item.txnDate);
    return h("dl", { class: "facts", "data-role": "moov-movement" }, [
      fact("Source", "Wallet payment"),
      fact("Amount", moneyText(queueAmountDecimal(item), { fallback: "amount unavailable" })),
      ...(day === "" ? [] : [fact("Date", day)]),
    ]);
  };
  const putMovementInDetails = (): void => {
    const why = card.querySelector<HTMLElement>(".why-disclosure");
    if (why === null) {
      card.append(movementLine());
      return;
    }
    const technical = why.querySelector('[data-role="technical-details"]');
    why.insertBefore(movementLine(), technical);
  };
  const dismissAction = (): HTMLElement | null => {
    if (opts.onDismissCard === undefined) return null;
    // The Worker refuses an account manager closing a currently escalated card
    // (dismissMoovQueueItem: 403); only the owner may. Do not offer it.
    if (!roleMayDismissMoovCard(item, opts.role)) return null;
    const button = h(
      "button",
      {
        type: "button",
        class: "btn btn-ghost",
        "data-action": "dismiss-card",
        "data-queue-id": String(item.id),
        "aria-label": "Dismiss this card",
      },
      ["Dismiss"],
    );
    button.addEventListener("click", () => {
      opts.onDismissCard?.(item);
    });
    return button;
  };

  /**
   * Y6 brief item 3: the card has ONE action row, read left to right as
   * primary, Skip for now, Dismiss, Ask the owner. The blocker card builds
   * that row at the bottom of the body; this card adds its controls to it
   * instead of hanging a second row off the approve section.
   */
  const actionRow = (): HTMLElement | null => {
    for (const child of card.children) {
      if (child instanceof HTMLElement && child.classList.contains("actions")) {
        return child;
      }
    }
    return null;
  };
  const placeInActionRow = (args: {
    primary?: HTMLElement;
    dismiss?: HTMLElement | null;
  }): void => {
    const row = actionRow();
    if (row === null) return;
    if (args.primary !== undefined) row.insertBefore(args.primary, row.firstChild);
    const dismiss = args.dismiss ?? null;
    if (dismiss === null) return;
    const skip = row.querySelector<HTMLElement>('[data-action="later"]');
    if (skip === null) row.append(dismiss);
    else row.insertBefore(dismiss, skip.nextSibling);
  };

  if (isMoovBreakdownQueueItem(item)) {
    // No account chooser and no approve: the Worker would refuse it (409).
    const bodyEl = card.querySelector('[data-role="blocker-body"]');
    if (bodyEl instanceof HTMLElement) {
      // Y5 brief item 2: a closed month says its sentence once, on the face.
      // The breakdown copy would say the same thing a second time.
      if (isClosedMonthCard(item)) {
        bodyEl.textContent = "";
        bodyEl.hidden = true;
      } else {
        bodyEl.textContent = moovBreakdownReasonSentence(item.body ?? null);
      }
    }
    const exitEl = card.querySelector('[data-role="blocker-exit"]');
    if (exitEl instanceof HTMLElement) {
      // Round-2 review, MUST-FIX 1; round-4 review, findings 1-4. The exit
      // line is per-reason data: a reason says the payment books itself only
      // where the pipeline really books the same movement once the named
      // cause is fixed (the fee-topup lines carry their own proof in
      // `MOOV_TOPUP_EXIT_COPY`), and where the fix is a NEW payment or a
      // person's decision the face already says so and the line is left
      // empty rather than contradicting it.
      if (MOOV_BREAKDOWN_NOT_SELF_BOOKING.has(code)) {
        exitEl.textContent = "";
        exitEl.hidden = true;
      } else {
        exitEl.textContent = isMoovFeeTopupClosableReason(code)
          ? moovFeeTopupExitCopy(code)
          : MOOV_BREAKDOWN_SELF_BOOKS_COPY;
      }
    }
    card.setAttribute("data-moov-breakdown", "true");
    putMovementInDetails();
    const dismiss = dismissAction();
    if (isMoovFeeTopupClosableReason(code)) {
      // M3. Close with a note. The Worker books nothing for a close and refuses any other
      // action on these cards.
      const closeNote = h("textarea", {
        "data-role": "moov-close-note",
        "aria-label": MOOV_FEE_TOPUP_CLOSE_NOTE_LABEL,
        maxlength: "500",
        rows: "3",
      });
      const closeBtn = h(
        "button",
        {
          type: "button",
          class: "btn",
          "data-action": "moov-close",
          "aria-label": MOOV_FEE_TOPUP_CLOSE_COPY,
        },
        [MOOV_FEE_TOPUP_CLOSE_COPY],
      );
      (closeBtn).disabled = true;
      const closeStatus = h(
        "p",
        { class: "status", role: "status", "data-role": "moov-close-status" },
        [],
      );
      closeNote.addEventListener("input", () => {
        (closeBtn).disabled =
          (closeNote).value.trim().length === 0;
      });
      closeBtn.addEventListener("click", () => {
        void (async () => {
          clear(closeStatus);
          opts.onBusyChange?.(true);
          const outcome = await runWrite({
            key: cardWriteKey(card, ""),
            scope: card,
            gate: () => (closeNote.value.trim().length === 0 ? "Write a note first." : null),
            send: () =>
              client.resolveQueueItem(item.id, {
                action: "reject",
                note: closeNote.value.trim(),
              }),
            stillCurrent: () => card.isConnected,
            live: closeStatus,
          });
          if (outcome.kind === "landed") {
            lockCardControls(card);
            const text = "Closed. Nothing was booked.";
            card.classList.add("resolved");
            await opts.onResolved({
              kind: "reject",
              confirmation: h("p", {}, [text]),
              confirmationText: text,
            });
            return;
          }
          if (outcome.kind === "refused" && outcome.status === 0) {
            opts.onBusyChange?.(false);
            return;
          }
          if (outcome.error !== undefined && isAlreadyAnsweredError(outcome.error)) {
            lockCardControls(card);
            opts.onAlreadyAnswered?.();
            return;
          }
          const message = paintOutcomeFailure(closeStatus, outcome, opts.role);
          opts.announce(message);
          closeBtn.disabled = closeNote.value.trim().length === 0;
          opts.onResolveFailed(message);
        })();
      });
      card.append(
        h("section", { class: "moov-close", "data-role": "moov-close" }, [
          h("label", {}, [MOOV_FEE_TOPUP_CLOSE_NOTE_LABEL, " ", closeNote]),
          h("div", { class: "actions" }, [closeBtn]),
          closeStatus,
        ]),
      );
      placeInActionRow({ dismiss });
    } else {
      placeInActionRow({ dismiss });
    }
    return card;
  }

  // Owner round 24: a refused payment is approved like a researched card, with a
  // category and a note, and never offers a rule (its descriptor came from a
  // payment whose labels were wrong).
  const refused = isMoovRefusedBreakdownQueueItem(item);
  const needsLocation = snapshot.selectableLocations.length > 1;
  const pairable = moovPairableType(item.descriptor) !== null;
  if (blocked) {
    // A2: no account choice can fix a closed month, an unopened account, a
    // setting, or an unbookable shape. Replace the approve control with the
    // presenter's role-aware next step. The card still offers Later/Dismiss.
    const dismiss = dismissAction();
    // The next step belongs where a suggest normally sits: right under the
    // face, above Show details and the buttons (Y5 brief item 2).
    const nextStep = h("p", { class: "card-suggest", "data-role": "blocked-action" }, [
      noLocationForChoice ? advice("add_place", readerRole(opts.role)) : moovFace.suggest,
    ]);
    const faceWhy = card.querySelector("details.why-disclosure");
    if (faceWhy !== null) card.insertBefore(nextStep, faceWhy);
    else card.append(nextStep);
    putMovementInDetails();
    placeInActionRow({ dismiss });
    return card;
  }

  const accountChooser = mountChartChooser({
    kind: "account",
    selectable: snapshot.selectableAccounts,
    companies,
    className: "account-input",
    ariaLabel: "Account",
    placeholder: "Account",
    load: chartLoad,
    likely: pairable
      ? snapshot.selectableAccounts.filter((name) => isLikelyBankCounterAccount(name))
      : [],
    groupKey: moovFace.memoryKey,
  });
  const locationChooser = mountLocationControl({
    selectable: snapshot.selectableLocations,
    companies,
    className: "location-input",
    ariaLabel: "Location",
    placeholder: "Location",
    initialValue: "",
    load: chartLoad,
  });

  // Opt-in, never pre-selected (owner 2026-08-02).
  const makeRuleInput = h("input", {
    type: "checkbox",
    "data-role": "moov-make-rule",
    // A screen reader hears the same words the sighted reader sees on the row
    // (rd r13 review 1, item 4): this used to say "next identical movement".
    "aria-label": MAKE_RULE_CHECKBOX_COPY,
  });
  const moovAmountLabel = `Only ${formatAmount(queueAmountDecimal(item))}`;
  const amountRadio = h("input", {
    type: "radio",
    name: `moov-rule-scope-${String(item.id)}`,
    value: "amount",
    checked: "true",
    "data-role": "moov-rule-scope-amount",
    "aria-label": moovAmountLabel,
  });
  const broadRadio = h("input", {
    type: "radio",
    name: `moov-rule-scope-${String(item.id)}`,
    value: "vendor",
    "data-role": "moov-rule-scope-broad",
    "aria-label": MOOV_RULE_BROAD_COPY,
  });
  const broadConfirm = h("input", {
    type: "checkbox",
    "data-role": "moov-rule-broad-confirm",
    "aria-label": MOOV_RULE_BROAD_CONFIRM_COPY,
  });
  const scopeFields = h(
    "div",
    { class: "fields", "data-role": "moov-rule-scope", hidden: "true" },
    [
      h("label", { class: "rule-check-row" }, [amountRadio, ` ${moovAmountLabel}`]),
      h("label", { class: "rule-check-row" }, [broadRadio, ` ${MOOV_RULE_BROAD_COPY}`]),
      h("label", { "data-role": "moov-rule-broad-confirm-row", hidden: "true" }, [
        broadConfirm,
        ` ${MOOV_RULE_BROAD_CONFIRM_COPY}`,
      ]),
    ],
  );
  const broadRow = scopeFields.querySelector<HTMLElement>(
    '[data-role="moov-rule-broad-confirm-row"]',
  );

  const noteInput = h("textarea", {
    "data-role": "moov-refused-note",
    "aria-label": MOOV_REFUSED_NOTE_LABEL,
    maxlength: "500",
    rows: "3",
  });
  const approveHint = h(
    "p",
    { class: "approve-hint", "data-role": "approve-disabled-hint" },
    [],
  );
  const approveBtn = h(
    "button",
    {
      type: "button",
      class: "btn btn-primary",
      "data-action": "moov-approve",
      "aria-label": moovFace.primaryLabel,
    },
    [moovFace.primaryLabel],
  );
  const status = h(
    "p",
    { class: "status", role: "status", "data-role": "moov-approve-status" },
    [],
  );

  const chosenAccount = (): string | null => accountChooser.getValue();
  const chosenLocation = (): string | null => locationChooser.getValue();
  // The sentence beside the checkbox and the rule fields of the request come
  // from the same function over the same two controls (round eight A-M3).
  const currentMoovRule = (): { fields: { ruleScope?: QueueRuleScope }; preview: string } =>
    moovRuleRequest(item, makeRuleInput.checked, broadRadio.checked);
  const moovRulePreviewEl = h("span", { class: "rule-preview", "data-role": "rule-preview" }, [
    currentMoovRule().preview,
  ]);
  const refresh = (): void => {
    moovRulePreviewEl.textContent = currentMoovRule().preview;
    const rule = (makeRuleInput).checked;
    scopeFields.hidden = !rule;
    if (rule) scopeFields.removeAttribute("hidden");
    else scopeFields.setAttribute("hidden", "true");
    const broad = rule && (broadRadio).checked;
    if (broadRow) {
      broadRow.hidden = !broad;
      if (broad) broadRow.removeAttribute("hidden");
      else broadRow.setAttribute("hidden", "true");
    }
    const account = chosenAccount();
    const location = chosenLocation();
    const ready =
      account !== null &&
      (!needsLocation || location !== null) &&
      (!broad || (broadConfirm).checked) &&
      // Owner round 25: the note is required on a refused card (it goes on the ledger).
      (!refused || noteInput.value.trim().length > 0);
    (approveBtn).disabled = !ready;
    approveBtn.classList.toggle("btn-book-ready", ready);
    const label =
      account === null
        ? moovFace.primaryLabel
        : `Book to ${accountLabelAmong(account, snapshot.selectableAccounts, companies)}`;
    approveBtn.textContent = label;
    approveBtn.setAttribute("aria-label", label);
    clear(approveHint);
    if (!ready) {
      // A5: name the action that is actually missing, not always the account.
      const hint =
        account === null
          ? moovFace.disabledHint.length > 0
            ? moovFace.disabledHint
            : ACCOUNT_HINT
          : needsLocation && location === null
            ? "Pick a location."
            : broad && !(broadConfirm).checked
              ? MOOV_RULE_BROAD_CONFIRM_COPY
              : refused && noteInput.value.trim().length === 0
                ? WRITE_FINDING_HINT
                : "";
      if (hint.length > 0) approveHint.append(document.createTextNode(hint));
    }
  };
  for (const el of [
    accountChooser.filter,
    locationChooser.filter,
    makeRuleInput,
    amountRadio,
    broadRadio,
    broadConfirm,
    noteInput,
  ]) {
    el.addEventListener("input", refresh);
    el.addEventListener("change", refresh);
  }
  bindValueSync(accountChooser.filter, refresh);
  bindValueSync(locationChooser.filter, refresh);
  refresh();

  // R4-13: one busy state across the card's controls. Approve used to disable
  // only itself, so Dismiss, Escalate and Skip stayed live while the request
  // was in flight and a second press could answer the same card twice.
  const setMoovBusy = (busy: boolean): void => {
    for (const control of card.querySelectorAll<HTMLButtonElement>("button")) {
      control.disabled = busy;
    }
    opts.onBusyChange?.(busy);
    if (!busy) refresh();
  };

  approveBtn.addEventListener("click", () => {
    const account = chosenAccount();
    if (account === null) return;
    const location = chosenLocation();
    const rule = !refused && makeRuleInput.checked;
    const note = refused ? noteInput.value.trim() : "";
    clear(status);
    void (async () => {
      opts.onBusyChange?.(true);
      const outcome = await runWrite({
        key: cardWriteKey(card, ""),
        scope: card,
        gate: () => null,
        send: () =>
          client.resolveQueueItem(item.id, {
            action: "approve",
            account,
            ...(location !== null ? { location } : {}),
            ...(note.length > 0 ? { note } : {}),
            makeRule: rule,
            ...moovRuleRequest(item, rule, broadRadio.checked).fields,
          }),
        stillCurrent: () => card.isConnected,
      });
      if (outcome.kind === "landed") {
        lockCardControls(card);
        // M2: the page callback runs after `runWrite` returns, so a re-render
        // under the write cannot leave the card stranded.
        // The line names the account only when its words are shared with
        // another account of the company, so the answer says which one was
        // booked (F2); a unique account is already named on the button.
        const shown = stageAccountLabels(opts).whenShared(item.company, account);
        const named = shown === null ? "" : ` to ${shown}`;
        const text = rule
          ? `Saved${named} with a rule. The books pick it up at the next booking run.`
          : `Saved${named}. The books pick it up at the next booking run.`;
        card.classList.add("resolved");
        await opts.onResolved({
          kind: "approve",
          confirmation: h("p", {}, [text]),
          confirmationText: text,
        });
        return;
      }
      if (outcome.error !== undefined && isAlreadyAnsweredError(outcome.error)) {
        // R4-09: someone else answered; re-read the list so no live Approve
        // stays on the card.
        lockCardControls(card);
        opts.onAlreadyAnswered?.();
        return;
      }
      const message = paintOutcomeFailure(status, outcome, opts.role);
      opts.announce(message);
      setMoovBusy(false);
      opts.onResolveFailed(message);
    })();
  });

  const dismissSectionAction = dismissAction();
  const section = h("section", { class: "moov-approve", "data-role": "moov-approve" }, [
    ...(refused
      ? [h("p", { "data-role": "moov-refused-explainer" }, [MOOV_REFUSED_BREAKDOWN_COPY])]
      : []),
    ...(refused && moovRefusedTopupSentence(item.body ?? null) !== null
      ? [
          h("p", { "data-role": "moov-refused-topup" }, [
            moovRefusedTopupSentence(item.body ?? null) ?? "",
          ]),
        ]
      : []),
    ...(!refused && moovApprovalFailureSentence(item.body ?? null) !== null
      ? [
          h("p", { "data-role": "moov-approval-failure" }, [
            moovApprovalFailureSentence(item.body ?? null) ?? "",
          ]),
        ]
      : []),
    ...(refused && moovRefusedFailureSentence(item.body ?? null) !== null
      ? [
          h("p", { "data-role": "moov-refused-failure" }, [
            moovRefusedFailureSentence(item.body ?? null) ?? "",
          ]),
        ]
      : []),
    h("p", { class: "muted", "data-role": "moov-timing" }, [MOOV_APPROVE_TIMING_COPY]),
    // The hint belongs under the picker it is about, not in the action row
    // beside the disabled button (Y6 brief item 3).
    h("div", { class: "fields" }, [
      accountChooser.root,
      locationChooser.root,
      approveHint,
    ]),
    ...(refused
      ? [h("label", {}, [MOOV_REFUSED_NOTE_LABEL, " ", noteInput])]
      : [
          moreOptionsPanel([
            // The sentence that says what the rule would cover sits WITH its
            // checkbox: one control, one label (Y6 brief item 3).
            h("label", { class: "rule-check-row rule-check-with-preview" }, [
              makeRuleInput,
              h("span", { class: "rule-check-text" }, [
                h("span", { class: "rule-check-title" }, [MAKE_RULE_CHECKBOX_COPY]),
                moovRulePreviewEl,
              ]),
            ]),
            scopeFields,
          ]),
        ]),
    status,
  ]);
  const face = card.querySelector('[data-role="card-face"]');
  if (face !== null) face.after(section);
  else card.append(section);
  putMovementInDetails();
  placeInActionRow({ primary: approveBtn, dismiss: dismissSectionAction });
  return card;
}

/**
 * The "Technical" subject, in plain words.
 *
 * A wallet card's subject is Worker wording plus the movement's raw id
 * ("Moov wallet unbooked — <uuid>"). "Moov" is internal vocabulary, so the
 * subject says what the card is instead. The raw id is NOT part of the subject
 * any more; an account manager still gets it, labelled, as its own Technical
 * fact below — they are the one who quotes it to the Worker (R4-14, round-2
 * review SHOULD-FIX 6; the Y5 invariant that an account manager keeps ids).
 */
function blockerSubjectWords(item: QueueItem): string {
  const subject = item.subject?.trim() ?? "";
  if (subject.startsWith(MOOV_CARD_SUBJECT_PREFIX)) return "Wallet payment not booked";
  return amVisibleDisplayText(subject.length > 0 ? subject : "Blocker");
}

/** The raw wallet movement id a card's subject carries, or null. */
function blockerMovementId(item: QueueItem): string | null {
  const subject = item.subject?.trim() ?? "";
  if (!subject.startsWith(MOOV_CARD_SUBJECT_PREFIX)) return null;
  const id = subject.slice(MOOV_CARD_SUBJECT_PREFIX.length).trim();
  return id.length > 0 ? amVisibleDisplayText(id) : null;
}

function renderBlockerStageCard(opts: StageCardOpts): HTMLElement {
  const { item, client } = opts;
  const role: StaffRole = opts.role ?? "account_manager";
  // C7 (narrowed): the owner is never offered a plain hand-off to himself,
  // but "Keep bank line" (hard-match-refuse) is a disposition the Worker
  // accepts from the owner, and Confirm match / Not this transaction are
  // attach:/reject: dispositions, not hand-offs. Hide ONLY the plain
  // "Escalate to owner" control.
  //
  // Round-3 review, item 3 (orchestrator's decision): the two mapping blockers
  // are a bank line with no account on it. Neither person can fix them from the
  // console and neither can the hub, so neither role is offered a hand-off
  // ("Ask the owner") on them. The card states the fact and stops.
  const showEscalate =
    !isMappingBlockerCard(item) &&
    (role !== "owner" || blockerExitKind(item) === "hard-match-refuse");
  const card = h("article", {
    class: "queue-card blocker-card",
    "data-queue-id": String(item.id),
    "data-company": item.company,
    "data-reason": item.reason,
    "data-role": "blocker-card",
  });

  const presented = cardFaceBlock(opts, "blocker-subject");
  const subjectText = blockerSubjectWords(item);


  const bodyText = item.body?.trim() || "";
  const bodyPara = h(
    "p",
    { class: "blocker-body", "data-role": "blocker-body" },
    [
      amVisibleMultilineDisplayText(
        roleAwareEscalateClause(bodyText, role) || "—",
      ),
    ],
  );

  const exitSentence = blockerExitSentence(item, role);
  const exitLine = h(
    "p",
    { class: "blocker-exit muted", "data-role": "blocker-exit" },
    [exitSentence],
  );
  // Decision 4: the raw clearer name is not on the face for either role; it is
  // a fact under "Show details" so support can still trace the clear.
  const clearerCode = blockerClearerCode(item);

  // Y5 brief item 5: the owner is shown the plain facts and the exit sentence.
  // The subject (which carries ids), the reason code and the raw body live
  // behind "Technical", and only for an account manager. A wallet movement's
  // id keeps its own labelled fact — plain words replace the Worker's subject,
  // not the identifier the account manager quotes (round-2 review SHOULD-FIX 6).
  const movementId = blockerMovementId(item);
  const technical = technicalDisclosure(
    role,
    [
      fact("Subject", subjectText),
      fact("Reason", item.reason),
      ...(movementId === null ? [] : [fact("Movement", movementId)]),
    ],
    bodyText.length > 0 ? [bodyPara] : [],
  );

  const why = h("details", { class: "why-disclosure" }, [
    showDetailsSummary(),
    exitLine,
    h("dl", { class: "facts" }, [
      fact("Waiting", formatAge(item.createdAt)),
      ...(item.dueDate ? [fact("Due", safeText(item.dueDate))] : []),
      ...(clearerCode === null ? [] : [fact("Clears", clearerCode)]),
    ]),
    ...(technical === null ? [] : [technical]),
  ]);

  const status = h(
    "p",
    { class: "status", role: "status", "data-role": "status" },
    [],
  );

  const escalateNoteInput = h("input", {
    type: "text",
    class: "note-input",
    placeholder: blockerEscalatePlaceholder(item),
    maxlength: "500",
    "aria-label": "Escalation note",
    "data-role": "escalate-note",
  });
  // R16O-6: placeholder text vanishes on the first keystroke — prefill the
  // required keep-bank prefix so the instruction survives typing, and so
  // success copy ("Keep-bank recorded") cannot lie about a missing prefix.
  // PREFIX is lowercase ("keep-bank:"); toLowerCase().startsWith below
  // accepts the prefilled value without the AM having to edit it.
  if (blockerExitKind(item) === "hard-match-refuse") {
    escalateNoteInput.value = `${HARD_MATCH_REFUSE_KEEP_BANK_ESCALATION_PREFIX} `;
  }
  const escalateBtnLabel = blockerEscalateButtonLabel(item);
  const escalateConfirmLabel = blockerEscalateConfirmLabel(item);
  const escalateConfirmBtn = h(
    "button",
    {
      type: "button",
      class: "btn btn-primary",
      "data-action": "escalate-confirm",
      "aria-label": escalateConfirmLabel,
      hidden: "true",
    },
    [escalateConfirmLabel],
  );
  const escalateCancelBtn = h(
    "button",
    {
      type: "button",
      class: "btn btn-ghost",
      "data-action": "escalate-cancel",
      "aria-label": "Cancel escalation",
      hidden: "true",
    },
    ["Cancel"],
  );
  const escalateBtn = h(
    "button",
    {
      type: "button",
      class: "btn btn-ghost",
      "data-action": "escalate",
      "aria-label": escalateBtnLabel,
    },
    [escalateBtnLabel],
  );
  const escalateFields = h(
    "div",
    {
      class: "fields",
      "data-role": "escalate-fields",
      hidden: "true",
    },
    [
      h("label", {}, ["Escalation note", escalateNoteInput]),
      h("div", { class: "actions" }, [escalateConfirmBtn, escalateCancelBtn]),
    ],
  );

  const returnToAmBtn = h(
    "button",
    {
      type: "button",
      class: "btn btn-ghost",
      "data-action": "return-to-am",
      "aria-label": "Return to account manager",
      hidden: "true",
    },
    ["Return to account manager"],
  );

  const laterBtn = h(
    "button",
    {
      type: "button",
      class: "btn btn-ghost",
      "data-action": "later",
      "aria-label": SKIP_FOR_NOW_LABEL,
    },
    [SKIP_FOR_NOW_LABEL],
  );
  if (!opts.canLater) {
    laterBtn.hidden = true;
    laterBtn.setAttribute("hidden", "true");
  }

  const isMatchReview = blockerExitKind(item) === "receipt-match-review";
  const confirmMatchBtn = h(
    "button",
    {
      type: "button",
      class: "btn btn-primary",
      "data-action": "confirm-match",
      "aria-label": "Confirm match",
    },
    ["Confirm match"],
  );
  const rejectMatchBtn = h(
    "button",
    {
      type: "button",
      class: "btn btn-ghost",
      "data-action": "reject-match",
      "aria-label": "Not this transaction",
    },
    ["Not this transaction"],
  );
  if (!isMatchReview) {
    confirmMatchBtn.hidden = true;
    confirmMatchBtn.setAttribute("hidden", "true");
    rejectMatchBtn.hidden = true;
    rejectMatchBtn.setAttribute("hidden", "true");
  }

  const escalatedBanner = isWithOwner(item)
    ? h(
        "p",
        {
          class: "escalation-banner",
          "data-role": "escalation-banner",
        },
        [
          `Escalated by ${amVisibleDisplayText(safeText(item.escalatedBy, "staff"))}: ${amVisibleDisplayText(safeText(item.escalationNote, ""))}`,
        ],
      )
    : null;

  if (isWithOwner(item) || !showEscalate) {
    escalateBtn.hidden = true;
    escalateBtn.setAttribute("hidden", "true");
  }
  if (isWithOwner(item)) {
    confirmMatchBtn.hidden = true;
    confirmMatchBtn.setAttribute("hidden", "true");
    rejectMatchBtn.hidden = true;
    rejectMatchBtn.setAttribute("hidden", "true");
    if (opts.role === "owner") {
      returnToAmBtn.hidden = false;
      returnToAmBtn.removeAttribute("hidden");
    }
  }

  const correctionHost = h("div", {
    class: "receipt-match-correction",
    "data-role": "receipt-match-correction",
  });
  if (!isMatchReview || isWithOwner(item)) {
    correctionHost.hidden = true;
    correctionHost.setAttribute("hidden", "true");
  }

  const setCorrectionVisible = (visible: boolean): void => {
    correctionHost.hidden = !visible;
    if (visible) {
      correctionHost.removeAttribute("hidden");
    } else {
      correctionHost.setAttribute("hidden", "true");
    }
  };

  const paintCorrectionPage = (page: ReceiptMatchCorrectionPage): void => {
    clear(correctionHost);
    const cardLast4 = page.receipt.cardLast4;
    correctionHost.append(
      h("p", { "data-role": "correction-lead" }, [
        "Pick the right transaction. Confirm match keeps the proposal; Not this transaction stays available if none of these is it.",
      ]),
      h("p", { class: "muted", "data-role": "correction-receipt-facts" }, [
        // R4-23: a date a person reads and money in dollars, never a raw ISO
        // date and a bare decimal.
        amVisibleDisplayText(
          `Receipt ${dateText(page.receipt.receiptDate)} · ${formatOutPositiveMoney(page.receipt.amountDecimal)} · ${page.receipt.vendor}` +
            (cardLast4 ? ` · card ${cardLast4}` : " · no card last-4"),
        ),
      ]),
    );
    if (page.candidates.length === 0) {
      correctionHost.append(
        h("p", { "data-role": "correction-empty" }, [
          cardLast4 === null
            ? "This receipt has no card last-4, so there is no bounded candidate list. If the right transaction is not the proposal, reject it — the transaction returns to the missing-receipts shelf. The receipt stays available for a later unique match."
            : "No posted transactions in this window share this receipt's card. If the right transaction is not here, reject the proposal — the transaction returns to the missing-receipts shelf. The receipt stays available for a later unique match.",
        ]),
      );
      return;
    }
    const ul = h("ul", { "data-role": "correction-candidates" });
    for (const candidate of page.candidates) {
      const isProposed =
        candidate.plaidTransactionId === page.proposedPlaidTransactionId;
      const li = h("li", {
        "data-role": "correction-candidate",
        "data-proposed": isProposed ? "true" : "false",
      });
      const rawName = amVisibleDisplayText(candidate.name ?? "");
      const merchant = amVisibleDisplayText(candidate.merchantName ?? "");
      li.append(
        h("div", { "data-role": "correction-candidate-name" }, [
          // R4-23: the same display forms the rest of the console uses, and one
          // sign: the bank feed's line and the hub's receipt read alike
          // (rd L2 review round 1).
          amVisibleDisplayText(
            `${dateText(candidate.txnDate)} · ${formatOutPositiveMoney(candidate.amountDecimal)}` +
              (candidate.accountLast4 ? ` · card ${candidate.accountLast4}` : "") +
              (rawName ? ` · ${rawName}` : ""),
          ),
        ]),
      );
      if (merchant.length > 0 && merchant !== rawName) {
        li.append(h("div", { class: "muted" }, [merchant]));
      }
      li.append(
        h("div", { class: "muted" }, [
          amVisibleDisplayText(
            `Receipt was ${dateText(page.receipt.receiptDate)} · ${formatOutPositiveMoney(page.receipt.amountDecimal)} · ${page.receipt.vendor}`,
          ),
        ]),
      );
      if (isProposed) {
        li.append(
          h("p", { "data-role": "correction-proposed-label" }, ["Proposed match"]),
        );
      } else if (candidate.alreadyHasReceipt) {
        li.append(
          h("p", { class: "banner banner-warn" }, [
            "A receipt already exists for this transaction",
          ]),
        );
      } else {
        li.append(
          h(
            "button",
            {
              type: "button",
              class: "btn btn-primary",
              "data-action": "correct-match",
              "data-plaid-transaction-id": candidate.plaidTransactionId,
              "aria-label": "Use this transaction",
            },
            ["Use this transaction"],
          ),
        );
      }
      ul.append(li);
    }
    correctionHost.append(ul);
    correctionHost.append(
      h("p", { class: "muted" }, [
        "If none of these is the match, Not this transaction returns the card transaction to the missing-receipts shelf.",
      ]),
    );
  };

  const loadCorrection = (): void => {
    if (!isMatchReview || isWithOwner(item)) {
      return;
    }
    setCorrectionVisible(true);
    clear(correctionHost);
    correctionHost.append(
      h("p", { class: "muted", "data-role": "correction-loading" }, [
        "Loading candidate transactions…",
      ]),
    );
    void (async () => {
      try {
        const page = await client.getReceiptMatchCorrection(item.company, item.id);
        paintCorrectionPage(page);
      } catch {
        clear(correctionHost);
        correctionHost.append(
          h("p", { class: "muted", "data-role": "correction-load-error" }, [
            "Couldn't load candidate transactions. Confirm or reject still work.",
          ]),
        );
      }
    })();
  };

  if (isMatchReview && !isWithOwner(item)) {
    loadCorrection();
  }

  let escalateOpen = false;
  let sendToPractice: ReturnType<typeof sendToPracticeControls> | null = null;
  const setEscalateOpen = (open: boolean): void => {
    escalateOpen = open;
    escalateFields.hidden = !open;
    if (open) {
      sendToPractice?.setOpen(false);
      sendToPractice?.setButtonVisible(false);
      escalateFields.removeAttribute("hidden");
      escalateConfirmBtn.hidden = false;
      escalateCancelBtn.hidden = false;
      escalateConfirmBtn.removeAttribute("hidden");
      escalateCancelBtn.removeAttribute("hidden");
      escalateBtn.hidden = true;
      escalateBtn.setAttribute("hidden", "true");
      laterBtn.hidden = true;
      laterBtn.setAttribute("hidden", "true");
      confirmMatchBtn.hidden = true;
      confirmMatchBtn.setAttribute("hidden", "true");
      rejectMatchBtn.hidden = true;
      rejectMatchBtn.setAttribute("hidden", "true");
      if (isMatchReview) {
        setCorrectionVisible(false);
      }
    } else {
      escalateFields.setAttribute("hidden", "true");
      escalateConfirmBtn.hidden = true;
      escalateCancelBtn.hidden = true;
      escalateConfirmBtn.setAttribute("hidden", "true");
      escalateCancelBtn.setAttribute("hidden", "true");
      if (!isWithOwner(item)) {
        if (showEscalate) {
          escalateBtn.hidden = false;
          escalateBtn.removeAttribute("hidden");
        }
        if (isMatchReview) {
          confirmMatchBtn.hidden = false;
          confirmMatchBtn.removeAttribute("hidden");
          rejectMatchBtn.hidden = false;
          rejectMatchBtn.removeAttribute("hidden");
          setCorrectionVisible(true);
        }
      }
      sendToPractice?.setButtonVisible(true);
      if (opts.canLater) {
        laterBtn.hidden = false;
        laterBtn.removeAttribute("hidden");
      }
    }
  };

  if (canSendQueueItemToPractice(item, opts.hubLinkedSlugs)) {
    sendToPractice = sendToPracticeControls({
      item,
      client,
      card,
      status,
      ...(opts.role === undefined ? {} : { role: opts.role }),
      announce: opts.announce,
      onEscalated: opts.onEscalated,
      onResolveFailed: opts.onResolveFailed,
      onBusyChange: (busy: boolean) => opts.onBusyChange?.(busy),
      ...(opts.onAlreadyAnswered === undefined
        ? {}
        : { onAlreadyAnswered: opts.onAlreadyAnswered }),
      onOpen: () => {
        setEscalateOpen(false);
        laterBtn.hidden = true;
        laterBtn.setAttribute("hidden", "true");
      },
      onClose: () => {
        if (opts.canLater && !escalateOpen) {
          laterBtn.hidden = false;
          laterBtn.removeAttribute("hidden");
        }
      },
    });
  }

  const escrowDateInput = h("input", {
    type: "date",
    class: "note-input",
    "aria-label": "Escrow disbursement date",
    "data-role": "escrow-disbursement-date",
    max: localTodayIsoDate(),
  });
  const escrowAmountInput = h("input", {
    type: "text",
    class: "note-input",
    inputmode: "decimal",
    placeholder: "Amount (e.g. 200.00)",
    "aria-label": "Escrow disbursement amount",
    "data-role": "escrow-disbursement-amount",
  });
  const escrowKindSelect = h("select", {
    class: "note-input",
    "aria-label": "Escrow disbursement kind",
    "data-role": "escrow-disbursement-kind",
  });
  const kindBlank = h("option", { value: "" }, ["Kind"]);
  escrowKindSelect.append(kindBlank);
  for (const kind of ESCROW_DISBURSEMENT_KINDS) {
    const label =
      kind === "mortgage_insurance"
        ? "mortgage insurance"
        : kind;
    const opt = h("option", { value: kind }, [label]);
    if (item.escrowDisbursementKind === kind) {
      opt.setAttribute("selected", "true");
    }
    escrowKindSelect.append(opt);
  }
  if (item.escrowDisbursementDate) {
    escrowDateInput.value = item.escrowDisbursementDate;
  }
  if (item.escrowDisbursementAmount) {
    escrowAmountInput.value = item.escrowDisbursementAmount;
  }
  const recordEscrowBtn = h(
    "button",
    {
      type: "button",
      class: "btn btn-primary",
      "data-action": "record-escrow-disbursement",
      "aria-label": "Record escrow disbursement",
    },
    ["Record disbursement"],
  );
  const escrowFields =
    item.reason === "escrow-balance"
      ? h(
          "div",
          {
            class: "fields",
            "data-role": "escrow-disbursement-fields",
          },
          [
            h("label", {}, ["Event date", escrowDateInput]),
            h("label", {}, ["Amount", escrowAmountInput]),
            h("label", {}, ["Kind", escrowKindSelect]),
            h("div", { class: "actions" }, [recordEscrowBtn]),
          ],
        )
      : null;
  if (escrowFields === null) {
    recordEscrowBtn.hidden = true;
  }

  const setBusy = (busy: boolean): void => {
    opts.onBusyChange?.(busy);
    laterBtn.disabled = busy;
    escalateBtn.disabled = busy;
    escalateConfirmBtn.disabled = busy;
    escalateCancelBtn.disabled = busy;
    returnToAmBtn.disabled = busy;
    recordEscrowBtn.disabled = busy;
    confirmMatchBtn.disabled = busy;
    rejectMatchBtn.disabled = busy;
    correctionHost
      .querySelectorAll<HTMLButtonElement>('[data-action="correct-match"]')
      .forEach((btn) => {
        btn.disabled = busy;
      });
    sendToPractice?.setBusy(busy);
  };

  const postMatchReviewNote = (
    note: string,
    confirmationText: string,
  ): void => {
    void (async () => {
      clear(status);
      opts.onBusyChange?.(true);
      const outcome = await runWrite({
        key: cardWriteKey(card, ""),
        scope: card,
        gate: () => null,
        send: async () => {
          card.classList.add("exiting");
          await Promise.all([
            client.escalateQueueItem(item.company, item.id, { note }),
            sleep(STAGE_MS),
          ]);
        },
        stillCurrent: () => card.isConnected,
      });
      if (outcome.kind === "landed") {
        lockCardControls(card);
        card.classList.add("resolved");
        opts.onEscalated({ confirmationText });
        return;
      }
      card.classList.remove("exiting");
      if (outcome.error !== undefined && isAlreadyAnsweredError(outcome.error)) {
        lockCardControls(card);
        opts.onAlreadyAnswered?.();
        return;
      }
      const message = paintOutcomeFailure(status, outcome, opts.role);
      opts.announce(message);
      setBusy(false);
      opts.onResolveFailed(message);
    })();
  };

  laterBtn.addEventListener("click", () => {
    opts.onLater();
  });

  confirmMatchBtn.addEventListener("click", () => {
    postMatchReviewNote(
      `${RECEIPT_MATCH_REVIEW_ATTACH_PREFIX} AM confirmed the proposed match`,
      "Match confirmed.",
    );
  });
  rejectMatchBtn.addEventListener("click", () => {
    postMatchReviewNote(
      `${RECEIPT_MATCH_REVIEW_REJECT_PREFIX} not this transaction`,
      "Match rejected — transaction returns to the missing-receipt shelf.",
    );
  });

  correctionHost.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const btn = target.closest<HTMLButtonElement>(
      '[data-action="correct-match"]',
    );
    if (btn === null || btn.disabled) return;
    const plaidTransactionId = btn.getAttribute("data-plaid-transaction-id");
    if (plaidTransactionId === null || plaidTransactionId.length === 0) return;
    void (async () => {
      clear(status);
      const outcome = await runWrite({
        key: cardWriteKey(card, `correct-receipt-match-${plaidTransactionId}`),
        scope: card,
        gate: () => null,
        send: async () => {
          card.classList.add("exiting");
          await Promise.all([
            client.correctReceiptMatch(item.company, item.id, {
              plaidTransactionId,
            }),
            sleep(STAGE_MS),
          ]);
        },
        stillCurrent: () => card.isConnected,
      });
      if (outcome.kind === "landed") {
        lockCardControls(card);
        card.classList.add("resolved");
        opts.onEscalated({ confirmationText: "Match corrected." });
        return;
      }
      card.classList.remove("exiting");
      if (outcome.error !== undefined && isAlreadyAnsweredError(outcome.error)) {
        lockCardControls(card);
        opts.onAlreadyAnswered?.();
        return;
      }
      const message = paintOutcomeFailure(status, outcome, opts.role);
      opts.announce(message);
      setBusy(false);
      opts.onResolveFailed(message);
    })();
  });

  escalateBtn.addEventListener("click", () => {
    setEscalateOpen(true);
    escalateNoteInput.focus();
  });
  escalateCancelBtn.addEventListener("click", () => {
    setEscalateOpen(false);
  });
  escalateConfirmBtn.addEventListener("click", () => {
    void (async () => {
      opts.onBusyChange?.(true);
      const outcome = await runWrite({
        key: cardWriteKey(card, ""),
        scope: card,
        gate: () => {
          const note = escalateNoteInput.value.trim();
          if (!note) return "Escalation note is required.";
          // R4-05: the Worker executes a note starting attach: / reject: as the
          // account manager's disposition. A free note must never resolve the card.
          const noteRefusal = receiptMatchNoteRefusal(item, note);
          if (noteRefusal !== null) return noteRefusal;
          // R16O-6: enforce keep-bank prefix client-side for hard-match-refuse
          // (Worker enforces too). Placeholder-only was theatre.
          if (
            blockerExitKind(item) === "hard-match-refuse" &&
            !note
              .toLowerCase()
              .startsWith(HARD_MATCH_REFUSE_KEEP_BANK_ESCALATION_PREFIX)
          ) {
            return `Note must start with "${HARD_MATCH_REFUSE_KEEP_BANK_ESCALATION_PREFIX}"`;
          }
          return null;
        },
        send: async () => {
          clear(status);
          card.classList.add("exiting");
          await Promise.all([
            client.escalateQueueItem(item.company, item.id, {
              note: escalateNoteInput.value.trim(),
            }),
            sleep(STAGE_MS),
          ]);
        },
        stillCurrent: () => card.isConnected,
        live: status,
      });
      if (outcome.kind === "landed") {
        lockCardControls(card);
        card.classList.add("resolved");
        opts.onEscalated({
          confirmationText: blockerEscalateSuccessText(item),
        });
        return;
      }
      if (outcome.kind === "refused" && outcome.status === 0) {
        // M1: the gate refused, so nothing is in flight.
        opts.onBusyChange?.(false);
        setBusy(false);
        return;
      }
      card.classList.remove("exiting");
      if (outcome.error !== undefined && isAlreadyAnsweredError(outcome.error)) {
        lockCardControls(card);
        opts.onAlreadyAnswered?.();
        return;
      }
      const message = paintOutcomeFailure(status, outcome, opts.role);
      opts.announce(message);
      setBusy(false);
      opts.onResolveFailed(message);
    })();
  });

  returnToAmBtn.addEventListener("click", () => {
    void (async () => {
      setBusy(true);
      clear(status);
      const outcome = await returnCardToAm({
        item,
        client,
        scope: card,
        stillCurrent: () => card.isConnected,
      });
      if (outcome.kind !== "landed") {
        if (outcome.error !== undefined && isAlreadyAnsweredError(outcome.error)) {
          lockCardControls(card);
          opts.onAlreadyAnswered?.();
          return;
        }
        const message = paintOutcomeFailure(status, outcome, opts.role);
        opts.announce(message);
        setBusy(false);
        return;
      }
      clear(status);
      status.append(document.createTextNode("Returned to account manager."));
      returnToAmBtn.hidden = true;
      returnToAmBtn.setAttribute("hidden", "true");
      if (escalatedBanner) escalatedBanner.remove();
      if (showEscalate) {
        escalateBtn.hidden = false;
        escalateBtn.removeAttribute("hidden");
      }
      if (isMatchReview) {
        confirmMatchBtn.hidden = false;
        confirmMatchBtn.removeAttribute("hidden");
        rejectMatchBtn.hidden = false;
        rejectMatchBtn.removeAttribute("hidden");
        loadCorrection();
      }
      setBusy(false);
    })().catch(() => undefined);
  });

  if (escrowFields !== null) {
    recordEscrowBtn.addEventListener("click", () => {
      void (async () => {
        const outcome = await runWrite({
          key: cardWriteKey(card, "escrow-disbursement"),
          scope: card,
          gate: () =>
            escrowDisbursementEntryError({
              date: escrowDateInput.value.trim(),
              amount: escrowAmountInput.value.trim(),
              kind: escrowKindSelect.value.trim(),
            }),
          send: () => {
            clear(status);
            return client.resolveQueueItem(item.id, {
              action: "approve",
              makeRule: false,
              escrowDisbursementDate: escrowDateInput.value.trim(),
              escrowDisbursementAmount: escrowAmountInput.value.trim(),
              escrowDisbursementKind: escrowKindSelect.value.trim() as
                | "taxes"
                | "insurance"
                | "mortgage_insurance",
            });
          },
          afterLanded: (stored) => {
            item.status = stored.status;
            if (stored.escrowDisbursementDate !== undefined) {
              item.escrowDisbursementDate = stored.escrowDisbursementDate;
            }
            if (stored.escrowDisbursementAmount !== undefined) {
              item.escrowDisbursementAmount = stored.escrowDisbursementAmount;
            }
            if (stored.escrowDisbursementKind !== undefined) {
              item.escrowDisbursementKind = stored.escrowDisbursementKind;
            }
            const recorded = "Recorded. This card stays until the books agree.";
            clear(status);
            status.append(document.createTextNode(recorded));
            opts.announce(recorded);
          },
          stillCurrent: () => card.isConnected,
          live: status,
        });
        if (outcome.kind === "landed") {
          setBusy(false);
          return;
        }
        if (outcome.kind === "refused" && outcome.status === 0) return;
        if (outcome.error !== undefined && isAlreadyAnsweredError(outcome.error)) {
          lockCardControls(card);
          opts.onAlreadyAnswered?.();
          return;
        }
        const message = paintOutcomeFailure(status, outcome, opts.role);
        opts.announce(message);
        setBusy(false);
      })();
    });
  }

  void escalateOpen;

  const children: Array<Node | string> = [
    presented.root,
    why,
  ];
  if (escalatedBanner) children.push(escalatedBanner);
  if (escrowFields) children.push(escrowFields);
  children.push(
    correctionHost,
    ...(showEscalate ? [escalateFields] : []),
    ...(sendToPractice ? [sendToPractice.fields] : []),
    h("div", { class: "actions" }, [
      laterBtn,
      confirmMatchBtn,
      rejectMatchBtn,
      ...(showEscalate ? [escalateBtn] : []),
      ...(sendToPractice ? [sendToPractice.button] : []),
      returnToAmBtn,
    ]),
    status,
  );
  card.append(...children);
  return card;
}

function loanStatementFigureRow(
  component: string,
  label: string,
  leg: StoredLoanStatementLeg,
): {
  root: HTMLElement;
  match: HTMLInputElement;
  mismatch: HTMLInputElement;
} {
  const displayAmount = leg.absent ? "absent (not 0.00)" : leg.amountRaw;
  const match = h("input", {
    type: "checkbox",
    "data-role": "loan-stmt-match",
    "data-component": component,
    "aria-label": `this matches the source line (${label})`,
  });
  const mismatch = h("input", {
    type: "checkbox",
    "data-role": "loan-stmt-mismatch",
    "data-component": component,
    "aria-label": `this does not match the source line (${label})`,
  });
  const root = h("div", {
    class: "loan-statement-figure",
    "data-role": "loan-statement-figure",
    "data-component": component,
  }, [
    h("p", { class: "loan-statement-figure-amount" }, [`${label} ${displayAmount}`]),
    h("p", { class: "muted loan-statement-citation" }, [
      `source: ${leg.citation}`,
    ]),
    h("label", { class: "loan-statement-match" }, [
      match,
      " this matches the source line",
    ]),
    h("label", { class: "loan-statement-mismatch" }, [
      mismatch,
      " this does not match the source line",
    ]),
  ]);
  return { root, match, mismatch };
}

function renderCategorisationStageCard(opts: StageCardOpts): HTMLElement {
  const { item, client, chartLoad } = opts;
  // The card's account words take the render's ONE directory (rd13 L1).
  const companies = accountLabelContext(opts.directory);
  const role: StaffRole = opts.role ?? "account_manager";
  const canEscalate = opts.role !== "owner";
  const chartSnapshot = chartLoad.snapshot;
  const card = h("article", {
    class: "queue-card",
    "data-queue-id": String(item.id),
    "data-company": item.company,
  });

  const cardKind = categorisationCardKind(item);
  const hubProposal = cardKind === "hub-proposal";
  const storedSplit = parseStoredHubApplied(item.aiRationale);
  const storedLoan = parseStoredLoanStatement(item.aiRationale);
  const loanStmtIdentity = looksLikeLs1(item.aiRationale);
  // Same parse that renders the legs. One-tap without this re-opens hollow
  // approval: the card asks to approve a split with no legs, and the click
  // posts. Encoding never travels through QueueResolution.note.
  const hubLegsRendered =
    storedSplit !== null && storedSplit.applied.length > 0;
  // Completeness is offerability, not identity. Fusing it into
  // isHubGlProposalItem made Approve-on-complete pass by coincidence;
  // after identity is reason-only this conjunct is the control.
  const canOneTapHubSplit =
    cardKind === "hub-proposal" &&
    hubLegsRendered &&
    hasCompleteSuggestion(item);
  // One kind. Headline, Yes-withholding, lump, copy, pre-fill, edit
  // label, and confirmation all read it — do not rejoin hub-proposal and
  // recovery from the two old predicates in parallel.
  const recoveryCard = cardKind === "recovery";
  const withholdOneTap = recoveryCard;
  // Claim, not surface string: r26a emits hub_split for locked hub splits
  // so this stays true; an explicit lump flag covers period_locked+lump.
  // claimsLumpBooking itself keys on categorisationCardKind.
  const lumpOverride = claimsLumpBooking(item);

  const presented = cardFaceBlock(opts, "card-face-title");
  const face = presented.face;

  // No separate amount headline: the open row above already carries the
  // amount, once (Y6 brief item 3).

  const unresolvedLocation = unresolvedLocationFromRationale(item.aiRationale);
  const selectableAccountSet = new Set(chartSnapshot.selectableAccounts);
  const selectableLocationSet = new Set(chartSnapshot.selectableLocations);
  const hasSuggestion = hasCompleteSuggestion(item);
  const suggestionInMembership =
    hasSuggestion &&
    item.suggestedAccount !== undefined &&
    item.suggestedLocation !== undefined &&
    selectableAccountSet.has(item.suggestedAccount) &&
    selectableLocationSet.has(item.suggestedLocation);
  const locationRequired =
    Boolean(unresolvedLocation) || !item.suggestedLocation;
  const loanBookableFees =
    storedLoan !== null && storedLoan.fees.amount === 0;
  const loanSplitOffer: LoanSplitBookOffer = {
    suggestionInMembership,
    locationRequired,
    suggestedAccountSelectable:
      item.suggestedAccount !== undefined &&
      selectableAccountSet.has(item.suggestedAccount),
    suggestedLocationSelectable:
      item.suggestedLocation !== undefined &&
      selectableLocationSet.has(item.suggestedLocation),
    loanBookableFees,
  };
  const factNodes: DocumentFragment[] = [
    // A human date, never the ISO the contract stores (Y6 brief item 3).
    fact("Date", dateText(item.txnDate) || "unknown"),
    fact("Merchant", safeText(item.merchant)),
  ];
  appendPlaidGrantFactNodes(factNodes, item);
  // Location refusals still use the one regex that names the unconfigured
  // leaf (K12-OP-1). Booking-failure recovery is not another regex: ARF1
  // is written at the enqueue chokepoint so a new refuse kind cannot hide
  // behind the disclosure by default (R24O-1).
  if (unresolvedLocation && !item.suggestedLocation) {
    factNodes.push(
      fact(
        "Location (not configured)",
        safeText(unresolvedLocation),
      ),
    );
  } else if (
    showsSuggestedLocationAsCardFact(item) &&
    item.suggestedLocation
  ) {
    factNodes.push(fact("Location", safeText(item.suggestedLocation)));
  }
  if (hubProposal && storedSplit !== null) {
    for (const leg of storedSplit.applied) {
      factNodes.push(fact(safeText(leg.account), safeText(leg.amount)));
    }
  }
  const facts = h("dl", { class: "facts" }, factNodes);
  if (item.receiptContext) {
    appendReceiptFacts(facts, item.receiptContext, item.company);
  }
  if (item.reason === "receipt_card_not_attached") {
    appendReceiptCardNotAttachedCopy(facts, item, opts.directory);
  }

  // Reuse is per-pair (`reusablePreviousAttempt`). Display is per-half
  // (`displayedPreviousAttempt`). Both already require a human previous
  // choice (`previousAttemptWasHumanChoice`). Lump still excludes both
  // (R24O-3). Do not feed the display helper into the input-value
  // ternary (R28O-5).
  const previous = reusablePreviousAttempt(item);
  const previousFact = displayedPreviousAttempt(
    item,
    companies,
    opts.chartLoad.snapshot.selectableAccounts,
  );
  const consequenceLines = bookingConsequenceCopy(item, loanSplitOffer, role);
  const consequenceParagraph = (line: string): HTMLParagraphElement =>
    h(
      "p",
      {
        class: "card-face-consequence",
        "data-role": "booking-consequence",
      },
      [amVisibleMultilineDisplayText(line)],
    );
  const consequenceNodes: HTMLElement[] = [];
  const [causeLine, ...restConsequence] = consequenceLines;
  if (causeLine !== undefined) {
    consequenceNodes.push(consequenceParagraph(causeLine));
  }
  if (previousFact !== null) {
    // After the cause, before the retry warning: why it failed → what
    // was already answered → what retrying does. Not txn metadata, not
    // Why, not a pre-filled input.
    consequenceNodes.push(
      h(
        "p",
        {
          class: "card-face-consequence",
          "data-role": "previous-attempt",
        },
        [amVisibleMultilineDisplayText(previousFact)],
      ),
    );
  }
  for (const line of restConsequence) {
    consequenceNodes.push(consequenceParagraph(line));
  }

  // Y5 brief item 5: the stored reason enum is a code; the owner reads the
  // plain why-queued sentence (or nothing) and an account manager can open
  // "Technical" for the code itself and the raw bank line (Y6 brief item 3:
  // no "Descriptor" label on the card face).
  const technicalFacts: DocumentFragment[] = [fact("Reason", item.reason)];
  if ((item.descriptor ?? "").trim().length > 0) {
    technicalFacts.push(fact("Descriptor", safeText(item.descriptor)));
  }
  const technical = technicalDisclosure(role, technicalFacts);
  const why = h("details", { class: "why-disclosure" }, [
    showDetailsSummary(),
    h("dl", { class: "facts" }, [
      ...whyQueuedDisclosureFacts(item),
      fact("AI confidence", formatConfidence(item.confidence)),
      fact("AI rationale", safeText(displayAiRationale(item.aiRationale))),
      fact("Waiting", formatAge(item.createdAt)),
    ]),
    ...(technical === null ? [] : [technical]),
  ]);

  const status = h(
    "div",
    { class: "status", role: "status", "data-role": "status" },
    [],
  );

  const noteInput = h("input", {
    type: "text",
    class: "note-input",
    placeholder: "Optional note (e.g. annual domain renewal)",
    maxlength: String(QUEUE_RESOLUTION_NOTE_MAX),
    "aria-label": "Optional note",
  });
  const previousInMembership =
    previous !== null &&
    selectableAccountSet.has(previous.account) &&
    selectableLocationSet.has(previous.location);
  const suggestedIsJudgment =
    item.suggestedAccount !== undefined &&
    item.suggestedAccount !== "" &&
    isJudgmentClassAccount(item.suggestedAccount);
  const allowAccountMemory =
    !lumpOverride &&
    item.reason !== "new-vendor" &&
    !recoveryCard &&
    !suggestedIsJudgment;
  // A hold that says "check the receipt first" (round 10, C-1) must not book
  // the AI's guess on one tap: its account fields are the offer and Book waits.
  const holdCheckFirst = holdRequiresAccountChoice(item);
  const accountPrefill =
    !holdCheckFirst &&
    allowAccountMemory &&
    item.suggestedAccount !== undefined &&
    selectableAccountSet.has(item.suggestedAccount)
      ? item.suggestedAccount
      : "";
  const locationPrefill = recoveryCard
    ? ""
    : (item.suggestedLocation !== undefined &&
        selectableLocationSet.has(item.suggestedLocation)
      ? item.suggestedLocation
      : "");
  const accountChooser = mountChartChooser({
    kind: "account",
    selectable: chartSnapshot.selectableAccounts,
    companies,
    className: "account-input",
    ariaLabel: "Account",
    placeholder: "Account",
    initialValue: accountPrefill,
    load: chartLoad,
    ...(allowAccountMemory ? { groupKey: face.memoryKey } : {}),
  });
  const locationChooser = mountLocationControl({
    selectable: chartSnapshot.selectableLocations,
    companies,
    className: "location-input",
    ariaLabel: "Location",
    placeholder: unresolvedLocation
      ? `Location (not ${amVisibleDisplayText(unresolvedLocation)})`
      : "Location",
    initialValue: locationPrefill,
    load: chartLoad,
    ...(!recoveryCard ? { groupKey: face.memoryKey } : {}),
  });
  const accountInput = accountChooser.filter;
  const locationInput = locationChooser.filter;
  // R4-19: a person who touched the location box must choose a place from the
  // list. An untouched prefill still books as suggested, and a one-location
  // company skips the choice entirely.
  let locationTouched = false;
  const markLocationTouched = (): void => {
    locationTouched = true;
  };
  locationInput.addEventListener("input", markLocationTouched);
  locationInput.addEventListener("change", markLocationTouched);
  // Opt-in standing rule (owner 2026-08-02): never pre-selected.
  const makeRuleInput = h("input", {
    type: "checkbox",
    "data-role": "make-rule",
    "aria-label": MAKE_RULE_CHECKBOX_COPY,
  });
  const amountScopeRadio = h("input", {
    type: "radio",
    name: `rule-scope-${String(item.id)}`,
    value: "amount",
    "data-role": "rule-scope-amount",
    "aria-label": ruleScopeAmountCopy(item),
  });
  const vendorScopeRadio = h("input", {
    type: "radio",
    name: `rule-scope-${String(item.id)}`,
    value: "vendor",
    "data-role": "rule-scope-vendor",
    "aria-label": RULE_SCOPE_VENDOR_COPY,
  });
  const ruleScopeFieldset = h(
    "fieldset",
    {
      class: "make-rule-scope",
      "data-role": "rule-scope",
      hidden: "true",
    },
    [
      h("legend", {}, ["How much this rule covers"]),
      h("label", {}, [amountScopeRadio, ` ${ruleScopeAmountCopy(item)}`]),
      h("label", {}, [vendorScopeRadio, ` ${RULE_SCOPE_VENDOR_COPY}`]),
    ],
  );
  const matchCandidates = matchTextCandidates(item.descriptor, item.merchant);
  const matchTextRadios = matchCandidates.map((span, index) =>
    h("input", {
      type: "radio",
      name: `match-text-${String(item.id)}`,
      value: span,
      "data-role": "match-text-option",
      "data-match-text": span,
      // Shown text is stripped; value and data-match-text stay the stored text
      // so the standing rule matches what the bank really wrote.
      "aria-label": `Match on ${amVisibleDisplayText(span)}`,
      ...(index === 0 ? { "data-match-text-first": "true" } : {}),
    }),
  );
  const matchTextFieldset = h(
    "fieldset",
    {
      class: "make-rule-scope",
      "data-role": "match-text",
      hidden: "true",
    },
    [
      h("legend", {}, [MATCH_TEXT_LEGEND]),
      ...matchTextRadios.map((radio, index) => {
        const span = matchCandidates[index] ?? "";
        return h("label", {}, [radio, ` ${amVisibleDisplayText(span)}`]);
      }),
    ],
  );
  const postingKindRadio = h("input", {
    type: "radio",
    name: `rule-kind-${String(item.id)}`,
    value: "posting",
    "data-role": "rule-kind-posting",
    "aria-label": RULE_KIND_POSTING_COPY,
  });
  const vendorIdentityKindRadio = h("input", {
    type: "radio",
    name: `rule-kind-${String(item.id)}`,
    value: "vendor_identity",
    "data-role": "rule-kind-vendor-identity",
    "aria-label": RULE_KIND_VENDOR_IDENTITY_COPY,
  });
  const ruleKindFieldset = h(
    "fieldset",
    {
      class: "make-rule-scope",
      "data-role": "rule-kind",
      hidden: "true",
    },
    [
      h("legend", {}, [RULE_KIND_LEGEND]),
      h("label", {}, [postingKindRadio, ` ${RULE_KIND_POSTING_COPY}`]),
      h("label", {}, [
        vendorIdentityKindRadio,
        ` ${RULE_KIND_VENDOR_IDENTITY_COPY}`,
      ]),
    ],
  );
  const contractorClassRadio = h("input", {
    type: "radio",
    name: `vendor-classification-${String(item.id)}`,
    value: "1099_contractor",
    "data-role": "vendor-classification-1099",
    "aria-label": VENDOR_CLASSIFICATION_1099_COPY,
  });
  const non1099ClassRadio = h("input", {
    type: "radio",
    name: `vendor-classification-${String(item.id)}`,
    value: "non_1099_vendor",
    "data-role": "vendor-classification-non-1099",
    "aria-label": VENDOR_CLASSIFICATION_NON_1099_COPY,
  });
  const vendorClassificationFieldset = h(
    "fieldset",
    {
      class: "make-rule-scope",
      "data-role": "vendor-classification",
      hidden: "true",
    },
    [
      h("legend", {}, [VENDOR_CLASSIFICATION_LEGEND]),
      h("label", {}, [contractorClassRadio, ` ${VENDOR_CLASSIFICATION_1099_COPY}`]),
      h("label", {}, [
        non1099ClassRadio,
        ` ${VENDOR_CLASSIFICATION_NON_1099_COPY}`,
      ]),
    ],
  );
  const selectedRuleScope = (): QueueRuleScope | null => {
    if (amountScopeRadio.checked) return "amount";
    if (vendorScopeRadio.checked) return "vendor";
    return null;
  };
  const selectedMatchText = (): string | null => {
    const chosen = matchTextRadios.find((radio) => radio.checked);
    const value = chosen?.value ?? "";
    return value.length > 0 ? value : null;
  };
  const selectedRuleKind = (): QueueRuleKind | null => {
    if (postingKindRadio.checked) return "posting";
    if (vendorIdentityKindRadio.checked) return "vendor_identity";
    return null;
  };
  const selectedVendorClassification = (): QueueVendorClassification | null => {
    if (contractorClassRadio.checked) return "1099_contractor";
    if (non1099ClassRadio.checked) return "non_1099_vendor";
    return null;
  };
  const attachList = h("div", { "data-role": "prior-payment-candidates" }, []);
  const attachStatus = h("p", { class: "muted", "data-role": "prior-payment-status" }, []);
  const attachYearAtBuild = attachTaxYearForQueueItem(item);
  const attachHeading = h("h3", { "data-role": "prior-payment-heading" }, [
    attachYearAtBuild === null
      ? ATTACH_PRIOR_PAYMENTS_HEADING
      : attachPriorPaymentsHeadingForYear(attachYearAtBuild),
  ]);
  const attachNon1099Check = h("input", {
    type: "checkbox",
    "data-role": "prior-payment-non-1099-check",
  });
  const attachNon1099Confirm = h(
    "label",
    {
      class: "make-rule-opt-in",
      "data-role": "prior-payment-non-1099-confirm",
      hidden: "true",
    },
    [attachNon1099Check, ` ${ATTACH_NON_1099_CONFIRM_COPY}`],
  );
  const attach1099MintCheck = h("input", {
    type: "checkbox",
    "data-role": "prior-payment-1099-mint-check",
  });
  const attach1099MintConfirm = h(
    "label",
    {
      class: "make-rule-opt-in",
      "data-role": "prior-payment-1099-mint-confirm",
      hidden: "true",
    },
    [attach1099MintCheck, ` ${ATTACH_1099_MINT_CONFIRM_COPY}`],
  );
  const attachMoveCheck = h("input", {
    type: "checkbox",
    "data-role": "prior-payment-move-check",
  });
  const attachMoveConfirm = h(
    "label",
    {
      class: "make-rule-opt-in",
      "data-role": "prior-payment-move-confirm",
      hidden: "true",
    },
    [attachMoveCheck, ` ${ATTACH_MOVE_CONFIRM_COPY}`],
  );
  const attachButton = h(
    "button",
    {
      type: "button",
      class: "btn",
      "data-action": "attach-prior-payments",
      disabled: "true",
    },
    ["Attach selected"],
  );
  const attachPanel = h(
    "section",
    {
      class: "prior-payment-attach",
      "data-role": "prior-payment-attach",
      hidden: "true",
    },
    [
      attachHeading,
      attachList,
      attachStatus,
      attachNon1099Confirm,
      attach1099MintConfirm,
      attachMoveConfirm,
      attachButton,
    ],
  );
  let attachLoadStarted = false;
  let acknowledgedClassification: QueueVendorClassification | null = null;
  const attachedPlaidIds = new Set<string>();
  const vendorMarker = `queue:${String(item.id)}`;
  const selectedAttachIds = (): string[] =>
    [...attachList.querySelectorAll<HTMLInputElement>('input[type="checkbox"][data-plaid-id]')]
      .filter((box) => box.checked)
      .map((box) => box.dataset.plaidId ?? "")
      .filter((id) => id.length > 0);
  const selectedNeedMove = (): boolean =>
    [
      ...attachList.querySelectorAll<HTMLInputElement>(
        'input[type="checkbox"][data-plaid-id]',
      ),
    ]
      .filter((box) => box.checked)
      .some((box) => {
        const current = box.dataset.attachedVendor ?? "";
        return current.length > 0 && current !== vendorMarker;
      });
  const hasAttachmentsOnThisVendor = (): boolean => {
    if (attachedPlaidIds.size > 0) return true;
    return [
      ...attachList.querySelectorAll<HTMLInputElement>(
        'input[type="checkbox"][data-plaid-id]',
      ),
    ].some((box) => (box.dataset.attachedVendor ?? "") === vendorMarker);
  };
  const paintAttachButton = (): void => {
    const ids = selectedAttachIds();
    const classification = selectedVendorClassification();
    const needsNon1099Confirm = classification === "non_1099_vendor";
    const needs1099MintConfirm =
      classification === "1099_contractor" &&
      hasAttachmentsOnThisVendor() &&
      acknowledgedClassification === "non_1099_vendor";
    const confirmed = !needsNon1099Confirm || attachNon1099Check.checked;
    const needsMove = selectedNeedMove();
    const moveConfirmed = !needsMove || attachMoveCheck.checked;
    attachButton.disabled = ids.length === 0 || !confirmed || !moveConfirmed;
    if (attachButton.disabled) attachButton.setAttribute("disabled", "true");
    else attachButton.removeAttribute("disabled");
    attachNon1099Confirm.hidden = !needsNon1099Confirm;
    if (needsNon1099Confirm) attachNon1099Confirm.removeAttribute("hidden");
    else {
      attachNon1099Confirm.setAttribute("hidden", "true");
      attachNon1099Check.checked = false;
    }
    attach1099MintConfirm.hidden = !needs1099MintConfirm;
    if (needs1099MintConfirm) attach1099MintConfirm.removeAttribute("hidden");
    else {
      attach1099MintConfirm.setAttribute("hidden", "true");
      attach1099MintCheck.checked = false;
    }
    attachMoveConfirm.hidden = !needsMove;
    if (needsMove) attachMoveConfirm.removeAttribute("hidden");
    else {
      attachMoveConfirm.setAttribute("hidden", "true");
      attachMoveCheck.checked = false;
    }
  };
  const renderAttachCandidates = (rows: PriorPaymentCandidate[]): void => {
    clear(attachList);
    const year = attachTaxYearForQueueItem(item);
    if (rows.length === 0) {
      attachList.append(
        h("p", { class: "muted", "data-role": "prior-payment-empty" }, [
          year === null
            ? ATTACH_PRIOR_PAYMENTS_NO_DATE_COPY
            : attachPriorPaymentsEmptyCopy(year),
        ]),
      );
      paintAttachButton();
      return;
    }
    for (const row of rows) {
      const twins = row.cohortPlaidIds.length > 1;
      const box = h("input", {
        type: "checkbox",
        "data-plaid-id": row.plaidTransactionId,
        "data-cohort": row.cohortPlaidIds.join(","),
        "data-attached-vendor": row.attachedVendorId ?? "",
        "aria-label": attachCandidateAriaLabel(amVisibleDisplayText(row.merchant ?? "—")),
      });
      box.addEventListener("change", () => {
        const cohort = (box.dataset.cohort ?? "").split(",").filter(Boolean);
        if (cohort.length > 1) {
          for (const twinId of cohort) {
            const twin = attachList.querySelector<HTMLInputElement>(
              `input[data-plaid-id="${twinId}"]`,
            );
            if (twin) twin.checked = box.checked;
          }
        }
        paintAttachButton();
      });
      const alreadyThis = row.attachedVendorId === vendorMarker;
      const attachedToOther =
        row.attachedVendorId !== null &&
        row.attachedVendorId !== vendorMarker;
      const canDetach =
        alreadyThis ||
        (row.attachedVendorId !== null &&
          row.attachedVendorId.startsWith("queue:"));
      if (alreadyThis) box.disabled = true;
      const detachBtn = canDetach
        ? h(
            "button",
            {
              type: "button",
              class: "btn btn-ghost",
              "data-action": "detach-prior-payment",
              "data-plaid-id": row.plaidTransactionId,
            },
            ["Detach"],
          )
        : null;
      if (detachBtn) {
        detachBtn.addEventListener("click", () => {
          void (async () => {
            const outcome = await runWrite({
              key: cardWriteKey(attachPanel, `detach-prior-payment-${row.plaidTransactionId}`),
              scope: attachPanel,
              gate: () => null,
              send: () =>
                client.detachPriorPayment(item.company, {
                  plaidId: row.plaidTransactionId,
                }),
              afterLanded: async () => {
                attachedPlaidIds.delete(row.plaidTransactionId);
                if (!hasAttachmentsOnThisVendor()) {
                  acknowledgedClassification = null;
                }
                attachLoadStarted = false;
                await loadAttachCandidates(true);
              },
              stillCurrent: () => card.isConnected,
            });
            if (outcome.kind === "landed") {
              if (outcome.afterLandedFailed === true) {
                attachStatus.textContent = "Could not load prior payments.";
              } else {
                opts.announce("Detached.");
              }
              return;
            }
            if (outcome.error !== undefined && isAlreadyAnsweredError(outcome.error)) {
              lockCardControls(card);
              opts.onAlreadyAnswered?.();
              return;
            }
            const message = paintOutcomeFailure(attachStatus, outcome, opts.role);
            opts.announce(message);
          })();
        });
      }
      attachList.append(
        h("div", { class: "prior-payment-row", "data-role": "prior-payment-row" }, [
          h("label", {}, [
            box,
            " ",
            h("strong", { "data-role": "prior-payment-merchant" }, [
              amVisibleDisplayText(row.merchant ?? "—"),
            ]),
            ` · ${dateText(row.txnDate) || "Unknown date"} · ${formatAmount(row.amount)}`,
            row.account !== ""
              ? ` · ${accountLabelAmong(row.account, chartSnapshot.selectableAccounts, companies)}`
              : "",
            row.location ? ` · ${amVisibleDisplayText(row.location)}` : "",
            " · Payee on the books ",
            h("span", { "data-role": "prior-payment-ledger-payee" }, [
              amVisibleDisplayText(row.ledgerPayee),
            ]),
            twins ? ` · ${ATTACH_TWINS_COPY}` : "",
            alreadyThis ? " · attached" : "",
            ...(attachedToOther
              ? [
                  " · on file as ",
                  h("span", { "data-role": "prior-payment-incumbent" }, [
                    amVisibleDisplayText(row.attachedVendorId ?? ""),
                  ]),
                ]
              : []),
          ]),
          ...(detachBtn ? [detachBtn] : []),
        ]),
      );
    }
    paintAttachButton();
  };
  const loadAttachCandidates = async (failLoudly = false): Promise<void> => {
    if (attachLoadStarted) return;
    const period = attachPeriodForQueueItem(item);
    if (period === null) return;
    attachLoadStarted = true;
    attachStatus.textContent = "";
    try {
      const page = await client.listPriorPaymentCandidates(
        item.company,
        period,
      );
      renderAttachCandidates(page.candidates);
    } catch (error) {
      attachLoadStarted = false;
      // In `afterLanded` the write has already landed: the failure is the
      // LIST that could not load, not the write. Throwing lets `runWrite`
      // report `afterLandedFailed` instead of demoting the outcome.
      if (failLoudly) throw error;
      attachStatus.textContent = "Could not load prior payments.";
    }
  };
  const paintAttachPanel = (): void => {
    const show =
      selectedRuleKind() === "vendor_identity" &&
      selectedVendorClassification() !== null;
    attachPanel.hidden = !show;
    if (show) {
      attachPanel.removeAttribute("hidden");
      const year = attachTaxYearForQueueItem(item);
      if (year === null) {
        attachHeading.textContent = ATTACH_PRIOR_PAYMENTS_HEADING;
        clear(attachList);
        attachList.append(
          h("p", { class: "muted", "data-role": "prior-payment-no-date" }, [
            ATTACH_PRIOR_PAYMENTS_NO_DATE_COPY,
          ]),
        );
      } else {
        attachHeading.textContent = attachPriorPaymentsHeadingForYear(year);
        void loadAttachCandidates();
      }
    } else {
      attachPanel.setAttribute("hidden", "true");
    }
    paintAttachButton();
  };
  attachButton.addEventListener("click", () => {
    const ids = [...new Set(selectedAttachIds())];
    if (ids.length === 0) return;
    void (async () => {
      const outcome = await runWrite({
        key: cardWriteKey(attachPanel, `attach-prior-payments-${ids.join(",")}`),
        scope: attachPanel,
        gate: () => null,
        send: () =>
          client.attachPriorPayments(item.company, {
            vendorId: vendorMarker,
            plaidIds: ids,
          }),
        afterLanded: async () => {
          for (const id of ids) attachedPlaidIds.add(id);
          acknowledgedClassification = selectedVendorClassification();
          attachLoadStarted = false;
          await loadAttachCandidates(true);
        },
        stillCurrent: () => card.isConnected,
      });
      if (outcome.kind === "landed") {
        // M3: `runWrite` re-enables what it disabled when it returns, so the
        // button is painted again from the new (empty) selection.
        paintAttachButton();
        if (outcome.afterLandedFailed === true) {
          attachStatus.textContent = "Could not load prior payments.";
        } else {
          opts.announce("Attached.");
        }
        return;
      }
      if (outcome.error !== undefined && isAlreadyAnsweredError(outcome.error)) {
        lockCardControls(card);
        opts.onAlreadyAnswered?.();
        return;
      }
      attachStatus.textContent = priorPaymentAttachFailureCopy(outcome.error);
    })();
  });
  attachNon1099Check.addEventListener("change", () => {
    if (attachNon1099Check.checked) {
      acknowledgedClassification = "non_1099_vendor";
    } else if (acknowledgedClassification === "non_1099_vendor") {
      acknowledgedClassification = null;
    }
    paintAttachButton();
  });
  attach1099MintCheck.addEventListener("change", () => {
    if (attach1099MintCheck.checked) {
      acknowledgedClassification = "1099_contractor";
    } else if (acknowledgedClassification === "1099_contractor") {
      acknowledgedClassification = null;
    }
    paintAttachButton();
  });
  attachMoveCheck.addEventListener("change", () => {
    paintAttachButton();
  });
  /** Every choice still missing, in the order the form shows them. */
  const missingStandingRuleChoices = (): string[] => {
    if (!makeRuleInput.checked) return [];
    const missing: string[] = [];
    if (selectedMatchText() === null) missing.push(MATCH_TEXT_REQUIRED_COPY);
    const kind = selectedRuleKind();
    if (kind === null) {
      missing.push(RULE_KIND_REQUIRED_COPY);
      // The width group is on screen too, so its missing choice is shown with
      // the others rather than on a later click (round 10, item 8h).
      if (selectedRuleScope() === null) missing.push(RULE_SCOPE_REQUIRED_COPY);
      return missing;
    }
    // Kind A is vendor-wide by definition; width is a posting-rule question
    // (R32O-2). Switching back to posting re-demands width if none selected.
    if (kind === "vendor_identity") {
      if (selectedVendorClassification() === null) {
        missing.push(VENDOR_CLASSIFICATION_REQUIRED_COPY);
      }
      return missing;
    }
    if (selectedRuleScope() === null) missing.push(RULE_SCOPE_REQUIRED_COPY);
    return missing;
  };
  /**
   * Round 10, item 8h: one click shows EVERY missing choice, not one per
   * click, and announces the same sentence once.
   */
  const refuseUntilStandingRuleChoices = (): boolean => {
    const missing = missingStandingRuleChoices();
    if (missing.length === 0) return false;
    const message = missing.join(" ");
    clear(status);
    status.append(document.createTextNode(message));
    opts.announce(message);
    return true;
  };
  // The fields the request carries and the sentence beside the checkbox come
  // from ONE function over what is picked right now (round eight A-M3).
  const currentStandingRule = (): { fields: StandingRuleFields; preview: string } =>
    standingRuleRequest(item, {
      matchText: selectedMatchText(),
      ruleKind: selectedRuleKind(),
      ruleScope: selectedRuleScope(),
      vendorClassification: selectedVendorClassification(),
    });
  const standingRuleFields = (): StandingRuleFields => currentStandingRule().fields;
  const rulePreviewEl = h("span", { class: "rule-preview", "data-role": "rule-preview" }, [
    currentStandingRule().preview,
  ]);
  const paintRulePreview = (): void => {
    rulePreviewEl.textContent = currentStandingRule().preview;
  };
  const extraConfirmInput = h("input", {
    type: "checkbox",
    "data-role": "vendor-scope-extra-confirm",
    "aria-label": VENDOR_SCOPE_EXTRA_CONFIRM_LABEL,
  });
  const extraConfirmHelp = h(
    "p",
    { class: "muted", "data-role": "vendor-scope-extra-confirm-help" },
    [VENDOR_SCOPE_EXTRA_CONFIRM_HELP],
  );
  const extraConfirmField = h(
    "label",
    {
      class: "vendor-scope-extra-confirm",
      "data-role": "vendor-scope-extra-confirm-field",
      hidden: "true",
    },
    [extraConfirmInput, ` ${VENDOR_SCOPE_EXTRA_CONFIRM_LABEL}`, extraConfirmHelp],
  );
  const extraConfirmNeeded = (): boolean => {
    if (!makeRuleInput.checked) return false;
    const span = selectedMatchText();
    if (span === null) return false;
    const vendorWide =
      selectedRuleScope() === "vendor" || selectedRuleKind() === "vendor_identity";
    return vendorWide && vendorScopeNeedsExtraConfirm(span);
  };
  const paintExtraConfirm = (): void => {
    const needed = extraConfirmNeeded();
    extraConfirmField.hidden = !needed;
    if (needed) extraConfirmField.removeAttribute("hidden");
    else extraConfirmField.setAttribute("hidden", "true");
    if (!needed) extraConfirmInput.checked = false;
  };
  const refuseUntilExtraConfirmed = (): boolean => {
    if (!extraConfirmNeeded() || extraConfirmInput.checked) return false;
    clear(status);
    status.append(document.createTextNode(VENDOR_SCOPE_EXTRA_CONFIRM_HELP));
    opts.announce(VENDOR_SCOPE_EXTRA_CONFIRM_HELP);
    return true;
  };
  const refuseUntilAttachEffectConfirmed = (): boolean => {
    if (selectedRuleKind() !== "vendor_identity") return false;
    if (!hasAttachmentsOnThisVendor()) return false;
    const kind = selectedVendorClassification();
    if (kind === "non_1099_vendor" && acknowledgedClassification !== "non_1099_vendor") {
      clear(status);
      status.append(document.createTextNode(ATTACH_NON_1099_CONFIRM_COPY));
      opts.announce(ATTACH_NON_1099_CONFIRM_COPY);
      return true;
    }
    if (
      kind === "1099_contractor" &&
      acknowledgedClassification === "non_1099_vendor"
    ) {
      clear(status);
      status.append(document.createTextNode(ATTACH_1099_MINT_CONFIRM_COPY));
      opts.announce(ATTACH_1099_MINT_CONFIRM_COPY);
      return true;
    }
    return false;
  };
  const hideStandingRuleChoosers = (): void => {
    amountScopeRadio.checked = false;
    vendorScopeRadio.checked = false;
    postingKindRadio.checked = false;
    vendorIdentityKindRadio.checked = false;
    for (const radio of matchTextRadios) radio.checked = false;
    ruleScopeFieldset.hidden = true;
    ruleScopeFieldset.setAttribute("hidden", "true");
    matchTextFieldset.hidden = true;
    matchTextFieldset.setAttribute("hidden", "true");
    ruleKindFieldset.hidden = true;
    ruleKindFieldset.setAttribute("hidden", "true");
    contractorClassRadio.checked = false;
    non1099ClassRadio.checked = false;
    vendorClassificationFieldset.hidden = true;
    vendorClassificationFieldset.setAttribute("hidden", "true");
    paintAttachPanel();
    paintExtraConfirm();
  };
  const paintStandingRuleWidth = (): void => {
    // Hide width for Kind A; do not clear a previously chosen width so
    // switching back to posting keeps it (R32O-2 mirror).
    const hideWidth = selectedRuleKind() === "vendor_identity";
    ruleScopeFieldset.hidden = hideWidth;
    if (hideWidth) ruleScopeFieldset.setAttribute("hidden", "true");
    else ruleScopeFieldset.removeAttribute("hidden");
  };
  const paintVendorClassification = (): void => {
    // Show only for Kind A. Do not clear a previously chosen declaration
    // so switching back to Kind A keeps it (same mirror as width).
    // Neither radio is pre-selected on first show.
    const show = selectedRuleKind() === "vendor_identity";
    vendorClassificationFieldset.hidden = !show;
    if (show) vendorClassificationFieldset.removeAttribute("hidden");
    else vendorClassificationFieldset.setAttribute("hidden", "true");
    paintAttachPanel();
  };
  const showStandingRuleChoosers = (): void => {
    matchTextFieldset.hidden = false;
    matchTextFieldset.removeAttribute("hidden");
    ruleKindFieldset.hidden = false;
    ruleKindFieldset.removeAttribute("hidden");
    paintStandingRuleWidth();
    paintVendorClassification();
    paintExtraConfirm();
  };
  makeRuleInput.addEventListener("change", () => {
    if (!makeRuleInput.checked) {
      hideStandingRuleChoosers();
      return;
    }
    showStandingRuleChoosers();
  });
  amountScopeRadio.addEventListener("change", () => {
    paintExtraConfirm();
  });
  vendorScopeRadio.addEventListener("change", () => {
    paintExtraConfirm();
  });
  postingKindRadio.addEventListener("change", () => {
    paintStandingRuleWidth();
    paintVendorClassification();
    paintExtraConfirm();
  });
  vendorIdentityKindRadio.addEventListener("change", () => {
    paintStandingRuleWidth();
    paintVendorClassification();
    paintExtraConfirm();
  });
  contractorClassRadio.addEventListener("change", () => {
    paintAttachPanel();
    paintExtraConfirm();
  });
  non1099ClassRadio.addEventListener("change", () => {
    paintAttachPanel();
    paintExtraConfirm();
  });
  for (const radio of matchTextRadios) {
    radio.addEventListener("change", () => {
      paintExtraConfirm();
    });
  }
  // Registered after the handlers above, so a change that clears the choosers
  // (unticking the box) is repainted from the cleared state.
  for (const control of [
    makeRuleInput,
    ...matchTextRadios,
    amountScopeRadio,
    vendorScopeRadio,
    postingKindRadio,
    vendorIdentityKindRadio,
    contractorClassRadio,
    non1099ClassRadio,
  ]) {
    control.addEventListener("change", paintRulePreview);
  }

  // One-tap Yes only when BOTH account and location are resolved. An account
  // suggestion with an unresolved location is a half-answer — omit Yes and
  // lead with entry fields so the AM must decide location (R12c). new-vendor
  // is never one-tap even with a complete suggestion (K12-OP-2).
  const newVendorNoOneTap = item.reason === "new-vendor";
  const informedEntry =
    withholdOneTap ||
    !hasSuggestion ||
    newVendorNoOneTap ||
    !suggestionInMembership ||
    holdCheckFirst;
  // One-tap Yes is the collapsed card. When the suggestion is not in
  // membership(), fields are already the primary UI — hide neither
  // Use this account nor Dismiss behind Something else (rounds 28/29:
  // a control that only exists after a click is a High).
  // Card-shaped: unresolved location, or never suggested. Do not fold
  // recoveryCard into this flag — Use it again does not read it today, and
  // a future helper that started reading it from that path would treat a
  // required field as a property of the card. Recovery typed-edit is gated
  // on the values this handler is about to POST, below.

  const showLoanOverlay =
    (cardKind === "ordinary" || cardKind === "recovery") && storedLoan !== null;
  const unreadStatement =
    parseLoanStatementUnreadWhy(item.aiRationale) !== null;
  const loanUploadCandidate =
    (cardKind === "ordinary" || cardKind === "recovery") &&
    (storedLoan !== null ||
      loanStmtIdentity ||
      unreadStatement ||
      hasLoanStatementCharacter(item));
  const loanUploadBlockedProtected =
    loanUploadCandidate && firstLineIsProtectedRationale(item.aiRationale);
  const showLoanUpload =
    loanUploadCandidate &&
    !loanUploadBlockedProtected &&
    !loanStatementUploadRefusedForPending(item);

  type LoanFigureCtl = {
    match: HTMLInputElement;
    mismatch: HTMLInputElement;
    label: string;
    amountRaw: string;
    absent: boolean;
  };
  const loanFigures: LoanFigureCtl[] = [];
  let loanConfirmRoot: HTMLElement | null = null;
  const loanBookBtn = h(
    "button",
    {
      type: "button",
      class: "btn btn-primary",
      "data-action": "book-loan-split",
      "aria-label": "Book this split",
      disabled: "true",
    },
    ["Book this split"],
  );
  const paintLoanBook = (): void => {
    if (!showLoanOverlay) {
      loanBookBtn.disabled = true;
      return;
    }
    const allMatched = loanFigures.every((fig) => fig.match.checked);
    const disagreement = loanFigures.some((fig) => fig.mismatch.checked);
    loanBookBtn.disabled =
      !allMatched ||
      disagreement ||
      !loanBookableFees ||
      !suggestionInMembership;
  };
  if (showLoanOverlay) {
    const principalRow = loanStatementFigureRow(
      "principal",
      "Principal",
      storedLoan.principal,
    );
    const interestRow = loanStatementFigureRow(
      "interest",
      "Interest",
      storedLoan.interest,
    );
    const escrowRow = loanStatementFigureRow(
      "escrow",
      "Escrow",
      storedLoan.escrow,
    );
    const feesRow = loanStatementFigureRow("fees", "Fees", storedLoan.fees);
    loanFigures.push(
      {
        match: principalRow.match,
        mismatch: principalRow.mismatch,
        label: "Principal",
        amountRaw: storedLoan.principal.amountRaw,
        absent: storedLoan.principal.absent,
      },
      {
        match: interestRow.match,
        mismatch: interestRow.mismatch,
        label: "Interest",
        amountRaw: storedLoan.interest.amountRaw,
        absent: storedLoan.interest.absent,
      },
      {
        match: escrowRow.match,
        mismatch: escrowRow.mismatch,
        label: "Escrow",
        amountRaw: storedLoan.escrow.amountRaw,
        absent: storedLoan.escrow.absent,
      },
      {
        match: feesRow.match,
        mismatch: feesRow.mismatch,
        label: "Fees",
        amountRaw: storedLoan.fees.amountRaw,
        absent: storedLoan.fees.absent,
      },
    );
    const feeNote = loanBookableFees
      ? null
      : h(
          "p",
          { class: "card-face-consequence", "data-role": "loan-statement-fees" },
          [
            `A non-zero Fees component cannot book as a loan split. ${loanStatementLumpWarning(item)}`,
          ],
        );
    const mismatchNext = h(
      "p",
      {
        class: "card-face-consequence",
        "data-role": "loan-statement-mismatch-next",
      },
      [
        // Round-2 review, item 8 (decided): the owner's card states the fact,
        // and adds the re-read line ONLY where the upload control is really on
        // the card (showLoanUpload) — never an offer of a control that is not
        // rendered. The account manager keeps its own ask untouched.
        role === "owner" && showLoanUpload
          ? `${loanStatementMismatchNextStepCopy(role)} ${LOAN_STATEMENT_MISMATCH_REREAD_COPY}`
          : loanStatementMismatchNextStepCopy(role),
      ],
    );
    loanConfirmRoot = h(
      "div",
      { class: "loan-statement-confirm", "data-role": "loan-statement-confirm" },
      [
        h("p", { class: "loan-statement-section" }, [
          `Read from ${loanStatementPaidSectionPhrase(storedLoan)}. ` +
            "Nothing is pre-selected. Confirm each figure against the source line.",
        ]),
        principalRow.root,
        interestRow.root,
        escrowRow.root,
        feesRow.root,
        mismatchNext,
        ...(feeNote ? [feeNote] : []),
      ],
    );
    for (const fig of loanFigures) {
      fig.match.addEventListener("change", paintLoanBook);
      fig.mismatch.addEventListener("change", paintLoanBook);
    }
    paintLoanBook();
  }

  const loanFileInput = h("input", {
    type: "file",
    accept: "application/pdf",
    "data-role": "loan-statement-file",
    "aria-label": "Mortgage statement PDF",
  });
  const loanUploadBtn = h(
    "button",
    {
      type: "button",
      class: "btn btn-secondary",
      "data-action": "upload-loan-statement",
      "aria-label": "Upload mortgage statement PDF",
    },
    ["Upload mortgage statement PDF"],
  );
  const loanUploadRoot = showLoanUpload
    ? h(
        "div",
        { class: "loan-statement-upload", "data-role": "loan-statement-upload" },
        [
          h("p", { class: "muted" }, [
            storedLoan
              ? LOAN_STATEMENT_UPLOAD_REREAD_COPY
              : LOAN_STATEMENT_UPLOAD_INVITE_COPY,
          ]),
          h("label", {}, ["Mortgage statement PDF", loanFileInput]),
          loanUploadBtn,
        ],
      )
    : loanUploadBlockedProtected
      ? h(
          "p",
          {
            class: "card-face-consequence",
            "data-role": "loan-statement-upload-blocked",
          },
          [LOAN_STATEMENT_UPLOAD_BLOCKED_COPY],
        )
      : null;

  // Round-4 review, finding 5: the shared chart-error message
  // (`chart_load.ts`, not this lane's file) tells the reader to escalate to
  // the owner, which the role rule forbids on the owner's own card. The render
  // site is where the role is known, so the role-aware sentence is built here;
  // the account manager keeps the ask. Flagged in the PR for the orchestrator:
  // `CHART_LOAD_ERROR_MESSAGE` itself still carries the owner-escalation word.
  const chartNotice =
    chartLoad.status === "error"
      ? h(
          "p",
          { class: "muted", "data-role": "chart-snapshot-error" },
          [
            "The chart could not be loaded. Try again.",
          ],
        )
      : chartLoadIsGenuinelyUnloaded(chartLoad)
        ? h(
            "p",
            { class: "muted", "data-role": "chart-snapshot-absent" },
            ["The chart has not been loaded yet."],
          )
        : null;

  const bookHint = h(
    "p",
    {
      class: "approve-hint",
      "data-role": "approve-disabled-hint",
      hidden: "true",
    },
    [],
  );

  /**
   * GRANT G15 (rd15 lane B, owner 2026-11-02). Answer this card by spreading
   * its bank line across several accounts and places.
   *
   * The parts must add up to the line to the cent; the shared split editor
   * keeps Save off until they do, and the Worker refuses parts that do not
   * add up. A split answer is sent alone — never with an account, a place or
   * a standing rule — and the panel says so in the card.
   *
   * Only a bank transaction card that books an account may be split: a hub
   * proposal and a loan statement already carry the run's own split, a removed
   * line has nothing to split, and a card with no amount cannot be divided.
   */
  const splitTotal = item.amount === undefined ? null : decimalToCents(item.amount);
  // This renderer is the categorisation cards only: "ordinary" and "recovery".
  // A hub proposal is the third kind and is excluded above, so `!hubProposal`
  // IS "a card that books one account".
  const splitOfferable =
    splitTotal !== null &&
    splitTotal !== 0n &&
    !hubProposal &&
    storedLoan === null &&
    !loanStmtIdentity &&
    item.eventType !== "removed";
  const splitMount = h("div", {
    "data-role": "split-mount",
    hidden: "true",
  });
  const splitBtn = h(
    "button",
    {
      type: "button",
      class: "btn btn-ghost",
      "data-action": "split-line",
      "aria-label": "Split this line across several accounts",
    },
    ["Split this line…"],
  );
  if (!splitOfferable) {
    splitBtn.hidden = true;
    splitBtn.setAttribute("hidden", "true");
  }
  const splitPlaces = (): readonly string[] =>
    chartSnapshot.selectableLocations.length > 0
      ? chartSnapshot.selectableLocations
      : chartSnapshot.locations;
  let splitOpen = false;
  const closeSplit = (): void => {
    splitOpen = false;
    while (splitMount.firstChild) splitMount.removeChild(splitMount.firstChild);
    splitMount.hidden = true;
    splitMount.setAttribute("hidden", "true");
    splitBtn.hidden = false;
    splitBtn.removeAttribute("hidden");
    // A standing rule is offered again only because the split is closed.
    makeRuleInput.disabled = false;
    paintStandingRuleWidth();
  };
  const saveSplit = async (
    lines: readonly SplitLineDraft[],
  ): Promise<void> => {
    if (splitTotal === null) return;
    const cents = resolvedSplitCents(lines, splitTotal);
    const parts: QueueSplitPart[] = [];
    for (let index = 0; index < lines.length; index += 1) {
      const line = lines[index];
      const amount = cents[index];
      if (line === undefined || amount === null || amount === undefined) return;
      if (line.account.length === 0 || line.location.length === 0) return;
      parts.push({
        account: line.account,
        location: line.location,
        amount: centsToAmount(amount),
      });
    }
    if (parts.length < 2) return;
    const result: CategorisationResult = { kind: "edit", splitAnswer: true };
    opts.onBusyChange?.(true);
    const outcome = await runWrite({
      key: cardWriteKey(card, ""),
      scope: card,
      gate: () => null,
      send: async () => {
        clear(status);
        card.classList.add("exiting");
        await client.resolveQueueItem(item.id, {
          action: "edit",
          splits: parts,
          ...(noteInput.value.trim() ? { note: noteInput.value.trim() } : {}),
        });
        await sleep(STAGE_MS);
      },
      stillCurrent: () => card.isConnected,
    });
    await finishCategorisation(outcome, result);
  };
  const openSplit = (): void => {
    if (splitTotal === null || splitOpen) return;
    splitOpen = true;
    splitBtn.hidden = true;
    splitBtn.setAttribute("hidden", "true");
    splitMount.hidden = false;
    splitMount.removeAttribute("hidden");
    // A split answer is never a standing rule; do not offer the tick while
    // the parts are being chosen.
    makeRuleInput.checked = false;
    hideStandingRuleChoosers();
    makeRuleInput.disabled = true;
    while (splitMount.firstChild) splitMount.removeChild(splitMount.firstChild);
    const editor = mountSplitEditor({
      totalCents: splitTotal,
      lines: [
        {
          id: "split-1",
          company: item.company,
          location: locationChooser.getValue() ?? "",
          account: accountChooser.getValue() ?? "",
          amountText: centsToAmount(splitTotal),
        },
        {
          id: "split-2",
          company: item.company,
          location: "",
          account: "",
          amountText: "",
        },
      ],
      companies: [
        {
          slug: item.company,
          // `face` is the card's own words in this function, so the company
          // name comes from the render's one directory (rd13 L1).
          label:
            opts.directory.name(item.company) ?? sentence("company_unknown", role),
        },
      ],
      labelCompanies: companies,
      locationsFor: splitPlaces,
      accountsFor: () => chartSnapshot.selectableAccounts,
      onSave: (lines) => {
        void saveSplit(lines);
      },
      onCancel: () => {
        closeSplit();
      },
    });
    splitMount.append(
      h("p", { class: "muted", "data-role": "split-rule-note" }, [
        SPLIT_NO_STANDING_RULE_TEXT,
      ]),
      editor.root,
    );
  };
  splitBtn.addEventListener("click", () => {
    openSplit();
  });

  const fields = h(
    "div",
    informedEntry
      ? { class: "fields", "data-role": "account-entry-primary" }
      : { class: "fields", hidden: "true" },
    [
      ...(chartNotice ? [chartNotice] : []),
      h("label", {}, ["Account", accountChooser.root]),
      // The hint sits under the picker it is about, not in the action row
      // beside the disabled button (Y6 brief item 3).
      bookHint,
      h("label", {}, ["Location", locationChooser.root]),
      // Round 3: the same slot Y2 built for Rules and Statement checks — the
      // owner is never told to escalate to themselves, and the line only
      // appears once a search finds nothing (it is an answer, not a fixture).
      // Round 11, R7-14: nothing in the console opens an account, so the line
      // no longer carries an "Open Rules" button (round-2 review, item 8).
      requestSlot("account", chartLoad, {
        role,
        chooser: accountChooser,
      }),
      requestSlot("location", chartLoad, { role, chooser: locationChooser }),
      splitBtn,
      splitMount,
      h("label", {}, ["Note", noteInput]),
      moreOptionsPanel([
        // The sentence about what the rule would cover sits WITH its checkbox
        // (Y6 brief item 3).
        h("label", { class: "rule-check-row rule-check-with-preview" }, [
          makeRuleInput,
          h("span", { class: "rule-check-text" }, [
            h("span", { class: "rule-check-title" }, [MAKE_RULE_CHECKBOX_COPY]),
            rulePreviewEl,
          ]),
        ]),
        matchTextFieldset,
        ruleKindFieldset,
        vendorClassificationFieldset,
        attachPanel,
        ruleScopeFieldset,
        extraConfirmField,
      ]),
    ],
  );

  const defaultBtn = h(
    "button",
    {
      type: "button",
      class: informedEntry ? "btn btn-primary" : "btn btn-primary btn-book-ready",
      "data-action": "approve-default",
      "aria-label": face.primaryLabel,
    },
    [face.primaryLabel],
  );

  const discloseBtn = h(
    "button",
    {
      type: "button",
      class: "btn btn-ghost",
      "data-action": "something-else",
      "aria-label": "Something else",
    },
    ["Something else…"],
  );

  const laterBtn = h(
    "button",
    {
      type: "button",
      class: "btn btn-ghost",
      "data-action": "later",
      "aria-label": SKIP_FOR_NOW_LABEL,
    },
    [SKIP_FOR_NOW_LABEL],
  );
  if (!opts.canLater) {
    laterBtn.hidden = true;
    laterBtn.setAttribute("hidden", "true");
  }

  const editLabel = lumpOverride
    ? "Book whole deposit to this account"
    : face.primaryLabel;
  const editBtn = h(
    "button",
    {
      type: "button",
      class: "btn btn-primary",
      "data-action": "edit",
      "aria-label": editLabel,
      ...(!informedEntry ? { hidden: "true" } : {}),
    },
    [editLabel],
  );
  // G1. The button that posts the chooser's account is named from the
  // chooser's CURRENT value on every card kind, one-tap or not. It used to
  // keep the suggestion's "Yes, book to X" after the person picked Y, so a
  // button said one account and posted another. The one-tap `defaultBtn`
  // is a different control: it accepts the suggestion and keeps naming it.
  const chosenEditLabel = (account: string): string => {
    const words = accountLabelAmong(account, chartSnapshot.selectableAccounts, companies);
    return lumpOverride ? `Book whole deposit to ${words}` : `Book to ${words}`;
  };
  // What the button says while nothing valid is chosen: the card face's own
  // wording when the account fields are the primary UI, else a plain ask.
  const idleEditLabel = informedEntry || lumpOverride ? editLabel : "Pick the account";
  const syncBookButton = (): void => {
    const account = accountChooser.getValue();
    const blocked = account === null;
    editBtn.disabled = blocked;
    editBtn.classList.toggle("btn-book-ready", !blocked);
    const label = account === null ? idleEditLabel : chosenEditLabel(account);
    editBtn.textContent = label;
    editBtn.setAttribute("aria-label", label);
    clear(bookHint);
    const hint = informedEntry ? face.disabledHint : ACCOUNT_HINT;
    if (blocked && hint.length > 0) {
      bookHint.hidden = false;
      bookHint.removeAttribute("hidden");
      bookHint.append(document.createTextNode(hint));
    } else {
      bookHint.hidden = true;
      bookHint.setAttribute("hidden", "true");
    }
  };
  accountInput.addEventListener("input", syncBookButton);
  accountInput.addEventListener("change", syncBookButton);
  bindValueSync(accountInput, syncBookButton);
  bindValueSync(locationInput, syncBookButton);
  syncBookButton();

  // Distinct from Use this account: not btn-primary, not the input default.
  // Absent (not hidden, not dead) when there is no reusable pair.
  const useAgainBtn =
    previous === null || !previousInMembership
      ? null
      : h(
          "button",
          {
            type: "button",
            class: "btn btn-secondary",
            "data-action": "use-previous-attempt",
            "aria-label": USE_PREVIOUS_ATTEMPT_LABEL,
          },
          [USE_PREVIOUS_ATTEMPT_LABEL],
        );

  const cancelBtn = h(
    "button",
    {
      type: "button",
      class: "btn btn-ghost",
      "data-action": "cancel-disclose",
      "aria-label": "Cancel",
      hidden: "true",
    },
    ["Cancel"],
  );

  const rejectBtn = h(
    "button",
    {
      type: "button",
      class: "btn-danger-text",
      "data-action": "reject",
      "aria-label": "Dismiss — don't book, don't make a rule",
      ...(!informedEntry ? { hidden: "true" } : {}),
    },
    ["Dismiss — don't book, don't make a rule"],
  );

  const escalateNoteInput = h("input", {
    type: "text",
    class: "note-input",
    placeholder: "Why you can't resolve this (required)",
    maxlength: "500",
    "aria-label": "Escalation note",
    "data-role": "escalate-note",
  });
  const escalateConfirmBtn = h(
    "button",
    {
      type: "button",
      class: "btn btn-primary",
      "data-action": "escalate-confirm",
      "aria-label": "Confirm sending to the owner",
      hidden: "true",
    },
    ["Send to owner"],
  );
  const escalateCancelBtn = h(
    "button",
    {
      type: "button",
      class: "btn btn-ghost",
      "data-action": "escalate-cancel",
      "aria-label": "Cancel escalation",
      hidden: "true",
    },
    ["Cancel"],
  );
  const escalateBtn = h(
    "button",
    {
      type: "button",
      class: "btn btn-ghost",
      "data-action": "escalate",
      "aria-label": ESCALATE_TO_OWNER_LABEL,
      ...(!canEscalate ? { hidden: "true" } : {}),
    },
    [ESCALATE_TO_OWNER_LABEL],
  );
  const escalateFields = h("div", {
    class: "fields",
    "data-role": "escalate-fields",
    hidden: "true",
  }, [
    h("label", {}, ["Escalation note", escalateNoteInput]),
    h("div", { class: "actions" }, [escalateConfirmBtn, escalateCancelBtn]),
  ]);

  const returnToAmBtn = h(
    "button",
    {
      type: "button",
      class: "btn btn-ghost",
      "data-action": "return-to-am",
      "aria-label": "Return to account manager",
      hidden: "true",
    },
    ["Return to account manager"],
  );

  const escalatedBanner =
    isWithOwner(item)
      ? h("p", {
          class: "escalation-banner",
          "data-role": "escalation-banner",
        }, [
          `Escalated by ${amVisibleDisplayText(safeText(item.escalatedBy, "staff"))}: ${amVisibleDisplayText(safeText(item.escalationNote, ""))}`,
        ])
      : null;

  // Already escalated → hide escalate; Owner can return-to-am.
  if (isWithOwner(item)) {
    escalateBtn.hidden = true;
    escalateBtn.setAttribute("hidden", "true");
    if (opts.role === "owner") {
      returnToAmBtn.hidden = false;
      returnToAmBtn.removeAttribute("hidden");
    }
  }

  let disclosed = informedEntry;
  let escalateOpen = false;
  let sendToPractice: ReturnType<typeof sendToPracticeControls> | null = null;

  const setEscalateOpen = (open: boolean): void => {
    escalateOpen = open;
    escalateFields.hidden = !open;
    if (open) {
      sendToPractice?.setOpen(false);
      sendToPractice?.setButtonVisible(false);
      escalateFields.removeAttribute("hidden");
      escalateConfirmBtn.hidden = false;
      escalateCancelBtn.hidden = false;
      escalateConfirmBtn.removeAttribute("hidden");
      escalateCancelBtn.removeAttribute("hidden");
      escalateBtn.setAttribute("hidden", "true");
      escalateBtn.hidden = true;
      if (!escalateNoteInput.value.trim()) {
        const mismatched = loanFigures
          .filter((fig) => fig.mismatch.checked)
          .map((fig) => fig.label);
        const mismatchNote = loanStatementMismatchEscalateNote(mismatched, role);
        if (mismatchNote) {
          escalateNoteInput.value = mismatchNote.slice(0, 500);
        }
      }
    } else {
      escalateFields.setAttribute("hidden", "true");
      escalateConfirmBtn.hidden = true;
      escalateCancelBtn.hidden = true;
      escalateConfirmBtn.setAttribute("hidden", "true");
      escalateCancelBtn.setAttribute("hidden", "true");
      if (!isWithOwner(item) && !disclosed) {
        escalateBtn.hidden = false;
        escalateBtn.removeAttribute("hidden");
      }
      sendToPractice?.setButtonVisible(true);
    }
  };

  if (canSendQueueItemToPractice(item, opts.hubLinkedSlugs)) {
    sendToPractice = sendToPracticeControls({
      item,
      client,
      card,
      status,
      ...(opts.role === undefined ? {} : { role: opts.role }),
      announce: opts.announce,
      onEscalated: opts.onEscalated,
      onResolveFailed: opts.onResolveFailed,
      onBusyChange: (busy: boolean) => opts.onBusyChange?.(busy),
      ...(opts.onAlreadyAnswered === undefined
        ? {}
        : { onAlreadyAnswered: opts.onAlreadyAnswered }),
      onOpen: () => {
        setEscalateOpen(false);
        laterBtn.hidden = true;
        laterBtn.setAttribute("hidden", "true");
      },
      onClose: () => {
        if (opts.canLater && !disclosed && !escalateOpen) {
          laterBtn.hidden = false;
          laterBtn.removeAttribute("hidden");
        }
      },
    });
  }

  const setDisclosed = (open: boolean): void => {
    // Suggestion-less, recovery, and new-vendor cards stay in account-entry mode —
    // nothing to collapse to, and no Yes one-tap to restore.
    if (informedEntry) {
      disclosed = true;
      return;
    }
    disclosed = open;
    fields.hidden = !open;
    if (open) fields.removeAttribute("hidden");
    else fields.setAttribute("hidden", "true");

    // Default state: Yes primary, Something else, Later.
    // Disclosed: Use this account primary; Yes secondary; Cancel; Dismiss.
    defaultBtn.className = open ? "btn btn-secondary" : "btn btn-primary";
    editBtn.hidden = !open;
    cancelBtn.hidden = !open;
    rejectBtn.hidden = !open;
    discloseBtn.hidden = open;
    if (open) {
      editBtn.removeAttribute("hidden");
      cancelBtn.removeAttribute("hidden");
      rejectBtn.removeAttribute("hidden");
      discloseBtn.setAttribute("hidden", "true");
      // Later hidden in disclosed state (Cancel is the exit).
      laterBtn.hidden = true;
      laterBtn.setAttribute("hidden", "true");
      setEscalateOpen(false);
      if (!isWithOwner(item)) {
        escalateBtn.hidden = true;
        escalateBtn.setAttribute("hidden", "true");
      }
    } else {
      editBtn.setAttribute("hidden", "true");
      cancelBtn.setAttribute("hidden", "true");
      rejectBtn.setAttribute("hidden", "true");
      discloseBtn.removeAttribute("hidden");
      if (opts.canLater) {
        laterBtn.hidden = false;
        laterBtn.removeAttribute("hidden");
      }
      if (!isWithOwner(item) && canEscalate && !escalateOpen) {
        escalateBtn.hidden = false;
        escalateBtn.removeAttribute("hidden");
      }
    }
  };

  const setBusy = (busy: boolean): void => {
    opts.onBusyChange?.(busy);
    defaultBtn.disabled = busy;
    editBtn.disabled = busy;
    if (useAgainBtn !== null) useAgainBtn.disabled = busy;
    rejectBtn.disabled = busy;
    discloseBtn.disabled = busy;
    laterBtn.disabled = busy;
    cancelBtn.disabled = busy;
    escalateBtn.disabled = busy;
    escalateConfirmBtn.disabled = busy;
    escalateCancelBtn.disabled = busy;
    returnToAmBtn.disabled = busy;
    sendToPractice?.setBusy(busy);
    loanUploadBtn.disabled = busy;
    if (busy) {
      loanBookBtn.disabled = true;
    } else {
      paintLoanBook();
      // A failed write unlocks the card; the edit button goes back to what
      // the chooser holds (disabled when nothing valid is chosen).
      syncBookButton();
    }
  };

  type CategorisationResult = {
    kind: "approve" | "edit" | "reject";
    account?: string;
    location?: string;
    makeRule?: boolean;
    makeRuleIgnoredAsSplit?: boolean;
    /**
     * GRANT G15 (rd15 lane B): this answer recorded several parts of the bank
     * line, not one account. Its completion sentence names no account.
     */
    splitAnswer?: boolean;
    ruleScope?: QueueRuleScope;
    matchText?: string;
    ruleKind?: QueueRuleKind;
    vendorClassification?: QueueVendorClassification;
  };

  /** The one finish path for a categorisation card write (dispatch-b §3.1). */
  const finishCategorisation = async (
    outcome: WriteOutcome,
    result: CategorisationResult | null,
  ): Promise<void> => {
    if (outcome.kind === "landed") {
      // Keep every control locked after success: the card is exiting and must
      // never accept a second press (double-submit defense).
      lockCardControls(card);
      if (result === null) return;
      const { node, text } = buildConfirmation(
        result.kind,
        item,
        companies,
        chartSnapshot.selectableAccounts,
        result.account,
        opts.navigateTo,
        result.makeRule === true,
        result.location,
        result.makeRuleIgnoredAsSplit === true,
        result.ruleScope,
        result.matchText,
        result.ruleKind,
        result.vendorClassification,
        result.splitAnswer === true,
      );
      card.classList.add("resolved");
      await opts.onResolved({
        kind: result.kind,
        confirmation: node,
        confirmationText: text,
      });
      return;
    }
    // Rollback: undo exit animation, show error, re-enable, announce.
    card.classList.remove("exiting");
    const error = outcome.error;
    const existing = existingStandingRuleFromError(error);
    const message = paintOutcomeFailure(status, outcome, opts.role);
    if (existing) {
      clear(status);
      status.append(
        renderExistingStandingRule(
          existing,
          chartSnapshot.selectableAccounts,
          accountLabelContext(opts.directory),
        ),
      );
    }
    opts.announce(existing ? STANDING_RULE_ALREADY_EXISTS_HEADLINE : message);
    if (
      !existing &&
      error !== undefined &&
      isAlreadyAnsweredError(error)
    ) {
      // Another person already answered: re-read the list so this card's
      // Book control cannot stay live (round 10, C-6).
      lockCardControls(card);
      setBusy(true);
      opts.onAlreadyAnswered?.();
      return;
    }
    setBusy(false);
    // Round 3, item 2: a standing rule that already exists is not a generic
    // write failure; the person is told the rule is already there.
    opts.onResolveFailed(
      existing ? STANDING_RULE_ALREADY_EXISTS_HEADLINE : message,
    );
  };

  discloseBtn.addEventListener("click", () => {
    setDisclosed(true);
  });
  cancelBtn.addEventListener("click", () => {
    setDisclosed(false);
  });
  laterBtn.addEventListener("click", () => {
    opts.onLater();
  });

  defaultBtn.addEventListener("click", () => {
    void (async () => {
      const makeRule = makeRuleInput.checked;
      if (hubProposal) {
        let result: CategorisationResult | null = null;
        opts.onBusyChange?.(true);
        const outcome = await runWrite({
          key: cardWriteKey(card, ""),
          scope: card,
          gate: () => null,
          send: async () => {
            clear(status);
            card.classList.add("exiting");
            if (!hubLegsRendered) {
              throw new AuthoredRefusal(
                "This hub GL split cannot be approved from this card (stored proposal missing or unparseable).",
              );
            }
            const amNote = noteInput.value.trim();
            await client.resolveQueueItem(item.id, {
              action: "approve",
              // A multi-leg split is not a standing-rule shape. Writer
              // already ignores make_rule on this reason; do not send a
              // token the next run will not honour.
              makeRule: false,
              ...(amNote ? { note: amNote } : {}),
            });
            await sleep(STAGE_MS);
            result = {
              kind: "approve",
              makeRule: false,
              makeRuleIgnoredAsSplit: makeRule,
            };
          },
          stillCurrent: () => card.isConnected,
        });
        await finishCategorisation(outcome, result);
        return;
      }
      if (refuseUntilStandingRuleChoices()) return;
      if (refuseUntilExtraConfirmed()) return;
      if (refuseUntilAttachEffectConfirmed()) return;
      const extras = standingRuleFields();
      const result: CategorisationResult = item.suggestedAccount
        ? {
            kind: "approve",
            account: item.suggestedAccount,
            ...(item.suggestedLocation !== undefined
              ? { location: item.suggestedLocation }
              : {}),
            makeRule,
            ...(makeRule ? extras : {}),
          }
        : {
            kind: "approve",
            makeRule,
            ...(makeRule ? extras : {}),
          };
      opts.onBusyChange?.(true);
      const outcome = await runWrite({
        key: cardWriteKey(card, ""),
        scope: card,
        gate: () => null,
        send: async () => {
          clear(status);
          card.classList.add("exiting");
          await client.resolveQueueItem(item.id, {
            action: "approve",
            // One-tap approve books the txn only; standing rule is opt-in via
            // the disclosed checkbox (never pre-selected).
            makeRule,
            ...(makeRule ? extras : {}),
            ...(noteInput.value.trim() ? { note: noteInput.value.trim() } : {}),
          });
          await sleep(STAGE_MS);
        },
        stillCurrent: () => card.isConnected,
      });
      await finishCategorisation(outcome, result);
    })();
  });

  loanBookBtn.addEventListener("click", () => {
    void (async () => {
      paintLoanBook();
      if (loanBookBtn.disabled) return;
      if (storedLoan === null) return;
      const amNote = noteInput.value.trim();
      const result: CategorisationResult = {
        kind: "approve",
        makeRule: false,
        ...(item.suggestedAccount !== undefined
          ? { account: item.suggestedAccount }
          : {}),
        ...(item.suggestedLocation !== undefined
          ? { location: item.suggestedLocation }
          : {}),
      };
      opts.onBusyChange?.(true);
      const outcome = await runWrite({
        key: cardWriteKey(card, ""),
        scope: card,
        gate: () => null,
        send: async () => {
          clear(status);
          card.classList.add("exiting");
          await client.resolveQueueItem(item.id, {
            action: "approve",
            makeRule: false,
            ...(amNote ? { note: amNote } : {}),
          });
          await sleep(STAGE_MS);
        },
        stillCurrent: () => card.isConnected,
      });
      await finishCategorisation(outcome, result);
    })();
  });

  loanUploadBtn.addEventListener("click", () => {
    void (async () => {
      if (!showLoanUpload) {
        clear(status);
        status.append(
          document.createTextNode(
            loanUploadBlockedProtected
              ? LOAN_STATEMENT_UPLOAD_BLOCKED_COPY
              : "This card cannot take a mortgage statement.",
          ),
        );
        return;
      }
      const file = loanFileInput.files?.[0];
      if (!file) {
        clear(status);
        status.append(document.createTextNode("Choose a PDF first."));
        return;
      }
      if (file.type !== "" && file.type !== "application/pdf") {
        clear(status);
        status.append(document.createTextNode("Upload a PDF."));
        return;
      }
      const txnDate = item.txnDate;
      const period =
        txnDate !== undefined && /^\d{4}-\d{2}-\d{2}$/.test(txnDate)
          ? txnDate.slice(0, 7)
          : "";
      if (!period) {
        clear(status);
        status.append(document.createTextNode("This card has no booking date."));
        return;
      }
      setBusy(true);
      clear(status);
      const outcome = await runWrite({
        key: cardWriteKey(card, "loan-statement-upload"),
        scope: card,
        gate: () => null,
        send: () =>
          client.uploadStatement(
            item.company,
            period,
            loanStatementAccount(item.id),
            file,
          ),
        afterLanded: () => {
          loanFileInput.value = "";
          clear(status);
          status.append(
            document.createTextNode(
              LOAN_STATEMENT_UPLOAD_RECEIVED_COPY,
            ),
          );
        },
        stillCurrent: () => card.isConnected,
      });
      if (outcome.kind !== "landed") {
        const message = paintOutcomeFailure(status, outcome, opts.role);
        opts.announce(message);
      }
      setBusy(false);
    })();
  });

  const runEdit = async (account: string, location: string): Promise<void> => {
    const makeRule = makeRuleInput.checked;
    const extras = standingRuleFields();
    const result: CategorisationResult = {
      kind: "edit",
      account,
      location,
      makeRule,
      ...(makeRule ? extras : {}),
    };
    opts.onBusyChange?.(true);
    const outcome = await runWrite({
      key: cardWriteKey(card, ""),
      scope: card,
      gate: () => null,
      send: async () => {
        clear(status);
        card.classList.add("exiting");
        await client.resolveQueueItem(item.id, {
          action: "edit",
          account,
          ...(location ? { location } : {}),
          makeRule,
          ...(makeRule ? extras : {}),
          ...(noteInput.value.trim() ? { note: noteInput.value.trim() } : {}),
        });
        await sleep(STAGE_MS);
      },
      stillCurrent: () => card.isConnected,
    });
    await finishCategorisation(outcome, result);
  };

  editBtn.addEventListener("click", () => {
    void (async () => {
      const account = accountChooser.getValue();
      const location = locationChooser.getValue() ?? "";
      if (!account) {
        clear(status);
        status.append(
          document.createTextNode(
            accountInput.value.trim()
              ? "Account must be a listed chart name."
              : "Account is required to edit.",
          ),
        );
        return;
      }
      // R4-19: a touched location box must name a place from the list. A blank
      // (or unlisted) value used to be omitted from the body, so the Worker
      // booked at the suggestion the person never chose.
      if (locationTouched && !location) {
        clear(status);
        status.append(document.createTextNode("Choose a place from the list"));
        return;
      }
      // Incomplete booking: location must be decided when it was the gate
      // (or never suggested). A tap that resolves only the account is the
      // half-answer that jammed sync (K12-OP-2).
      // Recovery typed-edit: #713 emptied the location box; the AM states a
      // location before this write. Keys on the values this path is about
      // to POST, not on the card. Use it again does not enter this handler
      // — it posts remembered.account / remembered.location without reading
      // the inputs. Do not ungate this on postEditFromValues: ordinary /
      // new-vendor / hub-proposal typed-edits that clear the prefill are
      // out of scope.
      if (!location && (locationRequired || recoveryCard)) {
        clear(status);
        status.append(
          document.createTextNode(
            locationInput.value.trim()
              ? "Location must be a listed location."
              : unresolvedLocation
                ? `Location is required — ${amVisibleDisplayText(unresolvedLocation)} is not configured.`
                : "Location is required.",
          ),
        );
        return;
      }
      if (refuseUntilStandingRuleChoices()) return;
      if (refuseUntilExtraConfirmed()) return;
      if (refuseUntilAttachEffectConfirmed()) return;
      await runEdit(account, location);
    })().catch(() => undefined);
  });

  if (useAgainBtn !== null && previous !== null) {
    const remembered = previous;
    useAgainBtn.addEventListener("click", () => {
      if (refuseUntilStandingRuleChoices()) return;
      if (refuseUntilExtraConfirmed()) return;
      if (refuseUntilAttachEffectConfirmed()) return;
      void runEdit(remembered.account, remembered.location);
    });
  }

  rejectBtn.addEventListener("click", () => {
    void (async () => {
      const result: CategorisationResult = { kind: "reject" };
      opts.onBusyChange?.(true);
      const outcome = await runWrite({
        key: cardWriteKey(card, ""),
        scope: card,
        gate: () => null,
        send: async () => {
          clear(status);
          card.classList.add("exiting");
          await client.resolveQueueItem(item.id, {
            action: "reject",
            makeRule: false,
            ...(noteInput.value.trim() ? { note: noteInput.value.trim() } : {}),
          });
          await sleep(STAGE_MS);
        },
        stillCurrent: () => card.isConnected,
      });
      await finishCategorisation(outcome, result);
    })();
  });

  escalateBtn.addEventListener("click", () => {
    setDisclosed(false);
    setEscalateOpen(true);
    escalateNoteInput.focus();
  });
  escalateCancelBtn.addEventListener("click", () => {
    setEscalateOpen(false);
  });
  escalateConfirmBtn.addEventListener("click", () => {
    void (async () => {
      opts.onBusyChange?.(true);
      const outcome = await runWrite({
        key: cardWriteKey(card, ""),
        scope: card,
        gate: () => (escalateNoteInput.value.trim() ? null : "Escalation note is required."),
        send: async () => {
          clear(status);
          card.classList.add("exiting");
          await Promise.all([
            client.escalateQueueItem(item.company, item.id, {
              note: escalateNoteInput.value.trim(),
            }),
            sleep(STAGE_MS),
          ]);
        },
        stillCurrent: () => card.isConnected,
        live: status,
      });
      if (outcome.kind === "landed") {
        lockCardControls(card);
        // M2: hand off on the outcome, not in `afterLanded`, so a re-render
        // under the write cannot strand the card.
        card.classList.add("resolved");
        opts.onEscalated({ confirmationText: "Escalated to owner." });
        return;
      }
      if (outcome.kind === "refused" && outcome.status === 0) {
        // M1: the gate refused and nothing was sent, so clear the busy state
        // the card set before the write.
        opts.onBusyChange?.(false);
        setBusy(false);
        return;
      }
      card.classList.remove("exiting");
      if (outcome.error !== undefined && isAlreadyAnsweredError(outcome.error)) {
        lockCardControls(card);
        opts.onAlreadyAnswered?.();
        return;
      }
      const message = paintOutcomeFailure(status, outcome, opts.role);
      opts.announce(message);
      setBusy(false);
      opts.onResolveFailed(message);
    })();
  });

  returnToAmBtn.addEventListener("click", () => {
    void (async () => {
      setBusy(true);
      clear(status);
      const outcome = await returnCardToAm({
        item,
        client,
        scope: card,
        stillCurrent: () => card.isConnected,
      });
      if (outcome.kind !== "landed") {
        if (outcome.error !== undefined && isAlreadyAnsweredError(outcome.error)) {
          lockCardControls(card);
          opts.onAlreadyAnswered?.();
          return;
        }
        const message = paintOutcomeFailure(status, outcome, opts.role);
        opts.announce(message);
        setBusy(false);
        return;
      }
      clear(status);
      status.append(document.createTextNode("Returned to account manager."));
      returnToAmBtn.hidden = true;
      returnToAmBtn.setAttribute("hidden", "true");
      if (escalatedBanner) escalatedBanner.remove();
      escalateBtn.hidden = false;
      escalateBtn.removeAttribute("hidden");
      setBusy(false);
    })().catch(() => undefined);
  });

  void disclosed;

  if (isHoldApproveWithheld(item)) {
    // A blocked hold (a closed month): no account choice can book it, so the
    // card carries no Book control, no account or location chooser and no
    // rule fields. The face already says what happened and the role-aware
    // next step. Same predicate the bulk Book asks (bulkBookDecision). Dismiss
    // is also withheld: it would drop a payment that books once the month is
    // reopened. Later stays, and an account manager can still ask the owner.
    card.setAttribute("data-hold-blocked", "true");
    const blockedChildren: Array<Node | string> = [
      presented.root,
      facts,
      ...consequenceNodes,
      why,
    ];
    if (escalatedBanner) blockedChildren.push(escalatedBanner);
    blockedChildren.push(
      ...(canEscalate ? [escalateFields] : []),
      h("div", { class: "actions" }, [
        laterBtn,
        ...(canEscalate ? [escalateBtn] : []),
        returnToAmBtn,
      ]),
      status,
    );
    card.append(...blockedChildren);
    return card;
  }

  const children: Array<Node | string> = [
    presented.root,
    facts,
    ...consequenceNodes,
    ...(loanConfirmRoot ? [loanConfirmRoot] : []),
    ...(loanUploadRoot ? [loanUploadRoot] : []),
    why,
  ];
  if (escalatedBanner) children.push(escalatedBanner);
  children.push(
    fields,
    ...(canEscalate ? [escalateFields] : []),
    ...(sendToPractice ? [sendToPractice.fields] : []),
    // ONE action row, read left to right as primary, Skip for now, Dismiss,
    // Ask the owner (Y6 brief item 3). The primary is whichever control books
    // this card: "Use this account" on a disclosed card, otherwise Yes / the
    // loan split; `editBtn` first keeps the primary first in both states.
    h("div", { class: "actions", "data-role": "card-action-row" }, [
      editBtn,
      // Affirmative with no account is a trap — omit Approve / Something else
      // entirely when there is no suggestion (Input 3). Hub one-tap is gated
      // on the same parse that renders the legs; Something else… still offers
      // the lump when a suggestion exists. Recovery cards withhold Yes
      // (R24O-1) — the AM must choose from the open fields.
      // LS1 overlay: Book this split is the confirm control; ordinary Yes
      // is withheld so the AM cannot one-tap a lump over a cited split.
      ...(canOneTapHubSplit && suggestionInMembership
        || (offersOrdinaryYesOneTap(item) && suggestionInMembership && !holdCheckFirst)
        ? [defaultBtn]
        : []),
      ...(showLoanOverlay ? [loanBookBtn] : []),
      ...(!informedEntry ? [discloseBtn] : []),
      laterBtn,
      ...(useAgainBtn !== null ? [useAgainBtn] : []),
      cancelBtn,
      rejectBtn,
      ...(canEscalate ? [escalateBtn] : []),
      ...(sendToPractice ? [sendToPractice.button] : []),
      returnToAmBtn,
    ]),
    status,
  );
  card.append(...children);
  return card;
}

/**
 * Sole production writer of the make-rule confirmation sentence.
 * GRANT (16): amount names `$X.XX`; vendor names "any amount". Amount is
 * `formatAmount(queueAmountDecimal(item))` — the same field the card face displays.
 * Owner grant 2026-08-24: quotes the chosen match literal; Kind A names
 * that later matches return to To review and that a missing receipt does not
 * block this booking. Kind B names the account and location when pinned.
 */
export type StandingRuleSentenceOpts = {
  matchText?: string;
  ruleKind?: QueueRuleKind;
  location?: string;
  vendorClassification?: QueueVendorClassification;
};

export function standingRuleOnNextRunSentence(
  item: QueueItem,
  accountLabel: string,
  ruleScope: QueueRuleScope,
  opts: StandingRuleSentenceOpts = {},
): string {
  const match = amVisibleDisplayText(
    opts.matchText?.trim() || ruleMatchLabel(item),
  );
  if (opts.ruleKind === "vendor_identity") {
    const kindBit =
      opts.vendorClassification === "1099_contractor"
        ? "as a 1099 contractor"
        : opts.vendorClassification === "non_1099_vendor"
          ? "as a non-1099 vendor"
          : "as the vendor";
    return (
      `This identifies "${match}" ${kindBit}. ` +
      `Later matches return to ${navLabel("queue")} for account and location. ` +
      `A missing receipt does not block this booking.`
    );
  }
  const loc = opts.location === undefined ? undefined : amVisibleDisplayText(opts.location.trim());
  const accountBit = loc ? `${accountLabel} at ${loc}` : accountLabel;
  // R4-15: the pipeline may stamp `not_created` (a no-op, a superseded
  // approval, a refusal, a failed mint or a wallet overlap), and the console
  // cannot read that outcome, so the sentence hedges instead of promising. The
  // decided sentence is kept verbatim; the rule it would make follows it.
  const lead =
    "A standing rule will be added on the next update if nothing conflicts.";
  if (ruleScope === "vendor") {
    return `${lead} It matches "${match}" at any amount → ${accountBit}.`;
  }
  return `${lead} It matches "${match}" of ${unsignedAmount(item)} → ${accountBit}.`;
}

/**
 * `accountPath` is the stored account (the value the Worker receives); the
 * copy always shows its readable label, never the colon path (Y5 brief item 6).
 */
function recoveryEditConfirmation(
  item: QueueItem,
  companies: AccountLabelContext,
  offeredAccounts: readonly string[],
  accountPath: string,
  location: string | undefined,
  makeRule: boolean,
  ruleScope?: QueueRuleScope,
  matchText?: string,
  ruleKind?: QueueRuleKind,
  vendorClassification?: QueueVendorClassification,
): string {
  const lump = claimsLumpBooking(item);
  const hedge = recoveryEditHedge(item, { lump });
  const displayedPrefillAccount = lump ? "" : (item.suggestedAccount ?? "");
  const displayedPrefillLocation = item.suggestedLocation ?? "";
  const submittedLocation = location ?? "";
  // Replay copy claims a human previous attempt. Gate on provenance, not
  // value equality alone — typing the ARF1 arm's model/rule/receipt pair
  // must not say "the previous attempt".
  const isReplay =
    previousAttemptWasHumanChoice(item) &&
    accountPath === displayedPrefillAccount &&
    submittedLocation === displayedPrefillLocation;
  const shownAccount =
    accountPath === ""
      ? "an account"
      : accountLabelAmong(accountPath, offeredAccounts, companies);
  let lead: string;
  if (lump) {
    const stored = parseStoredHubApplied(item.aiRationale);
    const batch = stored?.sourceBatchId;
    lead = batch
      ? `Booking the whole deposit to ${shownAccount} as a single account — hub batch ${amVisibleDisplayText(batch)} stays pending.`
      : `Booking the whole deposit to ${shownAccount} as a single account — the hub split stays pending.`;
  } else if (isReplay) {
    lead = `Recorded the same account and location as the previous attempt (${shownAccount}${submittedLocation ? ` / ${amVisibleDisplayText(submittedLocation)}` : ""}).`;
  } else {
    lead = `Recorded ${shownAccount}${submittedLocation ? ` / ${amVisibleDisplayText(submittedLocation)}` : ""}.`;
  }
  return makeRule
    ? `${lead} ${hedge} ${standingRuleOnNextRunSentence(item, shownAccount, ruleScope ?? "amount", {
        ...(matchText !== undefined ? { matchText } : {}),
        ...(ruleKind !== undefined ? { ruleKind } : {}),
        ...(location !== undefined ? { location } : {}),
        ...(vendorClassification !== undefined ? { vendorClassification } : {}),
      })}`
    : `${lead} ${hedge}`;
}

function buildConfirmation(
  kind: "approve" | "edit" | "reject",
  item: QueueItem,
  companies: AccountLabelContext,
  offeredAccounts: readonly string[],
  account: string | undefined,
  navigateTo?: (route: RouteName, opts?: { company?: CompanySlug }) => Promise<void>,
  makeRule = false,
  location?: string,
  makeRuleIgnoredAsSplit = false,
  ruleScope?: QueueRuleScope,
  matchText?: string,
  ruleKind?: QueueRuleKind,
  vendorClassification?: QueueVendorClassification,
  splitAnswer = false,
): { node: HTMLElement; text: string } {
  const wrap = h("div", { class: "confirm-line", "data-role": "confirmation" });
  const cardKind = categorisationCardKind(item);
  const recoveryCard = cardKind === "recovery";
  const sentenceOpts: StandingRuleSentenceOpts = {
    ...(matchText !== undefined ? { matchText } : {}),
    ...(ruleKind !== undefined ? { ruleKind } : {}),
    ...(location !== undefined ? { location } : {}),
    ...(vendorClassification !== undefined ? { vendorClassification } : {}),
  };
  const showRulesLink =
    makeRule &&
    Boolean(navigateTo) &&
    ruleKind !== "vendor_identity";
  if (kind === "reject") {
    const text = recoveryCard
      ? RECOVERY_REJECT_CONFIRMATION
      : "Dismissed — no rule created.";
    wrap.append(document.createTextNode(text));
    return { node: wrap, text };
  }
  // GRANT G15 (rd15 lane B): a split answer names no single account and can
  // never carry a standing rule, so it gets its own sentence and nothing that
  // names an account or a rule.
  if (splitAnswer) {
    wrap.append(document.createTextNode(SPLIT_CONFIRMATION));
    return { node: wrap, text: SPLIT_CONFIRMATION };
  }
  // The stored name is what is submitted; the copy always reads as a label
  // (Y5 brief item 6), the same words the picker and the button use.
  const submittedAccount = account ?? item.suggestedAccount ?? "";
  // Among the accounts the chooser offered, so two that read alike are named
  // apart here exactly as they were in the list (re-check E6).
  const accountLabel =
    submittedAccount === ""
      ? "an account"
      : accountLabelAmong(submittedAccount, offeredAccounts, companies);
  if (kind === "approve" && cardKind === "hub-proposal") {
    const text = makeRuleIgnoredAsSplit
      ? "Booking this hub GL split — a multi-leg split cannot become a standing rule."
      : "Booking this hub GL split — no standing rule.";
    wrap.append(document.createTextNode(text));
    return { node: wrap, text };
  }
  if (kind === "approve" && parseStoredLoanStatement(item.aiRationale) !== null) {
    const stored = parseStoredLoanStatement(item.aiRationale);
    const section = stored
      ? loanStatementPaidSectionPhrase(stored)
      : "the paid section";
    const text =
      `Booking this loan payment split from ${section} — no standing rule.`;
    wrap.append(document.createTextNode(text));
    return { node: wrap, text };
  }
  if (kind === "edit" && recoveryCard) {
    const text = recoveryEditConfirmation(
      item,
      companies,
      offeredAccounts,
      submittedAccount,
      location,
      makeRule,
      ruleScope,
      matchText,
      ruleKind,
      vendorClassification,
    );
    wrap.append(document.createTextNode(text));
    if (showRulesLink && navigateTo) {
      wrap.append(document.createTextNode(" "));
      wrap.append(viewRulesButton(item.company, navigateTo));
    }
    return { node: wrap, text };
  }
  if (kind === "edit" && claimsLumpBooking(item)) {
    const stored = parseStoredHubApplied(item.aiRationale);
    const batch = stored?.sourceBatchId;
    const lump = batch
      ? `Booking the whole deposit to ${accountLabel} as a single account — hub batch ${batch} stays pending.`
      : `Booking the whole deposit to ${accountLabel} as a single account — the hub split stays pending.`;
    const text = makeRule
      ? `${lump} ${standingRuleOnNextRunSentence(item, accountLabel, ruleScope ?? "amount", sentenceOpts)}`
      : lump;
    wrap.append(document.createTextNode(text));
    if (showRulesLink && navigateTo) {
      wrap.append(document.createTextNode(" "));
      wrap.append(viewRulesButton(item.company, navigateTo));
    }
    return { node: wrap, text };
  }
  if (!makeRule) {
    // Future tense: a simple Book only APPROVES the card. The booking happens
    // on the next run and can still fail (R15d / R14O-7/8). Past-tense
    // "Booked to" asserted an outcome the write had not achieved.
    // The rule offer is only made where the card offers the checkbox; the
    // hub-split, loan-statement and lump branches above carry no rule.
    const text =
      `Approved. It will be booked to ${accountLabel} on the next run. ` +
      `Tick '${MAKE_RULE_CHECKBOX_COPY}' to book this payee the same way next time.`;
    wrap.append(document.createTextNode(text));
    return { node: wrap, text };
  }
  // Future-tense: rule materializes on a later pipeline run (addendum §2).
  const text = standingRuleOnNextRunSentence(
    item,
    accountLabel,
    ruleScope ?? "amount",
    sentenceOpts,
  );
  wrap.append(document.createTextNode(text));
  if (showRulesLink && navigateTo) {
    wrap.append(document.createTextNode(" "));
    wrap.append(viewRulesButton(item.company, navigateTo));
  }
  return { node: wrap, text };
}

/**
 * Cross-nav to Rules for a company. Shared by the queue confirmation line
 * and the failed-apply card (F7) — one control, threaded; do not duplicate.
 */
function viewRulesButton(
  company: CompanySlug,
  navigateTo: (route: RouteName, opts?: { company?: CompanySlug }) => Promise<void>,
): HTMLButtonElement {
  const link = h(
    "button",
    {
      type: "button",
      class: "btn btn-ghost",
      "data-action": "view-rules",
      "aria-label": "View rules",
    },
    ["View rules"],
  );
  link.addEventListener("click", () => {
    void navigateTo("rules", { company });
  });
  return link;
}

function ruleMatchLabel(item: QueueItem): string {
  const raw = item.merchant?.trim() || item.descriptor?.trim() || "transaction";
  return raw;
}

/**
 * The aria-label for one attach candidate: the same words the row shows.
 *
 * rc-3 row 20 (LOW): the label used to fall back to the Plaid transaction id
 * when the merchant was missing — an internal id is not a word, and a screen
 * reader must hear what the sighted reader sees. The row shows "—" there, so
 * the label says the same.
 */
export function attachCandidateAriaLabel(visibleMerchant: string): string {
  return `Attach ${visibleMerchant}`;
}

/**
 * Today cash garnish. Null snapshots are completeness failures — never flattened
 * into a partial sum (C-20). Credit accounts are excluded from cash (C-10).
 */
export function formatCashLine(
  snapshots: ReadonlyArray<BalancesSnapshot | null>,
): { status: "ok"; text: string } | { status: "error"; message: string } | null {
  if (snapshots.some((s) => s === null)) {
    return {
      status: "error",
      message: "Couldn't check balances across all companies.",
    };
  }
  // B1c-3 (rc-4 B2): the cash total is `cashFigure`'s, so "no cash account"
  // and "no accounts" can never print as "$0.00 across 0 accounts." — the old
  // sum returned 0n when accounts existed but none was a depository account.
  const figures = snapshots.map((snapshot) => cashFigure(snapshot));
  if (figures.some((figure) => figure.kind === "unknown")) return null;
  const cashFigures = figures.filter(
    (figure): figure is { kind: "cash"; cents: bigint } => figure.kind === "cash",
  );
  // Every company is `no_cash_account` (only credit cards) or `no_accounts`
  // (no bank): there is no cash figure to print, so print none. "No bank
  // connected yet." is the page's own line for that state, not this garnish.
  if (cashFigures.length === 0) return null;
  const cents = cashFigures.reduce((total, figure) => total + figure.cents, 0n);
  const cashCount = snapshots
    .flatMap((snapshot) => snapshot?.accounts ?? [])
    .filter((account) => CASH_ACCOUNT_TYPES.has(account.type)).length;
  const noun = cashCount === 1 ? "account" : "accounts";
  return {
    status: "ok",
    text: `${formatCents(cents)} across ${String(cashCount)} ${noun}.`,
  };
}

async function loadCompanySlugs(
  client: AdminClient,
  labels: Map<string, string>,
): Promise<string[]> {
  if (labels.size > 0) return [...labels.keys()];
  try {
    const { companies } = await client.listCompanies();
    return companies.map((c: Company) => c.slug);
  } catch {
    return [];
  }
}

/**
 * Count action-needed standing-rule rows that feed the AM *work* count:
 * - `awaiting_approval` (AM decide) — counts
 * - `failed` — **not counted here** (F6). Reward *headline* honesty (Q3)
 *   derives from what `paintRuleApprovalQueue` actually rendered — do not
 *   re-fold failed into this total (that reintroduces permanent unreachability
 *   of the empty reward while the absorbing failed row remains).
 * - `rejected` — visible on Today but does **not** count (N-B)
 *
 * null = an action-needed fan-out is incomplete (do not claim
 * "Nothing needs you"). Settles each status independently (N-A).
 */
export async function countAwaitingRuleApprovals(
  client: AdminClient,
  labels: Map<string, string> = new Map(),
): Promise<number | null> {
  const slugs = await loadCompanySlugs(client, labels);
  if (slugs.length === 0) {
    try {
      const { companies } = await client.listCompanies();
      if (companies.length === 0) return 0;
    } catch {
      return null;
    }
  }
  const companies =
    slugs.length > 0 ? slugs : (await client.listCompanies()).companies.map((c) => c.slug);
  const results = await mapWithConcurrency(
    companies,
    RULE_APPROVAL_FANOUT,
    async (slug) => {
      // N-A / F6: only awaiting counts as outstanding *work*. Failed presence
      // gates the reward *headline* via paintRuleApprovalQueue's return (Q3).
      const awaiting = await settledRuleList(client, slug, "awaiting_approval");
      if (awaiting === null) {
        return { ok: false as const, n: 0 };
      }
      return { ok: true as const, n: awaiting.length };
    },
  );
  if (results.some((r) => !r.ok)) return null;
  return results.reduce((sum, r) => sum + r.n, 0);
}

/**
 * Paint standing-rule approval / failed / rejected sections on Today.
 * Returns how many failed-apply cards were actually rendered, or null when
 * the failed fan-out is incomplete (Q3 — one artifact for the reward
 * headline; never a second independent ?status=failed sample).
 */
async function paintRuleApprovalQueue(opts: {
  client: AdminClient;
  host: HTMLElement;
  labels: Map<string, string>;
  /** The render's one company directory (B1c-1). */
  directory: face.CompanyDirectory;
  stillCurrent: () => boolean;
  announce: (message: string) => void;
  onChanged: (message?: string) => void;
  navigateTo?: (route: RouteName, opts?: { company?: CompanySlug }) => Promise<void>;
  /** Owner Today: show dismissed failures with who/when (H1 audit trail). */
  showDismissedAudit?: boolean;
  /** R4-04: an account's words for a rule's company (twin-aware). */
  accountWords?: (company: CompanySlug, account: AccountPath) => string;
  /** The reader's role, so a refused rule decision is worded for them. */
  role?: StaffRole | null | undefined;
}): Promise<number | null> {
  const {
    client,
    host,
    labels,
    role,
    stillCurrent,
    announce,
    onChanged,
    navigateTo,
    showDismissedAudit = false,
    accountWords = (_company: CompanySlug, account: AccountPath) =>
      accountLabelAmong(account, [], accountLabelContext(opts.directory)),
  } = opts;
  clear(host);
  host.append(
    h("p", { class: "muted", "data-role": "rule-approval-loading" }, [
      "Checking standing-rule approvals…",
    ]),
  );

  let companies: string[];
  try {
    companies = await loadCompanySlugs(client, labels);
    if (companies.length === 0) {
      const listed = await client.listCompanies();
      companies = listed.companies.map((c) => c.slug);
    }
  } catch {
    if (!stillCurrent()) return null;
    clear(host);
    host.append(
      h("p", {
        class: "banner banner-err",
        role: "status",
        "data-role": "rule-approval-error",
      }, ["Couldn't load companies for rule approvals."]),
    );
    return null;
  }
  if (!stillCurrent()) return null;

  // N-A: each status settles on its own. A 500 on rejected/failed must not
  // discard awaiting_approval (that recreates the High that stranded approve).
  // Do not wrap these three in one try/catch.
  const results = await mapWithConcurrency(
    companies,
    RULE_APPROVAL_FANOUT,
    async (slug) => {
      const [awaiting, failedApply, rejected, dismissed] = await Promise.all([
        settledRuleList(client, slug, "awaiting_approval"),
        settledRuleList(client, slug, "failed"),
        settledRuleList(client, slug, "rejected"),
        showDismissedAudit
          ? settledRuleList(client, slug, "dismissed")
          : Promise.resolve([] as RuleChangeRequest[]),
      ]);
      return { slug, awaiting, failedApply, rejected, dismissed };
    },
  );
  if (!stillCurrent()) return null;

  const awaitingRows: RuleChangeRequest[] = [];
  const failedRows: RuleChangeRequest[] = [];
  const rejectedRows: RuleChangeRequest[] = [];
  const dismissedRows: RuleChangeRequest[] = [];
  const sectionErrors: string[] = [];
  let failedFanoutComplete = true;
  let dismissedFanoutComplete = true;
  // A company name from the one directory: an unknown company reads "this
  // company", never `SYN-RENTALS` (decision 14; rc-3 row 1).
  const labelForSlug = (slug: CompanySlug): string =>
    face.company(slug, opts.directory, "sentence");

  for (const result of results) {
    const label = labelForSlug(result.slug);
    // V-A: per-company independence — one company's null does not discard
    // another company's rows (already true via the loop; keep it that way).
    if (result.awaiting === null) {
      sectionErrors.push(`${label} (awaiting approval)`);
    } else {
      awaitingRows.push(...result.awaiting);
    }
    if (result.failedApply === null) {
      failedFanoutComplete = false;
      sectionErrors.push(`${label} (apply failed)`);
    } else {
      // Owner 2026-08-03: one list, all creators — do not filter by origin.
      failedRows.push(...result.failedApply);
    }
    if (result.rejected === null) {
      sectionErrors.push(`${label} (recently rejected)`);
    } else {
      rejectedRows.push(...result.rejected);
    }
    if (showDismissedAudit) {
      if (result.dismissed === null) {
        dismissedFanoutComplete = false;
        sectionErrors.push(`${label} (dismissed apply failures)`);
      } else {
        dismissedRows.push(...result.dismissed);
      }
    }
  }

  const byCreated = (a: RuleChangeRequest, b: RuleChangeRequest): number => {
    const t = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    if (t !== 0) return t;
    return a.id.localeCompare(b.id);
  };
  awaitingRows.sort(byCreated);
  // Failed: action-needed — show and count every row (no window, no cap).
  // Newest-first by createdAt for stable reading order.
  failedRows.sort((a, b) => {
    const ta = Date.parse(a.amDecidedAt ?? a.createdAt);
    const tb = Date.parse(b.amDecidedAt ?? b.createdAt);
    const sa = Number.isNaN(ta) ? 0 : ta;
    const sb = Number.isNaN(tb) ? 0 : tb;
    if (sb !== sa) return sb - sa;
    return a.id.localeCompare(b.id);
  });
  // Rejected: informational — window + cap only (asymmetry vs failed).
  const recentRejected = selectRecentRejected(rejectedRows);
  dismissedRows.sort((a, b) => {
    const ta = Date.parse(a.dismissedAt ?? a.createdAt);
    const tb = Date.parse(b.dismissedAt ?? b.createdAt);
    const sa = Number.isNaN(ta) ? 0 : ta;
    const sb = Number.isNaN(tb) ? 0 : tb;
    if (sb !== sa) return sb - sa;
    return a.id.localeCompare(b.id);
  });

  clear(host);
  if (sectionErrors.length > 0) {
    host.append(
      h("p", {
        class: "banner banner-err",
        role: "status",
        "data-role": "rule-approval-partial",
      }, [
        `Couldn't check rule approvals for ${sectionErrors.join(", ")}.`,
      ]),
    );
  }
  if (
    awaitingRows.length === 0 &&
    failedRows.length === 0 &&
    recentRejected.shown.length === 0 &&
    dismissedRows.length === 0
  ) {
    // Empty discovery is silence — not a second "all caught up" headline.
    // Q3: still report painted failed count (0) or null if fan-out incomplete.
    if (!failedFanoutComplete) return null;
    if (showDismissedAudit && !dismissedFanoutComplete) return null;
    return 0;
  }

  const labelFor = labelForSlug;
  const windowDays = String(RECENT_REJECTED_WINDOW_MS / (24 * 60 * 60 * 1000));
  const limitStr = String(RECENT_REJECTED_LIMIT);

  if (awaitingRows.length > 0) {
    host.append(
      h("h2", {
        class: "rule-approval-title",
        "data-role": "rule-approval-title",
      }, ["Standing rules awaiting approval"]),
    );
    host.append(
      h("p", { class: "muted", "data-role": "rule-approval-blurb" }, [
        "Hub answers already booked those transactions. Approve only if the same categorisation should apply to future matches.",
      ]),
    );
    const list = h("ul", {
      class: "rule-approval-list",
      "data-role": "rule-approval-list",
    });
    for (const row of awaitingRows) {
      list.append(
        buildRuleApprovalCard({
          row,
          companyLabel: labelFor(row.company),
          directory: opts.directory,
          accountWords: (account) => accountWords(row.company, account),
          client,
          announce,
          onChanged,
          stillCurrent,
          role,
        }),
      );
    }
    host.append(list);
  }

  if (failedRows.length > 0) {
    const n = failedRows.length;
    const title =
      n === 1
        ? "1 approved rule didn't apply"
        : `${String(n)} approved rules didn't apply`;
    host.append(
      h("h2", {
        class: "rule-approval-title",
        "data-role": "rule-apply-failed-title",
      }, [title]),
    );
    // F3/F7 + feat-failed-rule-dismiss: say only what the product does.
    // `failed` apply is never retried automatically (pipeline fetches pending
    // only). Recovery is re-request / remake; then dismiss so the row leaves
    // the list. Dismiss means the human handled it — not that the rule exists.
    host.append(
      // R4-21: one plain sentence on the face. The bookkeeping internals
      // (the rules file, the pipeline, who may re-submit what) are technical
      // detail and live behind Show details.
      h("p", { class: "muted", "data-role": "rule-apply-failed-blurb" }, [
        "This rule was approved but could not be applied, so it is not in force. Once you have dealt with it, dismiss the row.",
      ]),
      h(
        "details",
        { class: "why-disclosure", "data-role": "rule-apply-failed-detail" },
        [
          showDetailsSummary(),
          h("p", { class: "muted" }, [
            "A failed apply is never retried automatically. A rule that came from the hub can be remade by categorising the next matching transaction and opting in. An owner-made rule has to be re-submitted from Rules by an owner; account managers cannot add or turn off standing rules. Each row below shows the apply reason and who requested it. Dismiss records who and when and keeps the owner's audit list; it does not create or fix the rule.",
          ]),
        ],
      ),
    );
    const failedList = h("ul", {
      class: "rule-approval-list",
      "data-role": "rule-apply-failed-list",
    });
    for (const row of failedRows) {
      failedList.append(
        buildFailedApplyCard({
          row,
          companyLabel: labelFor(row.company),
          directory: opts.directory,
          accountWords: (account) => accountWords(row.company, account),
          client,
          announce,
          onChanged,
          stillCurrent,
          role,
          ...(navigateTo ? { navigateTo } : {}),
        }),
      );
    }
    host.append(failedList);
  }

  if (recentRejected.shown.length > 0) {
    // N-F: heading uses totalInWindow (pre-slice), not shown.length.
    const n = recentRejected.totalInWindow;
    const title =
      n === 1
        ? "1 recently rejected standing rule"
        : `${String(n)} recently rejected standing rules`;
    host.append(
      h("h2", {
        class: "rule-approval-title",
        "data-role": "rule-rejected-title",
      }, [title]),
    );
    // F8: the cap sentence is live state, not a fixed string — after Show all
    // it must stop claiming rows are hidden and stop pointing at a control
    // that has removed itself. Rebuilt on every paint.
    const rejectedBlurb = h("p", {
      class: "muted",
      "data-role": "rule-rejected-blurb",
    });
    const paintRejectedBlurb = (showingAll: boolean): void => {
      const parts = [
        `Rejected in the last ${windowDays} days (informational — needs no action on this screen). These will not become standing rules.`,
      ];
      if (recentRejected.capFired && !showingAll) {
        parts.push(
          `Showing the ${limitStr} most recent of ${String(n)} (cap fired) — use Show all to reach the rest.`,
        );
      } else if (recentRejected.capFired) {
        parts.push(`Showing all ${String(n)}.`);
      } else {
        parts.push(`Up to ${limitStr} shown.`);
      }
      parts.push(
        "Reject cannot be undone. If that was a mistake, categorise the next matching transaction and opt in to make a rule again.",
      );
      clear(rejectedBlurb);
      rejectedBlurb.append(document.createTextNode(parts.join(" ")));
    };
    paintRejectedBlurb(false);
    host.append(rejectedBlurb);
    const rejectedList = h("ul", {
      class: "rule-approval-list",
      "data-role": "rule-rejected-list",
    });
    const paintRejectedCards = (rows: RuleChangeRequest[]): void => {
      clear(rejectedList);
      for (const row of rows) {
        rejectedList.append(
          buildRejectedRuleCard({
            row,
            companyLabel: labelFor(row.company),
            directory: opts.directory,
            accountWords: (account) => accountWords(row.company, account),
          }),
        );
      }
    };
    paintRejectedCards(recentRejected.shown);
    host.append(rejectedList);
    // F4: cap disclosed AND reachable — Show all reveals the hidden rows.
    if (recentRejected.capFired) {
      const showAllBtn = h(
        "button",
        {
          type: "button",
          class: "btn",
          "data-action": "show-all-rejected",
          "data-role": "rule-rejected-show-all",
        },
        [`Show all ${String(n)}`],
      );
      showAllBtn.addEventListener("click", () => {
        paintRejectedCards(recentRejected.inWindow);
        paintRejectedBlurb(true); // F8: disclosure must track what is shown
        showAllBtn.hidden = true;
        showAllBtn.setAttribute("hidden", "true");
      });
      host.append(showAllBtn);
    }
  }
  if (showDismissedAudit && dismissedRows.length > 0) {
    const n = dismissedRows.length;
    const title =
      n === 1
        ? "1 dismissed apply failure (audit)"
        : `${String(n)} dismissed apply failures (audit)`;
    host.append(
      h("h2", {
        class: "rule-approval-title",
        "data-role": "rule-apply-dismissed-title",
      }, [title]),
    );
    host.append(
      h("p", { class: "muted", "data-role": "rule-apply-dismissed-blurb" }, [
        "Handled failures leave the action list but stay visible here with who dismissed and when. Dismiss does not create or fix the standing rule.",
      ]),
    );
    const dismissedList = h("ul", {
      class: "rule-approval-list",
      "data-role": "rule-apply-dismissed-list",
    });
    for (const row of dismissedRows) {
      dismissedList.append(
        buildDismissedApplyCard({
          row,
          companyLabel: labelFor(row.company),
          directory: opts.directory,
          accountWords: (account) => accountWords(row.company, account),
        }),
      );
    }
    host.append(dismissedList);
  }

  // L1: headline derives from cards actually painted here — not a second sample.
  return failedFanoutComplete ? failedRows.length : null;
}

/**
 * What kind of rule request this is, in words (rd r12 F1, R4). The values are
 * the response contract's `originKind` on rule change requests (an owner's
 * append or deactivate, and a rule minted from a hub answer). An origin this
 * table does not know reads the general words, never the raw value.
 */
const RULE_ORIGIN_WORDS: Readonly<Record<string, string>> = {
  admin_append: "A new rule",
  admin_deactivate: "Turning a rule off",
  hub_question: "A new rule from a hub answer",
};

function ruleOriginWords(origin: string): string {
  return Object.prototype.hasOwnProperty.call(RULE_ORIGIN_WORDS, origin)
    ? (RULE_ORIGIN_WORDS[origin] ?? "A change to a rule")
    : "A change to a rule";
}

/**
 * The face facts every rule request card shares: what kind of request it is,
 * in words, and who asked when the stored identity is an email address. A
 * sign-in id is not shown on the face (`face.person`); it is in Show details.
 */
function appendRuleRequestFacts(facts: HTMLElement, row: RuleChangeRequest): void {
  const requester = face.person(row.requestedBy);
  if (requester !== null) facts.append(fact("Requested by", requester));
  if (row.originKind) facts.append(fact("Request", ruleOriginWords(row.originKind)));
}

/** The raw request values, for support only, behind "Show details". */
function ruleRequestDetails(row: RuleChangeRequest): HTMLElement {
  const raw: DocumentFragment[] = [fact("Requested by", safeText(row.requestedBy))];
  if (row.originKind) raw.push(fact("Origin", safeText(row.originKind)));
  if (row.dismissedBy) raw.push(fact("Dismissed by", safeText(row.dismissedBy)));
  return h("details", { class: "why-disclosure", "data-role": "rule-request-details" }, [
    showDetailsSummary(),
    h("dl", { class: "facts" }, raw),
  ]);
}

/**
 * Failed-apply card — reader with creator attribution plus a confirm-gated
 * dismiss (feat-failed-rule-dismiss). Dismiss means "I have handled this";
 * it does not create or fix the standing rule. Not one-click and not
 * pre-selected (anchoring corollary). Escalate-to-Owner for failed rule
 * rows still needs escalated_* on rule_change_requests (outside this grant).
 */
function buildFailedApplyCard(opts: {
  row: RuleChangeRequest;
  companyLabel: string;
  /** The render's one company directory, for the account words (rd13 L1). */
  directory: face.CompanyDirectory;
  client: AdminClient;
  announce: (message: string) => void;
  onChanged: (message?: string) => void;
  stillCurrent: () => boolean;
  navigateTo?: (route: RouteName, opts?: { company?: CompanySlug }) => Promise<void>;
  accountWords?: (account: string) => string;
  role?: StaffRole | null | undefined;
}): HTMLElement {
  const { row, companyLabel, client, announce, onChanged, stillCurrent, navigateTo, role } =
    opts;
  const accountWords =
    opts.accountWords ??
    ((account: string) =>
      accountLabelAmong(account, [], accountLabelContext(opts.directory)));
  const card = h("li", {
    class: "rule-approval-card",
    "data-role": "rule-apply-failed-card",
    "data-request-id": row.id,
    "data-company": row.company,
    "data-status": "failed",
    "data-origin-kind": row.originKind ?? "",
  });
  const proposed = proposedRuleNodes(row.payloadJson, accountWords);
  const facts = h("dl", { class: "facts" });
  facts.append(fact("Company", companyLabel));
  appendRuleRequestFacts(facts, row);
  facts.append(fact("Proposed", safeText(proposed.sentence)));
  facts.append(
    fact(
      "Apply failed",
      safeText(row.lastError?.trim() || "No reason recorded"),
    ),
  );
  card.append(facts);
  if (proposed.node !== null) card.append(proposed.node);
  card.append(ruleRequestDetails(row));

  // Confirm-gated dismiss — not the default / primary action. Opening the
  // confirm is required; Cancel returns to the card. Copy must not imply the
  // system fixed the rule.
  const dismissBtn = h(
    "button",
    {
      type: "button",
      class: "btn btn-ghost",
      "data-action": "dismiss-failed-rule",
      "aria-label": "Dismiss from this list after you have handled it",
    },
    ["Dismiss from list"],
  );
  const dismissConfirmBtn = h(
    "button",
    {
      type: "button",
      class: "btn",
      "data-action": "dismiss-failed-rule-confirm",
      "aria-label": "Confirm dismiss — I have handled this",
      hidden: "true",
    },
    ["Confirm dismiss"],
  );
  const dismissCancelBtn = h(
    "button",
    {
      type: "button",
      class: "btn btn-ghost",
      "data-action": "dismiss-failed-rule-cancel",
      "aria-label": "Cancel dismiss",
      hidden: "true",
    },
    ["Cancel"],
  );
  const dismissConfirmCopy = h("p", {
    class: "confirm-copy muted",
    "data-role": "dismiss-failed-confirm-copy",
    hidden: "true",
  }, [
    "I have handled this — remove it from the list. This does not create or fix the standing rule.",
  ]);
  const dismissFields = h("div", {
    class: "fields",
    "data-role": "dismiss-failed-fields",
    hidden: "true",
  }, [
    dismissConfirmCopy,
    h("div", { class: "actions" }, [dismissConfirmBtn, dismissCancelBtn]),
  ]);
  const status = h("p", {
    class: "muted",
    "data-role": "dismiss-failed-status",
    hidden: "true",
  });

  let confirmOpen = false;
  const setConfirmOpen = (open: boolean): void => {
    confirmOpen = open;
    dismissFields.hidden = !open;
    dismissConfirmCopy.hidden = !open;
    if (open) {
      dismissFields.removeAttribute("hidden");
      dismissConfirmCopy.removeAttribute("hidden");
      dismissConfirmBtn.hidden = false;
      dismissCancelBtn.hidden = false;
      dismissConfirmBtn.removeAttribute("hidden");
      dismissCancelBtn.removeAttribute("hidden");
      dismissBtn.hidden = true;
      dismissBtn.setAttribute("hidden", "true");
    } else {
      dismissFields.setAttribute("hidden", "true");
      dismissConfirmCopy.setAttribute("hidden", "true");
      dismissConfirmBtn.hidden = true;
      dismissCancelBtn.hidden = true;
      dismissConfirmBtn.setAttribute("hidden", "true");
      dismissCancelBtn.setAttribute("hidden", "true");
      dismissBtn.hidden = false;
      dismissBtn.removeAttribute("hidden");
    }
  };
  // Start closed — dismiss must never be the pre-selected / one-click path.
  setConfirmOpen(false);

  const lock = (busy: boolean): void => {
    dismissBtn.disabled = busy;
    dismissConfirmBtn.disabled = busy;
    dismissCancelBtn.disabled = busy;
  };

  dismissBtn.addEventListener("click", () => {
    if (dismissBtn.disabled) return;
    setConfirmOpen(true);
  });
  dismissCancelBtn.addEventListener("click", () => {
    if (dismissCancelBtn.disabled) return;
    setConfirmOpen(false);
  });
  dismissConfirmBtn.addEventListener("click", () => {
    if (dismissConfirmBtn.disabled || !confirmOpen) return;
    lock(true);
    void (async () => {
      const outcome = await runWrite({
        key: cardWriteKey(card, "dismiss-rule-request"),
        scope: card,
        gate: () => null,
        send: () => client.dismissRuleChangeRequest(row.company, row.id),
        stillCurrent,
      });
      if (outcome.kind === "landed") {
        lockCardControls(card);
        if (!stillCurrent()) return;
        // Pass message through onChanged — announce-then-re-render destroys live.
        onChanged("Dismissed from the list — the standing rule was not changed.");
        return;
      }
      if (!stillCurrent()) return;
      clear(status);
      status.hidden = false;
      status.removeAttribute("hidden");
      const message = paintOutcomeFailure(status, outcome, role);
      announce(message);
      if (outcome.error instanceof AdminApiError && outcome.error.status === 409) {
        // The request moved under us: say the shared conflict sentence and
        // re-read; never leave a live decision control.
        pendingTodayAnnounce = message;
        onChanged();
        return;
      }
      lock(false);
    })();
  });

  const actions = h("div", { class: "rule-approval-actions" }, [dismissBtn, dismissFields]);
  // View rules only when the caller can reach Rules (Owner). AM is denied that
  // route — a dead control beside dismiss is the anchoring hazard (M2). Added
  // first, after the row exists, so the branded company reaches the one control
  // builder directly.
  if (navigateTo !== undefined) {
    actions.prepend(viewRulesButton(row.company, navigateTo));
  }
  card.append(actions, status);
  return card;
}

/**
 * Owner audit card for a dismissed apply failure — who/when visible (H1).
 * Read-only; the action list no longer shows this row.
 */
function buildDismissedApplyCard(opts: {
  row: RuleChangeRequest;
  companyLabel: string;
  /** The render's one company directory, for the account words (rd13 L1). */
  directory: face.CompanyDirectory;
  accountWords?: (account: string) => string;
}): HTMLElement {
  const { row, companyLabel } = opts;
  const accountWords =
    opts.accountWords ??
    ((account: string) =>
      accountLabelAmong(account, [], accountLabelContext(opts.directory)));
  const card = h("li", {
    class: "rule-approval-card",
    "data-role": "rule-apply-dismissed-card",
    "data-request-id": row.id,
    "data-company": row.company,
    "data-status": "dismissed",
  });
  const proposed = proposedRuleNodes(row.payloadJson, accountWords);
  const facts = h("dl", { class: "facts" });
  facts.append(fact("Company", companyLabel));
  appendRuleRequestFacts(facts, row);
  const dismissedBy = face.person(row.dismissedBy);
  if (dismissedBy !== null) facts.append(fact("Dismissed by", dismissedBy));
  const dismissedAt = dateText(row.dismissedAt);
  if (dismissedAt !== "") facts.append(fact("Dismissed", dismissedAt));
  facts.append(fact("Proposed", safeText(proposed.sentence)));
  facts.append(
    fact(
      "Apply failed",
      safeText(row.lastError?.trim() || "No reason recorded"),
    ),
  );
  card.append(facts);
  if (proposed.node !== null) card.append(proposed.node);
  card.append(ruleRequestDetails(row));
  return card;
}

/**
 * Recently-rejected card — reader only. Makes a misclick visible without
 * adding an undo/approve affordance. Does not count as outstanding work (N-B).
 */
function buildRejectedRuleCard(opts: {
  row: RuleChangeRequest;
  companyLabel: string;
  /** The render's one company directory, for the account words (rd13 L1). */
  directory: face.CompanyDirectory;
  accountWords?: (account: string) => string;
}): HTMLElement {
  const { row, companyLabel } = opts;
  const accountWords =
    opts.accountWords ??
    ((account: string) =>
      accountLabelAmong(account, [], accountLabelContext(opts.directory)));
  const card = h("li", {
    class: "rule-approval-card",
    "data-role": "rule-rejected-card",
    "data-request-id": row.id,
    "data-company": row.company,
    "data-status": "rejected",
  });
  const proposed = proposedRuleNodes(row.payloadJson, accountWords);
  const facts = h("dl", { class: "facts" });
  facts.append(fact("Company", companyLabel));
  appendRuleRequestFacts(facts, row);
  facts.append(fact("Proposed", safeText(proposed.sentence)));
  facts.append(
    fact(
      "Rejected",
      safeText(row.lastError?.trim() || "No reason recorded"),
    ),
  );
  card.append(facts);
  if (proposed.node !== null) card.append(proposed.node);
  card.append(ruleRequestDetails(row));
  return card;
}

function buildRuleApprovalCard(opts: {
  row: RuleChangeRequest;
  companyLabel: string;
  /** The render's one company directory, for the account words (rd13 L1). */
  directory: face.CompanyDirectory;
  client: AdminClient;
  announce: (message: string) => void;
  onChanged: (message?: string) => void;
  stillCurrent: () => boolean;
  accountWords?: (account: string) => string;
  role?: StaffRole | null | undefined;
}): HTMLElement {
  const { row, companyLabel, client, announce, onChanged, stillCurrent, role } = opts;
  const accountWords =
    opts.accountWords ??
    ((account: string) =>
      accountLabelAmong(account, [], accountLabelContext(opts.directory)));
  const card = h("li", {
    class: "rule-approval-card",
    "data-role": "rule-approval-card",
    "data-request-id": row.id,
    "data-company": row.company,
  });

  const proposed = proposedRuleNodes(row.payloadJson, accountWords);
  const txn = row.justifyingTransaction;
  const facts = h("dl", { class: "facts" });
  facts.append(fact("Company", companyLabel));
  appendRuleRequestFacts(facts, row);
  facts.append(fact("Proposed", safeText(proposed.sentence)));
  if (txn) {
    const bits = [
      txn.txnDate ? dateText(txn.txnDate) : null,
      txn.amount ? formatAmount(txn.amount) : null,
      txn.direction ?? null,
    ].filter((b): b is string => typeof b === "string" && b.length > 0);
    if (bits.length > 0) {
      facts.append(fact("From transaction", safeText(bits.join(" · "))));
    }
  }
  card.append(facts);
  if (proposed.node !== null) card.append(proposed.node);
  card.append(ruleRequestDetails(row));

  const noteInput = h("input", {
    type: "text",
    class: "rule-approval-note",
    "data-role": "rule-approval-note",
    maxlength: "500",
    placeholder: "Rejection reason (optional — kept when provided)",
    "aria-label": "Optional rejection reason",
  });

  const approveBtn = h(
    "button",
    {
      type: "button",
      class: "btn btn-primary",
      "data-action": "approve-rule-request",
    },
    ["Approve standing rule"],
  );
  const rejectBtn = h(
    "button",
    {
      type: "button",
      class: "btn",
      "data-action": "reject-rule-request",
    },
    ["Reject"],
  );
  const status = h("p", {
    class: "muted",
    "data-role": "rule-approval-status",
    hidden: "true",
  });

  approveBtn.addEventListener("click", () => {
    if (approveBtn.disabled) return;
    void (async () => {
      const outcome = await runWrite({
        key: cardWriteKey(card, "approve-rule-request"),
        scope: card,
        gate: () => null,
        send: () =>
          client.decideRuleChangeRequest(row.company, row.id, {
            decision: "approve",
          }),
        stillCurrent,
      });
      if (outcome.kind === "landed") {
        lockCardControls(card);
        if (!stillCurrent()) return;
        onChanged("Standing rule approved. It applies to new matches from the next update.");
        return;
      }
      if (!stillCurrent()) return;
      clear(status);
      status.hidden = false;
      status.removeAttribute("hidden");
      const message = paintOutcomeFailure(status, outcome, role);
      announce(message);
      if (outcome.error instanceof AdminApiError && outcome.error.status === 409) {
        // The re-read replaces this card; keep the sentence for the new page.
        pendingTodayAnnounce = message;
        onChanged();
      }
    })();
  });

  // F4 / anchoring: reject is one click — same cost as approve. Note is
  // optional (API already allows it); when provided it is retained as lastError.
  rejectBtn.addEventListener("click", () => {
    if (rejectBtn.disabled) return;
    const note = noteInput.value.trim();
    void (async () => {
      const outcome = await runWrite({
        key: cardWriteKey(card, "reject-rule-request"),
        scope: card,
        gate: () => null,
        send: () =>
          client.decideRuleChangeRequest(row.company, row.id, {
            decision: "reject",
            ...(note.length > 0 ? { note } : {}),
          }),
        stillCurrent,
      });
      if (outcome.kind === "landed") {
        lockCardControls(card);
        if (!stillCurrent()) return;
        onChanged(
          note.length > 0
            ? "Standing rule rejected — reason retained."
            : "Standing rule rejected.",
        );
        return;
      }
      if (!stillCurrent()) return;
      clear(status);
      status.hidden = false;
      status.removeAttribute("hidden");
      const message = paintOutcomeFailure(status, outcome, role);
      announce(message);
      if (outcome.error instanceof AdminApiError && outcome.error.status === 409) {
        pendingTodayAnnounce = message;
        onChanged();
      }
    })();
  });

  card.append(
    h("div", { class: "rule-approval-actions" }, [
      noteInput,
      approveBtn,
      rejectBtn,
    ]),
    status,
  );
  return card;
}

/**
 * R4-04. A proposed standing rule, in words a person can check before
 * approving it: who it pays, the exact amount, the place, which card, and
 * which account it books to. The pattern itself is not a face fact — it goes
 * behind "Show details", because `(?i)MENARDS` is not what the owner approved
 * and the account it named was an unreadable colon path.
 */
type ProposedRuleParts = {
  /** The plain sentence on the card face. */
  sentence: string;
  /** The stored pattern, shown only behind Show details. */
  pattern: string | null;
  /** Everything else the payload binds, for the same disclosure. */
  technical: string[];
};

/**
 * A stored pattern, as words a person reads. Never returns `(?i)`.
 *
 * A rule pattern is a regex, but most hub-minted ones are a payee with its
 * punctuation escaped (`SQ \*COFFEE\.CO`). Printing that raw on the face put a
 * backslash in front of a coffee shop's name (round-2 review, MUST-FIX 4).
 *
 * - The inline flags are stripped.
 * - A pattern built only from literals and `\X` escapes is UNESCAPED for the
 *   face, so it reads as the payee it is.
 * - Anything carrying real regex syntax (`|`, `.*`, character classes, a
 *   `\d`/`\w`/`\b` class, a backreference...) is not paraphrased: the face says
 *   "text matching a pattern" and the regex stays behind Show details, where a
 *   reader can still see exactly what approves.
 * - A truncated pattern must not end in a lone backslash: the Worker cuts
 *   minted patterns at 180 characters (`hub_questions.ts` `slice(0, 180)`),
 *   which can split a `\X` pair, and "SQ \*COFFEE.CO\" is not words.
 */
const REGEX_UNESCAPED_SYNTAX = /[.*+?^${}()|[\]\\]/;
/** A backslash before a letter or digit is never punctuation: it is a class
 * (`\d`, `\w`, `\s`), an anchor (`\b`), a control escape (`\n`) or a
 * backreference. Anything else (`\.`, `\*`, `\/`) escapes one literal. */
const REGEX_CLASS_ESCAPES = /[A-Za-z0-9]/;

function rulePayeeWords(pattern: string): string | null {
  const cleaned = pattern.replace(/^\(\?[a-zA-Z]*\)/, "").trim();
  let words = "";
  for (let index = 0; index < cleaned.length; index += 1) {
    const char = cleaned[index] ?? "";
    if (char === "\\") {
      const next = cleaned[index + 1];
      // A trailing lone backslash is the 180-character cut: drop it.
      if (next === undefined) break;
      // `\d`, `\w`, `\b`, `\1` and friends are regex, not punctuation.
      if (REGEX_CLASS_ESCAPES.test(next)) return null;
      words += next;
      index += 1;
      continue;
    }
    if (REGEX_UNESCAPED_SYNTAX.test(char)) return null;
    words += char;
  }
  const trimmed = words.trim();
  return trimmed.length === 0 ? null : trimmed;
}

function proposedRuleParts(
  payloadJson: string | undefined,
  accountWords: (account: string) => string,
): ProposedRuleParts {
  const fallback: ProposedRuleParts = {
    sentence: "Standing rule proposal",
    pattern: null,
    technical: [],
  };
  if (!payloadJson) return fallback;
  let payload: {
    rule?: {
      // R12 A2-3/M2: the payload's match is the portal's `Rule["match"]`, so
      // the G2 source guard resolves this read to the same guarded symbol as
      // every other rule amount (a raw `moneyText(match.amount)` goes red).
      match?: Rule["match"];
      // `post` stays optional per field: this is untrusted JSON, and a
      // payload may omit either even though a stored `Rule` requires both.
      post?: { account?: string; location?: string };
    };
  };
  try {
    payload = JSON.parse(payloadJson) as typeof payload;
  } catch {
    return fallback;
  }
  const match = payload.rule?.match;
  const post = payload.rule?.post;
  const account = post?.account?.trim();
  if (account === undefined || account === "") return fallback;
  // Every value here is stored staff or bank text (untrusted): it reaches a
  // sentence only through the shared strip (rule 20).
  const pattern = amVisibleDisplayText(
    match?.name_regex?.trim() || match?.merchant_regex?.trim() || "",
  ).trim();
  const payee = rulePayeeWords(pattern);
  // R12 A2-3: a rule pin is read through the one codec, never `moneyText` on
  // the raw spec (`moneyText("-25.00")` reads as money out).
  const money = ruleAmountWords(match?.amount?.trim(), moneyText);
  const location = amVisibleDisplayText(post?.location?.trim() ?? "").trim();
  const last4 = amVisibleDisplayText(match?.account_last4?.trim() ?? "").trim();
  const bits: string[] = [];
  // Round-3 review, item 4: a rule that binds no payee at all — no
  // `name_regex`, no `merchant_regex`, an empty pattern, or nothing but the
  // `(?i)` flag — applies to any payee. It is not "text matching a pattern":
  // there is no pattern to match. That wording is kept for a real regex only.
  const anyPayee = pattern.replace(/^\(\?[a-zA-Z]*\)/, "").trim() === "";
  if (anyPayee) {
    // Reads as one clause: "Any payee, $25.00 at hq with card ending 1234."
    bits.push(`Any payee, ${money}`);
  } else if (payee === null) {
    // A real regex is not paraphrased into a payee: the face says what it is,
    // and the pattern itself stays behind Show details.
    bits.push("Pays text matching a pattern");
    bits.push(money);
  } else {
    bits.push(`Pays "${amVisibleDisplayText(payee)}"`);
    bits.push(money);
  }
  if (location !== "") {
    bits.push(`at ${amVisibleDisplayText(location)}`);
  }
  if (last4 !== "") {
    bits.push(`with card ending ${amVisibleDisplayText(last4)}`);
  }
  const sentence = `${bits.join(" ")}. Books to ${accountWords(account)}.`;
  const technical: string[] = [];
  if (pattern !== "") technical.push(`Match pattern: ${pattern}`);
  if (match?.merchant_regex !== undefined && match.merchant_regex.trim() !== "") {
    technical.push(
      `Merchant pattern: ${amVisibleDisplayText(match.merchant_regex.trim())}`,
    );
  }
  if (last4 !== "") technical.push(`Card: ending ${last4}`);
  technical.push(`Posts to: ${account}`);
  if (location !== "") {
    technical.push(`Place: ${amVisibleDisplayText(location)}`);
  }
  return { sentence, pattern: pattern === "" ? null : pattern, technical };
}

/** The card-face "+ Show details" pair for a proposed rule (R4-04). */
function proposedRuleNodes(
  payloadJson: string | undefined,
  accountWords: (account: string) => string,
): { sentence: string; node: HTMLElement | null } {
  const parts = proposedRuleParts(payloadJson, accountWords);
  if (parts.technical.length === 0) return { sentence: parts.sentence, node: null };
  const node = h("details", { class: "why-disclosure", "data-role": "proposed-rule-details" }, [
    showDetailsSummary(),
    h(
      "dl",
      { class: "facts" },
      parts.technical.map((line) => fact("Rule", line)),
    ),
  ]);
  return { sentence: parts.sentence, node };
}

/** The one-sentence form, for a caller that needs no disclosure node. */
export function summarizeProposedRule(
  payloadJson: string | undefined,
  companies: AccountLabelContext,
  accountWords: (account: string) => string = (account) =>
    accountLabelAmong(account, [], companies),
): string {
  return proposedRuleParts(payloadJson, accountWords).sentence;
}

function appendReceiptCardNotAttachedCopy(
  facts: HTMLElement,
  item: QueueItem,
  directory: face.CompanyDirectory,
): void {
  facts.append(
    h("p", { "data-role": "receipt-card-not-attached-copy" }, [
      RECEIPT_CARD_NOT_ATTACHED_COPY,
    ]),
    fact("Card attached to receipt property", "no"),
    fact(
      "Matched company",
      face.company(item.receiptContext?.matchedCompany ?? item.company, directory, "sentence"),
    ),
  );
  if (item.receiptContext) {
    facts.append(fact("Receipt property", safeText(item.receiptContext.property)));
  }
  if (item.suggestedAccount) {
    const where = item.suggestedLocation
      ? ` / ${amVisibleDisplayText(item.suggestedLocation)}`
      : "";
    facts.append(
      fact("Pre-filled pair", safeText(`${item.suggestedAccount}${where}`)),
    );
  }
}

function fact(label: string, value: string): DocumentFragment {
  const frag = document.createDocumentFragment();
  frag.append(
    h("dt", {}, [label]),
    h("dd", {}, [amVisibleDisplayText(value)]),
  );
  return frag;
}

/**
 * Y5 brief item 5. The codes, subject ids and raw payloads an account manager
 * may need, behind a nested "Technical" disclosure. Nothing is rendered for
 * the owner: what the owner is shown is the plain sentence on the face and the
 * plain facts (date, amount, where it came from).
 */
function technicalDisclosure(
  role: StaffRole,
  facts: readonly DocumentFragment[],
  paragraphs: readonly HTMLElement[] = [],
): HTMLElement | null {
  if (role !== "account_manager") return null;
  if (facts.length === 0 && paragraphs.length === 0) return null;
  return h(
    "details",
    { class: "technical-disclosure", "data-role": "technical-details" },
    [
      h("summary", { "aria-label": "Technical", "data-action": "disclose-technical" }, [
        "Technical",
      ]),
      ...paragraphs,
      h("dl", { class: "facts" }, facts),
    ],
  );
}

function formatPlaidLocation(
  loc: NonNullable<QueueItem["plaidLocation"]>,
): string {
  const bits: string[] = [];
  // Bank-supplied text: every part goes through the strip.
  if (loc.address) bits.push(amVisibleDisplayText(loc.address));
  if (loc.city) bits.push(amVisibleDisplayText(loc.city));
  if (loc.region) bits.push(amVisibleDisplayText(loc.region));
  if (loc.postalCode) bits.push(amVisibleDisplayText(loc.postalCode));
  if (loc.country) bits.push(amVisibleDisplayText(loc.country));
  if (loc.storeNumber) bits.push(`store ${amVisibleDisplayText(loc.storeNumber)}`);
  if (loc.lat !== undefined && loc.lon !== undefined) {
    bits.push(`${String(loc.lat)}, ${String(loc.lon)}`);
  } else if (loc.lat !== undefined) {
    bits.push(`lat ${String(loc.lat)}`);
  } else if (loc.lon !== undefined) {
    bits.push(`lon ${String(loc.lon)}`);
  }
  return bits.join(", ");
}

/** Already-stored Plaid grant fields. Omit when absent — never render empty. */
function appendPlaidGrantFactNodes(
  factNodes: DocumentFragment[],
  item: QueueItem,
): void {
  if (item.originalDescription) {
    factNodes.push(
      fact("Original bank description", safeText(item.originalDescription)),
    );
  }
  if (item.paymentChannel) {
    factNodes.push(fact("Payment channel", safeText(item.paymentChannel)));
  }
  const meta = item.paymentMeta;
  if (meta) {
    if (meta.referenceNumber) {
      factNodes.push(fact("Bank reference", safeText(meta.referenceNumber)));
    }
    if (meta.ppdId) {
      factNodes.push(fact("PPD id", safeText(meta.ppdId)));
    }
    if (meta.payee) {
      factNodes.push(fact("Payee", safeText(meta.payee)));
    }
    if (meta.payer) {
      factNodes.push(fact("Payer", safeText(meta.payer)));
    }
    if (meta.reason) {
      factNodes.push(fact("Payment reason", safeText(meta.reason)));
    }
  }
  if (item.counterparties) {
    for (const counterparty of item.counterparties) {
      const bits = [amVisibleDisplayText(counterparty.name)];
      if (counterparty.type) bits.push(amVisibleDisplayText(counterparty.type));
      if (counterparty.website) bits.push(amVisibleDisplayText(counterparty.website));
      factNodes.push(fact("Counterparty", safeText(bits.join(" · "))));
    }
  }
  if (item.plaidCategory && item.plaidCategory.length > 0) {
    factNodes.push(
      fact("Plaid category", safeText(item.plaidCategory.join(" / "))),
    );
  }
  if (item.checkNumber) {
    factNodes.push(fact("Check number", safeText(item.checkNumber)));
  }
  if (item.plaidLocation) {
    const formatted = formatPlaidLocation(item.plaidLocation);
    if (formatted) {
      factNodes.push(fact("Bank location", safeText(formatted)));
    }
  }
}

/**
 * Plain words for the hub's expense type. The wire value is a code (the hub
 * sends `gl_account` when it names its own account instead of a type); a code
 * never reaches a card face. An unknown value reads "Other", never as sent.
 */
const RECEIPT_EXPENSE_TYPE_LABELS: Readonly<Record<string, string>> = {
  turnover: "Turnover",
  remodel: "Remodel",
  repair: "Repair",
  gas: "Gas",
  meals: "Meals",
  gl_account: "Set by the hub account",
};

export function receiptExpenseTypeLabel(expenseType: string): string {
  return Object.hasOwn(RECEIPT_EXPENSE_TYPE_LABELS, expenseType)
    ? (RECEIPT_EXPENSE_TYPE_LABELS[expenseType] ?? "Other")
    : "Other";
}

const RECEIPT_REVIEW_FLAG_LABELS: Readonly<Record<string, string>> = {
  wrong_card: "Wrong card",
  reimbursement: "Reimbursement",
  other: "Other",
};

export function receiptReviewFlagLabel(flag: string): string {
  return Object.hasOwn(RECEIPT_REVIEW_FLAG_LABELS, flag)
    ? (RECEIPT_REVIEW_FLAG_LABELS[flag] ?? "Other")
    : "Other";
}

/** Surface matched receipt on categorisation cards — safeText + origin gate. */
function appendReceiptFacts(
  facts: HTMLElement,
  receipt: QueueReceiptContext,
  company: CompanySlug,
): void {
  const matchLabel =
    receipt.matchVerified === true
      ? "verified"
      : receipt.matchVerified === false
        ? "unverified"
        : "no verification claim";
  facts.append(
    fact("Receipt vendor", safeText(receipt.vendor)),
    fact(
      "Receipt property / location",
      safeText(`${receipt.property} / ${receipt.location}`),
    ),
    fact("Expense type", safeText(receiptExpenseTypeLabel(receipt.expenseType))),
  );
  // The hub's own account name, as sent. Untrusted text: safeText only.
  if (receipt.glAccount !== undefined) {
    facts.append(fact("Hub account", safeText(receipt.glAccount)));
  }
  if (receipt.glParent !== undefined) {
    facts.append(fact("Hub parent account", safeText(receipt.glParent)));
  }
  facts.append(
    fact("Receipt match", safeText(matchLabel)),
    fact("Receipt source", safeText("MJN Ops receipt")),
  );
  if (receipt.reviewFlag !== null) {
    facts.append(
      fact("Receipt review flag", safeText(receiptReviewFlagLabel(receipt.reviewFlag))),
    );
  }
  if (receipt.note !== undefined) {
    facts.append(fact("Receipt note", safeText(receipt.note)));
  }
  facts.append(fact("Submitted by", safeText(receipt.submittedBy)));
  const href = safeReceiptImageHref(receipt.imageUrl, company);
  if (href !== null) {
    facts.append(
      h("dt", {}, ["Receipt image"]),
      h("dd", {}, [
        h(
          "a",
          { href, target: "_blank", rel: "noopener noreferrer" },
          ["View receipt"],
        ),
      ]),
    );
  } else {
    facts.append(fact("Receipt image", safeText("Receipt image unavailable")));
  }
}

function formatAmount(amount: string | undefined): string {
  if (!amount) return "—";
  return amount.startsWith("-") ? `−$${amount.slice(1)}` : `$${amount}`;
}

function formatConfidence(confidence: number | undefined): string {
  if (confidence === undefined) return "—";
  return `${String(Math.round(confidence * 100))}%`;
}

function formatAge(createdAt: string): string {
  const created = new Date(createdAt).getTime();
  if (Number.isNaN(created)) return "—";
  const ms = Date.now() - created;
  const days = Math.floor(ms / (24 * 60 * 60 * 1000));
  if (days <= 0) return "waiting less than a day";
  return `waiting ${String(days)} day${days === 1 ? "" : "s"}`;
}

function removalExplainText(opts: {
  recoverable: boolean;
  escalated: boolean;
  role?: StaffRole | null;
}): string {
  // Do not offer escalation in prose when the control is hidden (already
  // escalated), and do not tell an Owner to "escalate to the Owner".
  if (opts.recoverable) {
    return "Reversing appends a correction that mirrors the original entry — same account, amount, and location. You cannot re-categorise a reversal. Declining posts nothing and closes the row.";
  }
  if (opts.escalated) {
    return opts.role === "owner"
      ? "The entry this card came from could not be found, so it cannot be reversed from this card. Decline to close the row with no reversal, or return it to the account manager if it was escalated in error."
      : "The entry this card came from could not be found, so it cannot be reversed from this card. Decline to close the row with no reversal.";
  }
  if (opts.role === "owner") {
    return "The entry this card came from could not be found, so it cannot be reversed from this card. Decline to close the row with no reversal.";
  }
  return "The entry this card came from could not be found, so it cannot be reversed from this card. Send it to the owner with a note, or decline to close the row with no reversal.";
}

/**
 * The exported sentence names other files and tests pin. The words are the one
 * home's (`ui/failure.ts`), not a second table here: a sample refusal is read
 * through `failureCopy`, so queue.ts holds no outcome literal at all.
 *
 * `WRITE_FAILED_COPY` is a 4xx refusal ("That did not save. Try again.").
 * `CARD_CHANGED_COPY` / `NOT_ESCALATED_COPY` are round-12 E-B10's write-409
 * sentence ("This changed while you were working…"), and
 * `ALREADY_ANSWERED_COPY` is the card-409 sentence. A 409 that is not the
 * Worker's "already resolved" never reads as an answered card.
 */
const FOUR_HUNDRED_ERROR = new AdminApiError(400, null);
const FOUR_OH_NINE_ERROR = new AdminApiError(409, null);

export const WRITE_FAILED_COPY = failureCopy(FOUR_HUNDRED_ERROR, {
  role: "owner",
  context: "write",
}).sentence;
export const ALREADY_ANSWERED_COPY = failureCopy(FOUR_OH_NINE_ERROR, {
  role: "owner",
  context: "card",
}).sentence;
export const CARD_CHANGED_COPY = failureCopy(FOUR_OH_NINE_ERROR, {
  role: "owner",
  context: "write",
}).sentence;
export const NOT_ESCALATED_COPY = CARD_CHANGED_COPY;

/** The Worker's resolve-route 409 that can never succeed again. */
const ALREADY_RESOLVED_BODY = "Queue item is already resolved";

/**
 * True only when the Worker says the card is already resolved. The message is
 * compared INLINE (`===`), which is the one shape the error-text guard exempts:
 * a boolean question is not server words put on a face.
 */
function isAlreadyAnsweredError(error: unknown): boolean {
  if (!(error instanceof AdminApiError) || error.status !== 409) return false;
  const body = error.body;
  if (body === null || typeof body !== "object") return false;
  const err = (body as { error?: unknown }).error;
  if (err === null || typeof err !== "object") return false;
  if (typeof (err as { message?: unknown }).message !== "string") return false;
  // Round-2 LOW: trim and lowercase both sides, so a stray space or a
  // capitalisation change in the Worker's text does not stop a card being
  // recognised as already answered.
  return (
    (err as { message: string }).message.trim().toLowerCase() ===
    ALREADY_RESOLVED_BODY.trim().toLowerCase()
  );
}

/**
 * The same inline comparison for any Worker message a card must recognise by
 * exact text (the attach 422s, the not-hub-linked 403). It returns a boolean,
 * so no server sentence leaves this function.
 */
function bodyMessageIs(error: unknown, expected: string): boolean {
  if (!(error instanceof AdminApiError)) return false;
  const body = error.body;
  if (body === null || typeof body !== "object") return false;
  const err = (body as { error?: unknown }).error;
  if (err === null || typeof err !== "object") return false;
  return (err as { message?: unknown }).message === expected;
}


/** A rejected fetch is a connection problem, not a server answer. */
/**
 * The one sentence a person reads when a To review write fails (round 10,
 * C-6; round 12 decision 1 and E-B10). The words are `failureCopy`'s: a 409
 * only reads "Someone already answered this card…" when the Worker's body says
 * the card is already resolved; every other 409 reads the write-conflict
 * sentence. The owner's 403 reads decision 1's sentence and the account
 * manager keeps "Ask the owner.".
 */
export function writeFailureCopy(
  error: unknown,
  role?: StaffRole | null,
): string {
  const effective = readerRole(role);
  if (isAlreadyAnsweredError(error)) {
    return failureCopy(error, { role: effective, context: "card" }).sentence;
  }
  return failureCopy(error, { role: effective, context: "write" }).sentence;
}

/**
 * Paint a failed write: the one plain sentence, with the server's scrubbed
 * words behind "Show details". Returns the sentence so the caller can announce
 * it exactly once.
 */
export function appendWriteFailure(
  host: HTMLElement,
  error: unknown,
  role?: StaffRole | null,
): string {
  clear(host);
  const message = writeFailureCopy(error, role);
  host.append(document.createTextNode(message));
  const detail = failureCopy(error, {
    role: readerRole(role),
    context: "write",
  }).detail;
  if (detail !== null && detail !== message) {
    const details = h("details", { "data-role": "write-failure-detail" }, [
      h("summary", { "aria-label": "Show details" }, ["Show details"]),
      h("p", { class: "muted" }, [detail]),
    ]);
    host.append(document.createTextNode(" "));
    host.append(details);
  }
  return message;
}

/**
 * Word a non-landed `runWrite` outcome and paint it into `host` (dispatch-b
 * §3.1's one pattern). "Already resolved" is handled by the caller, which also
 * keeps the card's controls disabled and asks the page to re-read.
 */
export function paintOutcomeFailure(
  host: HTMLElement,
  outcome: WriteOutcome,
  role?: StaffRole | null,
): string {
  if (outcome.kind !== "landed" && outcome.error !== undefined) {
    return appendWriteFailure(host, outcome.error, role);
  }
  const sentence = outcomeSentence(outcome, readerRole(role));
  clear(host);
  host.append(document.createTextNode(sentence));
  return sentence;
}

/**
 * R4-28. A company the Worker refuses because it is not linked to the hub can
 * never be sent from this card, so it gets its own sentence for both roles —
 * never the account manager's "Ask the owner.", which told the owner to ask
 * himself.
 */
export const SEND_TO_PRACTICE_NOT_LINKED_COPY =
  "This company's books are not connected to the hub, so this card cannot be sent.";

export function sendToPracticeFailureCopy(
  error: unknown,
  role?: StaffRole | null,
): string {
  if (
    error instanceof AdminApiError &&
    error.status === 403 &&
    bodyMessageIs(error, "Company is not hub-linked")
  ) {
    return SEND_TO_PRACTICE_NOT_LINKED_COPY;
  }
  return writeFailureCopy(error, role);
}

/** Pull the server's human message out of a contract-shaped error body. */
function adminErrorRequestId(body: unknown): string | null {
  if (body === null || typeof body !== "object") return null;
  const err = (body as { error?: unknown }).error;
  if (err === null || typeof err !== "object") return null;
  const requestId = (err as { requestId?: unknown }).requestId;
  if (typeof requestId !== "string") return null;
  const trimmed = requestId.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * A card that has exited (or is exiting) must not come back live. `runWrite`
 * re-enables every control it disabled when it returns, so a success path
 * locks the detached card again: a stored reference and a late second press
 * cannot send a second request.
 */
function lockCardControls(card: HTMLElement): void {
  for (const control of card.querySelectorAll<
    HTMLButtonElement | HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
  >("button, input, select, textarea")) {
    control.disabled = true;
  }
}

/**
 * The single-flight key for one card instance. A card id alone collides across
 * two mounts of the page (a test mount, a re-render, or the same id read by two
 * pages); the element is the identity that "still current" is measured on, so
 * the key is bound to it. A second press on the same card shares the first run;
 * a rebuilt card gets its own key and its own write.
 */
const cardWriteKeys = new WeakMap<HTMLElement, string>();
let nextCardWriteKey = 0;

function cardWriteKey(card: HTMLElement, action: string): string {
  let id = cardWriteKeys.get(card);
  if (id === undefined) {
    nextCardWriteKey += 1;
    id = `card-${String(nextCardWriteKey)}`;
    cardWriteKeys.set(card, id);
  }
  return `${id}-${action}`;
}

// ---------------------------------------------------------------------------
// Q-c: bulk answer, dismiss, and "Answered recently".
//
// Everything here is a client-side loop over the EXISTING single resolve route
// plus grant G3 (Dismiss = reject with a 1-280 note; nothing is booked) and
// grant G5 (listQueue resolvedSince). The loop is paced at 40 requests a
// minute: the admin ceiling is 60/min and the shell also makes calls.
// ---------------------------------------------------------------------------

/** 40 requests a minute, measured between request starts. */
export const BULK_RESOLVE_INTERVAL_MS = 1_500;

/**
 * Mirrors `dismissMoovQueueItem` in `ingest-worker/src/admin.ts`: the owner or an
 * account manager may close a Moov wallet card, but an account manager may not
 * close a card that is currently escalated (the Worker answers 403). Do not
 * offer a Dismiss the Worker will refuse. An unknown role is treated as an
 * account manager, like the rest of the page.
 */
export function roleMayDismissMoovCard(
  item: QueueItem,
  role: StaffRole | null | undefined,
): boolean {
  return role === "owner" || !isWithOwner(item);
}

/** Worker lockstep: dismissMoovQueueItem requires 1..280 characters. */
export const BULK_DISMISS_NOTE_MAX = 280;

/** Exact grant-G3 warning. Never claims a dismissed card stops a month close. */
export const BULK_DISMISS_CONFIRM_COPY =
  "Dismissed cards will not come back for these payments. Nothing is booked, so any that were real payments stay unbooked.";

export const BULK_DISMISS_TOAST_COPY = "Dismissed. It won't come back for this payment.";
export const BULK_DISMISS_FEE_TOPUP_COPY =
  "Fee top-up cards can come back if the problem is still there.";

/**
 * The reopen line in the Dismiss dialog, plural-aware: one card is "it", many
 * are "them" (Y6 brief item 6). Every Y-lane sentence agrees with its count.
 */
export function bulkDismissUndoCopy(count: number): string {
  return count === 1
    ? "You can reopen it later from Dismissed."
    : "You can reopen them later from Dismissed.";
}

/**
 * The closable fee top-up cards that name a standing condition a person decided
 * on: a dismissed one stays dismissed for that reason. Keep it in step with
 * `FEE_TOPUP_DECISION_REASONS` / `isFeeTopupDecisionReason` in
 * `ingest-worker/src/moov_trust_fee_topup.ts`.
 */
const FEE_TOPUP_DECISION_REASONS: ReadonlySet<string> = new Set([
  "fee_topup_missing",
  "fee_receivable_deposit_reversed",
  "fee_topup_duplicate",
  "fee_topup_amount_mismatch",
  "fee_topup_without_receivable",
  "fee_topup_receivable_closed",
]);

export function isFeeTopupDecisionReason(code: string): boolean {
  return (
    FEE_TOPUP_DECISION_REASONS.has(code) ||
    (code.startsWith("topup_") && code !== "topup_transfer_not_completed")
  );
}

/**
 * True only for the cards the Worker raises again after a person dismissed
 * them: the operational closable fee top-up reasons (a wallet row not read yet,
 * an account not open, a month locked...). Mirrors `enqueueMoovUnbookedCard`
 * (`keepsDismissals = !isClosableFeeTopupReason`) and the decision-reason
 * check. NOT true for the decision reasons, nor for `fee_row_missing`,
 * `breakdown_operating_company_unknown` or `breakdown_refused_topup_*`: the
 * Worker suppresses those after a dismissal, so the dialog must not say they
 * can come back.
 */
export function isFeeTopupComingBackReason(body: string | null | undefined): boolean {
  const code = (body ?? "").trim();
  if (code.length === 0) return false;
  return isMoovFeeTopupClosableReason(code) && !isFeeTopupDecisionReason(code);
}

export function dismissSelectionMentionsFeeTopup(items: readonly QueueItem[]): boolean {
  return items.some((item) => isFeeTopupComingBackReason(item.body));
}

export const BULK_JUDGMENT_REFUSAL_COPY = "These need to be answered one at a time.";

export const BULK_ONE_COMPANY_COPY = "Pick cards from one company to book them together.";

/** Exclusions that keep a card out of a bulk Book. */
export type BulkBookExclusion =
  | "not_pending"
  | "needs_split"
  | "needs_note"
  | "needs_location"
  | "needs_setup"
  | "needs_owner"
  | "needs_one_at_a_time"
  | "waiting";

/**
 * Whether a card can be booked in bulk to one account, and if not why.
 *
 * Conservative by design: every card whose single face asks for a split, a
 * confirm string, an escrow detail, a reviewed decision, a note, or a
 * location the loop cannot collect stays out and is named in the bar. A Moov
 * tenant-split card (body carries a breakdown) never gets a single-account
 * Approve.
 */
export function bulkBookDecision(item: QueueItem): "book" | BulkBookExclusion {
  if (item.status !== "pending") return "not_pending";
  if (isRemovalQueueItem(item)) return "needs_split";
  if (isMoovMovementQueueItem(item)) {
    if (isMoovBreakdownQueueItem(item)) return "needs_split";
    // A refused payment in a closed month is blocked, not "needs a note".
    if (isBlockedMoovReason(item.body ?? "")) return "needs_setup";
    if (isMoovRefusedBreakdownQueueItem(item)) return "needs_note";
    // Same classification the single card uses: nothing to approve yet.
    if (isWaitingMoovReason(item.body ?? "")) return "waiting";
    if ((item.suggestedLocation ?? "") === "") return "needs_location";
    return "book";
  }
  if (isBlockerQueueItem(item)) return "needs_owner";
  if (isUntrackedCounterpartyQueueItem(item)) return "needs_split";
  // A blocked hold (a closed month) is withheld by the same predicate the
  // single card asks, and reports the way a blocked wallet card does.
  if (isHoldApproveWithheld(item)) return "needs_setup";
  const kind = categorisationCardKind(item);
  if (kind !== "ordinary") return "needs_split";
  if (item.reason === "unmatched-deposit" || item.reason === "booking-failed") {
    return "needs_split";
  }
  if (item.reason === "anomaly" || item.reason === "possible-personal") {
    return "needs_one_at_a_time";
  }
  if (item.reason === BULK_EXCLUDED_REASON) return "needs_split";
  if (parseStoredLoanStatement(item.aiRationale) !== null) return "needs_split";
  if (looksLikeLs1(item.aiRationale)) return "needs_split";
  if (claimsLumpBooking(item)) return "needs_split";
  if ((item.suggestedLocation ?? "") === "") return "needs_location";
  return "book";
}

export function selectBulkBook(items: readonly QueueItem[]): {
  book: QueueItem[];
  excluded: Array<{ item: QueueItem; reason: BulkBookExclusion }>;
} {
  const book: QueueItem[] = [];
  const excluded: Array<{ item: QueueItem; reason: BulkBookExclusion }> = [];
  for (const item of items) {
    const decision = bulkBookDecision(item);
    if (decision === "book") book.push(item);
    else excluded.push({ item, reason: decision });
  }
  return { book, excluded };
}

/**
 * The resolution for one bulk Book. The account is the person's choice; the
 * location is the card's own stored suggestion, or the company's only
 * location (the same rule the single card uses: one location is not a choice).
 */
export function bulkBookResolution(
  item: QueueItem,
  account: string,
  fallbackLocation = "",
): QueueResolution {
  const body: QueueResolution = { action: "approve", account };
  const location = item.suggestedLocation ?? "";
  const chosen = location !== "" ? location : fallbackLocation;
  if (chosen !== "") body.location = chosen;
  return body;
}

export function bulkDismissResolution(note: string): QueueResolution {
  return { action: "reject", note };
}

/** A note of 1..280 characters, nothing else. */
export function bulkDismissNoteProblem(note: string): string | null {
  const trimmed = note.trim();
  if (trimmed.length === 0) return "Write a short note first.";
  if (trimmed.length > BULK_DISMISS_NOTE_MAX) {
    return `Keep the note to ${String(BULK_DISMISS_NOTE_MAX)} characters or fewer.`;
  }
  return null;
}

export interface BulkQueueFailure {
  item: QueueItem;
  /** Plain reason. Never a raw server sentence, code, id or request id. */
  reason: string;
  status: number | null;
  /** A genuine "someone already answered this card" 409: the card is gone. */
  alreadyAnswered: boolean;
}

export interface BulkQueueOutcome {
  succeeded: QueueItem[];
  failures: BulkQueueFailure[];
}

/**
 * Plain reason for one failed card. Server text never reaches the screen.
 * Round 2, item 5: the same sentences the single-card path uses (C-6), so a
 * Dismiss and a bulk Book cannot describe one failure two ways.
 */
export function bulkQueueFailureReason(
  error: unknown,
  role?: StaffRole | null,
): string {
  const effective = readerRole(role);
  // Only the Worker's own "already resolved" is an answered card (E-B10).
  if (isAlreadyAnsweredError(error)) {
    return failureCopy(error, { role: effective, context: "card" }).sentence;
  }
  if (error instanceof AdminApiError) {
    if (error.status === 404) return "This card is no longer here.";
    if (error.status === 429) return "Books is busy. Try this one again in a minute.";
    if (error.status === 400 || error.status === 422) {
      return "This card needs a person to answer it.";
    }
  }
  return failureCopy(error, { role: effective, context: "write" }).sentence;
}

/** The pause the server asked for on a 429, in milliseconds. */
export function bulkRetryAfterMs(error: unknown): number | null {
  if (!(error instanceof AdminApiError) || error.status !== 429) return null;
  const body = error.body;
  if (body === null || typeof body !== "object") return null;
  const err = (body as { error?: unknown }).error;
  if (err === null || typeof err !== "object") return null;
  const details = (err as { details?: unknown }).details;
  if (details === null || typeof details !== "object") return null;
  const seconds = (details as { retryAfterSeconds?: unknown }).retryAfterSeconds;
  if (typeof seconds !== "number" || !Number.isFinite(seconds) || seconds < 0) return null;
  return Math.round(seconds * 1000);
}

export interface BulkQueueRunOptions {
  items: readonly QueueItem[];
  resolutionFor: (item: QueueItem) => QueueResolution;
  resolve: (id: number, body: QueueResolution) => Promise<unknown>;
  onProgress?: (done: number, total: number) => void;
  /** Between request starts. Defaults to BULK_RESOLVE_INTERVAL_MS. */
  paceMs?: number;
  sleepFn?: (ms: number) => Promise<void>;
  now?: () => number;
  /** The reader's role, for the role-aware 403 sentence (round 2, item 5). */
  role?: StaffRole | null;
}

/**
 * Run one item at a time over the existing single resolve. Paced; continues
 * past failures; on 429 waits the server's retry period and retries THAT item
 * once. An id is never resolved twice.
 */
export async function runBulkQueueAction(
  opts: BulkQueueRunOptions,
): Promise<BulkQueueOutcome> {
  const seen = new Set<number>();
  const items = opts.items.filter((item) => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
  const pace = Math.max(0, opts.paceMs ?? BULK_RESOLVE_INTERVAL_MS);
  const pause = opts.sleepFn ?? sleep;
  const now = opts.now ?? (() => Date.now());
  const succeeded: QueueItem[] = [];
  const failures: BulkQueueFailure[] = [];
  let lastStart: number | null = null;

  const waitForPace = async (): Promise<void> => {
    if (lastStart === null || pace === 0) return;
    const wait = lastStart + pace - now();
    if (wait > 0) await pause(wait);
  };

  for (const [index, item] of items.entries()) {
    await waitForPace();
    lastStart = now();
    let failure: BulkQueueFailure | null = null;
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        await opts.resolve(item.id, opts.resolutionFor(item));
        failure = null;
        break;
      } catch (error) {
        const retryAfter = bulkRetryAfterMs(error);
        if (retryAfter !== null && attempt === 0) {
          await pause(retryAfter);
          lastStart = now();
          continue;
        }
        let alreadyAnswered = false;
        if (isAlreadyAnsweredError(error)) alreadyAnswered = true;
        failure = {
          item,
          reason: bulkQueueFailureReason(error, opts.role),
          status: error instanceof AdminApiError ? error.status : null,
          alreadyAnswered,
        };
        break;
      }
    }
    if (failure === null) succeeded.push(item);
    else failures.push(failure);
    opts.onProgress?.(index + 1, items.length);
  }
  return { succeeded, failures };
}

// -- Answered recently (G5) -------------------------------------------------

export const BOOKING_RECENTLY_SAVED_COPY =
  "Saved. The books pick it up at the next booking run.";
/** The shared round-10 sentence (R4-24): `bookingFailedCopy(role)`. */
export const BOOKING_RUN_FAILED_COPY = bookingFailedCopy("account_manager");
export const BOOKING_RUN_STALE_COPY = `Booking has not run recently. ${advice(
  "check_now",
  "account_manager",
)}`;
/** The owner is the owner: the same facts, with nobody to tell. */
export const BOOKING_RUN_FAILED_OWNER_COPY = bookingFailedCopy("owner");
export const BOOKING_RUN_STALE_OWNER_COPY = "Booking has not run recently.";
/**
 * A health read that failed, or that does not mention the company, says
 * nothing about booking, and the page claims nothing (round eight B2: it said
 * "Booking has not run recently. Tell the owner." six minutes after a run).
 */
export const BOOKING_RUN_UNKNOWN_COPY = "We couldn't check when booking last ran.";

// -- Dismissed (G6) ---------------------------------------------------------

export const DISMISSED_EMPTY_COPY = "Nothing dismissed.";
/**
 * R4-06. A capped list that found nothing under this filter cannot say there
 * is nothing: the rows may sit past the cap. Never paint "0 for X" over it.
 */
export const DISMISSED_EMPTY_CAPPED_COPY =
  "Could not see every dismissal; more may exist.";
export const DISMISSED_FAILED_COPY = "Couldn't load dismissed cards.";
export const DISMISSED_CAP_COPY = `Showing the newest ${String(FLEET_QUEUE_LIST_LIMIT)}.`;
/**
 * Reopen copies the dismissal to a NEW pending card and leaves the dismissed
 * row unmodified. WR's list hides a dismissal while a later pending card
 * exists, so do not claim this row stays visible here.
 */
export const DISMISSED_EXPLAIN_COPY =
  `Reopen adds a new card to ${navLabel("queue")}. It books nothing.`;

export const DISMISSED_REOPEN_SUCCESS = `Reopened. A new card is in ${navLabel("queue")}.`;
export const DISMISSED_REOPEN_RELOAD = "Reopened. Reload the page to see it.";
export const DISMISSED_REOPEN_CONFLICT =
  "This card was already reopened or changed. The list is up to date.";
/** Kept for a caller with no role: the AM's refused-card advice, no re-ask. */
export const DISMISSED_REOPEN_FORBIDDEN = advice("reopen_card", "account_manager", {
  escalatedCard: true,
});
export const DISMISSED_REOPEN_FAILED = "Couldn't reopen this card. Try again.";

const SHORT_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

/** The presenter's placeholder for "no date"; never show it as a real day. */
function isPlaceholderDate(iso: string): boolean {
  return iso.startsWith("1970-01-01");
}

/**
 * "Oct 20" from a date-only or ISO string, or null when missing or unreadable.
 * A date outside the current year carries its year ("Dec 30, 2025").
 */
export function shortDateLabel(
  iso: string | null | undefined,
  now: Date = new Date(),
): string | null {
  if (iso === null || iso === undefined || isPlaceholderDate(iso)) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (match === null) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const label = SHORT_MONTHS[month - 1];
  if (label === undefined || !Number.isInteger(day) || day < 1 || day > 31) {
    return null;
  }
  return year === now.getFullYear()
    ? `${label} ${String(day)}`
    : `${label} ${String(day)}, ${String(year)}`;
}

/**
 * Short date for when the dismissal happened, in the same English style as the
 * payment date. Date-only values use their own calendar day (never shifted by
 * a timezone); a full timestamp uses the operator's local day.
 */
export function dismissedAtLabel(
  resolvedAt: string | null | undefined,
  now: Date = new Date(),
): string {
  if (
    resolvedAt === null ||
    resolvedAt === undefined ||
    resolvedAt.trim() === "" ||
    isPlaceholderDate(resolvedAt)
  ) {
    return "recently";
  }
  if (resolvedAt.includes("T")) {
    const at = new Date(resolvedAt);
    if (!Number.isNaN(at.getTime())) {
      return at.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        ...(at.getFullYear() === now.getFullYear() ? {} : { year: "numeric" }),
      });
    }
  }
  return shortDateLabel(resolvedAt, now) ?? "recently";
}

/** The stored dismiss note is untrusted text; hazard-strip before showing it. */
export function dismissedNoteText(note: string | null | undefined): string | null {
  const trimmed = (note ?? "").trim();
  if (trimmed.length === 0) return null;
  const shown = amVisibleDisplayText(trimmed);
  return shown.trim().length === 0 ? null : shown;
}

/**
 * Plain message for a reopen failure. Never surfaces a server message.
 *
 * The 409 stays this card's own sentence (someone answered it, so the list is
 * out of date) and everything else this page cannot word keeps its fallback.
 * A 401, a 403 and a 429 are the one home's (`failureCopy`, card context): the
 * owner's 403 is decision 1's "You do not have access to do that." and the
 * account manager keeps "Ask the owner." (round 10, item 8e). The role is the
 * page's own; an unknown one reads as the account manager (decision 10).
 */
export function dismissedReopenFailureCopy(
  error: unknown,
  role?: StaffRole | null,
): string {
  if (error instanceof AdminApiError) {
    if (error.status === 409) return DISMISSED_REOPEN_CONFLICT;
    if (error.status === 401 || error.status === 403 || error.status === 429) {
      return failureCopy(error, { role: readerRole(role), context: "card" }).sentence;
    }
    // M4: a 5xx (or a proxy timeout) may have reopened the card. Saying
    // "Couldn't reopen this card. Try again." would claim a write did not
    // happen that may have happened, so it reads as an unconfirmed write.
    if (error.status >= 500 || error.status === 408 || error.status === 425 || error.status === 499) {
      return failureCopy(error, { role: readerRole(role), context: "write" }).sentence;
    }
    return DISMISSED_REOPEN_FAILED;
  }
  // A rejected fetch or an unreadable answer: no server answer, so the request
  // may have landed. Same rule as a 5xx.
  return failureCopy(error, { role: readerRole(role), context: "write" }).sentence;
}

export const ANSWERED_RECENTLY_WINDOW_DAYS = 7;
const ANSWERED_WINDOW_MS = ANSWERED_RECENTLY_WINDOW_DAYS * 24 * 60 * 60 * 1000;
const BOOKING_FRESH_MS = 24 * 60 * 60 * 1000;

/**
 * A row the pipeline resolved on its own is not "what you answered". The
 * Worker writes `system:<event>`; the hub writes `hub:<role>`.
 */
export function isSystemResolvedBy(resolvedBy: string | undefined): boolean {
  const value = (resolvedBy ?? "").trim().toLowerCase();
  return value.startsWith("system:") || value.startsWith("hub:");
}

/**
 * Answers from the last seven days, newest first. The Worker already filters
 * status and date; this repeats the invariant client-side so a malformed or
 * stale page cannot smuggle a pipeline row or an old answer into the section.
 */
export function answeredRecentlyItems(
  items: readonly QueueItem[],
  nowMs: number,
  windowMs: number = ANSWERED_WINDOW_MS,
): QueueItem[] {
  const since = nowMs - windowMs;
  const rows: Array<{ item: QueueItem; at: number }> = [];
  for (const item of items) {
    if (item.status !== "approved" && item.status !== "edited") continue;
    const account = (item.resolvedAccount ?? "").trim();
    if (account === "") continue;
    if (isSystemResolvedBy(item.resolvedBy)) continue;
    const at = Date.parse(item.resolvedAt ?? "");
    if (Number.isNaN(at) || at < since || at > nowMs) continue;
    rows.push({ item, at });
  }
  rows.sort((a, b) => b.at - a.at || b.item.id - a.item.id);
  return rows.map((row) => row.item);
}

/**
 * Warning for the answered section, from the LATEST processing run per
 * company (the health read returns one run per company and type, any status).
 * A failed run outranks a stale one; no run, or a success older than 24
 * hours, warns. A missing health read fails closed.
 */
export function bookingRunWarningCopy(
  healths: readonly CompanyHealth[] | null,
  companies: readonly string[],
  nowMs: number,
  role: StaffRole = "account_manager",
): string | null {
  if (companies.length === 0) return null;
  const owner = role === "owner";
  if (healths === null) return BOOKING_RUN_UNKNOWN_COPY;
  let stale = false;
  let unknown = false;
  for (const company of [...new Set(companies)]) {
    const health = healths.find((row) => row.company === company);
    if (health === undefined) {
      unknown = true;
      continue;
    }
    const run = health.lastRuns.find((row) => row.runType === "processing");
    if (run === undefined) {
      stale = true;
      continue;
    }
    if (run.status === "failed") {
      // R4-24: the one shared sentence, from the helper Home, Months and
      // Check now also read. A failed run outranks a stale one.
      return bookingFailedCopy(role);
    }
    if (run.status === "running") continue;
    const started = Date.parse(run.startedAt);
    if (Number.isNaN(started) || nowMs - started >= BOOKING_FRESH_MS) stale = true;
  }
  if (stale) return owner ? BOOKING_RUN_STALE_OWNER_COPY : BOOKING_RUN_STALE_COPY;
  return unknown ? BOOKING_RUN_UNKNOWN_COPY : null;
}

/** Total of the cards a bulk Book would send, or null when any amount is unknown. */
export function bulkTotalLabel(items: readonly QueueItem[]): string | null {
  if (items.length === 0) return null;
  let total = 0n;
  for (const item of items) {
    // The same sign Activity and the card rows use (round eight A-M2).
    const raw = (queueAmountDecimal(item) ?? "").trim();
    if (raw === "") return null;
    const cents = decimalToCents(raw);
    if (cents === null) return null;
    total += cents;
  }
  return formatCents(total);
}

/**
 * Bulk accept cap. Lockstep with packages/contracts bulkResolveRequestSchema
 * `.max(200)` and books-api.yaml. Named without QUEUE and LIMIT so
 * queue_limit_drift_lint does not treat it as the fleet list cap.
 */
export const BULK_ACCEPT_MAX = 200;

/** Lockstep with pipeline/config.py INTERCOMPANY_ACCOUNT_PATTERN. */
const BULK_INTERCOMPANY_ACCOUNT_PATTERN =
  /^(Assets|Liabilities):([A-Z][A-Z0-9]*):(Due-From|Due-To):([A-Z][A-Z0-9]*)$/;

const BULK_LOAN_PATH_SEGMENT = "Loans";

export const BULK_EXCLUDED_REASON = "receipt_card_not_attached";

/**
 * Owner (Equity root), loan (Loans segment), or intercompany.
 * Boolean form of classify_booking_account with no own-bank exception.
 */
export function isJudgmentClassAccount(account: string): boolean {
  if (BULK_INTERCOMPANY_ACCOUNT_PATTERN.test(account)) return true;
  const parts = account.split(":");
  if (parts[0] === "Equity") return true;
  return parts.includes(BULK_LOAN_PATH_SEGMENT);
}

export type BulkAcceptExclusion =
  | "judgment_account"
  | "receipt_card_not_attached"
  | "needs_account_choice"
  | "not_one_tap"
  | "removal"
  | "month_closed"
  | "needs_setup"
  | "needs_one_at_a_time"
  | "not_in_chart";

export interface BulkAcceptFilter {
  company: CompanySlug;
  minConfidence: number | null;
  account: string;
  /**
   * The company's chart. When given, a suggestion outside it is left out: the
   * single Yes refuses the same card (`suggestionInMembership`) and the Worker
   * answers 422 `invalid_resolution`.
   */
  offeredAccounts?: readonly string[];
}

export interface BulkAcceptSelection {
  accept: QueueItem[];
  excluded: Array<{ item: QueueItem; reason: BulkAcceptExclusion }>;
}

/**
 * Whether bulk Accept may take this card, and if not why.
 *
 * It is deliberately a SUBSET of bulk Book: every card bulk Book refuses is
 * refused here too, so the panel can never promise a card the booking path
 * (or the Worker) will skip. The first two checks name the shapes the brief
 * called out (a reversal and a closed-month hold) with their own panel copy;
 * the rest are mapped from `bulkBookDecision` so the two cannot drift.
 */
export function bulkAcceptDecision(
  item: QueueItem,
  offeredAccounts?: readonly string[],
): "accept" | BulkAcceptExclusion {
  if (item.reason === BULK_EXCLUDED_REASON) return "receipt_card_not_attached";
  // R4-01: a bank removal books a reversal. The single card says "Reverse it";
  // accepting the AI's guess in bulk would book a reversal the panel never
  // named.
  if (isRemovalQueueItem(item)) return "removal";
  // R4-02: a closed month is withheld by the same predicate the single card and
  // bulk Book ask; the approval would book once the month is reopened. Round 2:
  // a closed month is not a books-setup problem, so it gets its own line (the
  // card's own chip is "Month closed") instead of "needs a setup fix".
  if (isHoldApproveWithheld(item) || isClosedMonthCard(item)) return "month_closed";
  // C-1 by a second path (round 2, item 1): a check-first hold needs a person
  // to pick the account on the card; accepting its AI guess in bulk would book
  // what the card says it will not book.
  if (holdRequiresAccountChoice(item)) return "needs_account_choice";
  const book = bulkBookDecision(item);
  if (book === "needs_setup") return "needs_setup";
  if (book === "needs_one_at_a_time" || book === "needs_owner") {
    return "needs_one_at_a_time";
  }
  if (book !== "book") return "not_one_tap";
  if (
    item.suggestedAccount !== undefined &&
    item.suggestedAccount !== "" &&
    isJudgmentClassAccount(item.suggestedAccount)
  ) {
    return "judgment_account";
  }
  // R4-10: the single Yes requires the suggestion to be in the company's chart
  // (`suggestionInMembership`). Without this the panel counts a card the Worker
  // will skip with 422.
  if (
    offeredAccounts !== undefined &&
    item.suggestedAccount !== undefined &&
    item.suggestedAccount !== "" &&
    !offeredAccounts.includes(item.suggestedAccount)
  ) {
    return "not_in_chart";
  }
  if (!offersOrdinaryYesOneTap(item)) return "not_one_tap";
  return "accept";
}

function matchesBulkAcceptFilter(item: QueueItem, filters: BulkAcceptFilter): boolean {
  if (filters.company === "" || item.company !== filters.company) return false;
  if (item.status !== "pending") return false;
  if (filters.account !== "" && item.suggestedAccount !== filters.account) return false;
  if (filters.minConfidence !== null) {
    if (item.confidence === undefined) return false;
    if (item.confidence < filters.minConfidence) return false;
  }
  return true;
}

export function selectBulkAccept(
  items: readonly QueueItem[],
  filters: BulkAcceptFilter,
): BulkAcceptSelection {
  const accept: QueueItem[] = [];
  const excluded: BulkAcceptSelection["excluded"] = [];
  for (const item of items) {
    if (!matchesBulkAcceptFilter(item, filters)) continue;
    const decision = bulkAcceptDecision(item, filters.offeredAccounts);
    if (decision === "accept") accept.push(item);
    else excluded.push({ item, reason: decision });
  }
  return { accept, excluded };
}

export function parseBulkConfidence(
  raw: string,
): { ok: true; value: number | null } | { ok: false } {
  const trimmed = raw.trim();
  if (trimmed === "") return { ok: true, value: null };
  if (!/^(?:0|1|0\.\d{1,4}|1\.0{1,4})$/.test(trimmed)) return { ok: false };
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value < 0 || value > 1) return { ok: false };
  return { ok: true, value };
}

function bulkExclusionCopy(reason: BulkAcceptExclusion): string {
  if (reason === "judgment_account") {
    return "Left out: owner, loan, or intercompany";
  }
  if (reason === "receipt_card_not_attached") {
    return "Left out: the card is not attached";
  }
  if (reason === "needs_account_choice") {
    return "Left out: pick the account on the card first";
  }
  if (reason === "removal") {
    return "Left out: a reversal is answered one at a time";
  }
  if (reason === "month_closed") {
    return "Left out: its month is closed";
  }
  if (reason === "needs_setup") {
    return "Left out: needs a setup fix";
  }
  if (reason === "needs_one_at_a_time") {
    return "Left out: answer it one at a time";
  }
  if (reason === "not_in_chart") {
    return "Left out: the account is not in the chart";
  }
  return "Left out: not an AI suggestion to accept in bulk";
}

/**
 * ONE plain line per reason for the cards bulk accept will not take, so the
 * panel cannot print the same sentence sixteen times (Y6 brief item 5). The
 * words are the same reason the per-card row names, just counted.
 */
export function bulkExclusionSummary(
  reason: BulkAcceptExclusion,
  count: number,
): string {
  const cards = pluralText(count, "card", "cards");
  const verb = count === 1 ? "is" : "are";
  if (reason === "judgment_account") {
    const suggestions = pluralText(count, "suggestion", "suggestions");
    return `${suggestions} ${verb} for the owner, a loan or a company-to-company account and ${verb} left out`;
  }
  if (reason === "receipt_card_not_attached") {
    return `${cards} ${count === 1 ? "has" : "have"} no receipt attached and ${verb} left out`;
  }
  if (reason === "needs_account_choice") {
    return `${cards} ${count === 1 ? "needs" : "need"} an account chosen on the card first and ${verb} left out`;
  }
  if (reason === "removal") {
    return `${cards} ${verb} ${count === 1 ? "a reversal" : "reversals"} and ${verb} answered one at a time`;
  }
  if (reason === "month_closed") {
    // A closed month is not a setup fix: the card's own chip says "Month
    // closed", so the panel says the same thing (round 2, item 5).
    return count === 1
      ? `${cards} is in a closed month and is left out`
      : `${cards} are in closed months and are left out`;
  }
  if (reason === "needs_setup") {
    return `${cards} ${count === 1 ? "needs" : "need"} a setup fix first and ${verb} left out`;
  }
  if (reason === "needs_one_at_a_time") {
    return `${cards} ${count === 1 ? "needs" : "need"} to be answered one at a time and ${verb} left out`;
  }
  if (reason === "not_in_chart") {
    return `${cards} ${count === 1 ? "has an account that is" : "have accounts that are"} not in this company's chart and ${verb} left out`;
  }
  return `${cards} ${verb} not AI suggestions and ${verb} left out`;
}

/**
 * One bulk-accept row's payment, as a person reads it: who it was with and how
 * much (rd r12 F1, R2). A wallet card carries no merchant, so a row that read
 * only the merchant printed "— — Income › Rent". The name follows round-11
 * decision 6b's order ("merchant name and fall back is good"): the merchant,
 * else the bank or wallet description, else a plain stand-in.
 */
function bulkPaymentWords(item: QueueItem): string {
  const named =
    amVisibleDisplayText(item.merchant ?? "").trim() ||
    amVisibleDisplayText(item.descriptor ?? "").trim() ||
    "A payment";
  const amount = moneyText(queueAmountDecimal(item));
  return amount === "" ? named : `${named} · ${amount}`;
}

export function mountBulkAcceptPanel(opts: {
  items: readonly QueueItem[];
  client: AdminClient;
  live: HTMLElement;
  stillCurrent: () => boolean;
  onAccepted: (message: string) => void;
  /**
   * Called after a bulk call failed, with the failure sentence the call
   * wrote. The list is re-read before any result is painted: a 500 can arrive
   * after the server approved some cards, and those cards must leave the list
   * (X2). The sentence travels with the call so the next render can announce
   * it instead of losing it with this one (round 2, MUST-FIX 1).
   */
  onFailed?: (message: string) => void;
  /**
   * True when a bulk accept this page started is still running (round 2,
   * item 3). A refresh rebuilds this panel; it must come back saying the run
   * is in flight rather than offering a second press.
   */
  busy?: boolean;
  /** The panel started or finished a bulk call. */
  onBusyChange?: (busy: boolean) => void;
  /** When set, the panel is opened from that company's group header. */
  fixedCompany?: CompanySlug;
  /** The render's one company directory (B1c-1). */
  directory?: face.CompanyDirectory;
  /** Company slug -> display name, for a caller that has no directory. */
  companyLabels?: ReadonlyMap<string, string>;
  /** The shell's ledger segment to company name read (rd13 L1). */
  companySegments?: ReadonlyMap<string, string>;
  /**
   * The accounts a company offers (its chart). Two suggested accounts that
   * read the same are told apart within this set and the suggestions in the
   * list (F2); absent, the suggestions alone are the set.
   */
  offeredAccounts?: (company: CompanySlug) => readonly AccountPath[];
  /**
   * The person's role, used to pick the decided failure sentence after a failed
   * bulk call (round 3, item 3). The sentence is `writeFailureCopy`, never the
   * server's own body text.
   */
  role?: StaffRole | null;
}): HTMLElement {
  const fixedCompany = opts.fixedCompany ?? null;
  // The panel's ONE label context: its caller's directory, else its names.
  const labelCompanies = accountLabelContext(
    opts.directory !== undefined ? opts.directory : opts.companyLabels,
    opts.companySegments,
  );
  const suggestionLabels = companyAccountLabels((company) => [
    ...new Set([
      ...(opts.offeredAccounts?.(company) ?? []),
      ...opts.items
        .filter((item) => item.company === company && item.suggestedAccount)
        .map((item) => item.suggestedAccount ?? ""),
    ]),
  ], labelCompanies);
  const companySelect = h("select", { "data-role": "bulk-accept-company" }, [
    h("option", { value: "" }, ["Choose a company"]),
  ]);
  const companies = [...new Set(opts.items.map((item) => item.company))].sort();
  for (const company of companies) {
    companySelect.append(
      h("option", { value: company }, [
        face.company(company, presenterDirectory(opts), "sentence"),
      ]),
    );
  }
  if (fixedCompany !== null) companySelect.value = fixedCompany;
  const confidenceInput = h("input", {
    type: "text",
    inputmode: "decimal",
    "data-role": "bulk-accept-confidence",
    placeholder: "Any",
    autocomplete: "off",
  });
  const accountSelect = h("select", { "data-role": "bulk-accept-account" }, [
    h("option", { value: "" }, ["Any account"]),
  ]);
  const count = h("p", { "data-role": "bulk-accept-count" });
  const list = h("ul", { "data-role": "bulk-accept-list" });
  // The cards bulk accept leaves out are summarised in ONE line; the list of
  // which cards they are sits behind "Show which" (Y6 brief item 5). The old
  // panel printed "-- Left out: not an AI suggestion…" once per card.
  const excludedSummary = h("div", { "data-role": "bulk-accept-excluded-summary" });
  const excludedList = h("ul", { "data-role": "bulk-accept-excluded-list" });
  const excludedDetails = h(
    "details",
    { "data-role": "bulk-accept-excluded" },
    [
      h(
        "summary",
        { "data-action": "show-excluded-suggestions" },
        ["Show which"],
      ),
      excludedList,
    ],
  );
  const button = h(
    "button",
    { type: "button", "data-action": "bulk-accept" },
    ["Accept AI suggestions"],
  );
  let accountCompany = "\0";

  const currentFilter = (): BulkAcceptFilter | null => {
    const confidence = parseBulkConfidence(confidenceInput.value);
    if (!confidence.ok) return null;
    return {
      company: companySelect.value,
      minConfidence: confidence.value,
      account: accountSelect.value,
      ...(opts.offeredAccounts === undefined
        ? {}
        : { offeredAccounts: opts.offeredAccounts(companySelect.value) }),
    };
  };

  const syncAccountOptions = (): void => {
    const company = companySelect.value;
    if (company === accountCompany) return;
    accountCompany = company;
    const previous = accountSelect.value;
    clear(accountSelect);
    accountSelect.append(h("option", { value: "" }, ["Any account"]));
    const names = [
      ...new Set(
        opts.items
          .filter((item) => item.company === company && item.suggestedAccount)
          .map((item) => item.suggestedAccount ?? ""),
      ),
    ]
      .filter((name) => name !== "")
      .sort();
    for (const name of names) {
      // The value stays the stored account path (the filter keys on it); the
      // label is the account in words.
      accountSelect.append(h("option", { value: name }, [suggestionLabels.label(company, name)]));
    }
    accountSelect.value = names.includes(previous) ? previous : "";
  };

  // Round 2, item 3: a run this page started is still running. The panel is
  // rebuilt by every whole-page render, so the running state travels with the
  // panel rather than with the node the first press disabled.
  let running = opts.busy === true;

  const paint = (): void => {
    syncAccountOptions();
    clear(list);
    clear(excludedSummary);
    clear(excludedList);
    excludedDetails.hidden = true;
    excludedDetails.setAttribute("hidden", "true");
    if (running) {
      count.textContent = "Accepting AI suggestions…";
      button.disabled = true;
      return;
    }
    const confidence = parseBulkConfidence(confidenceInput.value);
    if (!confidence.ok) {
      count.textContent = "Confidence must be between 0 and 1.";
      button.disabled = true;
      return;
    }
    if (companySelect.value === "") {
      count.textContent = "Choose a company to see suggestions.";
      button.disabled = true;
      return;
    }
    const filters = {
      company: companySelect.value,
      minConfidence: confidence.value,
      account: accountSelect.value,
      ...(opts.offeredAccounts === undefined
        ? {}
        : { offeredAccounts: opts.offeredAccounts(companySelect.value) }),
    };
    const selection = selectBulkAccept(opts.items, filters);
    const excludedCounts = new Map<BulkAcceptExclusion, number>();
    for (const item of opts.items) {
      if (!matchesBulkAcceptFilter(item, filters)) continue;
      const decision = bulkAcceptDecision(item, filters.offeredAccounts);
      if (decision === "accept") {
        // Worded outside the h() children so the branded company reaches the
        // one twin-aware account helper directly (B1c-2).
        const suggestionWords = suggestionLabels.label(
          item.company,
          item.suggestedAccount ?? "",
        );
        list.append(
          h(
            "li",
            {
              "data-role": "bulk-accept-row",
              "data-bulk-id": String(item.id),
              "data-bulk-decision": "accept",
              "data-bulk-reason": "",
            },
            [`${bulkPaymentWords(item)} — ${suggestionWords}`],
          ),
        );
        continue;
      }
      excludedCounts.set(decision, (excludedCounts.get(decision) ?? 0) + 1);
      excludedList.append(
        h(
          "li",
          {
            "data-role": "bulk-accept-row",
            "data-bulk-id": String(item.id),
            "data-bulk-decision": "excluded",
            "data-bulk-reason": decision,
          },
          [`${bulkPaymentWords(item)} — ${bulkExclusionCopy(decision)}`],
        ),
      );
    }
    for (const reason of [
      "not_one_tap",
      "judgment_account",
      "receipt_card_not_attached",
      "needs_account_choice",
      "removal",
      "month_closed",
      "needs_setup",
      "needs_one_at_a_time",
      "not_in_chart",
    ] as const) {
      const many = excludedCounts.get(reason) ?? 0;
      if (many === 0) continue;
      excludedSummary.append(
        h("p", { "data-role": "bulk-accept-excluded-line" }, [
          bulkExclusionSummary(reason, many),
        ]),
      );
    }
    if (selection.excluded.length > 0) {
      excludedDetails.hidden = false;
      excludedDetails.removeAttribute("hidden");
    }
    if (selection.accept.length > BULK_ACCEPT_MAX) {
      count.textContent = `${pluralText(selection.accept.length, "suggestion", "suggestions")} match. Bulk accept sends at most ${String(BULK_ACCEPT_MAX)}. Narrow the filter.`;
      button.disabled = true;
      return;
    }
    if (selection.accept.length === 0) {
      count.textContent = "No AI suggestions match this filter.";
      button.disabled = true;
      return;
    }
    // One helper for the count, so one card never reads "1 AI suggestions"
    // (rd-polish-rules §1).
    count.textContent = `${pluralText(selection.accept.length, "AI suggestion", "AI suggestions")} will be accepted.`;
    button.disabled = false;
  };

  companySelect.addEventListener("change", paint);
  confidenceInput.addEventListener("input", paint);
  accountSelect.addEventListener("change", paint);
  button.addEventListener("click", () => {
    const filters = currentFilter();
    if (filters === null) return;
    const selection = selectBulkAccept(opts.items, filters);
    if (
      selection.accept.length === 0 ||
      selection.accept.length > BULK_ACCEPT_MAX
    ) {
      return;
    }
    const ids = selection.accept.map((item) => item.id);
    const company = filters.company;
    running = true;
    button.disabled = true;
    opts.onBusyChange?.(true);
    void (async () => {
      const returned: {
        value: Awaited<ReturnType<typeof opts.client.bulkResolveQueue>> | null;
      } = { value: null };
      const outcome = await runWrite({
        key: cardWriteKey(panel, slugKey(company, `bulk-accept-${ids.join(",")}`)),
        scope: panel,
        gate: () => null,
        send: async () => {
          const body = await opts.client.bulkResolveQueue(company, ids);
          if (!Array.isArray(body.resolved) || !Array.isArray(body.skipped)) {
            throw new Error("Accept AI suggestions returned an unexpected response");
          }
          returned.value = body;
          return body;
        },
        stillCurrent: opts.stillCurrent,
      });
      running = false;
      opts.onBusyChange?.(false);
      if (outcome.kind === "landed" && returned.value !== null) {
        if (!opts.stillCurrent()) return;
        const result = returned.value;
        const skipped = result.skipped.length;
        opts.onAccepted(
          skipped === 0
            ? `Accepted ${pluralText(result.resolved.length, "AI suggestion", "AI suggestions")}.`
            : `Accepted ${pluralText(result.resolved.length, "AI suggestion", "AI suggestions")}. Skipped ${String(skipped)}.`,
        );
        return;
      }
      if (!opts.stillCurrent()) return;
      // Round 3, item 3: carry the decided plain sentence the single-card
      // writes use, not the server's body text.
      const failureText = paintOutcomeFailure(opts.live, outcome, opts.role);
      opts.live.textContent = `Accept AI suggestions failed: ${failureText}`;
      // X2: re-read first, so cards the server approved before the failure
      // leave the list; painting the old list would promise they are still
      // open.
      if (opts.onFailed !== undefined) {
        opts.onFailed(failureText);
        return;
      }
      paint();
    })();
  });

  const companyField = h("label", {}, ["Company", companySelect]);
  if (fixedCompany !== null) {
    companyField.hidden = true;
    companyField.setAttribute("hidden", "true");
  }
  const panel = h("section", { class: "bulk-accept", "data-role": "bulk-accept" }, [
    h("h2", {}, ["Accept AI suggestions"]),
    h("p", { class: "muted" }, [
      "Filter by company, confidence, and account, then accept. Owner, loan, and intercompany suggestions stay for one-by-one review. So do reversals, cards in a closed month, cards that need a setup fix, cards the AI flagged as unusual or possibly personal, cards whose receipt is not attached, cards that ask you to pick the account first, and suggestions that are not on the company's chart.",
    ]),
    companyField,
    h("label", {}, ["Minimum confidence", confidenceInput]),
    h("label", {}, ["Account", accountSelect]),
    count,
    list,
    excludedSummary,
    excludedDetails,
    button,
  ]);
  paint();
  return panel;
}
